/* Generato da scripts/extract-app.mjs — non modificare a mano; rieseguire extract + build */
import React from 'react';
import * as ReactDOM from 'react-dom/client';
import { ensureHtml2canvas, ensureMarked, ensurePdfLib, ensureXlsx } from './libs.js';
import {
  IcPlus,IcSearch,IcTrash,IcEdit,IcCheck,IcX,IcCopy,IcFile,IcImage,IcClip,IcUsers,IcHome,IcPkg,IcMail,IcPrint,
  IcSettings,IcChart,IcDl,IcUpload,IcAlert,IcEye,IcLock,IcLogout,IcUser,IcGrid,IcMenu,IcTrend,IcClipboard,IcCard,
  IcWrench,IcLayers,IcCogStack,IcDroplet,IcCalendar,IcSparkles,IcChevronLeft,
} from './icons.jsx';

const { useState, useEffect, useContext, createContext, useMemo, useCallback, useRef } = React;

const COMPLEVIA_FRONTEND_VERSION=window.CPLVCRM_BOOT?.version||'';

// ========== API CLIENT ==========
const API_BASE_PREFERRED=window.CPLVCRM_BOOT?.apiBasePreferred||'';
const API_BASES_RAW=window.CPLVCRM_BOOT?.apiBases||[];
const TRUSTED_API_BASES=[...new Set([API_BASE_PREFERRED,...API_BASES_RAW].filter(Boolean))];
const isSameOriginApiBase=base=>{
  const s=String(base||'');
  if(!s)return false;
  if(s.startsWith('/'))return true;
  try{return s.startsWith(window.location.origin)}catch(e){return false}
};
const isTrustedApiBase=base=>{
  if(!base)return false;
  if(TRUSTED_API_BASES.includes(base))return true;
  if(String(base).startsWith('/api/')&&TRUSTED_API_BASES.some(b=>String(b).startsWith('/api/')))return true;
  return false;
};
let sessionEstablished=false;
const pickDefaultApiBase=()=>{
  const same=TRUSTED_API_BASES.filter(isSameOriginApiBase);
  if(same.length)return same[0];
  return TRUSTED_API_BASES[0]||'';
};
const APP_LOGO_URL=window.CPLVCRM_BOOT?.logoUrl||'';
const INITIAL_BRAND_SETTINGS=window.CPLVCRM_BOOT?.brandSettings||{};
let MANUALE_CONTENT=window.CPLVCRM_BOOT?.manualeContent||'';
const CPLVCRM_PERMISSIONS_BOOT=window.CPLVCRM_BOOT?.permissions||{modules:{},actions:{},roles:[]};
let cplvcrmPermissionsState=CPLVCRM_PERMISSIONS_BOOT;
const getCplvcrmPermissions=()=>window.cplvcrmPermissions||cplvcrmPermissionsState||CPLVCRM_PERMISSIONS_BOOT;
const setCplvcrmPermissions=p=>{
  if(!p||typeof p!=='object')return;
  cplvcrmPermissionsState=p;
  window.cplvcrmPermissions=p;
};
setCplvcrmPermissions(CPLVCRM_PERMISSIONS_BOOT);
const APP_MODE=window.CPLVCRM_BOOT?.appMode||'suite';
const PAGE_MODULE=window.CPLVCRM_BOOT?.pageModule||'hub';
const PAGE_TITLE=window.CPLVCRM_BOOT?.pageTitle||'Complevia';
const DEFAULT_TAB_OVERRIDE=window.CPLVCRM_BOOT?.defaultTab||'';
const MODULE_SCOPE=Array.isArray(window.CPLVCRM_BOOT?.moduleScope)?window.CPLVCRM_BOOT.moduleScope.filter(Boolean):[];
const MODULE_LINKS=Array.isArray(window.CPLVCRM_BOOT?.moduleLinks)?window.CPLVCRM_BOOT.moduleLinks:[];
window.cplvcrmPermissions=getCplvcrmPermissions();
const API_BASES=TRUSTED_API_BASES;
let preferredApiBase=(()=>{
  const defaultBase=pickDefaultApiBase();
  try{
    const storedVersion=localStorage.getItem('complevia_api_base_version')||'';
    const stored=localStorage.getItem('complevia_api_base')||'';
    if(stored&&isTrustedApiBase(stored)&&storedVersion===String(COMPLEVIA_FRONTEND_VERSION||'')){
      if(isSameOriginApiBase(stored)||!TRUSTED_API_BASES.some(isSameOriginApiBase))return stored;
      try{
        localStorage.removeItem('complevia_api_base');
        localStorage.removeItem('complevia_api_base_version');
      }catch(e){}
    }
  }catch(e){}
  return defaultBase;
})();
const clearStoredApiBase=()=>{
  try{
    localStorage.removeItem('complevia_api_base');
    localStorage.removeItem('complevia_api_base_version');
  }catch(e){}
};
const EFFECTIVE_ROLE_ALIASES={
  ape_regina:'admin',admin:'admin',
  ape_stratega:'manager',capo_alveare:'manager',manager:'manager',
  rivenditore:'commerciale',agente:'commerciale',ape_coordinatrice:'commerciale',commerciale:'commerciale',
  ape_operaia:'operativo',hive_ospite:'operativo',operativo:'operativo'
};
function cplvcrmEffectiveRole(rawRole){
  const r=(rawRole||'').toString().toLowerCase().trim();
  return EFFECTIVE_ROLE_ALIASES[r]||'operativo';
}
function canAccessModule(moduleId,user){
  if(!user)return false;
  const raw=(user.ruolo||'').toString().toLowerCase().trim();
  if(moduleId==='assistenza'&&raw==='rivenditore')return false;
  const perms=getCplvcrmPermissions();
  const rule=(perms&&perms.modules&&perms.modules[moduleId])||[];
  return rule.indexOf(cplvcrmEffectiveRole(raw))!==-1;
}
function canPerformAction(action,user){
  const perms=getCplvcrmPermissions();
  if(!perms||!perms.actions||!Object.keys(perms.actions).length)return true;
  if(!(action in perms.actions))return cplvcrmEffectiveRole(user&&user.ruolo)==='admin';
  const rule=perms.actions[action];
  if(rule===null)return true;
  if(!user)return false;
  if(!Array.isArray(rule))return cplvcrmEffectiveRole(user.ruolo)==='admin';
  const raw=(user.ruolo||'').toString().toLowerCase().trim();
  if(action&&action.indexOf('assistenza_')===0&&raw==='rivenditore')return false;
  return rule.indexOf(cplvcrmEffectiveRole(raw))!==-1;
}
let csrfToken='';
const clientDebugCache=new Map();
// === Circuit breaker API: protegge l'app durante outage upstream (es. 502 Aruba) ===
// Quando l'upstream è giù, NON inondiamo il browser di richieste fallite (che a loro volta
// scatenavano altre chiamate a debug_log_client → "Failed to fetch" / risorse insufficienti).
const apiCircuit={failures:[],openUntil:0,upstreamOutage:false,outageReason:'',recoveryTimer:null};
const API_CIRCUIT_WINDOW_MS=10000;   // finestra entro cui contiamo i fallimenti
const API_CIRCUIT_THRESHOLD=3;       // 3 fallimenti in 10s → apri il circuit
const API_CIRCUIT_OPEN_MS=30000;     // tieni chiuse le chiamate per 30s
const apiCircuitIsOpen=()=>apiCircuit.openUntil>Date.now();
const apiCircuitRemainingSec=()=>Math.max(0,Math.ceil((apiCircuit.openUntil-Date.now())/1000));
function apiCircuitRecordFailure(reason='',isUpstreamOutage=false){
  const now=Date.now();
  apiCircuit.failures.push(now);
  apiCircuit.failures=apiCircuit.failures.filter(t=>now-t<API_CIRCUIT_WINDOW_MS);
  if(isUpstreamOutage){apiCircuit.upstreamOutage=true;apiCircuit.outageReason=reason}
  if(apiCircuit.failures.length>=API_CIRCUIT_THRESHOLD||isUpstreamOutage){
    apiCircuit.openUntil=now+API_CIRCUIT_OPEN_MS;
    try{window.dispatchEvent(new CustomEvent('complevia-api-status',{detail:{state:'warning',message:'Server temporaneamente non raggiungibile. Riprovo automaticamente fra '+Math.ceil(API_CIRCUIT_OPEN_MS/1000)+' secondi.'}}))}catch(e){}
    if(apiCircuit.recoveryTimer)clearTimeout(apiCircuit.recoveryTimer);
    apiCircuit.recoveryTimer=setTimeout(apiCircuitAutoRetry,API_CIRCUIT_OPEN_MS+200);
  }
}
function apiCircuitRecordSuccess(){
  if(apiCircuit.upstreamOutage||apiCircuit.openUntil>0||apiCircuit.failures.length>0){
    apiCircuit.failures=[];
    apiCircuit.openUntil=0;
    apiCircuit.upstreamOutage=false;
    apiCircuit.outageReason='';
    if(apiCircuit.recoveryTimer){clearTimeout(apiCircuit.recoveryTimer);apiCircuit.recoveryTimer=null}
    try{window.dispatchEvent(new CustomEvent('complevia-api-status',{detail:{state:'connected'}}))}catch(e){}
  }
}
async function apiCircuitAutoRetry(){
  apiCircuit.recoveryTimer=null;
  try{
    const base=preferredApiBase||API_BASES[0]||'';
    if(!base)return;
    const r=await fetch(apiUrl('ping',{},base),{credentials:'include',signal:typeof AbortSignal!=='undefined'&&AbortSignal.timeout?AbortSignal.timeout(15000):undefined});
    if(r&&r.ok){
      const d=await r.json().catch(()=>null);
      if(d&&d.success)apiCircuitRecordSuccess();
    }
  }catch(e){}
}
const apiBaseWithAction=(base,action)=>{
  const joiner=String(base||'').includes('?')?'&':'?';
  return `${base}${joiner}action=${encodeURIComponent(action)}`;
};
const apiUrl=(action,params={},baseOverride='')=>{
  const base=baseOverride||preferredApiBase||API_BASES[0]||'';
  let url=apiBaseWithAction(base,action);
  Object.entries(params||{}).forEach(([k,v])=>{url+=`&${k}=${encodeURIComponent(v)}`});
  if(String(url).startsWith('/'))url=new URL(url,window.location.origin).href;
  return url;
};
const orderedApiBases=()=>{
  const same=API_BASES.filter(isSameOriginApiBase);
  const remote=API_BASES.filter(b=>!isSameOriginApiBase(b));
  const pref=preferredApiBase&&API_BASES.includes(preferredApiBase)?preferredApiBase:null;
  const order=[];
  if(pref&&isSameOriginApiBase(pref))order.push(pref);
  same.forEach(b=>{if(b!==pref)order.push(b)});
  if(pref&&!isSameOriginApiBase(pref))order.push(pref);
  remote.forEach(b=>{if(b!==pref)order.push(b)});
  return [...new Set(order.length?order:API_BASES)];
};
const rememberApiBase=base=>{
  if(!isTrustedApiBase(base))return;
  if(!isSameOriginApiBase(base)&&API_BASES.some(isSameOriginApiBase))return;
  preferredApiBase=base;
  try{
    localStorage.setItem('complevia_api_base',base);
    localStorage.setItem('complevia_api_base_version',String(COMPLEVIA_FRONTEND_VERSION||''));
  }catch(e){}
  try{window.dispatchEvent(new CustomEvent('complevia-api-status',{detail:{state:'connected',base}}))}catch(e){}
};
const apiFetchTimeoutMs=12000;
const apiFetchOpts=(opts={})=>{
  if(typeof AbortSignal!=='undefined'&&typeof AbortSignal.timeout==='function'){
    return{...opts,signal:AbortSignal.timeout(apiFetchTimeoutMs)};
  }
  return opts;
};
const apiResponseLooksLikeHtml=(rawText='',contentType='')=>{
  const ct=String(contentType||'').toLowerCase();
  if(ct.includes('text/html'))return true;
  const sample=String(rawText||'').trim().slice(0,200).toLowerCase();
  return sample.startsWith('<!doctype')||sample.startsWith('<html')||sample.startsWith('<?xml')||sample.startsWith('<!');
};
const apiNonJsonErrorMessage=(rawText='',contentType='')=>apiResponseLooksLikeHtml(rawText,contentType)
  ?'Il server ha risposto con una pagina web invece di dati JSON. Di solito è cache/CDN, permalink o plugin disattivato: apri /?complevia_api=1&action=ping in un’altra scheda (deve mostrare JSON), poi Impostazioni → Permalink → Salva e ricarica l’app.'
  :'Errore di comunicazione con il server. Endpoint API non valido o risposta non riconosciuta.';
const markApiWarning=(message,base='')=>{
  try{window.dispatchEvent(new CustomEvent('complevia-api-status',{detail:{state:'warning',message,base}}))}catch(e){}
};
async function sendClientDebugLog(kind,message,details={},level='error'){
  // Evita storm: se l'upstream è giù o il circuit è aperto, NON inoltrare debug log
  // (raddoppierebbe il traffico verso un server già in crisi).
  if(apiCircuit.upstreamOutage||apiCircuitIsOpen())return;
  try{
    const cacheKey=`${kind}|${message}|${JSON.stringify(details||{})}`;
    const nowTs=Date.now();
    if(clientDebugCache.has(cacheKey)&&nowTs-clientDebugCache.get(cacheKey)<5000)return;
    clientDebugCache.set(cacheKey,nowTs);
    const headers={'Content-Type':'application/json'};
    if(csrfToken)headers['X-CSRF-Token']=csrfToken;
    await fetch(apiUrl('debug_log_client',{},preferredApiBase||API_BASES[0]||''),{
      method:'POST',
      headers,
      credentials:'same-origin',
      body:JSON.stringify({
        kind,
        message,
        details,
        level,
        url:window.location.href,
      }),
    });
  }catch(e){}
}
const calendarFeedUrl=token=>token?apiUrl('hive_calendar_feed',{token}):'';
const taskCalendarUrl=item=>{
  if(!item?.due_at)return'';
  const datePart=String(item.due_at).slice(0,10);
  const timePart=String(item.due_time||'').slice(0,5);
  const start=datePart.replaceAll('-','');
  let dates='';
  if(timePart){
    const startTs=`${start}T${timePart.replace(':','')}00`;
    const endDate=new Date(`${datePart}T${timePart}:00`);
    endDate.setHours(endDate.getHours()+1);
    const endTs=`${endDate.getFullYear()}${String(endDate.getMonth()+1).padStart(2,'0')}${String(endDate.getDate()).padStart(2,'0')}T${String(endDate.getHours()).padStart(2,'0')}${String(endDate.getMinutes()).padStart(2,'0')}00`;
    dates=`${startTs}/${endTs}`;
  }else{
    const endDate=new Date(`${datePart}T00:00:00`);
    endDate.setDate(endDate.getDate()+1);
    const end=`${endDate.getFullYear()}${String(endDate.getMonth()+1).padStart(2,'0')}${String(endDate.getDate()).padStart(2,'0')}`;
    dates=`${start}/${end}`;
  }
  const details=[
    item.description||'',
    item.notes?`Note: ${item.notes}`:'',
    item.hive_name?`Reparto: ${item.hive_name}`:'',
    item.created_by_name?`Assegnato da: ${item.created_by_name}`:'',
  ].filter(Boolean).join('\n');
  return`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(item.title||'Compito Complevia')}&dates=${dates}&details=${encodeURIComponent(details)}`;
};
async function api(action,method,body,params){
  method=method||'GET';body=body||null;params=params||{};
  // Circuit breaker: se l'upstream è in outage, fallisci subito (eccetto il ping di recovery)
  if(action!=='ping'&&apiCircuitIsOpen()){
    throw new Error('Server temporaneamente non raggiungibile. Riprovo automaticamente fra '+apiCircuitRemainingSec()+' secondi.');
  }
  // Use POST with _method override for PUT/DELETE (some hosts block them)
  const actualMethod=(method==='PUT'||method==='DELETE')?'POST':method;
  const headers={'Content-Type':'application/json'};
  if(actualMethod!=='GET'&&csrfToken)headers['X-CSRF-Token']=csrfToken;
  const opts={method:actualMethod,headers,credentials:'include'};
  if(body&&actualMethod!=='GET')opts.body=JSON.stringify(body);
  const bases=orderedApiBases();
  let lastError=new Error('Errore di comunicazione con il server.');
  for(let i=0;i<bases.length;i++){
    const base=bases[i];
    let url=apiUrl(action,params,base);
    if(method==='PUT'||method==='DELETE')url+=`&_method=${method}`;
    let res;
    try{
      res=await fetch(url,apiFetchOpts(opts));
    }catch(e){
      lastError=e instanceof Error?e:new Error(String(e));
      apiCircuitRecordFailure('network',false);
      await sendClientDebugLog('api-network',`Errore rete API ${action}`,{action,method,url,body,error:e?.message||String(e),base},'critical');
      if(i<bases.length-1)continue;
      break;
    }
    const nextCsrf=res.headers.get('x-csrf-token');
    if(nextCsrf)csrfToken=nextCsrf;
    let rawText='';
    try{rawText=await res.text()}catch(e){
      lastError=e instanceof Error?e:new Error(String(e));
      await sendClientDebugLog('api-read-error',`Impossibile leggere la risposta per ${action}`,{action,method,status:res.status,url,body,error:e?.message||String(e),base},'error');
      if(i<bases.length-1)continue;
      break;
    }
    let data;
    try{data=rawText?JSON.parse(rawText):{}}catch(e){
      const contentType=res.headers.get('content-type')||'';
      // Risposta HTML upstream = outage definitivo (Aruba 502 page, ecc.). Apri il circuit subito.
      const isHtmlOutage=apiResponseLooksLikeHtml(rawText,contentType);
      apiCircuitRecordFailure(isHtmlOutage?'upstream-html':'non-json',isHtmlOutage);
      await sendClientDebugLog('api-non-json',`Risposta non JSON per ${action}`,{action,method,status:res.status,url,body,contentType,response:rawText.substring(0,1000),base},'error');
      if(isSameOriginApiBase(base))clearStoredApiBase();
      lastError=new Error(apiNonJsonErrorMessage(rawText,contentType));
      if(i<bases.length-1)continue;
      break;
    }
    if(data&&data.csrf_token){
      csrfToken=data.csrf_token;
    }
    if(!res.ok){
      if(res.status===401){
        const sessionCritical=action==='me'||action==='logout'||(sessionEstablished&&['settings','frontend_permissions'].includes(action));
        if(sessionCritical){
          sessionEstablished=false;
          try{window.dispatchEvent(new CustomEvent('complevia-auth-expired',{detail:{action,url}}))}catch(e){}
        }
      }
      // 5xx upstream (502/503/504) = potenziale outage; apri circuit dopo soglia o subito su 5xx ripetuti
      if(res.status>=500)apiCircuitRecordFailure('5xx-'+res.status,res.status===502||res.status===503||res.status===504);
      await sendClientDebugLog('api-error',data.error||`Errore API ${action}`,{action,method,status:res.status,url,body,response:data,base},res.status>=500?'error':'warning');
      lastError=new Error(data.error||'Errore server');
      if((res.status===404||res.status===405||res.status>=500)&&i<bases.length-1)continue;
      break;
    }
    rememberApiBase(base);
    apiCircuitRecordSuccess();
    if(action==='me'||action==='login')sessionEstablished=true;
    return data;
  }
  clearStoredApiBase();
  preferredApiBase=API_BASE_PREFERRED||API_BASES[0]||'';
  markApiWarning('Impossibile contattare il server API. Verifica che il plugin Complevia sia attivo, apri in un’altra scheda: /?complevia_api=1&action=ping (deve rispondere JSON), poi ricarica. Se usi cache/CDN, escludi le URL con complevia_api.');
  if(cplvcrmOfflineActive()&&method!=='GET'&&CPLVCRM_OFFLINE_MUTATIONS.has(action)){
    return cplvcrmQueueOfflineMutation(action,method,body,params);
  }
  throw lastError;
}
const CPLVCRM_OFFLINE_QUEUE_KEY='complevia_offline_queue_v1';
const CPLVCRM_OFFLINE_MUTATIONS=new Set(['preventivo_create']);
const cplvcrmOfflineActive=()=>typeof navigator!=='undefined'&&(!navigator.onLine||(window.CPLVCRM_NATIVE&&window.CPLVCRM_NATIVE.offline===true));
function cplvcrmCodiceOffertaPrefix(user){
  const name=String(user?.nome||user?.username||'x').trim();
  const initial=(name[0]||'x').toUpperCase();
  return 'm'+(/^[A-Z]$/.test(initial)?initial:'X');
}
function cplvcrmFormatYmd(d){
  const dt=d instanceof Date?d:new Date();
  return String(dt.getFullYear()).slice(-2)+String(dt.getMonth()+1).padStart(2,'0')+String(dt.getDate()).padStart(2,'0');
}
function cplvcrmReadOfflineQueue(){
  try{const raw=localStorage.getItem(CPLVCRM_OFFLINE_QUEUE_KEY);return raw?JSON.parse(raw):[]}catch(e){return[]}
}
function cplvcrmWriteOfflineQueue(items){
  try{localStorage.setItem(CPLVCRM_OFFLINE_QUEUE_KEY,JSON.stringify(items||[]))}catch(e){}
}
function cplvcrmGenerateCodiceOfferta(user,existingCodes=[]){
  const prefix=cplvcrmCodiceOffertaPrefix(user)+cplvcrmFormatYmd(new Date());
  const escaped=prefix.replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
  const re=new RegExp('^'+escaped+'-(\\d+)$');
  let max=0;
  const bump=c=>{
    const m=String(c||'').match(re);
    if(m)max=Math.max(max,parseInt(m[1],10)||0);
  };
  (existingCodes||[]).forEach(bump);
  cplvcrmReadOfflineQueue().forEach(item=>{if(item?.body?.codice)bump(item.body.codice)});
  return prefix+'-'+String(max+1).padStart(3,'0');
}
function cplvcrmMergeOfflinePreventivi(rows){
  const ghosts=cplvcrmReadOfflineQueue().filter(q=>q.action==='preventivo_create').map(q=>({
    id:q.id,
    codice:q.body?.codice||'',
    cliente_id:q.body?.cliente_id||null,
    stato:q.body?.stato||'bozza',
    agente:q.body?.agente||'',
    data:q.body?.data||today(),
    _offline:true,
    righe:[],
  }));
  return [...ghosts,...(rows||[])];
}
function cplvcrmQueueOfflineMutation(action,method,body,params={}){
  const queue=cplvcrmReadOfflineQueue();
  const entry={id:'local-'+Date.now()+'-'+Math.random().toString(36).slice(2,8),action,method:method||'POST',body,params,createdAt:new Date().toISOString()};
  queue.push(entry);
  cplvcrmWriteOfflineQueue(queue);
  if(action==='preventivo_create'){
    return{success:true,data:{id:entry.id,codice:body?.codice||'',_offline:true},_queued:true};
  }
  return{success:true,data:{_offline:true},_queued:true};
}
async function cplvcrmFlushOfflineQueue(){
  if(cplvcrmOfflineActive())return{flushed:0,remaining:cplvcrmReadOfflineQueue().length};
  const queue=cplvcrmReadOfflineQueue();
  if(!queue.length)return{flushed:0,remaining:0};
  const remaining=[];
  let flushed=0;
  for(const item of queue){
    try{
      await api(item.action,item.method||'POST',item.body,item.params||{});
      flushed+=1;
    }catch(e){
      remaining.push(item);
    }
  }
  cplvcrmWriteOfflineQueue(remaining);
  try{window.dispatchEvent(new CustomEvent('complevia-offline-sync',{detail:{flushed,remaining:remaining.length}}))}catch(e){}
  return{flushed,remaining:remaining.length};
}
if(typeof window!=='undefined'){
  window.cplvcrmFlushOfflineQueue=cplvcrmFlushOfflineQueue;
  window.cplvcrmGenerateCodiceOfferta=cplvcrmGenerateCodiceOfferta;
}
const AGENT_MARGIN_CATEGORIES=['A','B','C','D'];
const LISTINO_TIPO_OPTIONS=[
  {value:'principale',label:'Listino principale'},
  {value:'ricambi',label:'Ricambi'},
];
const DISCOUNT_RULE_OPTIONS=[{value:'',label:'Nessuna'},{value:'A',label:'A'},{value:'B',label:'B'},{value:'C',label:'C'},{value:'D',label:'D'},{value:'E',label:'E'}];
const toNum=v=>typeof v==='number'?v:(parseFloat(v)||0);
const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
const round2=v=>Math.round((toNum(v)||0)*100)/100;
const normalizeListinoTipoClient=v=>String(v||'principale').trim().toLowerCase()==='ricambi'?'ricambi':'principale';
const listinoTipoLabel=v=>LISTINO_TIPO_OPTIONS.find(item=>item.value===normalizeListinoTipoClient(v))?.label||'Listino principale';
const duplicatedArticleValue=(value,suffix,joiner=' ')=>{
  const base=String(value||'').trim();
  const cleanSuffix=String(suffix||'').trim();
  if(!base)return cleanSuffix;
  if(!cleanSuffix)return base;
  const composed=`${joiner}${cleanSuffix}`.toLowerCase();
  if(base.toLowerCase().endsWith(composed)||base.toLowerCase().endsWith(cleanSuffix.toLowerCase()))return base;
  return `${base}${joiner}${cleanSuffix}`.trim();
};
const normalizeArticleMatchValue=v=>{
  const raw=String(v||'').trim().toLowerCase();
  if(!raw)return '';
  const normalized=typeof raw.normalize==='function'?raw.normalize('NFD'):raw;
  return normalized.replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]+/g,'');
};
const findArticleByReference=(articles,reference)=>{
  const raw=String(reference||'').trim();
  if(!raw||!Array.isArray(articles)||!articles.length)return null;
  const exactByName=articles.find(a=>String(a?.nome||'').trim()===raw);
  if(exactByName)return exactByName;
  const exactBySku=articles.find(a=>String(a?.sku||'').trim()===raw);
  if(exactBySku)return exactBySku;
  const key=normalizeArticleMatchValue(raw);
  if(!key)return null;
  return articles.find(a=>{
    const articleNameKey=normalizeArticleMatchValue(a?.nome);
    const articleSkuKey=normalizeArticleMatchValue(a?.sku);
    return articleNameKey===key||articleSkuKey===key;
  })||null;
};
const getArticoloBasePrice=art=>toNum(art?.prezzo_listino)||calcPrezzi(art||{}).prezzoConsigliato||0;
const normalizeDiscountRule=v=>{
  const code=String(v||'').trim().toUpperCase();
  return /^[A-Z]$/.test(code)?code:'';
};
const getDiscountRuleCode=art=>{
  const code=normalizeDiscountRule(art?.tipo_sconto||art?.tipoSconto||'');
  return ['A','B','C','D'].includes(code)?code:'';
};
const normalizeClienteTipo=v=>{
  const tipo=String(v||'cliente').trim().toLowerCase();
  if(tipo==='rivenditore')return 'rivenditore';
  if(tipo==='potenziale')return 'potenziale';
  return 'cliente';
};
const yesNoFlagToBool=v=>String(v||'').trim().toUpperCase()==='SI';
const boolToYesNo=v=>v?'SI':'';
const getClienteIdentityKeys=cliente=>{
  const keys=[];
  const piva=normalizeToken(cliente?.piva);
  const cf=normalizeToken(cliente?.cf);
  const email=normalizeToken(cliente?.email);
  const azienda=normalizeToken(cliente?.azienda);
  const nome=normalizeToken(cliente?.nome);
  const cognome=normalizeToken(cliente?.cognome);
  const citta=normalizeToken(cliente?.citta);
  const cap=normalizeToken(cliente?.cap);
  const telefono=normalizeToken(cliente?.telefono);
  if(piva)keys.push(`piva:${piva}`);
  if(cf)keys.push(`cf:${cf}`);
  if(email)keys.push(`email:${email}`);
  if(azienda&&citta)keys.push(`azienda_citta:${azienda}|${citta}`);
  if(azienda&&cap)keys.push(`azienda_cap:${azienda}|${cap}`);
  if(azienda&&telefono)keys.push(`azienda_tel:${azienda}|${telefono}`);
  if(nome&&cognome&&email)keys.push(`persona_email:${nome}|${cognome}|${email}`);
  if(nome&&cognome&&telefono)keys.push(`persona_tel:${nome}|${cognome}|${telefono}`);
  return [...new Set(keys)];
};
const CLIENTI_DUPLICATE_ACTION_OPTIONS=[
  {value:'merge',label:'Unisci con esistente'},
  {value:'skip',label:'Scarta nuovo duplicato'},
  {value:'keep_both',label:'Mantieni entrambi'},
  {value:'replace_existing',label:'Sostituisci esistente con nuovo'},
];
const clientiImportActionLabel=value=>CLIENTI_DUPLICATE_ACTION_OPTIONS.find(item=>item.value===value)?.label||'Inserisci come nuovo';
const settingEnabled=v=>!['0','false','no','off',''].includes(String(v??'1').trim().toLowerCase());
const MODULE_SETTING_KEYS={crm:'module_crm_enabled',preventivi:'module_preventivi_enabled',hive:'module_hive_enabled',assistenza:'module_assistenza_enabled',stimaConsumi:'module_stima_consumi_enabled'};
const getDiscountProfileFromCliente=cliente=>normalizeClienteTipo(cliente?.tipo)==='rivenditore'?'rivenditore':'cliente';
const getDiscountProfileLabel=profile=>profile==='rivenditore'?'Rivenditore':'Cliente';
const progressiveClientBDiscount=(settings,quantity)=>{
  const startQty=Math.max(1,toNum(settings?.client_discount_B_start_qty??10));
  const start=clamp(toNum(settings?.client_discount_B_start??0.10),0,0.95);
  const stepQty=Math.max(0.0001,toNum(settings?.client_discount_B_step_qty??10));
  const step=clamp(toNum(settings?.client_discount_B_step??0.10),0,0.95);
  const max=Math.max(start,clamp(toNum(settings?.client_discount_B_max??0.40),0,0.95));
  if(toNum(quantity)<startQty)return 0;
  const blocks=Math.floor(Math.max(0,toNum(quantity)-startQty)/stepQty);
  return clamp(start+(blocks*step),0,max);
};
const getDiscountPolicy=(art,settings,profile='cliente',quantity=0)=>{
  const normalizedProfile=profile==='rivenditore'?'rivenditore':'cliente';
  const ruleCode=getDiscountRuleCode(art);
  let maxDiscount=0;
  let label='Da definire';
  let note='Nessuna regola automatica configurata';
  if(normalizedProfile==='rivenditore'){
    const dealerMap={A:toNum(settings?.reseller_discount_A??0.20),B:toNum(settings?.reseller_discount_B??0.50),C:toNum(settings?.reseller_discount_C??0.15),D:toNum(settings?.reseller_discount_D??0.30)};
    if(ruleCode&&dealerMap[ruleCode]!=null){
      maxDiscount=clamp(dealerMap[ruleCode],0,0.95);
      label=`Rivenditore ${ruleCode} ${Math.round(maxDiscount*100)}%`;
      note='Sconto fisso per rivenditore';
    }
  }else if(ruleCode==='A'){
    maxDiscount=clamp(toNum(settings?.client_discount_A??0.10),0,0.95);
    label=`Cliente A ${Math.round(maxDiscount*100)}%`;
    note='Sconto fisso per ordini continuativi';
  }else if(ruleCode==='B'){
    maxDiscount=progressiveClientBDiscount(settings,quantity);
    label=`Cliente B fino al ${Math.round(maxDiscount*100)}%`;
    note='Progressivo: 10% a 10 Kg, 20% a 20 Kg, fino al 40%';
  }else if(ruleCode==='C'){
    maxDiscount=clamp(toNum(settings?.client_discount_C??0),0,0.95);
    label=maxDiscount>0?`Cliente C ${Math.round(maxDiscount*100)}%`:'Cliente C su trattativa';
    note='Da valutare in base alla trattativa';
  }else if(ruleCode==='D'){
    maxDiscount=clamp(toNum(settings?.client_discount_D??0),0,0.95);
    label=maxDiscount>0?`Cliente D ${Math.round(maxDiscount*100)}%`:'Cliente D su trattativa';
    note='Da valutare in base alla trattativa';
  }
  return{profile:normalizedProfile,profileLabel:getDiscountProfileLabel(normalizedProfile),ruleCode,maxDiscount,label,note};
};
const getDiscountSummary=(art,settings)=>{
  const dealer=getDiscountPolicy(art,settings,'rivenditore',0);
  const client=getDiscountPolicy(art,settings,'cliente',0);
  return{
    ruleCode:getDiscountRuleCode(art),
    dealer:dealer.ruleCode?dealer.label:'Da definire',
    client:!client.ruleCode?'Da definire':(client.ruleCode==='A'?client.label:client.note)
  };
};
const getArticoloMaxDiscount=(art,settings,profile='cliente',quantity=0)=>{
  if(!art)return 0;
  if(art.sconto_massimo!=null&&!Number.isNaN(parseFloat(art.sconto_massimo)))return clamp(toNum(art.sconto_massimo),0,0.95);
  return getDiscountPolicy(art,settings,profile,quantity).maxDiscount;
};

// ========== FORMATTING ==========
const fmt=(v,t='eur')=>{
  if(v!=null&&typeof v==='object'){console.error('fmt received object:',v);v=0;}
  const n=typeof v==='string'?parseFloat(v)||0:(typeof v==='number'?v:0);
  if(t==='pct')return(n*100).toFixed(1)+'%';
  return new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(n||0);
};
const fmtDate=(d)=>{
  if(!d)return '';
  if(typeof d==='object'){console.error('fmtDate received object:',d);return '';}
  try{return new Date(String(d)+'T00:00:00').toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit',year:'numeric'})}
  catch(e){return String(d)}
};
const fmtDateTime=(d)=>{
  if(!d)return'';
  try{return new Date(String(d)).toLocaleString('it-IT',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}
  catch(e){return String(d)}
};
const fmtTaskDue=(date,time='')=>{
  if(!date)return'';
  const day=fmtDate(date);
  return time?`${day} · ${time}`:day;
};
const fmtMinutes=min=>{
  const total=Math.max(0,parseInt(min)||0);
  if(total===0)return'0 min';
  const h=Math.floor(total/60);
  const m=total%60;
  if(h&&m)return`${h}h ${m}m`;
  if(h)return`${h}h`;
  return`${m} min`;
};
const ASSISTENZA_MODALITA=[
  {value:'remoto',label:'Da remoto'},
  {value:'telefono',label:'Al telefono'},
  {value:'visita',label:'Visita cliente'},
];
const ASSISTENZA_CATEGORIE=[
  {value:'assistenza',label:'Assistenza'},
  {value:'demo',label:'Demo cliente'},
  {value:'supporto_commerciale',label:'Supporto commerciale'},
  {value:'installazione',label:'Installazione'},
  {value:'formazione',label:'Formazione'},
  {value:'attivita_interna',label:'Attività interna'},
  {value:'studio_soluzione',label:'Studio soluzione'},
  {value:'preconsegna',label:'Preconsegna'},
  {value:'collaudo',label:'Collaudo'},
];
const ASSISTENZA_STATI=[
  {value:'bozza',label:'Bozza'},
  {value:'pianificata',label:'Pianificata'},
  {value:'confermata',label:'Confermata'},
  {value:'in_corso',label:'In corso'},
  {value:'completata',label:'Completata'},
  {value:'annullata',label:'Annullata'},
  {value:'rinviata',label:'Rinviata'},
];
const ASSISTENZA_FATTURABILITA=[
  {value:'si',label:'Fatturabile'},
  {value:'parziale',label:'Parziale'},
  {value:'no',label:'Non fatturabile'},
];
const ASSISTENZA_PRIORITA=[
  {value:'bassa',label:'Bassa'},
  {value:'media',label:'Media'},
  {value:'alta',label:'Alta'},
  {value:'urgente',label:'Urgente'},
];
const assistenzaModeLabel=value=>ASSISTENZA_MODALITA.find(item=>item.value===value)?.label||'Da remoto';
const assistenzaCategoriaLabel=value=>ASSISTENZA_CATEGORIE.find(item=>item.value===value)?.label||'Assistenza';
const assistenzaStatoLabel=value=>ASSISTENZA_STATI.find(item=>item.value===value)?.label||'Completata';
const assistenzaFatturabileLabel=value=>ASSISTENZA_FATTURABILITA.find(item=>item.value===value)?.label||'Fatturabile';
const assistenzaFirmaStatusLabel=(status,method)=>{
  if(status==='signed'&&method==='otp')return'Firmato via OTP';
  if(status==='signed'&&method==='draw')return'Firmato su schermo';
  if(status==='pending')return'OTP inviato';
  return'Non firmato';
};
const assistenzaFirmaStatusClass=(status,method)=>{
  if(status==='signed'&&method==='otp')return'bg-green-50 text-green-700 border-green-200';
  if(status==='signed'&&method==='draw')return'bg-emerald-50 text-emerald-700 border-emerald-200';
  if(status==='pending')return'bg-amber-50 text-amber-700 border-amber-200';
  return'bg-slate-100 text-slate-600 border-slate-200';
};
const assistenzaSectionTabs=[
  {id:'dashboard',label:'Dashboard'},
  {id:'agenda',label:'Agenda'},
  {id:'clienti',label:'Clienti e Diario'},
  {id:'statistiche',label:'Statistiche'},
];
const assistenzaMonthStart=date=>{
  const d=new Date(date.getFullYear(),date.getMonth(),1);
  const day=(d.getDay()+6)%7;
  d.setDate(d.getDate()-day);
  return d;
};
const assistenzaMonthCells=date=>{
  const start=assistenzaMonthStart(date);
  return Array.from({length:42},(_,idx)=>{
    const current=new Date(start);
    current.setDate(start.getDate()+idx);
    return current;
  });
};
const assistenzaMonthLabel=date=>date.toLocaleDateString('it-IT',{month:'long',year:'numeric'});
const assistenzaDayKey=date=>{
  const y=date.getFullYear();
  const m=String(date.getMonth()+1).padStart(2,'0');
  const d=String(date.getDate()).padStart(2,'0');
  return `${y}-${m}-${d}`;
};
const assistenzaAddMonths=(date,delta)=>new Date(date.getFullYear(),date.getMonth()+delta,1);
const assistenzaNormalizeIssue=text=>{
  const raw=String(text||'').trim().replace(/\s+/g,' ');
  if(!raw)return'Senza problematica';
  return raw.length>72?`${raw.slice(0,72)}...`:raw;
};
const AssistenzaBarList=({items=[],empty='Nessun dato disponibile',valueFormatter=v=>String(v),colorClass='bg-accent-400'})=>(
  <div className="space-y-3">
    {items.length===0?<div className="text-sm text-gray-400">{empty}</div>:items.map((item,idx)=>{
      const max=Math.max(...items.map(entry=>Number(entry.value)||0),1);
      const width=Math.max(8,Math.round(((Number(item.value)||0)/max)*100));
      return <div key={`${item.label}-${idx}`} className="space-y-1">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-slate-600 truncate">{S(item.label)}</span>
          <span className="font-medium text-navy-600 shrink-0">{valueFormatter(item.value)}</span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full rounded-full ${colorClass}`} style={{width:`${width}%`}}/>
        </div>
      </div>;
    })}
  </div>
);
const clientiPreferredLabel=(current,candidate)=>{
  const a=S(current||'').trim();
  const b=S(candidate||'').trim();
  if(!a)return b;
  const aUpper=a===a.toUpperCase();
  const bUpper=b===b.toUpperCase();
  if(aUpper&&!bUpper)return b;
  if(!aUpper&&bUpper)return a;
  return b.length>a.length?b:a;
};
const clientiParseMerceologie=value=>{
  const raw=String(value||'').trim();
  if(!raw)return['Non classificata'];
  const map=new Map();
  raw.split(/\s*\|\s*|\s*;\s*|\s*,\s*/).map(item=>item.trim().replace(/\s+/g,' ')).filter(Boolean).forEach(label=>{
    const key=normalizeToken(label);
    if(!key||key===normalizeToken('Non classificata'))return;
    map.set(key,clientiPreferredLabel(map.get(key),label));
  });
  const values=[...map.values()];
  return values.length?values:['Non classificata'];
};
const clientiNormalizeMerceologia=value=>clientiParseMerceologie(value).join(' | ');
const clientiBuildTopList=(rows,getValue,limit=8)=>{
  const map=new Map();
  rows.forEach(row=>{
    const label=String(getValue(row)||'').trim()||'Non classificata';
    const key=normalizeToken(label)||'non-classificata';
    const entry=map.get(key)||{label,count:0};
    entry.label=clientiPreferredLabel(entry.label,label);
    entry.count+=1;
    map.set(key,entry);
  });
  return [...map.values()]
    .map(item=>({label:item.label,value:item.count}))
    .sort((a,b)=>b.value-a.value||a.label.localeCompare(b.label,'it'))
    .slice(0,limit);
};
const clientiBuildCategoryStats=(rows,limit=10)=>{
  const map=new Map();
  rows.forEach(row=>{
    clientiParseMerceologie(row?.categoria_merceologica).forEach(label=>{
      const key=normalizeToken(label)||'non-classificata';
      const entry=map.get(key)||{label,count:0};
      entry.label=clientiPreferredLabel(entry.label,label);
      entry.count+=1;
      map.set(key,entry);
    });
  });
  return [...map.values()]
    .map(item=>({label:item.label,value:item.count}))
    .sort((a,b)=>b.value-a.value||a.label.localeCompare(b.label,'it'))
    .slice(0,limit);
};
const clientiCsvEscape=value=>{
  const text=String(value??'');
  const escaped=text.replace(/"/g,'""');
  return /[;"\n]/.test(escaped)?`"${escaped}"`:escaped;
};
const clientiDownloadCsv=(filename,cols,rows)=>{
  const header=cols.map(col=>clientiCsvEscape(col.label)).join(';');
  const body=rows.map(row=>cols.map(col=>clientiCsvEscape(typeof col.value==='function'?col.value(row):row[col.value])).join(';')).join('\n');
  const csv=`\uFEFF${header}\n${body}`;
  downloadBlob(new Blob([csv],{type:'text/csv;charset=utf-8'}),filename);
};
const AssistenzaCalendar=({events=[],monthDate,onPrev,onNext,onSelectClient})=>{
  const todayKey=assistenzaDayKey(new Date());
  const monthCells=assistenzaMonthCells(monthDate);
  const monthIndex=monthDate.getMonth();
  const grouped=events.reduce((acc,event)=>{
    const key=String(event.date||'');
    if(!key)return acc;
    if(!acc[key])acc[key]=[];
    acc[key].push(event);
    return acc;
  },{});
  Object.values(grouped).forEach(list=>list.sort((a,b)=>String(a.time||'').localeCompare(String(b.time||''))));
  return <Card title="Calendario Interventi Pianificati" action={<div className="flex items-center gap-2"><Btn s="sm" v="secondary" onClick={onPrev}>Mese prec.</Btn><span className="text-sm font-medium text-navy-600 min-w-[150px] text-center capitalize">{assistenzaMonthLabel(monthDate)}</span><Btn s="sm" v="secondary" onClick={onNext}>Mese succ.</Btn></div>}>
    <div className="grid grid-cols-7 gap-2 text-xs uppercase tracking-wide text-slate-400 mb-2">
      {['Lun','Mar','Mer','Gio','Ven','Sab','Dom'].map(day=><div key={day} className="px-2">{day}</div>)}
    </div>
    <div className="grid grid-cols-7 gap-2">
      {monthCells.map(day=>{
        const key=assistenzaDayKey(day);
        const dayEvents=grouped[key]||[];
        const isCurrentMonth=day.getMonth()===monthIndex;
        const isToday=key===todayKey;
        return <div key={key} className={`min-h-[118px] rounded-2xl border p-2 flex flex-col gap-2 ${isCurrentMonth?'bg-white border-slate-200':'bg-slate-50 border-slate-100 opacity-70'} ${isToday?'ring-2 ring-accent-300':''}`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-semibold ${isCurrentMonth?'text-navy-600':'text-slate-400'}`}>{day.getDate()}</span>
            {dayEvents.length?<span className="text-[11px] px-1.5 py-0.5 rounded-full bg-accent-50 text-accent-700">{dayEvents.length}</span>:null}
          </div>
          <div className="space-y-1 overflow-hidden">
            {dayEvents.slice(0,3).map((event,idx)=><button key={`${event.id}-${idx}`} onClick={()=>onSelectClient?.(event)} className={`w-full text-left px-2 py-1 rounded-xl text-[11px] leading-tight ${event.kind==='confermato'?'bg-emerald-50 text-emerald-700':'bg-blue-50 text-blue-700'} truncate`}>
              {event.time?`${event.time} · `:''}{S(event.title||'Intervento')}
            </button>)}
            {dayEvents.length>3?<div className="text-[11px] text-slate-400 px-1">+{dayEvents.length-3} altri</div>:null}
          </div>
        </div>;
      })}
    </div>
  </Card>;
};
const clienteDisplayName=cliente=>{
  if(cliente?.cliente_azienda)return S(cliente.cliente_azienda);
  if(cliente?.azienda)return S(cliente.azienda);
  const full=[cliente?.cliente_nome||cliente?.nome,cliente?.cliente_cognome||cliente?.cognome].filter(Boolean).join(' ').trim();
  return full||'Cliente senza nome';
};
const escapeHtml=str=>String(str??'').replace(/[&<>"']/g,ch=>({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[ch]||ch));
const escapeHtmlMultiline=str=>escapeHtml(str).replace(/\n/g,'<br/>');
async function downloadStoredFile(filename,downloadName=''){
  const fd=await api('file_download','GET',null,{filename});
  const bytes=Uint8Array.from(atob(fd.data||''),c=>c.charCodeAt(0));
  const blob=new Blob([bytes],{type:fd.mime||'application/octet-stream'});
  const url=URL.createObjectURL(blob);
  const a=document.createElement('a');
  a.href=url;
  a.download=downloadName||fd.filename||filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(()=>URL.revokeObjectURL(url),1000);
}
function downloadBlob(blob,filename){
  const url=URL.createObjectURL(blob);
  const a=document.createElement('a');
  a.href=url;
  a.download=filename||'documento.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(()=>URL.revokeObjectURL(url),1000);
}
async function embedPdfImageFromSource(pdf,src){
  if(!pdf||!src)return null;
  try{
    const bytes=await fetch(src).then(res=>res.arrayBuffer());
    const lower=String(src).toLowerCase();
    if(lower.includes('data:image/jpeg')||lower.includes('data:image/jpg')||lower.endsWith('.jpg')||lower.endsWith('.jpeg')){
      return await pdf.embedJpg(bytes);
    }
    return await pdf.embedPng(bytes);
  }catch(e){
    return null;
  }
}
async function decoratePdfWithPageChrome(pdf,{logoSrc='',headerMode='after-first'}={}){
  if(!pdf)return;
  const pages=pdf.getPages();
  if(!pages.length)return;
  const PDFLib=await ensurePdfLib();
  const {StandardFonts,rgb}=PDFLib;
  const font=await pdf.embedFont(StandardFonts.Helvetica);
  const logoImage=await embedPdfImageFromSource(pdf,logoSrc);
  const pageCount=pages.length;
  pages.forEach((page,index)=>{
    const{width,height}=page.getSize();
    const marginX=34;
    const footerY=18;
    const headerLineY=height-68;
    const showHeader=headerMode==='all'||(headerMode==='after-first'&&index>0);
    if(showHeader){
      if(logoImage){
        const maxW=92;
        const maxH=56.7;
        const scale=Math.min(maxW/logoImage.width,maxH/logoImage.height);
        const w=logoImage.width*scale;
        const h=logoImage.height*scale;
        page.drawImage(logoImage,{
          x:marginX,
          y:height-18-h,
          width:w,
          height:h
        });
      }
      page.drawLine({
        start:{x:marginX,y:headerLineY},
        end:{x:width-marginX,y:headerLineY},
        thickness:0.8,
        color:rgb(0.86,0.89,0.92)
      });
    }
    const footerText=`${index+1} di ${pageCount}`;
    const footerWidth=font.widthOfTextAtSize(footerText,9);
    page.drawText(footerText,{
      x:width-marginX-footerWidth,
      y:footerY,
      size:9,
      font,
      color:rgb(0.39,0.45,0.55)
    });
  });
}
async function appendElementAsPaginatedPdf(pdf,target,opts={}){
  if(!pdf||!target)return;
  const pageWidth=opts.pageWidth||595.28;
  const pageHeight=opts.pageHeight||841.89;
  const margin=opts.margin??20;
  const scale=opts.scale||2;
  const topInset=opts.topInset??0;
  const bottomInset=opts.bottomInset??0;
  const width=Math.max(
    Math.ceil(target.scrollWidth||0),
    Math.ceil(target.offsetWidth||0),
    Math.ceil(target.getBoundingClientRect?.().width||0),
    794
  );
  const height=Math.max(
    Math.ceil(target.scrollHeight||0),
    Math.ceil(target.offsetHeight||0),
    Math.ceil(target.getBoundingClientRect?.().height||0),
    1
  );
  await ensureHtml2canvas();
  const canvas=await html2canvas(target,{
    scale,
    useCORS:true,
    backgroundColor:'#ffffff',
    width,
    height,
    windowWidth:width,
    windowHeight:height,
    scrollX:0,
    scrollY:0,
  });
  const drawWidth=pageWidth-(margin*2);
  const drawHeight=canvas.height*(drawWidth/canvas.width);
  const contentTop=margin+topInset;
  const contentBottom=margin+bottomInset;
  const visibleHeight=Math.max(1,pageHeight-contentTop-contentBottom);
  const pageCanvasHeight=Math.max(1,Math.floor((visibleHeight*canvas.width)/drawWidth));
  let sourceTop=0;
  while(sourceTop<canvas.height-0.5){
    const sliceHeight=Math.min(pageCanvasHeight,canvas.height-sourceTop);
    const pageCanvas=document.createElement('canvas');
    pageCanvas.width=canvas.width;
    pageCanvas.height=sliceHeight;
    const ctx=pageCanvas.getContext('2d');
    ctx.fillStyle='#ffffff';
    ctx.fillRect(0,0,pageCanvas.width,pageCanvas.height);
    ctx.drawImage(
      canvas,
      0,sourceTop,canvas.width,sliceHeight,
      0,0,pageCanvas.width,pageCanvas.height
    );
    const imgBytes=await fetch(pageCanvas.toDataURL('image/png')).then(r=>r.arrayBuffer());
    const png=await pdf.embedPng(imgBytes);
    const sliceDrawHeight=sliceHeight*(drawWidth/canvas.width);
    const page=pdf.addPage([pageWidth,pageHeight]);
    page.drawImage(png,{x:margin,y:pageHeight-contentTop-sliceDrawHeight,width:drawWidth,height:sliceDrawHeight});
    sourceTop+=sliceHeight;
  }
}
function sanitizePdfFilename(name,fallback='documento'){
  const clean=String(name||'').trim().replace(/[\\/:*?"<>|]+/g,'-').replace(/\s+/g,' ');
  return `${clean||fallback}.pdf`;
}
async function createAssistenzaPdf(filename,build){
  const PDFLib=await ensurePdfLib();
  const {PDFDocument,StandardFonts,rgb}=PDFLib;
  const pdf=await PDFDocument.create();
  const fonts={
    regular:await pdf.embedFont(StandardFonts.Helvetica),
    bold:await pdf.embedFont(StandardFonts.HelveticaBold),
  };
  const colors={
    text:rgb(0.06,0.09,0.16),
    muted:rgb(0.39,0.45,0.55),
    accent:rgb(0.12,0.23,0.37),
    border:rgb(0.86,0.89,0.92),
    soft:rgb(0.97,0.98,0.99),
  };
  const pageSize={width:595.28,height:841.89,margin:40};
  let page=pdf.addPage([pageSize.width,pageSize.height]);
  let y=pageSize.height-pageSize.margin;
  const wrapText=(text,font,size,maxWidth)=>{
    const words=String(text||'').replace(/\r/g,'').split(/\s+/).filter(Boolean);
    if(words.length===0)return[''];
    const lines=[];
    let current='';
    words.forEach(word=>{
      const test=current?`${current} ${word}`:word;
      if(font.widthOfTextAtSize(test,size)<=maxWidth){
        current=test;
      }else{
        if(current)lines.push(current);
        if(font.widthOfTextAtSize(word,size)<=maxWidth){
          current=word;
        }else{
          let chunk='';
          [...word].forEach(char=>{
            const probe=chunk+char;
            if(font.widthOfTextAtSize(probe,size)<=maxWidth){
              chunk=probe;
            }else{
              if(chunk)lines.push(chunk);
              chunk=char;
            }
          });
          current=chunk;
        }
      }
    });
    if(current)lines.push(current);
    return lines;
  };
  const newPage=()=>{
    page=pdf.addPage([pageSize.width,pageSize.height]);
    y=pageSize.height-pageSize.margin;
  };
  const ensureSpace=needed=>{
    if(y-needed<pageSize.margin)newPage();
  };
  const drawTextLines=(text,{x=pageSize.margin,maxWidth=pageSize.width-pageSize.margin*2,size=11,font='regular',color=colors.text,leading=4}={})=>{
    const fontRef=fonts[font]||fonts.regular;
    const lines=String(text??'').split('\n').flatMap(part=>wrapText(part||' ',fontRef,size,maxWidth));
    lines.forEach(line=>{
      ensureSpace(size+leading);
      page.drawText(String(line||' '),{x,y,size,font:fontRef,color});
      y-=size+leading;
    });
    return lines.length*(size+leading);
  };
  const gap=value=>{y-=value||8;};
  const drawRule=()=>{
    ensureSpace(12);
    page.drawLine({start:{x:pageSize.margin,y},end:{x:pageSize.width-pageSize.margin,y},thickness:1,color:colors.border});
    y-=12;
  };
  const drawLabeledValue=(label,value,opts={})=>{
    drawTextLines(label,{...opts,size:9,font:'bold',color:colors.muted,leading:3});
    drawTextLines(value||'-',{...opts,size:11,font:'regular',color:colors.text,leading:4});
  };
  const drawInfoGrid=items=>{
    const colGap=16;
    const cols=items.length>=3?3:items.length===2?2:1;
    const width=(pageSize.width-pageSize.margin*2-colGap*(cols-1))/cols;
    let rowMaxBottom=y;
    items.forEach((item,index)=>{
      const col=index%cols;
      if(col===0)rowMaxBottom=y;
      const rowY=y;
      const x=pageSize.margin+(width+colGap)*col;
      const savedY=y;
      y=rowY;
      page.drawRectangle({x,y:rowY-72,width,height:72,borderWidth:1,borderColor:colors.border,color:colors.soft});
      y=rowY-14;
      drawLabeledValue(item.label,item.value,{x:x+10,maxWidth:width-20});
      rowMaxBottom=Math.min(rowMaxBottom,y);
      y=savedY;
      if(col===cols-1||index===items.length-1){
        y=rowMaxBottom-12;
      }
    });
  };
  const drawTable=(headers,rows,widths)=>{
    const totalWidth=pageSize.width-pageSize.margin*2;
    const normalized=widths||headers.map(()=>1/headers.length);
    const colWidths=normalized.map(w=>w*totalWidth);
    const drawRow=(cells,{header=false}={})=>{
      const fontRef=header?fonts.bold:fonts.regular;
      const size=header?9:10;
      const paddingX=6;
      const paddingY=7;
      const lineSets=cells.map((cell,idx)=>wrapText(String(cell??''),fontRef,size,Math.max(10,colWidths[idx]-paddingX*2)));
      const lineCount=Math.max(...lineSets.map(lines=>Math.max(1,lines.length)),1);
      const rowHeight=paddingY*2+(lineCount*(size+3));
      ensureSpace(rowHeight+6);
      let x=pageSize.margin;
      cells.forEach((cell,idx)=>{
        page.drawRectangle({x,y:y-rowHeight,width:colWidths[idx],height:rowHeight,borderWidth:1,borderColor:colors.border,color:header?colors.soft:rgb(1,1,1)});
        let lineY=y-paddingY-size;
        lineSets[idx].forEach(line=>{
          page.drawText(String(line||' '),{x:x+paddingX,y:lineY,size,font:fontRef,color:header?colors.muted:colors.text});
          lineY-=size+3;
        });
        x+=colWidths[idx];
      });
      y-=rowHeight;
    };
    drawRow(headers,{header:true});
    rows.forEach(row=>drawRow(row));
    y-=10;
  };
  const embedImage=async src=>{
    if(!src)return null;
    try{
      const bytes=await fetch(src).then(res=>res.arrayBuffer());
      const lower=String(src).toLowerCase();
      if(lower.includes('data:image/jpeg')||lower.includes('data:image/jpg')||lower.endsWith('.jpg')||lower.endsWith('.jpeg')){
        return await pdf.embedJpg(bytes);
      }
      return await pdf.embedPng(bytes);
    }catch(e){
      return null;
    }
  };
  const drawStandardHeader=async({settings={},clientName='',clientContact='',clientAddress='',clientMeta=[],docLabel='Offerta',code='',dateText='',extraText=''}={})=>{
    ensureSpace(210);
    const az=getAzienda(settings||{});
    const contentWidth=pageSize.width-pageSize.margin*2;
    const topY=y;
    const logoWidth=136;
    const topGap=18;
    const companyWidth=188;
    const topHeight=112;
    const clientBoxWidth=contentWidth-176-topGap;
    const docBoxWidth=158;
    const secondGap=18;
    const secondY=topY-topHeight-18;
    const secondHeight=96;
    const drawBox=(x,top,width,height,fill=rgb(1,1,1))=>{
      page.drawRectangle({x,y:top-height,width,height,borderWidth:1,borderColor:colors.border,color:fill});
    };
    const drawLinesAt=(lines,{x,top,maxWidth,size=11,font='regular',color=colors.text,leading=4,align='left'}={})=>{
      const fontRef=fonts[font]||fonts.regular;
      let cursor=top;
      lines.forEach(rawLine=>{
        const wrapped=wrapText(rawLine||' ',fontRef,size,maxWidth);
        wrapped.forEach(line=>{
          const lineWidth=fontRef.widthOfTextAtSize(String(line||' '),size);
          const textX=align==='right'?x+maxWidth-lineWidth:x;
          page.drawText(String(line||' '),{x:textX,y:cursor-size,size,font:fontRef,color});
          cursor-=size+leading;
        });
      });
      return cursor;
    };
    const logoX=pageSize.margin;
    const companyX=pageSize.width-pageSize.margin-companyWidth;
    drawBox(logoX,topY,logoWidth,topHeight,rgb(1,1,1));
    drawBox(companyX,topY,companyWidth,topHeight,rgb(1,1,1));
    const logoImage=await embedImage(settings?.az_logo||'');
    if(logoImage){
      const maxW=logoWidth-20;
      const maxH=topHeight-20;
      const scale=Math.min(maxW/logoImage.width,maxH/logoImage.height);
      const w=logoImage.width*scale;
      const h=logoImage.height*scale;
      page.drawImage(logoImage,{x:logoX+(logoWidth-w)/2,y:topY-topHeight+(topHeight-h)/2,width:w,height:h});
    }else{
      drawLinesAt([az.nome||'Azienda',az.sottotitolo||''],{x:logoX+12,top:topY-28,maxWidth:logoWidth-24,size:az.sottotitolo?18:20,font:'bold',color:colors.accent});
      if(az.sottotitolo){
        drawLinesAt([az.sottotitolo],{x:logoX+12,top:topY-60,maxWidth:logoWidth-24,size:9,font:'regular',color:colors.muted});
      }
    }
    drawLinesAt(['Azienda'],{x:companyX+16,top:topY-14,maxWidth:companyWidth-32,size:8,font:'bold',color:colors.muted,align:'right'});
    drawLinesAt(
      [
        az.nome||'',
        az.sottotitolo||'',
        az.via||'',
        [az.cap,az.citta,az.provincia,az.paese].filter(Boolean).join(' '),
        az.tel?`Tel. ${az.tel}`:'',
        az.email||'',
        az.web||'',
        az.piva?`P.IVA ${az.piva}`:'',
      ].filter(Boolean),
      {x:companyX+16,top:topY-28,maxWidth:companyWidth-32,size:9,font:'regular',color:colors.text,leading:3,align:'right'}
    );
    const clientX=pageSize.margin;
    const docX=pageSize.width-pageSize.margin-docBoxWidth;
    drawBox(clientX,secondY,clientBoxWidth,secondHeight,colors.soft);
    drawBox(docX,secondY,docBoxWidth,secondHeight,rgb(1,1,1));
    drawLinesAt(['Cliente'],{x:clientX+16,top:secondY-14,maxWidth:clientBoxWidth-32,size:8,font:'bold',color:colors.muted});
    let clientCursor=drawLinesAt([clientName||'-'],{x:clientX+16,top:secondY-30,maxWidth:clientBoxWidth-32,size:15,font:'bold',color:colors.accent});
    if(clientContact){
      clientCursor=drawLinesAt([`Alla c.a. ${clientContact}`],{x:clientX+16,top:clientCursor-2,maxWidth:clientBoxWidth-32,size:10,font:'regular',color:colors.text});
    }
    if(clientAddress){
      clientCursor=drawLinesAt([clientAddress],{x:clientX+16,top:clientCursor-6,maxWidth:clientBoxWidth-32,size:10,font:'regular',color:colors.text});
    }
    if((clientMeta||[]).length){
      drawLinesAt([(clientMeta||[]).join('   ')],{x:clientX+16,top:secondY-secondHeight+24,maxWidth:clientBoxWidth-32,size:9,font:'regular',color:colors.muted});
    }
    drawLinesAt([docLabel],{x:docX+14,top:secondY-14,maxWidth:docBoxWidth-28,size:8,font:'bold',color:colors.muted,align:'right'});
    drawLinesAt([code?`N. ${code}`:'-'],{x:docX+14,top:secondY-30,maxWidth:docBoxWidth-28,size:11,font:'bold',color:colors.accent,align:'right'});
    drawLinesAt([dateText?`Data ${dateText}`:''],{x:docX+14,top:secondY-48,maxWidth:docBoxWidth-28,size:9,font:'regular',color:colors.text,align:'right'});
    if(extraText){
      drawLinesAt([extraText],{x:docX+14,top:secondY-68,maxWidth:docBoxWidth-28,size:9,font:'regular',color:colors.muted,align:'right'});
    }
    y=secondY-secondHeight-22;
  };
  await build({pdf,page,fonts,colors,pageSize,ensureSpace,drawTextLines,drawRule,drawLabeledValue,drawInfoGrid,drawTable,gap,getY:()=>y,setY:value=>{y=value},newPage,drawStandardHeader,embedImage});
  await decoratePdfWithPageChrome(pdf,{logoSrc:'',headerMode:'none'});
  const bytes=await pdf.save();
  downloadBlob(new Blob([bytes],{type:'application/pdf'}),sanitizePdfFilename(filename));
}
async function exportClientiAnalyticsPdf({settings,filters,kpi,sections,totalRecords}){
  const PDFLib=await ensurePdfLib();
  const {PDFDocument,StandardFonts,rgb}=PDFLib;
  const pdf=await PDFDocument.create();
  const fonts={regular:await pdf.embedFont(StandardFonts.Helvetica),bold:await pdf.embedFont(StandardFonts.HelveticaBold)};
  const colors={text:rgb(0.06,0.09,0.16),muted:rgb(0.42,0.47,0.56),accent:rgb(0.12,0.23,0.37),border:rgb(0.86,0.89,0.92),soft:rgb(0.97,0.98,0.99)};
  const pageSize={width:595.28,height:841.89,margin:42};
  let page=pdf.addPage([pageSize.width,pageSize.height]);
  let y=pageSize.height-pageSize.margin;
  const newPage=()=>{page=pdf.addPage([pageSize.width,pageSize.height]);y=pageSize.height-pageSize.margin;};
  const ensureSpace=needed=>{if(y-needed<pageSize.margin)newPage();};
  const wrapText=(text,font,size,maxWidth)=>{
    const words=String(text||'').split(/\s+/).filter(Boolean);
    if(!words.length)return[''];
    const lines=[];let current='';
    words.forEach(word=>{
      const probe=current?`${current} ${word}`:word;
      if(font.widthOfTextAtSize(probe,size)<=maxWidth){current=probe;return;}
      if(current)lines.push(current);
      current=word;
    });
    if(current)lines.push(current);
    return lines;
  };
  const drawLines=(text,{x=pageSize.margin,maxWidth=pageSize.width-pageSize.margin*2,size=11,font='regular',color=colors.text,leading=4}={})=>{
    const ref=fonts[font]||fonts.regular;
    String(text??'').split('\n').flatMap(line=>wrapText(line||' ',ref,size,maxWidth)).forEach(line=>{
      ensureSpace(size+leading);
      page.drawText(String(line||' '),{x,y,size,font:ref,color});
      y-=size+leading;
    });
  };
  const gap=v=>{y-=v||8;};
  const rule=()=>{ensureSpace(14);page.drawLine({start:{x:pageSize.margin,y},end:{x:pageSize.width-pageSize.margin,y},thickness:1,color:colors.border});y-=12;};
  const drawKpiGrid=items=>{
    const cols=2,gapX=14,boxH=58,boxW=(pageSize.width-pageSize.margin*2-gapX)/cols;
    for(let i=0;i<items.length;i+=cols){
      ensureSpace(boxH+10);
      const rowItems=items.slice(i,i+cols);
      const rowTop=y;
      rowItems.forEach((item,colIdx)=>{
        const x=pageSize.margin+(boxW+gapX)*colIdx;
        page.drawRectangle({x,y:rowTop-boxH,width:boxW,height:boxH,borderWidth:1,borderColor:colors.border,color:colors.soft});
        page.drawText(String(item.value??0),{x:x+12,y:rowTop-24,size:18,font:fonts.bold,color:colors.accent});
        page.drawText(String(item.label||''),{x:x+12,y:rowTop-42,size:9,font:fonts.bold,color:colors.muted});
      });
      y-=boxH+10;
    }
  };
  const drawSection=(title,items)=>{
    if(!items?.length)return;
    ensureSpace(34);
    page.drawText(String(title),{x:pageSize.margin,y,size:13,font:fonts.bold,color:colors.accent});
    y-=18;
    items.forEach((item,idx)=>{
      ensureSpace(20);
      const rowY=y;
      page.drawText(`${idx+1}. ${String(item.label||'N/D')}`,{x:pageSize.margin,y:rowY,size:10.5,font:fonts.regular,color:colors.text});
      const valueText=String(item.value??0);
      const valueWidth=fonts.bold.widthOfTextAtSize(valueText,10.5);
      page.drawText(valueText,{x:pageSize.width-pageSize.margin-valueWidth,y:rowY,size:10.5,font:fonts.bold,color:colors.accent});
      y-=16;
      page.drawLine({start:{x:pageSize.margin,y},end:{x:pageSize.width-pageSize.margin,y},thickness:0.6,color:colors.border});
      y-=8;
    });
    gap(6);
  };
  const filterLines=[
    filters?.tipo?`Tipologia: ${filters.tipo}`:'',
    filters?.regione?`Regione: ${filters.regione}`:'',
    filters?.provincia?`Provincia: ${filters.provincia}`:'',
    filters?.citta?`Città: ${filters.citta}`:'',
    filters?.categoria?`Categoria: ${filters.categoria}`:'',
    filters?.pagamento?`Pagamento: ${filters.pagamento}`:'',
    filters?.search?`Ricerca: ${filters.search}`:'',
  ].filter(Boolean);
  drawLines(settings?.az_nome||'Complevia',{size:18,font:'bold',color:colors.accent});
  drawLines('Report contatti',{size:22,font:'bold',color:colors.text,leading:6});
  drawLines(`Generato il ${new Date().toLocaleString('it-IT')}`,{size:10,color:colors.muted});
  drawLines(`Totale anagrafiche analizzate: ${totalRecords||0}`,{size:10,color:colors.muted});
  if(filterLines.length){
    gap(6);
    drawLines(`Filtri attivi: ${filterLines.join(' · ')}`,{size:10,color:colors.muted});
  }
  gap(10);
  rule();
  drawKpiGrid([
    {label:'Totale contatti',value:kpi?.totale||0},
    {label:'Clienti',value:kpi?.clientiCount||0},
    {label:'Rivenditori',value:kpi?.rivenditoriCount||0},
    {label:'Potenziali',value:kpi?.potenzialiCount||0},
    {label:'Categorie',value:kpi?.categorieCount||0},
    {label:'Regioni',value:kpi?.regioniCount||0},
  ]);
  gap(4);
  drawSection('Categorie merceologiche',sections?.merceologiche||[]);
  drawSection('Tipologia contatto',sections?.tipo||[]);
  drawSection('Regioni',sections?.regioni||[]);
  drawSection('Province',sections?.province||[]);
  drawSection('Città',sections?.citta||[]);
  drawSection('Pagamenti',sections?.pagamenti||[]);
  await decoratePdfWithPageChrome(pdf,{logoSrc:'',headerMode:'none'});
  const bytes=await pdf.save();
  downloadBlob(new Blob([bytes],{type:'application/pdf'}),sanitizePdfFilename(`report_contatti_${today()}`,'report_contatti'));
}
async function exportAssistenzaInterventoPdf(intervento,cliente,settings={}){
  const azienda=clienteDisplayName(cliente);
  const contatto=[cliente?.nome,cliente?.cognome].filter(Boolean).join(' ').trim();
  const sede=[cliente?.via,cliente?.cap,cliente?.citta,normalizeProvincia(cliente?.provincia),cliente?.regione].filter(Boolean).join(', ');
  const allegati=[
    intervento?.scheda_tecnica_original_name?`Scheda tecnica: ${intervento.scheda_tecnica_original_name}`:'',
    intervento?.test_ugelli_original_name?`Test ugelli: ${intervento.test_ugelli_original_name}`:'',
  ].filter(Boolean).join('\n');
  const codice=String(intervento?.codice||'scheda-intervento').trim()||'scheda-intervento';
  await createAssistenzaPdf(codice,async({drawTextLines,drawRule,drawInfoGrid,gap,drawStandardHeader,embedImage,page,pageSize,ensureSpace,getY,setY})=>{
    await drawStandardHeader({
      settings,
      clientName:azienda,
      clientContact:contatto,
      clientAddress:sede,
      clientMeta:[cliente?.piva?`P.IVA: ${cliente.piva}`:'',cliente?.email?`Email: ${cliente.email}`:'',cliente?.telefono?`Tel: ${cliente.telefono}`:''].filter(Boolean),
      docLabel:'Scheda intervento',
      code:intervento?.codice||'-',
      dateText:fmtDate(intervento?.data_intervento||String(intervento?.created_at||'').slice(0,10)||''),
      extraText:intervento?.tecnico_nome||intervento?.tecnico_user_nome?`Tecnico: ${intervento?.tecnico_nome||intervento?.tecnico_user_nome}`:'',
    });
    drawInfoGrid([
      {label:'Tecnico',value:intervento?.tecnico_nome||intervento?.tecnico_user_nome||'-'},
      {label:'Modalita',value:assistenzaModeLabel(intervento?.modalita||'remoto')},
      {label:'Tempo impiegato',value:fmtMinutes(intervento?.tempo_minuti||0)},
    ]);
    const oreImpiegate=Number(intervento?.ore_contratto_utilizzate||0);
    const oreResidue=intervento?.ore_contratto_residue;
    if(oreImpiegate>0||oreResidue!=null){
      drawInfoGrid([
        {label:'Ore contratto (questo intervento)',value:fmtOreContratto(oreImpiegate)},
        {label:'Ore residue contratto',value:oreResidue!=null?fmtOreContratto(oreResidue):'—'},
        {label:'Pacchetto',value:intervento?.contratto_attivo?.pacchetto_label||intervento?.contratto_cliente_attivo?.pacchetto_label||'—'},
      ]);
    }
    if(intervento?.orario_intervento){
      drawInfoGrid([{label:'Orario intervento',value:intervento.orario_intervento}]);
    }
    drawInfoGrid([
      {label:'Macchina / Modello',value:intervento?.stampante_modello||intervento?.macchina_modello||'-'},
      {label:'Numero seriale',value:intervento?.stampante_numero_seriale||intervento?.numero_seriale||'-'},
      {label:'Titolo intervento',value:intervento?.titolo||'-'},
    ]);
    drawRule();
    drawTextLines('Problematica segnalata',{size:12,font:'bold'});
    drawTextLines(intervento?.problematica||'-',{size:11});
    gap(8);
    drawTextLines('Attivita svolta',{size:12,font:'bold'});
    drawTextLines(intervento?.attivita_svolta||'-',{size:11});
    gap(8);
    drawTextLines('Esito intervento',{size:12,font:'bold'});
    drawTextLines(intervento?.esito||'-',{size:11});
    gap(8);
    drawTextLines('Materiale utilizzato',{size:12,font:'bold'});
    drawTextLines(intervento?.materiali_usati||'-',{size:11});
    gap(8);
    drawTextLines('Allegati intervento',{size:12,font:'bold'});
    drawTextLines(allegati||'Nessun allegato registrato',{size:11});
    gap(16);
    drawRule();
    drawTextLines(`Tecnico esecutore: ${intervento?.tecnico_nome||intervento?.tecnico_user_nome||'-'}`,{size:11,font:'bold'});
    gap(10);
    drawTextLines('Firma cliente / presa visione',{size:11,font:'bold'});
    if(oreImpiegate>0||oreResidue!=null){
      drawTextLines('Il cliente prende visione del consumo ore sul contratto di assistenza:',{size:10,color:PDFLib.rgb(0.39,0.45,0.55)});
      drawInfoGrid([
        {label:'Ore impiegate (intervento)',value:fmtOreContratto(oreImpiegate)},
        {label:'Ore residue (dopo intervento)',value:oreResidue!=null?fmtOreContratto(oreResidue):'—'},
      ]);
      gap(6);
    }
    drawInfoGrid([
      {label:'Stato firma',value:assistenzaFirmaStatusLabel(intervento?.firma_cliente_status||'unsigned',intervento?.firma_cliente_method||'')},
      {label:'Firmatario',value:intervento?.firma_cliente_nome||contatto||'-'},
      {label:'Email firma',value:intervento?.cliente_email_firma||cliente?.email||'-'},
    ]);
    if(intervento?.firma_cliente_signed_at){
      drawInfoGrid([{label:'Data firma',value:fmtDateTime(intervento.firma_cliente_signed_at)}]);
    }
    if(intervento?.firma_cliente_signature_data){
      const signatureImage=await embedImage(intervento.firma_cliente_signature_data);
      if(signatureImage){
        ensureSpace(100);
        const boxHeight=78;
        const boxWidth=pageSize.width-pageSize.margin*2;
        const currentY=getY();
        page.drawRectangle({x:pageSize.margin,y:currentY-boxHeight,width:boxWidth,height:boxHeight,borderWidth:1,borderColor:PDFLib.rgb(0.86,0.89,0.93),color:PDFLib.rgb(0.99,0.99,1)});
        const maxW=boxWidth-24;
        const maxH=boxHeight-20;
        const scale=Math.min(maxW/signatureImage.width,maxH/signatureImage.height);
        const w=signatureImage.width*scale;
        const h=signatureImage.height*scale;
        page.drawImage(signatureImage,{x:pageSize.margin+12,y:currentY-boxHeight+(boxHeight-h)/2,width:w,height:h});
        setY(currentY-boxHeight-10);
      }
    }
    gap(22);
    if(intervento?.firma_cliente_status!=='signed'){
      drawTextLines('Firma cliente non ancora acquisita',{size:11,color:PDFLib.rgb(0.39,0.45,0.55)});
    }
  });
}
async function exportAssistenzaPreventivoPdf(preventivo,cliente,intervento,settings={}){
  const clienteNome=clienteDisplayName(cliente);
  const contatto=[cliente?.nome,cliente?.cognome].filter(Boolean).join(' ').trim();
  const indirizzo=[cliente?.via,cliente?.cap,cliente?.citta,normalizeProvincia(cliente?.provincia),cliente?.regione].filter(Boolean).join(', ');
  const stampante=preventivo?.stampante_modello||intervento?.stampante_modello||intervento?.macchina_modello||'-';
  const seriale=preventivo?.stampante_numero_seriale||intervento?.stampante_numero_seriale||intervento?.numero_seriale||'-';
  const rows=[];
  if(Number(preventivo?.km_qta||0)>0)rows.push([String(rows.length+1),preventivo?.km_articolo_nome||'KM',String(preventivo?.km_qta||0),fmt(preventivo?.km_prezzo_unitario||0),fmt(preventivo?.totale_km||0)]);
  if(Number(preventivo?.ore_viaggio_qta||0)>0)rows.push([String(rows.length+1),preventivo?.ore_viaggio_articolo_nome||'Ore viaggio',String(preventivo?.ore_viaggio_qta||0),fmt(preventivo?.ore_viaggio_prezzo_unitario||0),fmt(preventivo?.totale_ore_viaggio||0)]);
  if(Number(preventivo?.ore_manodopera_qta||0)>0)rows.push([String(rows.length+1),`Manodopera prevista (${S(preventivo?.num_tecnici||1)} tecnici)`,String(round2(toNum(preventivo?.ore_manodopera_qta)*Math.max(1,toNum(preventivo?.num_tecnici)||1))),fmt(preventivo?.manodopera_prezzo_unitario||0),fmt(preventivo?.totale_manodopera||0)]);
  (preventivo?.righe||[]).filter(r=>S(r.descrizione||'').trim()).forEach(row=>{
    rows.push([String(rows.length+1),row.descrizione||'',String(row.qta||0),fmt(row.prezzo_unitario||0),fmt(row.totale||0)]);
  });
  const codice=String(preventivo?.codice||'preventivo-assistenza-tecnica').trim()||'preventivo-assistenza-tecnica';
  await createAssistenzaPdf(codice,async({drawTextLines,drawRule,drawInfoGrid,drawTable,gap,drawStandardHeader})=>{
    await drawStandardHeader({
      settings,
      clientName:clienteNome,
      clientContact:contatto,
      clientAddress:indirizzo,
      clientMeta:[cliente?.piva?`P.IVA: ${cliente.piva}`:'',cliente?.email?`Email: ${cliente.email}`:'',cliente?.telefono?`Tel: ${cliente.telefono}`:''].filter(Boolean),
      docLabel:'Preventivo assistenza tecnica',
      code:preventivo?.codice||'-',
      dateText:fmtDate(preventivo?.data_preventivo||String(preventivo?.created_at||'').slice(0,10)||''),
      extraText:preventivo?.tecnico_nome||intervento?.tecnico_nome?`Tecnico: ${preventivo?.tecnico_nome||intervento?.tecnico_nome}`:'',
    });
    drawInfoGrid([
      {label:'Stato',value:String(preventivo?.stato||'bozza').toLowerCase()==='confermato'?'Confermato':'Bozza'},
      {label:'Data prevista intervento',value:preventivo?.data_prevista_intervento?fmtDate(preventivo.data_prevista_intervento):'-'},
      {label:'Orario previsto',value:preventivo?.orario_previsto_intervento||'-'},
    ]);
    if(preventivo?.confirmed_at){
      drawInfoGrid([{label:'Confermato il',value:fmtDate(String(preventivo.confirmed_at).slice(0,10))}]);
    }
    drawInfoGrid([
      {label:'Intervento collegato',value:[intervento?.codice||'-',intervento?.titolo||'-'].join('\n')},
      {label:'Stampante',value:stampante},
      {label:'Numero seriale',value:seriale},
    ]);
    drawRule();
    drawTextLines('Dettaglio economico',{size:12,font:'bold'});
    drawTable(
      ['#','Descrizione','Qta','Prezzo unit.','Totale'],
      rows.length?rows:[['1','Nessuna riga inserita','','','']],
      [0.08,0.44,0.12,0.18,0.18]
    );
    drawInfoGrid([
      {label:'Totale KM',value:fmt(preventivo?.totale_km||0)},
      {label:'Totale ore viaggio',value:fmt(preventivo?.totale_ore_viaggio||0)},
      {label:'Totale manodopera',value:fmt(preventivo?.totale_manodopera||0)},
    ]);
    drawInfoGrid([
      {label:'Numero tecnici',value:String(preventivo?.num_tecnici||1)},
      {label:'Ore manodopera previste',value:String(preventivo?.ore_manodopera_qta||0)},
      {label:'Ore uomo totali',value:String(round2(toNum(preventivo?.ore_manodopera_qta)*Math.max(1,toNum(preventivo?.num_tecnici)||1)))},
    ]);
    drawInfoGrid([
      {label:'Totale altre voci',value:fmt(preventivo?.totale_manuale||0)},
    ]);
    drawInfoGrid([
      {label:'Totale preventivo',value:fmt(preventivo?.totale||0)},
    ]);
    drawRule();
    drawTextLines('Note',{size:12,font:'bold'});
    drawTextLines(preventivo?.note||'-',{size:11});
    gap(16);
    drawRule();
    drawTextLines(`Tecnico / Emesso da: ${preventivo?.tecnico_nome||intervento?.tecnico_nome||'-'}`,{size:11,font:'bold'});
    gap(22);
    drawTextLines('Accettazione cliente',{size:11,color:PDFLib.rgb(0.39,0.45,0.55)});
  });
}
const today=()=>new Date().toISOString().slice(0,10);
const PROVINCE_CODE_MAP={
  agrigento:'AG',alessandria:'AL',ancona:'AN',aosta:'AO',lquila:'AQ',aquila:'AQ',arezzo:'AR',ascolipiceno:'AP',asti:'AT',avellino:'AV',
  bari:'BA',barlettaandriatrani:'BT',belluno:'BL',benevento:'BN',bergamo:'BG',biella:'BI',bologna:'BO',bolzano:'BZ',bozen:'BZ',brescia:'BS',brindisi:'BR',
  cagliari:'CA',caltanissetta:'CL',campobasso:'CB',caserta:'CE',catania:'CT',catanzaro:'CZ',chieti:'CH',como:'CO',cosenza:'CS',cremona:'CR',crotone:'KR',cuneo:'CN',
  enna:'EN',
  fermo:'FM',ferrara:'FE',firenze:'FI',foggia:'FG',forlicesena:'FC',forli:'FC',frosinone:'FR',
  genova:'GE',gorizia:'GO',grosseto:'GR',
  imperia:'IM',isernia:'IS',
  laspezia:'SP',laquila:'AQ',latina:'LT',lecce:'LE',lecco:'LC',livorno:'LI',lodi:'LO',lucca:'LU',
  macerata:'MC',mantova:'MN',massacarrara:'MS',matera:'MT',messina:'ME',milano:'MI',modena:'MO',monzabrianza:'MB',
  napoli:'NA',novara:'NO',nuoro:'NU',
  oristano:'OR',
  padova:'PD',palermo:'PA',parma:'PR',pavia:'PV',perugia:'PG',pesaroeurbino:'PU',pescara:'PE',piacenza:'PC',pisa:'PI',pistoia:'PT',pordenone:'PN',potenza:'PZ',prato:'PO',
  ragusa:'RG',ravenna:'RA',reggiocalabria:'RC',reggioemilia:'RE',rieti:'RI',rimini:'RN',roma:'RM',rovigo:'RO',
  salerno:'SA',sassari:'SS',savona:'SV',siena:'SI',siracusa:'SR',sondrio:'SO',sudsardegna:'SU',
  taranto:'TA',teramo:'TE',terni:'TR',torino:'TO',trapani:'TP',trento:'TN',treviso:'TV',trieste:'TS',
  udine:'UD',
  varese:'VA',venezia:'VE',verbanocusioossola:'VB',vercelli:'VC',verona:'VR',vibovalentia:'VV',vicenza:'VI',viterbo:'VT',
  rsm:'RSM',sanmarino:'RSM'
};
const normalizeText=v=>String(v??'').trim();
const normalizeToken=v=>normalizeText(v).toLowerCase();
const normalizeProvincia=v=>{
  const raw=normalizeText(v);
  if(!raw)return'';
  const compact=normalizeToken(raw).replace(/[^a-z]/g,'');
  if(PROVINCE_CODE_MAP[compact])return PROVINCE_CODE_MAP[compact];
  const letters=raw.toUpperCase().replace(/[^A-Z]/g,'');
  if(letters.length===2||letters.length===3)return letters;
  return letters.slice(0,2);
};
const uniqueTextValues=values=>{
  const map=new Map();
  values.forEach(v=>{
    const raw=normalizeText(v);
    if(!raw)return;
    const key=normalizeToken(raw);
    if(!map.has(key))map.set(key,raw);
  });
  return[...map.values()].sort((a,b)=>a.localeCompare(b,'it',{sensitivity:'base'}));
};
const parseStoredList=value=>{
  if(Array.isArray(value))return uniqueTextValues(value);
  const raw=String(value??'').trim();
  if(!raw)return[];
  try{
    const parsed=JSON.parse(raw);
    if(Array.isArray(parsed))return uniqueTextValues(parsed);
  }catch(e){}
  return uniqueTextValues(raw.split(/[,;]+/));
};
const triggerUrlDownload=url=>{
  const a=document.createElement('a');
  a.href=url;
  a.target='_blank';
  a.rel='noopener';
  a.click();
};
const fmtFileSize=bytes=>{
  const size=Math.max(0,toNum(bytes));
  if(size<1024)return Math.round(size)+' B';
  if(size<1024*1024)return (size/1024).toFixed(1)+' KB';
  return (size/(1024*1024)).toFixed(2)+' MB';
};
const DEFAULT_FISCAL_BENEFITS=[
  {id:'transizione_5',name:'Transizione 5.0 + Sabatini Green',enabled:true,cash_rate:0.40,cash_cap:0,cash_multiplier:1,sabatini_rate:0.10,sabatini_min_amount:20000,ammortamento_rate:0.24,tax_rate:0.24,tax_base_mode:'sabatini'},
  {id:'bando_inail',name:'Bando INAIL (65%)',enabled:true,cash_rate:0.65,cash_cap:130000,cash_multiplier:1,sabatini_rate:0.10,sabatini_min_amount:20000,ammortamento_rate:0.24,tax_rate:0.24,tax_base_mode:'cash_and_sabatini'},
  {id:'credito_4',name:'Credito 4.0 (20%)',enabled:true,cash_rate:0.20,cash_cap:0,cash_multiplier:1,sabatini_rate:0.10,sabatini_min_amount:20000,ammortamento_rate:0.24,tax_rate:0.24,tax_base_mode:'sabatini'},
  {id:'iper_180',name:'Iperammortamento 180% (Deduzione)',enabled:true,cash_rate:0.24,cash_cap:0,cash_multiplier:1.8,sabatini_rate:0.10,sabatini_min_amount:20000,ammortamento_rate:0.24,tax_rate:0.24,tax_base_mode:'sabatini'},
];
const FISCAL_TAX_BASE_OPTIONS=[
  {value:'none',label:'Nessuna'},
  {value:'sabatini',label:'Solo Sabatini'},
  {value:'cash',label:'Solo incentivo'},
  {value:'cash_and_sabatini',label:'Incentivo + Sabatini'},
];
const normalizeFiscalBenefit=(item,idx=0)=>{
  const base=DEFAULT_FISCAL_BENEFITS[idx]||DEFAULT_FISCAL_BENEFITS[0];
  const rawMode=String(item?.tax_base_mode??base.tax_base_mode??'none');
  return{
    id:String(item?.id||base.id||`benefit_${idx+1}`),
    name:String(item?.name||base.name||`Agevolazione ${idx+1}`),
    enabled:item?.enabled===undefined?!!base.enabled:!['0','false','off','no'].includes(String(item.enabled).toLowerCase()),
    cash_rate:Math.max(0,toNum(item?.cash_rate??base.cash_rate??0)),
    cash_cap:Math.max(0,toNum(item?.cash_cap??base.cash_cap??0)),
    cash_multiplier:Math.max(0,toNum(item?.cash_multiplier??base.cash_multiplier??1)),
    sabatini_rate:Math.max(0,toNum(item?.sabatini_rate??base.sabatini_rate??0)),
    sabatini_min_amount:Math.max(0,toNum(item?.sabatini_min_amount??base.sabatini_min_amount??0)),
    ammortamento_rate:Math.max(0,toNum(item?.ammortamento_rate??base.ammortamento_rate??0)),
    tax_rate:Math.max(0,toNum(item?.tax_rate??base.tax_rate??0)),
    tax_base_mode:['none','sabatini','cash','cash_and_sabatini'].includes(rawMode)?rawMode:'none',
  };
};
const parseFiscalBenefits=(settingsOrRaw)=>{
  const raw=typeof settingsOrRaw==='string'?settingsOrRaw:(settingsOrRaw?.fiscal_benefits||'');
  try{
    const parsed=JSON.parse(raw||'[]');
    if(Array.isArray(parsed)&&parsed.length>0)return parsed.map((item,idx)=>normalizeFiscalBenefit(item,idx));
  }catch(e){}
  return DEFAULT_FISCAL_BENEFITS.map((item,idx)=>normalizeFiscalBenefit(item,idx));
};
const calcFiscalBenefit=(benefit,imponibile)=>{
  const amount=Math.max(0,toNum(imponibile));
  const b=normalizeFiscalBenefit(benefit);
  const rawIncentive=amount*b.cash_rate*(b.cash_multiplier||1);
  const incentive=b.cash_cap>0?Math.min(rawIncentive,b.cash_cap):rawIncentive;
  const sabatini=amount>=b.sabatini_min_amount?amount*b.sabatini_rate:0;
  const fiscalSaving=amount*b.ammortamento_rate;
  const taxBase=b.tax_base_mode==='cash_and_sabatini'?(incentive+sabatini):b.tax_base_mode==='cash'?incentive:b.tax_base_mode==='sabatini'?sabatini:0;
  const taxes=-(taxBase*b.tax_rate);
  const realCost=amount-incentive-sabatini-fiscalSaving-taxes;
  return{
    ...b,
    imponibile:round2(amount),
    incentive:round2(incentive),
    sabatini:round2(sabatini),
    fiscalSaving:round2(fiscalSaving),
    taxes:round2(taxes),
    realCost:round2(realCost),
    saving:round2(amount-realCost),
    discountPct:amount>0?1-(realCost/amount):0,
  };
};
const calcFiscalBenefits=(settingsOrRaw,imponibile)=>{
  return parseFiscalBenefits(settingsOrRaw).filter(item=>item.enabled).map(item=>calcFiscalBenefit(item,imponibile));
};
// Safe text helper: ensures a value is safe to render as a React child
const S=(v)=>{if(v==null)return '';if(typeof v==='object'){console.error('S() received object:',v);return JSON.stringify(v);}return v;};
const STATI={bozza:{label:'Bozza',color:'bg-gray-100 text-gray-700'},inviata:{label:'Inviata',color:'bg-blue-100 text-blue-700'},accettata:{label:'Accettata',color:'bg-green-100 text-green-700'},rifiutata:{label:'Rifiutata',color:'bg-red-100 text-red-700'}};
const OPPORTUNITY_PHASES=[
  {id:'lead',label:'Lead',color:'bg-slate-100 text-slate-700'},
  {id:'qualificato',label:'Qualificato',color:'bg-blue-100 text-blue-700'},
  {id:'demo',label:'Demo',color:'bg-indigo-100 text-indigo-700'},
  {id:'preventivo',label:'Preventivo',color:'bg-amber-100 text-amber-700'},
  {id:'trattativa',label:'Trattativa',color:'bg-orange-100 text-orange-700'},
  {id:'chiuso_vinto',label:'Chiuso Vinto',color:'bg-green-100 text-green-700'},
  {id:'chiuso_perso',label:'Chiuso Perso',color:'bg-red-100 text-red-700'},
];
const OPPORTUNITY_PHASE_MAP=Object.fromEntries(OPPORTUNITY_PHASES.map(p=>[p.id,p]));
const phaseLabel=phase=>OPPORTUNITY_PHASE_MAP[phase]?.label||phase;
const phaseBadgeClass=phase=>OPPORTUNITY_PHASE_MAP[phase]?.color||'bg-gray-100 text-gray-700';
const HIVE_ROLE_LABELS={
  admin:'Admin',
  manager:'Manager',
  commerciale:'Commerciale',
  rivenditore:'Rivenditore',
  operativo:'Operativo',
  agente:'Commerciale',
  ape_regina:'Admin',
  ape_stratega:'Manager',
  capo_alveare:'Manager',
  ape_coordinatrice:'Commerciale',
  ape_operaia:'Operativo',
  hive_ospite:'Operativo'
};
const HIVE_ROLE_OPTIONS=[
  {value:'admin',label:'Admin'},
  {value:'manager',label:'Manager'},
  {value:'commerciale',label:'Commerciale'},
  {value:'rivenditore',label:'Rivenditore'},
  {value:'operativo',label:'Operativo'},
];
const USER_VISIBLE_SECTION_OPTIONS=[
  {value:'dashboard',label:'Dashboard'},
  {value:'pipeline',label:'Pipeline'},
  {value:'preventivi',label:'Preventivi'},
  {value:'stampa',label:'Stampa'},
  {value:'assistenza',label:'Assistenza'},
  {value:'clienti',label:'Clienti'},
  {value:'spese',label:'Nota spese'},
  {value:'listino',label:'Listino'},
  {value:'catalogo',label:'Catalogo'},
  {value:'depliant',label:'Depliant'},
  {value:'analisi',label:'Analisi'},
  {value:'manuale',label:'Manuale'},
  {value:'utenti',label:'Utenti'},
  {value:'impostazioni',label:'Impostazioni'},
  {value:'hiveflow',label:'Mansioni'},
  {value:'stima_consumi',label:'Stima consumi'},
];
const USER_VISIBLE_SECTION_GROUPS=[
  {id:'commerciale',label:'Commerciale',sections:['dashboard','pipeline','preventivi','stampa','stima_consumi','analisi']},
  {id:'operativo',label:'Operatività',sections:['assistenza','clienti','spese','listino','catalogo','depliant','hiveflow']},
  {id:'sistema',label:'Sistema',sections:['manuale','utenti','impostazioni']},
];
const MANSIONE_AREA_OPTIONS=[
  {value:'amministrazione',label:'Amministrazione'},
  {value:'tecnico',label:'Tecnico'},
  {value:'commerciale',label:'Commerciale'},
];
const MANSIONE_LEVEL_OPTIONS=[
  {value:'manager',label:'Manager'},
  {value:'operativo',label:'Operativo'},
];
const mansioneAreaLabel=value=>MANSIONE_AREA_OPTIONS.find(item=>item.value===value)?.label||'Commerciale';
const mansioneLevelLabel=value=>MANSIONE_LEVEL_OPTIONS.find(item=>item.value===value)?.label||'Operativo';
const parseMansioniAssignments=value=>{
  if(Array.isArray(value))return value.map(item=>({
    title:String(item?.title||item?.titolo||'').trim(),
    area:['amministrazione','tecnico','commerciale'].includes(String(item?.area||'').trim().toLowerCase())?String(item.area).trim().toLowerCase():'commerciale',
    level:String(item?.level||item?.livello||'operativo').trim().toLowerCase()==='manager'?'manager':'operativo',
    description:String(item?.description||item?.descrizione||'').trim(),
  }));
  try{return parseMansioniAssignments(JSON.parse(value||'[]'))}catch(e){return[]}
};
const mansioniSummaryLines=value=>{
  const rows=parseMansioniAssignments(value);
  if(rows.length===0)return[];
  return rows.map(item=>{
    const title=item.title||mansioneAreaLabel(item.area);
    return`${title} · ${mansioneAreaLabel(item.area)} · ${mansioneLevelLabel(item.level)}`;
  });
};
const effectiveMansioniRole=role=>{
  const value=String(role||'operativo').trim().toLowerCase();
  if(['admin','ape_regina'].includes(value))return'admin';
  if(['manager','ape_stratega','capo_alveare'].includes(value))return'manager';
  if(['commerciale','rivenditore','agente','ape_coordinatrice'].includes(value))return'commerciale';
  if(['operativo','ape_operaia','hive_ospite'].includes(value))return'operativo';
  return'operativo';
};
const getRawUserRole=user=>String(user?.ruolo||'operativo').trim().toLowerCase();
const sectionAllowedByRole=(user,sectionId)=>{
  if(!user)return false;
  const rawRole=getRawUserRole(user);
  switch(sectionId){
    case 'dashboard':
      return ['admin','manager'].includes(effectiveMansioniRole(rawRole));
    case 'pipeline':
      return canPerformAction('opportunities',user);
    case 'preventivi':
    case 'stampa':
      return canPerformAction('preventivi',user);
    case 'stima_consumi':
      return canPerformAction('consumi_calcola',user);
    case 'assistenza':
      return canPerformAction('assistenza_clienti',user);
    case 'clienti':
      return canPerformAction('clienti',user);
    case 'spese':
      return canPerformAction('spese',user);
    case 'listino':
      return canAccessModule('listino',user);
    case 'catalogo':
      return canAccessModule('catalogo',user);
    case 'depliant':
      return canPerformAction('documenti',user);
    case 'analisi':
      return canPerformAction('stats',user);
    case 'manuale':
    case 'profilo':
      return true;
    case 'utenti':
      return canPerformAction('users',user);
    case 'impostazioni':
      return canPerformAction('settings_update',user);
    case 'hiveflow':
      return canPerformAction('hive_items',user);
    default:
      return false;
  }
};
const getRoleVisibleSections=user=>{
  const source=user||{};
  const sectionIds=[...new Set([...USER_VISIBLE_SECTION_OPTIONS.map(item=>item.value),'profilo'])];
  return sectionIds.filter(sectionId=>sectionAllowedByRole(source,sectionId));
};
const normalizeFrontendVisibleSections=value=>{
  if(Array.isArray(value))return value.map(item=>String(item||'').trim()).filter(Boolean);
  try{return normalizeFrontendVisibleSections(JSON.parse(value||'[]'))}catch(e){return[]}
};
const getEffectiveVisibleSectionsForUser=user=>{
  if(!user)return[];
  const roleSections=getRoleVisibleSections(user);
  const chosen=normalizeFrontendVisibleSections(user.visible_sections);
  if(!chosen.length){
    return roleSections;
  }
  return roleSections.filter(section=>section==='profilo'||chosen.includes(section));
};
const getVisibleSectionSummary=user=>{
  const chosen=normalizeFrontendVisibleSections(user?.visible_sections);
  const effective=getEffectiveVisibleSectionsForUser(user);
  return{
    chosen,
    effective,
    inherited:chosen.length===0,
    blocked:getRoleVisibleSections(user).filter(section=>chosen.length&& !chosen.includes(section) && section!=='profilo'),
  };
};
const isAdminUser=user=>effectiveMansioniRole(user?.ruolo)==='admin';
const isPreventivoOwner=(user,preventivo)=>parseInt(user?.id||0,10)>0&&parseInt(preventivo?.user_id||0,10)===parseInt(user?.id||0,10);
const canDeletePreventivo=(user,preventivo)=>{
  if(!user||!preventivo)return false;
  if(isAdminUser(user))return true;
  return isPreventivoOwner(user,preventivo)&&String(preventivo.stato||'bozza')==='bozza';
};
const canEditPreventivo=(user,preventivo)=>{
  if(!user||!preventivo)return false;
  if(isAdminUser(user))return true;
  return isPreventivoOwner(user,preventivo)&&String(preventivo.stato||'bozza')==='bozza';
};
const HIVE_ITEM_TYPES=[
  {value:'micro',label:'Micro'},
  {value:'standard',label:'Standard'},
  {value:'urgente',label:'Urgente'},
  {value:'amministrativo',label:'Amministrativo'},
  {value:'commerciale',label:'Commerciale'},
  {value:'tecnico',label:'Tecnico'},
  {value:'produzione',label:'Produzione'},
  {value:'acquisto',label:'Acquisto'},
  {value:'approvazione',label:'Approvazione'},
  {value:'ricorrente',label:'Ricorrente'},
  {value:'meeting_follow_up',label:'Meeting follow-up'},
  {value:'personale',label:'Personale'},
  {value:'privato',label:'Privato'},
  {value:'esterno',label:'Esterno'},
];
const HIVE_ITEM_PRIORITIES=[
  {value:'bassa',label:'Bassa',color:'bg-slate-100 text-slate-700'},
  {value:'media',label:'Media',color:'bg-blue-100 text-blue-700'},
  {value:'alta',label:'Alta',color:'bg-amber-100 text-amber-700'},
  {value:'urgente',label:'Urgente',color:'bg-orange-100 text-orange-700'},
  {value:'critica',label:'Critica',color:'bg-red-100 text-red-700'},
];
const HIVE_ITEM_STATUSES=[
  {value:'inbox',label:'Inbox',color:'bg-slate-100 text-slate-700'},
  {value:'da_fare',label:'Da fare',color:'bg-blue-100 text-blue-700'},
  {value:'in_corso',label:'In corso',color:'bg-indigo-100 text-indigo-700'},
  {value:'in_attesa',label:'In attesa',color:'bg-amber-100 text-amber-700'},
  {value:'bloccato',label:'Bloccato',color:'bg-red-100 text-red-700'},
  {value:'da_verificare',label:'Verifica',color:'bg-purple-100 text-purple-700'},
  {value:'completato',label:'Fatto',color:'bg-green-100 text-green-700'},
  {value:'annullato',label:'Annullato',color:'bg-gray-200 text-gray-700'},
];
const HIVE_ITEM_VISIBILITY=[
  {value:'privato',label:'Privato'},
  {value:'team',label:'Team'},
  {value:'gerarchia',label:'Gerarchia'},
  {value:'azienda',label:'Azienda'},
  {value:'esterno',label:'Esterno'},
];
const hiveStatusLabel=status=>HIVE_ITEM_STATUSES.find(s=>s.value===status)?.label||status;
const hiveStatusClass=status=>HIVE_ITEM_STATUSES.find(s=>s.value===status)?.color||'bg-gray-100 text-gray-700';
const hivePriorityLabel=priority=>HIVE_ITEM_PRIORITIES.find(p=>p.value===priority)?.label||priority;
const hivePriorityClass=priority=>HIVE_ITEM_PRIORITIES.find(p=>p.value===priority)?.color||'bg-gray-100 text-gray-700';
// Dati aziendali default (sovrascritti da impostazioni se configurati)
const AZIENDA_DEFAULT={nome:'Complevia',sottotitolo:'Business Platform',tel:'+39 348 5601243',email:'luca@cavazzuti.eu',web:'www.cavazzuti.eu',via:'',cap:'',citta:'',provincia:'',paese:'Italia',piva:''};
function getAzienda(settings){
  const source={...INITIAL_BRAND_SETTINGS,...(settings||{})};
  return{
    nome:source.az_nome||AZIENDA_DEFAULT.nome,
    sottotitolo:source.az_sottotitolo||AZIENDA_DEFAULT.sottotitolo,
    tel:source.az_tel||AZIENDA_DEFAULT.tel,
    email:source.az_email||AZIENDA_DEFAULT.email,
    web:source.az_web||AZIENDA_DEFAULT.web,
    via:source.az_via||AZIENDA_DEFAULT.via,
    cap:source.az_cap||AZIENDA_DEFAULT.cap,
    citta:source.az_citta||AZIENDA_DEFAULT.citta,
    provincia:source.az_provincia||AZIENDA_DEFAULT.provincia,
    paese:source.az_paese||AZIENDA_DEFAULT.paese,
    piva:source.az_piva||AZIENDA_DEFAULT.piva,
  };
}
const CompanyLogo=({settings,maxH=80,maxW='220px',className='',style={}})=>{
  const source={...INITIAL_BRAND_SETTINGS,...(settings||{})};
  const logo=source.az_logo||APP_LOGO_URL;
  if(logo)return<img src={logo} alt="Logo" className={className} style={{display:'block',width:'auto',height:'auto',maxHeight:typeof maxH==='number'?maxH+'px':maxH,maxWidth:typeof maxW==='number'?maxW+'px':maxW,objectFit:'contain',objectPosition:'left center',...style}}/>;
  const az=getAzienda(source);
  return(<div><div style={{fontSize:'20px',fontWeight:800,color:'#1B3A6B',letterSpacing:'2px'}}>{az.nome}</div>
    <div style={{fontSize:'11px',color:'#666',letterSpacing:'4px'}}>{az.sottotitolo}</div></div>);
};
const PrintPageChrome=({settings})=><>
  <div className="print-only print-page-chrome-header">
    <CompanyLogo settings={settings} className="print-page-chrome-logo" maxH="20mm" maxW="32mm"/>
  </div>
  <div className="print-only print-page-chrome-footer">
    <span className="print-page-counter"></span>
  </div>
</>;

// ========== PRICING HELPERS ==========
function calcPrezzi(art){
  const cn=art.costo_netto||0,mm=art.margine_min||0,mr=art.markup_riv||0,mc=art.markup_cliente||0;
  const prezzoMinimo=mm<1?cn/(1-mm):cn;
  const prezzoRivenditore=mr<1?prezzoMinimo/(1-mr):prezzoMinimo;
  const prezzoConsigliato=mc<1?prezzoRivenditore/(1-mc):prezzoRivenditore;
  return{prezzoMinimo:Math.round(prezzoMinimo*100)/100,prezzoRivenditore:Math.round(prezzoRivenditore*100)/100,prezzoConsigliato:Math.round(prezzoConsigliato*100)/100};
}

// ========== ICONS ==========
// ========== UI COMPONENTS ==========
const StatoBadge=({stato})=>{const s=STATI[stato]||STATI.bozza;return<span className={`cplv-badge text-xs px-2.5 py-1 rounded-full font-semibold ${s.color}`}>{s.label}</span>};
const Card=({title,action,children,className=''})=>(
  <div className={`surface-card cplv-card min-w-0 rounded-[28px] md:rounded-[32px] p-4 md:p-6 ${className}`}>
    {(title||action)&&<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4 md:mb-5">
      {title&&<h3 className="font-semibold text-[1.05rem] tracking-[-0.02em] text-slate-900 break-words min-w-0">{title}</h3>}
      {action?<div className="flex flex-wrap gap-2 shrink-0">{action}</div>:null}
    </div>}
    {children}</div>);
const Btn=({children,onClick,v='primary',s='md',icon,disabled,className='',type='button'})=>{
  const base='cplv-btn inline-flex items-center justify-center gap-2 text-center leading-tight whitespace-normal max-w-full font-semibold transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]';
  const variants={
    primary:'cplv-btn--primary',
    secondary:'cplv-btn--secondary',
    danger:'cplv-btn--danger',
    success:'cplv-btn--success',
    ghost:'cplv-btn--ghost',
  };
  const sizes={sm:'cplv-btn--sm',md:'cplv-btn--md',lg:'cplv-btn--lg'};
  return<button type={type} onClick={onClick} disabled={disabled} className={`${base} ${variants[v]||variants.primary} ${sizes[s]||sizes.md} ${className}`}>{icon}{children}</button>};
const Inp=({label,value,onChange,type='text',placeholder,disabled,className='',required})=>(
  <div className={className}>{label&&<label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2">{label}{required&&<span className="text-red-500 ml-1">*</span>}</label>}
    <input type={type} value={value??''} onChange={e=>onChange(type==='number'?parseFloat(e.target.value)||0:e.target.value)}
      placeholder={placeholder} disabled={disabled} className="field-shell w-full px-4 md:px-4.5 py-3 rounded-[22px] text-[15px] outline-none transition disabled:bg-slate-100 disabled:text-slate-400"/></div>);
const Sel=({label,value,onChange,options=[],placeholder,disabled,className=''})=>(
  <div className={className}>{label&&<label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2">{label}</label>}
    <select value={value??''} onChange={e=>onChange(e.target.value)} disabled={disabled}
      className="field-shell w-full px-4 md:px-4.5 py-3 rounded-[22px] text-[15px] outline-none transition bg-white/95 disabled:bg-slate-100 disabled:text-slate-400">
      {placeholder&&<option value="">{placeholder}</option>}
      {options.map(o=>typeof o==='string'?<option key={o} value={o}>{o}</option>:<option key={o.value} value={o.value}>{o.label}</option>)}</select></div>);
function getDeviceProfile(){
  if(typeof window==='undefined')return 'desktop';
  const w=window.innerWidth;
  if(w<640)return 'phone';
  if(w<1024)return 'tablet';
  return 'desktop';
}
const UiCtx=createContext({device:'desktop',isPhone:false,isTablet:false,isDesktop:true});
function UiProvider({children}){
  const[device,setDevice]=useState(getDeviceProfile);
  useEffect(()=>{
    const onResize=()=>setDevice(getDeviceProfile());
    window.addEventListener('resize',onResize);
    return()=>window.removeEventListener('resize',onResize);
  },[]);
  const value=useMemo(()=>({
    device,
    isPhone:device==='phone',
    isTablet:device==='tablet',
    isDesktop:device==='desktop',
  }),[device]);
  return<UiCtx.Provider value={value}>{children}</UiCtx.Provider>;
}
function useDevice(){return useContext(UiCtx);}
function Page({title,subtitle,actions,children,className=''}){
  const{isPhone}=useDevice();
  return(
    <div className={`page-stack fade-in ${className}`}>
      {(title||subtitle||actions)?<header className={`page-header ${!isPhone?'page-header--row':''}`}>
        <div className="min-w-0 flex-1">
          {title?<h2 className="page-title">{title}</h2>:null}
          {subtitle?<p className="page-subtitle">{subtitle}</p>:null}
        </div>
        {actions?<div className="page-actions">{actions}</div>:null}
      </header>:null}
      {children}
    </div>
  );
}
function PageToolbar({children,className=''}){return<div className={`page-toolbar ${className}`}>{children}</div>;}
function TabBarScroll({tabs,active,onChange,className=''}){
  const{isPhone,isTablet}=useDevice();
  const chips=isPhone||isTablet;
  return(
    <div className={`${chips?'app-subnav-chips no-scrollbar border-b border-slate-200/80 pb-1':'flex flex-wrap gap-2 border-b border-slate-200/80 pb-1'} ${className}`}>
      {tabs.map(t=>{
        const key=t.k||t.id;
        const label=(t.l||t.label||'')+(t.n!=null?` (${t.n})`:'');
        if(chips)return <SegmentedNavButton key={key} compact active={active===key} onClick={()=>onChange(key)} icon={t.icon} label={label}/>;
        return <button key={key} type="button" onClick={()=>onChange(key)} className={`px-4 py-2.5 min-h-[44px] text-sm font-medium border-b-2 -mb-px transition ${active===key?'border-accent-400 text-accent-600':'border-transparent text-slate-500 hover:text-slate-700'}`}>{label}</button>;
      })}
    </div>
  );
}
function EditorActionBar({children,visible=true}){
  const{isPhone}=useDevice();
  if(!isPhone||!visible)return null;
  return <div className="editor-action-bar no-print">{children}</div>;
}
function ResponsiveTable({phoneRows,children,className=''}){
  const{isPhone}=useDevice();
  if(isPhone&&phoneRows)return<div className={`data-list-phone ${className}`}>{phoneRows}</div>;
  return<div className={`table-shell ${className}`}>{children}</div>;
}
function SegmentedNavButton({active,onClick,icon,label,compact=false}){
  return(
    <button type="button" onClick={onClick}
      className={`segmented-tab cplv-seg-tab ${active?'active':''} flex items-center gap-2 ${compact?'px-3 py-2.5 text-xs rounded-[14px]':'px-4 py-3 text-sm rounded-[18px]'} font-semibold`}>
      <span className={`cplv-nav-icon-wrap ${compact?'cplv-nav-icon-wrap--sm':'cplv-nav-icon-wrap--md'} ${active?'is-active':''}`}>{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}
const Modal=({title,children,onClose,wide,footer})=>{
  const{isPhone}=useDevice();
  return(
    <div className={`fixed inset-0 bg-slate-950/35 backdrop-blur-md flex z-50 overflow-y-auto modal-overlay ${isPhone?'modal-overlay--sheet items-stretch p-0':'items-start md:items-center justify-center p-3 md:p-4'}`} onClick={onClose}>
      <div className={`surface-card min-w-0 modal-panel ${isPhone?'modal-panel--sheet rounded-none max-h-full h-full':'rounded-[36px] my-2 md:my-0 max-h-[92vh]'} ${wide?'max-w-5xl':'max-w-xl'} w-full overflow-hidden flex flex-col p-5 md:p-6`} onClick={e=>e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4 shrink-0">
          <h2 className="text-[1.2rem] font-bold tracking-[-0.03em] text-slate-900 break-words min-w-0 pr-2">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Chiudi" className="w-11 h-11 inline-flex items-center justify-center hover:bg-white rounded-full transition border border-white/80 bg-white/75 shadow-[0_10px_24px_rgba(15,23,42,.06)]"><IcX s={20}/></button>
        </div>
        <div className="modal-body flex-1 overflow-y-auto min-h-0 -mx-1 px-1">{children}</div>
        {footer?<div className="modal-footer-sticky mt-4 shrink-0">{footer}</div>:null}
      </div>
    </div>
  );
};
function ClientCategoryOtherModal({onClose,onSave}){
  const[value,setValue]=useState('');
  return <Modal title="Nuova categoria" onClose={onClose}>
    <div className="space-y-4">
      <Inp label="Nome categoria" value={value} onChange={setValue} placeholder="Es. serigrafia"/>
      <div className="flex justify-end gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" onClick={()=>{const next=S(value).trim();if(!next)return;onSave(next);}}>Aggiungi</Btn>
      </div>
    </div>
  </Modal>;
}
function ClientCategoryPicker({label='Categorie merceologiche',value,onChange,maxItems=3}){
  const{showToast}=useContext(DataCtx);
  const[options,setOptions]=useState([]);
  const[loading,setLoading]=useState(false);
  const[showOther,setShowOther]=useState(false);
  const[pendingIndex,setPendingIndex]=useState(0);
  useEffect(()=>{
    let active=true;
    setLoading(true);
    api('clienti_category_list').then(d=>{
      if(!active)return;
      setOptions((d.data?.categories||[]).map(item=>typeof item==='string'?item:S(item.value)).filter(Boolean));
    }).catch(e=>{if(active)showToast(e.message,'error')}).finally(()=>{if(active)setLoading(false)});
    return()=>{active=false};
  },[]);
  const selected=useMemo(()=>clientiParseMerceologie(value).filter(item=>normalizeToken(item)!==normalizeToken('Non classificata')).slice(0,maxItems),[value,maxItems]);
  const allOptions=useMemo(()=>{
    const map=new Map();
    [...options,...selected].forEach(label=>{
      const raw=S(label).trim();
      if(!raw)return;
      const key=normalizeToken(raw);
      map.set(key,clientiPreferredLabel(map.get(key),raw));
    });
    return [...map.values()].sort((a,b)=>a.localeCompare(b,'it',{sensitivity:'base'}));
  },[options,selected]);
  const write=next=>{
    const clean=clientiParseMerceologie(next.join(' | ')).filter(item=>normalizeToken(item)!==normalizeToken('Non classificata')).slice(0,maxItems);
    onChange(clean.join(' | '));
  };
  const updateAt=(index,newValue)=>{
    const next=[...selected];
    next[index]=newValue;
    write(next.filter(Boolean));
  };
  const removeAt=index=>{
    const next=selected.filter((_,i)=>i!==index);
    write(next);
  };
  const addEmpty=()=>write([...selected,'']);
  const visibleRows=Math.max(1,Math.min(maxItems,selected.length+(selected.length<maxItems?1:0)));
  return <div>
    {label&&<label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>}
    <div className="space-y-2">
      {Array.from({length:visibleRows}).map((_,index)=>{
        const current=selected[index]||'';
        const rowOptions=allOptions.filter(opt=>!selected.some((item,i)=>i!==index&&normalizeToken(item)===normalizeToken(opt)));
        return <div key={index} className="flex gap-2 items-center">
          <select value={current} onChange={e=>{
            const next=e.target.value;
            if(next==='__other__'){
              setPendingIndex(index);
              setShowOther(true);
              return;
            }
            updateAt(index,next);
          }} className="field-shell w-full px-4 py-3 rounded-2xl text-sm outline-none transition bg-white/95">
            <option value="">{loading?'Carico categorie...':'Seleziona categoria'}</option>
            {rowOptions.map(opt=><option key={opt} value={opt}>{opt}</option>)}
            <option value="__other__">Altro…</option>
          </select>
          {current?<button type="button" onClick={()=>removeAt(index)} className="w-11 h-11 inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 hover:text-red-600 hover:border-red-200"><IcX s={16}/></button>:null}
        </div>;
      })}
      <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
        <span>Puoi associare fino a {maxItems} categorie.</span>
        {selected.length<maxItems?<button type="button" onClick={()=>{setPendingIndex(selected.length);setShowOther(true)}} className="font-semibold text-accent-500 hover:text-accent-700">Nuova categoria</button>:null}
      </div>
    </div>
    {showOther?<ClientCategoryOtherModal onClose={()=>setShowOther(false)} onSave={next=>{
      const normalized=S(next).trim().replace(/\s+/g,' ');
      if(!normalized)return;
      setOptions(prev=>uniqueTextValues([...prev,normalized]));
      const nextSelected=[...selected];
      nextSelected[pendingIndex]=normalized;
      write(nextSelected.filter(Boolean));
      setShowOther(false);
    }}/>:null}
  </div>;
}
function ClientCategoryMergeModal({onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const[loading,setLoading]=useState(true);
  const[categories,setCategories]=useState([]);
  const[selected,setSelected]=useState([]);
  const[target,setTarget]=useState('');
  const[search,setSearch]=useState('');
  const load=async()=>{
    setLoading(true);
    try{
      const d=await api('clienti_category_list');
      setCategories(d.data?.categories||[]);
    }catch(e){showToast(e.message,'error')}
    finally{setLoading(false);}
  };
  useEffect(()=>{load()},[]);
  const filtered=useMemo(()=>{
    if(!search.trim())return categories;
    const term=normalizeToken(search);
    return categories.filter(item=>normalizeToken(item.value||item).includes(term));
  },[categories,search]);
  const toggle=value=>{
    setSelected(prev=>prev.some(item=>normalizeToken(item)===normalizeToken(value))
      ? prev.filter(item=>normalizeToken(item)!==normalizeToken(value))
      : [...prev,value]);
  };
  const save=async()=>{
    if(selected.length<2){showToast('Seleziona almeno due categorie','error');return;}
    if(!S(target).trim()){showToast('Inserisci il nome della nuova categoria','error');return;}
    try{
      const d=await api('clienti_category_merge','POST',{source_categories:selected,target_category:target});
      showToast(`Categorie unite: ${d.data?.updated||0} clienti aggiornati`);
      onSaved?.();
    }catch(e){showToast(e.message,'error')}
  };
  return <Modal title="Unisci categorie" onClose={onClose} wide>
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">Seleziona le categorie da unire e definisci il nome della categoria finale. L'operazione aggiorna tutti i clienti in modo case-insensitive.</div>
      <Inp label="Cerca categoria" value={search} onChange={setSearch} placeholder="Es. serigrafia"/>
      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_.8fr] gap-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-4 max-h-[52vh] overflow-y-auto space-y-2">
          {loading?<Loading/>:filtered.map(item=>{
            const value=S(item.value||item);
            const active=selected.some(cat=>normalizeToken(cat)===normalizeToken(value));
            return <label key={value} className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 cursor-pointer transition ${active?'border-navy-600 bg-navy-50':'border-slate-200 hover:border-slate-300 bg-slate-50/60'}`}>
              <span className="flex items-center gap-3 min-w-0"><input type="checkbox" checked={active} onChange={()=>toggle(value)} className="w-4 h-4 accent-accent-500"/><span className="text-sm font-medium text-slate-700 truncate">{value}</span></span>
              <span className="text-xs text-slate-400 shrink-0">{item.count||0}</span>
            </label>;
          })}
        </div>
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Categorie selezionate</div>
            <div className="flex flex-wrap gap-2">{selected.length?selected.map(item=><span key={item} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700">{item}</span>):<span className="text-sm text-slate-400">Nessuna categoria selezionata</span>}</div>
          </div>
          <Inp label="Nuovo nome categoria" value={target} onChange={setTarget} placeholder="Es. serigrafia e ricamificio"/>
          <div className="flex justify-end gap-3">
            <Btn v="secondary" onClick={onClose}>Annulla</Btn>
            <Btn v="success" onClick={save}>Unisci categorie</Btn>
          </div>
        </div>
      </div>
    </div>
  </Modal>;
}
const Loading=()=><div className="flex items-center justify-center py-24"><div className="w-9 h-9 border-[3px] border-sky-300 border-t-sky-600 rounded-full animate-spin shadow-[0_0_0_10px_rgba(191,219,254,.25)]"/></div>;
const Empty=({icon,title,sub,action})=>(
  <div className="cplv-empty text-center py-16 fade-in">
    <div className="cplv-empty__icon w-20 h-20 flex items-center justify-center mx-auto mb-4">{icon}</div>
    <h2 className="text-[1.3rem] font-semibold tracking-[-0.03em] text-slate-900 mb-2 wrap-safe">{title}</h2>
    {sub&&<p className="text-slate-500 mb-6 wrap-safe max-w-2xl mx-auto leading-7">{sub}</p>}{action}</div>);
const Toast=({msg,type='success',onClose})=>{
  const{isPhone}=useDevice();
  useEffect(()=>{const t=setTimeout(onClose,3000);return()=>clearTimeout(t)},[onClose]);
  return<div className={`fixed z-[80] px-4 py-3 rounded-full shadow-[0_18px_42px_rgba(15,23,42,.18)] border text-sm font-medium backdrop-blur-sm max-w-[min(92vw,360px)] ${isPhone?'left-1/2 -translate-x-1/2':'right-4'} ${isPhone?'bottom-[calc(76px+env(safe-area-inset-bottom))]':'bottom-4'} ${type==='error'?'bg-red-500/92 border-red-400 text-white':'bg-white/92 border-white/90 text-slate-800'}`}>{msg}</div>};

// ========== ERROR BOUNDARY ==========
class ErrorBoundary extends React.Component{
  constructor(props){super(props);this.state={hasError:false,error:null,errorInfo:null}}
  static getDerivedStateFromError(error){return{hasError:true,error}}
  componentDidCatch(error,info){
    console.error('=== REACT ERROR BOUNDARY ===');
    console.error('Error:',error);
    console.error('Error message:',error?.message);
    console.error('Component stack:',info?.componentStack);
    sendClientDebugLog('react-boundary',error?.message||'Errore React',{stack:error?.stack||'',componentStack:info?.componentStack||''},'critical');
    this.setState({errorInfo:info});
  }
  render(){
    if(this.state.hasError){
      const msg=this.state.error?.message||'Errore sconosciuto';
      const stack=this.state.errorInfo?.componentStack||'';
      return React.createElement('div',{style:{padding:'40px',fontFamily:'Inter,sans-serif',maxWidth:'800px',margin:'0 auto'}},
        React.createElement('h2',{style:{color:'#dc2626',marginBottom:'16px'}},'Errore nell\'applicazione'),
        React.createElement('p',{style:{color:'#666',marginBottom:'16px',wordBreak:'break-word'}},msg),
        stack&&React.createElement('details',{style:{marginBottom:'16px',background:'#f9fafb',padding:'12px',borderRadius:'8px',border:'1px solid #e5e7eb'}},
          React.createElement('summary',{style:{cursor:'pointer',fontWeight:'600',color:'#374151'}},'Dettagli errore (component stack)'),
          React.createElement('pre',{style:{fontSize:'12px',color:'#6b7280',whiteSpace:'pre-wrap',marginTop:'8px'}},stack)),
        React.createElement('button',{onClick:()=>{this.setState({hasError:false,error:null,errorInfo:null});window.location.reload()},
          style:{padding:'10px 24px',background:'#4472C4',color:'white',border:'none',borderRadius:'8px',cursor:'pointer'}},'Ricarica pagina'));
    }
    return this.props.children;
  }
}

function DebugClientBridge(){
  useEffect(()=>{
    const onError=e=>{
      sendClientDebugLog('window-error',e.message||'Errore Javascript',{
        source:e.filename||'',
        line:e.lineno||0,
        column:e.colno||0,
        stack:e.error?.stack||'',
      },'error');
    };
    const onRejection=e=>{
      const reason=e.reason;
      sendClientDebugLog('unhandled-rejection',typeof reason==='string'?reason:(reason?.message||'Promise rejection'),{
        reason:typeof reason==='string'?reason:JSON.stringify(reason||{}),
        stack:reason?.stack||'',
      },'error');
    };
    window.addEventListener('error',onError);
    window.addEventListener('unhandledrejection',onRejection);
    return()=>{
      window.removeEventListener('error',onError);
      window.removeEventListener('unhandledrejection',onRejection);
    };
  },[]);
  return null;
}

// ========== AUTH CONTEXT ==========
const AuthCtx=createContext();
function AuthProvider({children}){
  const[user,setUser]=useState(null);
  const[loading,setLoading]=useState(true);
  const[permissionsEpoch,setPermissionsEpoch]=useState(0);
  const[impersonationUsers,setImpersonationUsers]=useState([]);
  const syncPermissions=useCallback(async()=>{
    try{
      const d=await api('frontend_permissions','GET');
      if(d?.permissions){
        setCplvcrmPermissions(d.permissions);
        setPermissionsEpoch(n=>n+1);
        return d.permissions;
      }
    }catch(e){}
    return getCplvcrmPermissions();
  },[]);
  const clearSessionCaches=useCallback(()=>{
    cplvcrmClearClientiCache();
    cplvcrmClearArticoliCache();
    csrfToken='';
  },[]);
  const refreshUser=useCallback(async()=>{
    const d=await api('me');
    if(d.success){
      sessionEstablished=true;
      setUser(d.user);
      await syncPermissions();
      return d.user;
    }
    sessionEstablished=false;
    return null;
  },[syncPermissions]);
  useEffect(()=>{refreshUser().catch(()=>{}).finally(()=>setLoading(false))},[refreshUser]);
  useEffect(()=>{
    const bindNative=()=>{
      if(!window.CPLVCRM_NATIVE)return;
      window.CPLVCRM_NATIVE.flushOfflineQueue=cplvcrmFlushOfflineQueue;
      window.CPLVCRM_NATIVE.generateCodiceOfferta=cplvcrmGenerateCodiceOfferta;
    };
    const onOnline=()=>{
      cplvcrmFlushOfflineQueue().then(r=>{
        if(r.flushed>0)refreshUser().catch(()=>{});
      }).catch(()=>{});
    };
    bindNative();
    window.addEventListener('online',onOnline);
    return()=>window.removeEventListener('online',onOnline);
  },[refreshUser]);
  useEffect(()=>{
    const onAuthExpired=()=>{
      sessionEstablished=false;
      clearSessionCaches();
      setUser(null);
      setLoading(false);
    };
    window.addEventListener('complevia-auth-expired',onAuthExpired);
    return()=>window.removeEventListener('complevia-auth-expired',onAuthExpired);
  },[clearSessionCaches]);
  const login=async(username,password)=>{
    clearSessionCaches();
    sessionEstablished=false;
    const d=await api('login','POST',{username,password});
    if(d.success){
      if(d.csrf_token)csrfToken=d.csrf_token;
      setUser(d.user);
      sessionEstablished=true;
      try{
        const me=await api('me');
        if(me?.success){
          if(me.csrf_token)csrfToken=me.csrf_token;
          setUser(me.user);
          sessionEstablished=true;
        }
      }catch(e){
        sessionEstablished=false;
        setUser(null);
        throw new Error('Login riuscito ma la sessione non resta attiva. Su iPhone/iPad apri l\'app dal link ufficiale (complevia-app.pages.dev) e consenti i cookie per questo sito.');
      }
      await syncPermissions();
      return d;
    }
    throw new Error(d.error);
  };
  const logout=async()=>{
    sessionEstablished=false;
    await api('logout','POST').catch(()=>{});
    clearSessionCaches();
    setUser(null);
  };
  const ruolo=user?.ruolo||'operativo';
  const effectiveRole=effectiveMansioniRole(ruolo);
  const impersonation=user?.impersonation||null;
  const isImpersonating=!!impersonation?.active;
  const realEffectiveRole=impersonation?.real_effective_role||effectiveRole;
  const canImpersonate=realEffectiveRole==='admin'&&!!impersonation?.can_manage;
  const isAdmin=effectiveRole==='admin';
  const isManager=effectiveRole==='manager';
  const isCommerciale=effectiveRole==='commerciale';
  const isOperativo=effectiveRole==='operativo';
  const isHiveRole=isOperativo;
  const loadImpersonationUsers=useCallback(async()=>{
    if(!canImpersonate){
      setImpersonationUsers([]);
      return[];
    }
    const d=await api('impersonation_users');
    const rows=d.data||[];
    setImpersonationUsers(rows);
    return rows;
  },[canImpersonate]);
  useEffect(()=>{loadImpersonationUsers().catch(()=>{})},[loadImpersonationUsers,user?.id,isImpersonating]);
  const startImpersonation=useCallback(async(targetUserId)=>{
    const d=await api('impersonation_start','POST',{user_id:targetUserId});
    if(d.success){
      clearSessionCaches();
      setUser(d.user);
      await syncPermissions();
      return d.user;
    }
    throw new Error(d.error||'Impossibile simulare utente');
  },[clearSessionCaches,syncPermissions]);
  const stopImpersonation=useCallback(async()=>{
    const d=await api('impersonation_stop','POST',{});
    if(d.success){
      clearSessionCaches();
      setUser(d.user);
      await syncPermissions();
      return d.user;
    }
    throw new Error(d.error||'Impossibile uscire dalla simulazione');
  },[clearSessionCaches,syncPermissions]);
  if(loading)return<div className="min-h-screen flex items-center justify-center bg-[linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)]"><div className="w-10 h-10 border-[3px] border-sky-300 border-t-sky-600 rounded-full animate-spin shadow-[0_0_0_10px_rgba(191,219,254,.22)]"/></div>;
  return<AuthCtx.Provider value={{user,setUser,refreshUser,login,logout,isAdmin,isManager,isCommerciale,isOperativo,isHiveRole,ruolo,effectiveRole,impersonation,isImpersonating,realEffectiveRole,canImpersonate,impersonationUsers,loadImpersonationUsers,startImpersonation,stopImpersonation,permissionsEpoch,syncPermissions}}>{children}</AuthCtx.Provider>;
}

function cplvcrmPerfLightEnabled(settings){
  try{
    const override=localStorage.getItem('cplvcrm_perf_light');
    if(override!==null)return override==='1';
  }catch(e){}
  if(settings&&settingEnabled(settings.performance_light_mode))return true;
  const conn=navigator.connection||navigator.mozConnection||navigator.webkitConnection;
  if(conn&&(conn.saveData||['slow-2g','2g','3g'].includes(String(conn.effectiveType||''))))return true;
  return false;
}
function cplvcrmFrontendVersion(){
  const boot=typeof window!=='undefined'?window.CPLVCRM_BOOT:null;
  return String(COMPLEVIA_FRONTEND_VERSION||boot?.version||'dev');
}
function cplvcrmArticoliCacheKey(){
  return`cplvcrm_articoli_${cplvcrmFrontendVersion()}`;
}
function cplvcrmClientiCacheKey(params={}){
  const v=cplvcrmFrontendVersion();
  const safe={
    light:String(params.light??'1'),
    enrich:String(params.enrich??'0'),
    limit:String(params.limit??'0'),
    offset:String(params.offset??'0'),
    q:String(params.q??''),
    tipo:String(params.tipo??''),
    citta:String(params.citta??''),
    provincia:String(params.provincia??''),
    regione:String(params.regione??''),
    categoria_merceologica:String(params.categoria_merceologica??''),
    pagamento:String(params.pagamento??''),
  };
  return`cplvcrm_clienti_${v}_${JSON.stringify(safe)}`;
}
function cplvcrmClearSessionCache(prefixes=[]){
  if(!Array.isArray(prefixes)||!prefixes.length)return;
  try{
    for(let i=sessionStorage.length-1;i>=0;i--){
      const key=sessionStorage.key(i);
      if(prefixes.some(prefix=>String(key||'').startsWith(prefix))){
        sessionStorage.removeItem(key);
      }
    }
  }catch(e){}
}
function cplvcrmClearClientiCache(){
  cplvcrmClearSessionCache(['cplvcrm_clienti_']);
}
function cplvcrmClearArticoliCache(){
  cplvcrmClearSessionCache(['cplvcrm_articoli_']);
}

// ========== DATA CONTEXT ==========
const DataCtx=createContext();
function DataProvider({children}){
  const{user}=useContext(AuthCtx);
  const[articoli,setArticoli]=useState([]);
  const[clienti,setClienti]=useState([]);
  const[preventivi,setPreventivi]=useState([]);
  const[opportunita,setOpportunita]=useState([]);
  const[opportunityStats,setOpportunityStats]=useState(null);
  const[documenti,setDocumenti]=useState([]);
  const[settings,setSettings]=useState({});
  const[toast,setToast]=useState(null);

  const showToast=(msg,type='success')=>setToast({msg,type});
  const visibleSections=getEffectiveVisibleSectionsForUser(user);
  const canSeeSection=useCallback(sectionId=>!visibleSections.length||visibleSections.includes(sectionId),[visibleSections]);
  const perfLight=useMemo(()=>cplvcrmPerfLightEnabled(settings),[settings]);
  const clientiRequestCacheRef=useRef({key:'',meta:null});
  const invalidateClientiCache=useCallback((opts={})=>{
    clientiRequestCacheRef.current={key:'',meta:null};
    cplvcrmClearClientiCache();
    if(opts.resetState)setClienti([]);
  },[]);
  const invalidateArticoliCache=useCallback((opts={})=>{
    cplvcrmClearArticoliCache();
    if(opts.resetState)setArticoli([]);
  },[]);

  const loadArticoli=useCallback(async(opts={})=>{
    const force=!!opts.force;
    const light=opts.light!==undefined?!!opts.light:perfLight;
    const cacheKey=cplvcrmArticoliCacheKey();
    if(!force){
      try{
        const cached=sessionStorage.getItem(cacheKey);
        if(cached){
          const parsed=JSON.parse(cached);
          if(Array.isArray(parsed)&&parsed.length){setArticoli(parsed);return parsed;}
        }
      }catch(e){}
    }
    try{
      const d=await api('articoli','GET',null,{light:light?'1':'0'});
      const rows=d.data||[];
      setArticoli(rows);
      try{sessionStorage.setItem(cacheKey,JSON.stringify(rows))}catch(e){}
      return rows;
    }catch(e){showToast(e.message,'error');throw e}
  },[perfLight,showToast]);
  const loadClienti=useCallback(async(filters={})=>{
    const force=!!filters.force;
    const hasQueryFilters=['q','tipo','citta','provincia','regione','categoria_merceologica','pagamento'].some(key=>String(filters[key]??'').trim()!=='');
    const isLookupRequest=!hasQueryFilters&&String(filters.offset??'0')==='0'&&String(filters.enrich??'0')==='0';
    try{
      const params={
        light:filters.light??(perfLight?'1':'0'),
        enrich:filters.enrich??'0',
        limit:filters.limit??(isLookupRequest?'0':'500'),
        offset:filters.offset??'0',
        ...filters,
      };
      delete params.force;
      const cacheKey=cplvcrmClientiCacheKey(params);
      if(isLookupRequest&&!force){
        if(clientiRequestCacheRef.current.key===cacheKey&&Array.isArray(clienti)&&clienti.length){
          const meta=clientiRequestCacheRef.current.meta||{total:clienti.length,limit:params.limit,offset:params.offset,enrich:params.enrich,light:params.light};
          return {...meta,data:clienti};
        }
        try{
          const cached=sessionStorage.getItem(cacheKey);
          if(cached){
            const parsed=JSON.parse(cached);
            if(parsed&&Array.isArray(parsed.data)){
              setClienti(parsed.data);
              clientiRequestCacheRef.current={key:cacheKey,meta:parsed.meta||null};
              return {...(parsed.meta||{total:parsed.data.length,limit:params.limit,offset:params.offset,enrich:params.enrich,light:params.light}),data:parsed.data};
            }
          }
        }catch(e){}
      }
      const d=await api('clienti','GET',null,params);
      const rows=d.data||[];
      const meta={...(d.meta||{}),total:(d.meta?.total??rows.length),data:undefined};
      if(isLookupRequest){
        setClienti(rows);
        clientiRequestCacheRef.current={key:cacheKey,meta};
        try{sessionStorage.setItem(cacheKey,JSON.stringify({data:rows,meta}))}catch(e){}
      }
      return {...meta,data:rows};
    }catch(e){showToast(e.message,'error');throw e}
  },[clienti,perfLight,showToast]);
  const loadPreventivi=useCallback(async(opts={})=>{
    try{
      const includeRighe=opts.includeRighe?'1':'0';
      const d=await api('preventivi','GET',null,{include_righe:includeRighe});
      const merged=cplvcrmMergeOfflinePreventivi(d.data||[]);
      setPreventivi(merged);
      return merged;
    }catch(e){
      if(cplvcrmOfflineActive()){
        const merged=cplvcrmMergeOfflinePreventivi([]);
        setPreventivi(merged);
        return merged;
      }
      showToast(e.message,'error');
      throw e;
    }
  },[showToast]);
  const loadOpportunita=useCallback(async()=>{try{const d=await api('opportunities');setOpportunita(d.data||[])}catch(e){showToast(e.message,'error')}},[]);
  const loadOpportunityStats=useCallback(async()=>{try{const d=await api('opportunity_stats');setOpportunityStats(d.data||null)}catch(e){showToast(e.message,'error')}},[]);
  const loadSettings=useCallback(async()=>{try{const d=await api('settings');setSettings(d.data||{})}catch(e){}},[]);
  const loadDocumenti=useCallback(async()=>{try{const d=await api('documenti');setDocumenti(d.data||[])}catch(e){}},[]);
  const previewQuote=useCallback(async(payload)=>{const d=await api('quote_preview','POST',payload);return d.data||null;},[]);

  useEffect(()=>{
    if(!user){
      invalidateArticoliCache({resetState:true});
      invalidateClientiCache({resetState:true});
      setPreventivi([]);
      setOpportunita([]);
      setOpportunityStats(null);
      setDocumenti([]);
      setSettings({});
      return;
    }
    const run=()=>{loadSettings()};
    if(typeof requestIdleCallback==='function'){
      requestIdleCallback(run,{timeout:2500});
    }else{
      setTimeout(run,0);
    }
  },[user,loadSettings,invalidateArticoliCache,invalidateClientiCache]);

  const allActiveArticles=useMemo(()=>articoli.filter(a=>a.attivo),[articoli]);
  const activeArticles=useMemo(()=>allActiveArticles.filter(a=>normalizeListinoTipoClient(a.listino_tipo)!=='ricambi'),[allActiveArticles]);
  const ricambiArticles=useMemo(()=>allActiveArticles.filter(a=>normalizeListinoTipoClient(a.listino_tipo)==='ricambi'),[allActiveArticles]);
  const sellableArticles=useMemo(()=>activeArticles.filter(a=>a.can_sell!==0),[activeArticles]);
  const categories=useMemo(()=>[...new Set(articoli.map(a=>a.categoria).filter(Boolean))].sort(),[articoli]);

  // Compute quote calculations
  const calcolaRiga=useCallback((riga)=>{
    const art=findArticleByReference(activeArticles,riga.articolo_nome);
    if(!art||!riga.qta)return{prezzoBase:0,prezzoNetto:0,totale:0,costoUnit:0,costoRiga:0,margineEur:0,marginePct:0};
    const prezzoBase=getArticoloBasePrice(art);
    const prezzoNetto=prezzoBase*(1-(riga.sconto||0));
    const totale=Math.round(riga.qta*prezzoNetto*100)/100;
    const costoRiga=Math.round(riga.qta*toNum(art.costo_netto)*100)/100;
    const margineEur=Math.round((totale-costoRiga)*100)/100;
    const marginePct=totale>0?margineEur/totale:0;
    return{prezzoBase,prezzoNetto,totale,costoUnit:toNum(art.costo_netto),costoRiga,margineEur,marginePct};
  },[activeArticles]);

  const calcolaRiepilogo=useCallback((prev)=>{
    if(!prev)return null;
    if((!prev.righe||!prev.righe.length)&&prev.totale_fattura!=null){
      const imponibile=toNum(prev.imponibile);
      const totaleFattura=toNum(prev.totale_fattura);
      const marginePct=toNum(prev.margine_pct);
      return{righeCalc:[],totaleRighe:imponibile,totaleCosto:0,imponibile,margineLordo:imponibile,marginePct,iva:Math.max(0,totaleFattura-imponibile),totaleFattura,provvEur:0,provvPct:0,mbo:0,utileNetto:imponibile,margineNetto:marginePct,rataMensile:0,coeff:0};
    }
    const righeCalc=(prev.righe||[]).map(r=>({...r,calc:calcolaRiga(r)}));
    const totaleRighe=righeCalc.reduce((s,r)=>s+r.calc.totale,0);
    const totaleCosto=righeCalc.reduce((s,r)=>s+r.calc.costoRiga,0);
    const imponibile=totaleRighe+(prev.trasporto||0)-(prev.trade_in||0);
    const margineLordo=imponibile-totaleCosto;
    const marginePct=imponibile>0?(imponibile-totaleCosto)/imponibile:0;
    const aliqIva=parseFloat(settings.aliquota_iva)||0.22;
    const iva=imponibile*aliqIva;
    const totaleFattura=imponibile+iva;
    // Provvigioni
    const baseCalcolo=settings.base_calcolo_provv||'Fatturato';
    const mMin=parseFloat(settings.margine_min_provv)||0.25;
    const mMax=parseFloat(settings.margine_max_provv)||0.5;
    const pMin=parseFloat(settings.provv_min)||0;
    const pMax=parseFloat(settings.provv_max)||0.1;
    const base=baseCalcolo==='Fatturato'?imponibile:margineLordo;
    let provvPct=marginePct<mMin?pMin:marginePct>mMax?pMax:pMin+(marginePct-mMin)*(pMax-pMin)/(mMax-mMin);
    const provvEur=base*provvPct;const mbo=provvEur*0.1;
    const utileNetto=imponibile-totaleCosto-provvEur;const margineNetto=imponibile>0?utileNetto/imponibile:0;
    // Noleggio
    let rt=[];try{rt=JSON.parse(settings.rate_table||'[]')}catch(e){}
    const dk=prev.noleggio_durata||'60';let coeff=0;
    for(let i=rt.length-1;i>=0;i--){if(imponibile>=rt[i].importo){coeff=rt[i].mesi[dk]||0;break}}
    const impNol=imponibile*(1-(prev.noleggio_anticipo||0));
    const rataMensile=coeff>0?Math.round(impNol*coeff/100*100)/100:0;
    return{righeCalc,totaleRighe,totaleCosto,imponibile,margineLordo,marginePct,iva,totaleFattura,provvEur,provvPct,mbo,utileNetto,margineNetto,rataMensile,coeff};
  },[settings,calcolaRiga]);

  const getCliente=(id)=>clienti.find(c=>c.id===parseInt(id))||null;

  const value={articoli,setArticoli,clienti,setClienti,preventivi,setPreventivi,opportunita,setOpportunita,opportunityStats,setOpportunityStats,documenti,setDocumenti,settings,setSettings,perfLight,
    allActiveArticles,activeArticles,ricambiArticles,sellableArticles,categories,calcolaRiga,calcolaRiepilogo,getCliente,previewQuote,
    loadArticoli,loadClienti,loadPreventivi,loadOpportunita,loadOpportunityStats,loadDocumenti,loadSettings,invalidateClientiCache,invalidateArticoliCache,showToast,toast,setToast};
  return<DataCtx.Provider value={value}>{children}</DataCtx.Provider>;
}

// ========== LOGIN PAGE ==========
function LoginPage(){
  const{login}=useContext(AuthCtx);
  const brandSettings=INITIAL_BRAND_SETTINGS||{};
  const azienda=getAzienda(brandSettings);
  const initialParams=useMemo(()=>new URLSearchParams(window.location.search),[]);
  const initialToken=initialParams.get('access_token')||'';
  const[mode,setMode]=useState(initialToken?'setup':'login');
  const[username,setUsername]=useState('');
  const[password,setPassword]=useState('');
  const[identifier,setIdentifier]=useState('');
  const[recoveryEmail,setRecoveryEmail]=useState('');
  const[setupPassword,setSetupPassword]=useState('');
  const[setupConfirm,setSetupConfirm]=useState('');
  const[showPassword,setShowPassword]=useState(false);
  const[error,setError]=useState('');
  const[info,setInfo]=useState('');
  const[loading,setLoading]=useState(false);
  const[accessToken]=useState(initialToken);
  const[tokenInfo,setTokenInfo]=useState(null);

  useEffect(()=>{
    if(mode!=='setup'||!accessToken)return;
    setLoading(true);
    api('auth_access_token_info','GET',null,{token:accessToken})
      .then(resp=>{setTokenInfo(resp.data||null);setError('')})
      .catch(err=>setError(err.message))
      .finally(()=>setLoading(false));
  },[mode,accessToken]);

  const resetMessages=()=>{setError('');setInfo('')};
  const goToMode=next=>{
    resetMessages();
    setMode(next);
    if(next!=='setup'){
      setSetupPassword('');
      setSetupConfirm('');
    }
  };
  const clearAccessLink=()=>{
    try{window.history.replaceState({},document.title,window.location.pathname)}catch(e){}
  };
  const handleSubmit=async(e)=>{
    e.preventDefault();setError('');setLoading(true);
    try{await login(username,password)}catch(e){setError(e.message)}finally{setLoading(false)}
  };
  const handleForgotPassword=async e=>{
    e.preventDefault();resetMessages();setLoading(true);
    try{
      const resp=await api('auth_forgot_password','POST',{identifier});
      setInfo(resp.message||'Se abbiamo trovato un account valido, abbiamo inviato il link di recupero.');
    }catch(err){setError(err.message)}finally{setLoading(false)}
  };
  const handleForgotUsername=async e=>{
    e.preventDefault();resetMessages();setLoading(true);
    try{
      const resp=await api('auth_forgot_username','POST',{email:recoveryEmail});
      setInfo(resp.message||'Se abbiamo trovato account associati, abbiamo inviato il promemoria username.');
    }catch(err){setError(err.message)}finally{setLoading(false)}
  };
  const handleAccessSetup=async e=>{
    e.preventDefault();resetMessages();
    if(setupPassword!==setupConfirm){setError('Le password non coincidono');return}
    setLoading(true);
    try{
      const resp=await api('auth_access_complete','POST',{token:accessToken,password:setupPassword});
      setInfo(resp.message||'Password impostata correttamente. Ora puoi accedere.');
      setUsername(tokenInfo?.username||'');
      setPassword('');
      setSetupPassword('');
      setSetupConfirm('');
      clearAccessLink();
      setMode('login');
    }catch(err){setError(err.message)}finally{setLoading(false)}
  };
  const modeMeta={
    login:{eyebrow:'Accesso sicuro',title:'Entra nel tuo spazio',subtitle:'Lavora su clienti, offerte, assistenza e attività con un’interfaccia più chiara e veloce.'},
    'forgot-password':{eyebrow:'Recupero password',title:'Reimposta l’accesso',subtitle:'Ti inviamo un link sicuro per scegliere una nuova password personale.'},
    'forgot-username':{eyebrow:'Recupero utente',title:'Trova il tuo username',subtitle:'Ricevi via email il promemoria degli accessi collegati al tuo indirizzo.'},
    setup:{eyebrow:'Attivazione account',title:'Completa l’accesso',subtitle:'Imposta la tua password personale e attiva l’account in modo protetto.'},
  }[mode]||{eyebrow:'Complevia',title:'Accedi',subtitle:''};
  return(
    <div className="login-stage cplv-ui-mac cplv-login-stage min-h-screen overflow-hidden flex items-center justify-center p-4 md:p-8">
      <div className="hero-orb w-72 h-72 bg-sky-200/80 top-10 left-6 md:top-10 md:left-24"></div>
      <div className="hero-orb w-80 h-80 bg-blue-100/90 bottom-6 right-4 md:right-16"></div>
      <div className="hero-orb w-64 h-64 bg-white/90 bottom-24 left-10 md:left-1/3"></div>
      <div className="login-grid">
        <div className="glass-bar login-brand-panel hidden lg:block">
          <div className="login-brand-content">
            <div className="space-y-6">
              <span className="eyebrow-label">Minimal business platform</span>
              <div className="space-y-5">
                <div className="logo-halo max-w-[390px]">
                  <CompanyLogo settings={brandSettings} maxH={110} maxW="100%" className="mx-auto" style={{objectPosition:'center center'}}/>
                </div>
                <div className="space-y-4 max-w-[560px]">
                  <h1 className="apple-title"><span className="accent">Complevia</span><br/>{S(azienda.nome)}</h1>
                  <p className="apple-subtitle">
                    Un ambiente più pulito, più leggibile e più elegante per lavorare su CRM, offerte,
                    assistenza e organizzazione operativa senza frizioni.
                  </p>
                </div>
              </div>
            </div>
            <div className="login-feature-grid">
              <div className="login-feature">
                <span className="login-feature-icon"><IcUsers s={18}/></span>
                <div>
                  <div className="font-semibold text-slate-900">Una sola vista, più chiarezza</div>
                  <div className="text-sm text-slate-500">Clienti, offerte, assistenza e attività rimangono leggibili anche da tablet e mobile.</div>
                </div>
              </div>
              <div className="login-feature">
                <span className="login-feature-icon"><IcGrid s={18}/></span>
                <div>
                  <div className="font-semibold text-slate-900">Interfaccia ordinata</div>
                  <div className="text-sm text-slate-500">Pulsanti, card e campi sono stati pensati per ridurre rumore visivo e velocizzare il lavoro.</div>
                </div>
              </div>
              <div className="login-feature">
                <span className="login-feature-icon"><IcTrend s={18}/></span>
                <div>
                  <div className="font-semibold text-slate-900">Esperienza premium</div>
                  <div className="text-sm text-slate-500">Minimalismo ispirato al mondo Apple: luce, profondità e gerarchie visive più immediate.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="surface-card login-form-panel max-w-xl w-full mx-auto">
          <div className="relative z-10">
            <div className="text-center lg:text-left mb-7 md:mb-8">
              <div className="flex justify-center lg:hidden mb-5">
                <div className="logo-halo w-full max-w-[320px]">
                  <CompanyLogo settings={brandSettings} maxH={84} maxW="100%" className="mx-auto" style={{objectPosition:'center center'}}/>
                </div>
              </div>
              <div className="space-y-3">
                <span className="eyebrow-label">{modeMeta.eyebrow}</span>
                <div className="space-y-2">
                  <h1 className="text-[2rem] md:text-[2.4rem] font-extrabold tracking-[-0.05em] text-slate-950">{modeMeta.title}</h1>
                  <p className="text-sm md:text-[15px] leading-7 text-slate-500 max-w-[540px] mx-auto lg:mx-0">{modeMeta.subtitle}</p>
                  <div className="pt-2">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">Complevia</div>
                    <div className="text-base md:text-lg font-bold tracking-[-0.03em] text-slate-900">{S(azienda.nome)}</div>
                    {azienda.sottotitolo?<div className="text-sm text-slate-500">{S(azienda.sottotitolo)}</div>:null}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-5">
        {mode==='login'?<form onSubmit={handleSubmit} className="space-y-4">
          <Inp label="Username o email" value={username} onChange={setUsername} placeholder="Inserisci username o email" required/>
          <div><label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2">Password<span className="text-red-500 ml-1">*</span></label>
            <input type={showPassword?'text':'password'} value={password} onChange={e=>setPassword(e.target.value)} placeholder="Inserisci password"
              className="field-shell w-full px-4 md:px-4.5 py-3 rounded-[22px] text-[15px] outline-none transition"/></div>
          <label className="flex items-center gap-2 text-sm text-slate-500">
            <input type="checkbox" checked={showPassword} onChange={e=>setShowPassword(e.target.checked)} className="accent-accent-400"/>
            <span>Mostra password</span>
          </label>
          {error&&<div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-[22px] border border-red-100">{error}</div>}
          {info&&<div className="bg-green-50 text-green-700 text-sm px-4 py-3 rounded-[22px] border border-green-100">{info}</div>}
          <Btn type="submit" className="w-full" s="lg" disabled={loading}>{loading?'Accesso...':'Accedi'}</Btn>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500 pt-1">
            <button type="button" onClick={()=>goToMode('forgot-password')} className="font-semibold hover:text-accent-500 transition-colors">Password dimenticata?</button>
            <button type="button" onClick={()=>goToMode('forgot-username')} className="font-semibold hover:text-accent-500 transition-colors">Utente dimenticato?</button>
          </div>
        </form>:null}

        {mode==='forgot-password'?<form onSubmit={handleForgotPassword} className="space-y-4">
          <div className="text-sm text-slate-500">Inserisci il tuo username o la tua email. Se troviamo un account valido, riceverai un link per impostare una nuova password.</div>
          <Inp label="Username o email" value={identifier} onChange={setIdentifier} placeholder="mario.rossi o nome@azienda.it" required/>
          {error&&<div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-[22px] border border-red-100">{error}</div>}
          {info&&<div className="bg-green-50 text-green-700 text-sm px-4 py-3 rounded-[22px] border border-green-100">{info}</div>}
          <div className="flex gap-3">
            <Btn type="button" v="secondary" className="flex-1" onClick={()=>goToMode('login')}>Torna al login</Btn>
            <Btn type="submit" className="flex-1" s="lg" disabled={loading}>{loading?'Invio...':'Invia link'}</Btn>
          </div>
        </form>:null}

        {mode==='forgot-username'?<form onSubmit={handleForgotUsername} className="space-y-4">
          <div className="text-sm text-slate-500">Inserisci la tua email. Se ci sono account collegati, riceverai un promemoria con lo username.</div>
          <Inp label="Email" type="email" value={recoveryEmail} onChange={setRecoveryEmail} placeholder="nome@azienda.it" required/>
          {error&&<div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-[22px] border border-red-100">{error}</div>}
          {info&&<div className="bg-green-50 text-green-700 text-sm px-4 py-3 rounded-[22px] border border-green-100">{info}</div>}
          <div className="flex gap-3">
            <Btn type="button" v="secondary" className="flex-1" onClick={()=>goToMode('login')}>Torna al login</Btn>
            <Btn type="submit" className="flex-1" s="lg" disabled={loading}>{loading?'Invio...':'Invia promemoria'}</Btn>
          </div>
        </form>:null}

        {mode==='setup'?<form onSubmit={handleAccessSetup} className="space-y-4">
          <div className="text-sm text-slate-500">
            {tokenInfo?.purpose==='reset'?'Imposta una nuova password per il tuo accesso.':'Completa il tuo accesso impostando una password personale.'}
          </div>
          {tokenInfo?<div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div><strong>Username:</strong> {S(tokenInfo.username||'-')}</div>
            {tokenInfo.nome?<div><strong>Utente:</strong> {S(tokenInfo.nome)}</div>:null}
          </div>:null}
          <Inp label="Nuova password" type={showPassword?'text':'password'} value={setupPassword} onChange={setSetupPassword} placeholder="Almeno 8 caratteri con lettere e numeri" required/>
          <Inp label="Conferma password" type={showPassword?'text':'password'} value={setupConfirm} onChange={setSetupConfirm} placeholder="Ripeti la password" required/>
          <label className="flex items-center gap-2 text-sm text-slate-500">
            <input type="checkbox" checked={showPassword} onChange={e=>setShowPassword(e.target.checked)} className="accent-accent-400"/>
            <span>Mostra password</span>
          </label>
          {error&&<div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-[22px] border border-red-100">{error}</div>}
          {info&&<div className="bg-green-50 text-green-700 text-sm px-4 py-3 rounded-[22px] border border-green-100">{info}</div>}
          <div className="flex gap-3">
            <Btn type="button" v="secondary" className="flex-1" onClick={()=>{clearAccessLink();goToMode('login')}}>Annulla</Btn>
            <Btn type="submit" className="flex-1" s="lg" disabled={loading||!accessToken}>{loading?'Salvataggio...':'Imposta password'}</Btn>
          </div>
        </form>:null}
            </div>
          </div>
        </div>
      </div>
    </div>);
}

function ProfiloPage(){
  const{user,refreshUser,logout}=useContext(AuthCtx);
  const{showToast}=useContext(DataCtx);
  const{isPhone}=useDevice();
  const[showPassword,setShowPassword]=useState(false);
  const[f,setF]=useState({
    nome:'',
    email:'',
    google_calendar_email:'',
    google_calendar_enabled:false,
    current_password:'',
    password:'',
    password_confirm:'',
  });
  const u=(k,v)=>setF(prev=>({...prev,[k]:v}));

  useEffect(()=>{
    setF({
      nome:user?.nome||'',
      email:user?.email||'',
      google_calendar_email:user?.google_calendar_email||'',
      google_calendar_enabled:!!user?.google_calendar_enabled,
      current_password:'',
      password:'',
      password_confirm:'',
    });
  },[user?.id,user?.nome,user?.email,user?.google_calendar_email,user?.google_calendar_enabled]);

  const save=async()=>{
    const wantsPasswordChange=!!(f.current_password||f.password||f.password_confirm);
    if(f.password&&f.password!==f.password_confirm){
      showToast('Le password non coincidono','error');
      return;
    }
    if(wantsPasswordChange&&!f.password){
      showToast('Inserisci la nuova password','error');
      return;
    }
    if(wantsPasswordChange&&!f.current_password){
      showToast('Inserisci la password attuale','error');
      return;
    }
    try{
      const response=await api('profile_update','PUT',{
        nome:f.nome||'',
        email:f.email||'',
        google_calendar_email:f.google_calendar_email||'',
        google_calendar_enabled:f.google_calendar_enabled?1:0,
        current_password:f.current_password||undefined,
        password:f.password||undefined,
      });
      await refreshUser();
      setF(prev=>({...prev,current_password:'',password:'',password_confirm:''}));
      if(response?.password_changed){
        showToast('Password aggiornata. Accedi di nuovo con la nuova password.');
        await logout();
        return;
      }
      showToast('Profilo aggiornato');
    }catch(e){showToast(e.message,'error')}
  };

  return(<Page title="Profilo personale" subtitle="Dati account, password e calendario Google">
    <Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Username" value={user?.username||''} onChange={()=>{}} disabled/>
        <Inp label="Nome completo" value={f.nome} onChange={v=>u('nome',v)}/>
        <Inp label="Email personale" value={f.email} onChange={v=>u('email',v)} placeholder="nome.cognome@azienda.it"/>
        <Inp label="Email Google Calendar" value={f.google_calendar_email} onChange={v=>u('google_calendar_email',v)} placeholder="nome.cognome@gmail.com"/>
      </div>
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 space-y-3">
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={!!f.google_calendar_enabled} onChange={e=>u('google_calendar_enabled',e.target.checked)} className="accent-accent-400"/>
          <div>
            <div className="text-sm font-medium text-gray-700">Calendario personale attivo</div>
            <div className="text-xs text-gray-500">I task assegnati con data e ora vengono pubblicati nel tuo calendario personale.</div>
          </div>
        </label>
        {user?.google_calendar_token&&f.google_calendar_enabled?<div>
          <div className="text-xs text-gray-500 mb-2">Link calendario personale</div>
          <input readOnly value={calendarFeedUrl(user.google_calendar_token)} className="w-full px-3 py-2 border rounded-lg text-sm bg-white text-slate-600"/>
        </div>:null}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <Inp label="Password attuale" type={showPassword?'text':'password'} value={f.current_password} onChange={v=>u('current_password',v)} placeholder="Richiesta solo se vuoi cambiarla"/>
        <Inp label="Nuova password" type={showPassword?'text':'password'} value={f.password} onChange={v=>u('password',v)} placeholder="Lascia vuoto per non cambiare"/>
        <Inp label="Conferma password" type={showPassword?'text':'password'} value={f.password_confirm} onChange={v=>u('password_confirm',v)} placeholder="Ripeti la nuova password"/>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-600 mt-3">
        <input type="checkbox" checked={showPassword} onChange={e=>setShowPassword(e.target.checked)} className="accent-accent-400"/>
        <span>Mostra password</span>
      </label>
      <div className={`flex ${isPhone?'flex-col':'justify-end'} gap-3 mt-5`}>
        <Btn v="success" className={isPhone?'w-full':''} icon={<IcCheck s={16}/>} onClick={save}>Salva profilo</Btn>
      </div>
    </Card>
  </Page>);
}

function ManualePage(){
  const brandSettings=useContext(DataCtx)?.settings||{};
  const azienda=getAzienda(brandSettings);
  const[manualHtml,setManualHtml]=useState('');
  const[manualLoading,setManualLoading]=useState(true);
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      setManualLoading(true);
      try{
        let content=MANUALE_CONTENT||'';
        if(!content.trim()){
          const res=await api('manuale_content','GET');
          content=res?.data?.content||'';
          if(content)MANUALE_CONTENT=content;
        }
        if(cancelled||!content.trim()){
          if(!cancelled)setManualHtml('');
          return;
        }
        const marked=await ensureMarked();
        if(cancelled)return;
        if(marked&&typeof marked.parse==='function'){
          setManualHtml(marked.parse(content,{gfm:true,breaks:true}));
        }else{
          setManualHtml(`<pre>${escapeHtml(content)}</pre>`);
        }
      }catch(e){
        if(!cancelled)setManualHtml('');
      }finally{
        if(!cancelled)setManualLoading(false);
      }
    })();
    return()=>{cancelled=true};
  },[]);

  return(<Page title="Guida operativa" subtitle="Manuale Complevia integrato nell'app" actions={
    <div className="rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-2 text-sm"><span className="text-slate-400">Azienda </span><strong className="text-navy-600">{S(azienda.nome)}</strong></div>
  }>
    <Card className="overflow-hidden">
      {manualLoading?<div className="text-sm text-slate-500 py-8 text-center">Caricamento manuale…</div>:manualHtml?<article className="manuale-content wrap-safe" dangerouslySetInnerHTML={{__html:manualHtml}}/>:<Empty icon={<IcFile s={34} c="text-slate-400"/>} title="Manuale non disponibile" sub="Non ho trovato ancora il file del manuale dentro il plugin."/>}
    </Card>
  </Page>);
}

// ========== DASHBOARD ==========
function DashboardPage(){
  const{isAdmin,isManager}=useContext(AuthCtx);
  const canViewStats=isAdmin||isManager;
  const{preventivi,getCliente,calcolaRiepilogo,opportunityStats,loadPreventivi}=useContext(DataCtx);
  const[stats,setStats]=useState(null);

  useEffect(()=>{
    const run=()=>loadPreventivi({includeRighe:false}).catch(()=>{});
    if(typeof requestIdleCallback==='function')requestIdleCallback(run,{timeout:2500});
    else setTimeout(run,80);
  },[loadPreventivi]);
  useEffect(()=>{
    if(!canViewStats)return;
    const run=()=>api('stats').then(d=>setStats(d.data)).catch(()=>{});
    if(typeof requestIdleCallback==='function')requestIdleCallback(run,{timeout:3000});
    else setTimeout(run,120);
  },[canViewStats]);

  const recentPrev=useMemo(()=>[...preventivi].sort((a,b)=>(b.updated_at||b.data||'').localeCompare(a.updated_at||a.data||'')).slice(0,10),[preventivi]);

  return(<Page title="Dashboard" subtitle="Panoramica commerciale">
    {stats&&<div className="stat-tile-grid">
      {[{label:'Totale',val:stats.totale,icon:<IcFile s={24} c="text-accent-400"/>,bg:'bg-blue-50'},
        {label:'Bozze',val:stats.bozze,icon:<IcEdit s={24} c="text-gray-500"/>,bg:'bg-gray-50'},
        {label:'Inviate',val:stats.inviate,icon:<IcEye s={24} c="text-blue-500"/>,bg:'bg-blue-50'},
        {label:'Accettate',val:stats.accettate,sub:fmt(stats.valore_accettate),icon:<IcCheck s={24} c="text-green-600"/>,bg:'bg-green-50'}
      ].map((s,i)=>(<div key={i} className={`stat-tile ${s.bg}`}>
        <div className="flex items-center justify-between mb-2 gap-2">{s.icon}<span className="text-xl md:text-2xl font-bold text-navy-600">{S(s.val)}</span></div>
        <div className="text-sm text-gray-600">{s.label}</div>
        {s.sub?<div className="text-xs text-green-600 font-medium mt-1">{s.sub}</div>:null}</div>))}
    </div>}
    {opportunityStats?<div className="stat-tile-grid">
      {[
        {label:'Lead attivi',val:opportunityStats.per_fase?.lead||0,bg:'bg-slate-50'},
        {label:'In trattativa',val:opportunityStats.per_fase?.trattativa||0,bg:'bg-orange-50'},
        {label:'Forecast pesato',val:fmt(opportunityStats.forecast_pesato),bg:'bg-indigo-50'},
        {label:'Chiuse vinte',val:opportunityStats.totale_vinte,bg:'bg-green-50'},
      ].map((item,i)=><div key={i} className={`stat-tile ${item.bg}`}>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{item.label}</div>
        <div className="text-2xl font-bold text-slate-900">{S(item.val)}</div>
      </div>)}
    </div>:null}
    <Card title="Preventivi recenti">
      {recentPrev.length===0?<p className="text-gray-400 text-center py-8">Nessun preventivo</p>:
      <div className="divide-y">{recentPrev.map(p=>{const cl=getCliente(p.cliente_id);const r=calcolaRiepilogo(p);return(
        <div key={p.id} className="flex items-center gap-4 py-3 px-2">
          <div className="flex-1 min-w-0"><div className="flex items-center gap-3 mb-1">
            <span className="font-bold text-navy-600 font-mono">{S(p.codice)}</span><StatoBadge stato={String(p.stato||'bozza')}/>
            <span className="text-xs text-gray-400">{fmtDate(p.data)}</span></div>
            <div className="text-sm text-gray-600 truncate">{cl?<strong>{S(cl.azienda||cl.nome)}</strong>:<span className="italic text-gray-400">Nessun cliente</span>}</div></div>
          <div className="text-right"><div className="font-bold text-navy-600">{fmt(r?r.totaleFattura:0)}</div>
            <div className="text-xs text-gray-400">{'Margine '+fmt(r?r.marginePct:0,'pct')}</div></div>
        </div>)})}</div>}
    </Card>
  </Page>);
}

// ========== UTENTI (Admin) ==========
function UtentiPage(){
  const[users,setUsers]=useState([]);
  const[showForm,setShowForm]=useState(false);
  const[editUser,setEditUser]=useState(null);
  const[permUser,setPermUser]=useState(null);
  const{showToast}=useContext(DataCtx);
  const{isPhone}=useDevice();
  const userMap=useMemo(()=>Object.fromEntries(users.map(row=>[String(row.id),row])),[users]);
  const stats=useMemo(()=>{
    const active=users.filter(row=>row.attivo).length;
    const restricted=users.filter(row=>normalizeFrontendVisibleSections(row.visible_sections).length>0).length;
    const impersonable=users.filter(row=>effectiveMansioniRole(row.ruolo)!=='admin').length;
    return{total:users.length,active,restricted,impersonable};
  },[users]);

  const load=async()=>{try{const d=await api('users');setUsers(d.data||[])}catch(e){showToast(e.message,'error')}};
  useEffect(()=>{load()},[]);
  const deleteUser=async(target)=>{
    if(!target?.id)return;
    if(!confirm(`Eliminare l'utente ${target.nome||target.username}?`))return;
    try{
      await api('user_delete','DELETE',null,{id:target.id});
      showToast('Utente eliminato');
      load();
    }catch(e){showToast(e.message,'error')}
  };

  return(<Page title="Utenti e gerarchie" subtitle="Accessi, ruoli e visibilità sezioni" actions={<Btn s={isPhone?'sm':'md'} icon={<IcPlus s={16}/>} onClick={()=>{setEditUser(null);setShowForm(true)}}>Nuovo utente</Btn>}>
    <div className="stat-tile-grid">
      <div className="stat-tile bg-slate-50"><div className="text-xs uppercase tracking-wide text-slate-400">Utenti totali</div><div className="mt-2 text-2xl font-bold text-navy-600">{stats.total}</div></div>
      <div className="stat-tile bg-emerald-50"><div className="text-xs uppercase tracking-wide text-slate-400">Attivi</div><div className="mt-2 text-2xl font-bold text-emerald-600">{stats.active}</div></div>
      <div className="stat-tile bg-amber-50"><div className="text-xs uppercase tracking-wide text-slate-400">Visibilità custom</div><div className="mt-2 text-2xl font-bold text-amber-600">{stats.restricted}</div></div>
      <div className="stat-tile bg-sky-50"><div className="text-xs uppercase tracking-wide text-slate-400">Simulabili</div><div className="mt-2 text-2xl font-bold text-sky-600">{stats.impersonable}</div></div>
    </div>
    <Card>
      <div className="divide-y">{users.map(u=>(
        <div key={u.id} className={`flex ${isPhone?'flex-col':'items-start'} gap-4 py-4 px-2`}>
          <div className="w-10 h-10 rounded-full bg-accent-50 flex items-center justify-center"><IcUser s={20} c="text-accent-400"/></div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-navy-600">{u.nome||u.username}</div>
            <div className="text-sm text-gray-500 truncate">
              @{u.username} · <span className="font-medium text-gray-700">{u.role_label||HIVE_ROLE_LABELS[u.ruolo]||u.ruolo}</span>
              {u.job_title?` · ${S(u.job_title)}`:''}
            </div>
            {u.email?<div className="text-xs text-gray-500 truncate">{S(u.email)}</div>:null}
            <div className="text-xs text-gray-500 truncate">{u.email?'Accesso sicuro con password personale gestita dall’utente':'Email mancante per attivazione autonoma'}</div>
            <div className="text-xs text-gray-400 truncate">
              {(u.manager_user_ids||[]).length?`Riporta a ${(u.manager_user_ids||[]).map(id=>userMap[String(id)]?.nome||userMap[String(id)]?.username).filter(Boolean).join(', ')}`:'Senza responsabile diretto'}
              {u.direct_reports_count?` · ${u.direct_reports_count} sotto`:'' 
              }
            </div>
            {parseMansioniAssignments(u.mansioni_json).length?<div className="flex flex-wrap gap-1 mt-2">
              {parseMansioniAssignments(u.mansioni_json).slice(0,4).map((mansione,idx)=><span key={idx} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{S((mansione.title||mansioneAreaLabel(mansione.area))+' · '+mansioneLevelLabel(mansione.level))}</span>)}
            </div>:null}
            {u.ruolo==='rivenditore'?(<div className="text-xs text-gray-500 mt-1 truncate">
              {`Zone: ${parseStoredList(u.allowed_regioni).slice(0,3).join(', ')||'nessuna regione'}${parseStoredList(u.allowed_province).length?` · Prov.: ${parseStoredList(u.allowed_province).slice(0,4).join(', ')}`:''}`}
            </div>):null}
            {(()=>{
              const summary=getVisibleSectionSummary(u);
              return(
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className={`text-[11px] px-2 py-1 rounded-full ${summary.inherited?'bg-emerald-50 text-emerald-700':'bg-amber-50 text-amber-700'}`}>
                    {summary.inherited?'Visibilità ereditata dal ruolo':'Visibilità personalizzata'}
                  </span>
                  {summary.effective.slice(0,5).map(section=><span key={section} className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-600">{USER_VISIBLE_SECTION_OPTIONS.find(item=>item.value===section)?.label||section}</span>)}
                  {summary.effective.length>5?<span className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-500">+{summary.effective.length-5}</span>:null}
                </div>
              );
            })()}
            {u.google_calendar_enabled?<div className="text-xs text-emerald-600 mt-1 truncate">{`Calendario personale attivo${u.google_calendar_email?` · ${u.google_calendar_email}`:''}`}</div>:null}
            {!parseMansioniAssignments(u.mansioni_json).length&&u.mansioni?<div className="text-xs text-gray-500 mt-1 line-clamp-2">{S(u.mansioni)}</div>:null}
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`text-xs px-2 py-1 rounded-full ${u.attivo?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>{u.attivo?'Attivo':'Disattivo'}</span>
            {['commerciale','agente','ape_coordinatrice','rivenditore'].includes(String(u.ruolo||'').trim().toLowerCase())?<Btn v="secondary" s="sm" onClick={()=>setPermUser(u)}>Permessi catalogo</Btn>:null}
            <div className="flex items-center gap-2">
              <Btn v="ghost" s="sm" onClick={()=>{setEditUser(u);setShowForm(true)}}><IcEdit s={16}/></Btn>
              <Btn v="danger" s="sm" onClick={()=>deleteUser(u)}><IcTrash s={16}/></Btn>
            </div>
          </div>
        </div>))}</div>
    </Card>
    {showForm&&<UserForm user={editUser} users={users} onClose={()=>setShowForm(false)} onSaved={()=>{setShowForm(false);load()}}/>}
    {permUser&&<AgentPermissionsModal user={permUser} onClose={()=>setPermUser(null)} onSaved={()=>{setPermUser(null);load()}}/>}
  </Page>);
}

function UserForm({user,users,onClose,onSaved}){
  const{showToast,clienti}=useContext(DataCtx);
  const defaultAssignment={title:'',area:'commerciale',level:'operativo',description:''};
  const[showPassword,setShowPassword]=useState(false);
  const[f,setF]=useState(user?{
    ...user,
    password:'',
    email:user.email||'',
    google_calendar_email:user.google_calendar_email||'',
    google_calendar_enabled:!!user.google_calendar_enabled,
    visible_sections:Array.isArray(user.visible_sections)?user.visible_sections:[],
    allowed_regioni:parseStoredList(user.allowed_regioni),
    allowed_province:parseStoredList(user.allowed_province),
    manager_user_ids:parseStoredList(user.manager_user_ids||[]).map(String),
    mansioni_json:parseMansioniAssignments(user.mansioni_json).length?parseMansioniAssignments(user.mansioni_json):[defaultAssignment]
  }:{
    username:'',
    nome:'',
    email:'',
    ruolo:'commerciale',
    manager_user_ids:[],
    job_title:'',
    mansioni:'',
    mansioni_json:[defaultAssignment],
    google_calendar_email:'',
    google_calendar_enabled:false,
    visible_sections:[],
    allowed_regioni:[],
    allowed_province:[],
    send_credentials_email:true,
    password:'',
    attivo:1
  });
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const managerOptions=useMemo(()=>users.filter(row=>row.id!==user?.id).map(row=>({value:String(row.id),label:`${S(row.nome||row.username)} · ${S(row.role_label||HIVE_ROLE_LABELS[row.ruolo]||row.ruolo)}`})),[users,user?.id]);
  const regionOptions=useMemo(()=>uniqueTextValues(clienti.map(c=>c.regione).filter(Boolean)),[clienti]);
  const provinceOptions=useMemo(()=>uniqueTextValues(clienti.map(c=>normalizeProvincia(c.provincia)).filter(Boolean)),[clienti]);
  const regionProvinceMap=useMemo(()=>{
    const map={};
    clienti.forEach(c=>{
      const region=normalizeText(c.regione);
      const province=normalizeProvincia(c.provincia);
      if(!region||!province)return;
      const key=normalizeToken(region);
      if(!map[key])map[key]=new Set();
      map[key].add(province);
    });
    return map;
  },[clienti]);
  const rolePreviewUser=useMemo(()=>({ruolo:f.ruolo,visible_sections:f.visible_sections}),[f.ruolo,f.visible_sections]);
  const roleVisibleSections=useMemo(()=>getRoleVisibleSections(rolePreviewUser),[rolePreviewUser]);
  const effectiveVisibleSections=useMemo(()=>getEffectiveVisibleSectionsForUser(rolePreviewUser),[rolePreviewUser]);
  const inheritedVisibility=normalizeFrontendVisibleSections(f.visible_sections).length===0;
  const toggleManager=id=>setF(prev=>{
    const current=(prev.manager_user_ids||[]).map(String);
    const next=current.includes(String(id))?current.filter(item=>item!==String(id)):[...current,String(id)];
    return{...prev,manager_user_ids:next};
  });
  const toggleVisibleSection=section=>setF(prev=>{
    const current=Array.isArray(prev.visible_sections)?prev.visible_sections:[];
    return{
      ...prev,
      visible_sections:current.includes(section)?current.filter(item=>item!==section):[...current,section]
    };
  });
  const updateAssignment=(index,key,value)=>setF(prev=>({...prev,mansioni_json:(prev.mansioni_json||[]).map((item,i)=>i===index?{...item,[key]:value}:item)}));
  const addAssignment=()=>setF(prev=>({...prev,mansioni_json:[...(prev.mansioni_json||[]),{...defaultAssignment}]}));
  const removeAssignment=index=>setF(prev=>({...prev,mansioni_json:(prev.mansioni_json||[]).filter((_,i)=>i!==index)}));
  const toggleTerritory=(field,value)=>setF(prev=>{
    const current=parseStoredList(prev[field]);
    const exists=current.includes(value);
    if(field==='allowed_regioni'&&!exists){
      const regionKey=normalizeToken(value);
      const autoProvince=[...(regionProvinceMap[regionKey]||new Set())];
      const nextProvince=uniqueTextValues([...parseStoredList(prev.allowed_province),...autoProvince]);
      return{...prev,allowed_regioni:[...current,value],allowed_province:nextProvince};
    }
    return{...prev,[field]:exists?current.filter(item=>item!==value):[...current,value]};
  });
  const save=async()=>{
    if(!f.username){showToast('Username obbligatorio','error');return}
    if(!user&&f.send_credentials_email&&!String(f.email||'').trim()){showToast('Inserisci una email per inviare il link di attivazione','error');return}
    try{
      const assignments=parseMansioniAssignments(f.mansioni_json).filter(item=>item.title||item.description||item.area!=='commerciale'||item.level!=='operativo');
      const payload={
        nome:f.nome,
        email:f.email||'',
        ruolo:f.ruolo,
        manager_user_ids:(f.manager_user_ids||[]).map(v=>parseInt(v)).filter(Boolean),
        job_title:f.job_title||assignments[0]?.title||'',
        mansioni:f.mansioni||mansioniSummaryLines(assignments).join('\n'),
        mansioni_json:assignments,
        google_calendar_email:f.google_calendar_email||'',
        google_calendar_enabled:f.google_calendar_enabled?1:0,
        visible_sections:Array.isArray(f.visible_sections)?f.visible_sections:[],
        allowed_regioni:parseStoredList(f.allowed_regioni),
        allowed_province:parseStoredList(f.allowed_province),
        send_credentials_email:f.send_credentials_email?1:0,
        password:f.password||undefined,
        attivo:f.attivo
      };
      let resp;
      if(user){resp=await api('user_update','PUT',payload,{id:user.id})}
      else{
        resp=await api('user_create','POST',{username:f.username,password:f.password||'',...payload})
      }
      if(user){
        showToast('Utente aggiornato');
      }else if(resp?.data?.email_sent){
        showToast('Utente creato ed email di attivazione inviata');
      }else if(resp?.data?.manual_password_set){
        showToast('Utente creato con password impostata manualmente');
      }else if(f.send_credentials_email){
        showToast(`Utente creato, ma email di attivazione non inviata${resp?.data?.email_error?`: ${resp.data.email_error}`:''}`,'error');
      }else{
        showToast('Utente creato');
      }
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };
  const deleteCurrentUser=async()=>{
    if(!user?.id)return;
    if(!confirm(`Eliminare l'utente ${user.nome||user.username}?`))return;
    try{
      await api('user_delete','DELETE',null,{id:user.id});
      showToast('Utente eliminato');
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };
  return(<Modal title={user?'Modifica Utente':'Nuovo Utente'} onClose={onClose}>
    <div className="space-y-4">
      <Inp label="Username" value={f.username} onChange={v=>u('username',v)} disabled={!!user} required/>
      <Inp label="Nome completo" value={f.nome} onChange={v=>u('nome',v)}/>
      <Inp label="Email personale" value={f.email||''} onChange={v=>u('email',v)} placeholder="nome.cognome@azienda.it"/>
      {!user?<label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <input type="checkbox" checked={!!f.send_credentials_email} onChange={e=>u('send_credentials_email',e.target.checked)} className="w-4 h-4 text-accent-500 rounded"/>
        <span>Invia subito email con link di attivazione e istruzioni</span>
      </label>:null}
      <Sel label="Ruolo accesso" value={f.ruolo} onChange={v=>u('ruolo',v)} options={HIVE_ROLE_OPTIONS}/>
      <Inp label="Titolo principale" value={f.job_title||''} onChange={v=>u('job_title',v)} placeholder="Responsabile commerciale / Operatore tecnico"/>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-slate-400">Ruolo effettivo</div>
          <div className="mt-2 font-semibold text-navy-600">{S(HIVE_ROLE_LABELS[f.ruolo]||f.ruolo||'Utente')}</div>
          <div className="text-xs text-slate-500 mt-1">Base di partenza per i permessi operativi.</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-slate-400">Sezioni base ruolo</div>
          <div className="mt-2 font-semibold text-navy-600">{roleVisibleSections.length}</div>
          <div className="text-xs text-slate-500 mt-1">Aree accessibili prima delle personalizzazioni.</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-slate-400">Sezioni attive finali</div>
          <div className="mt-2 font-semibold text-navy-600">{effectiveVisibleSections.length}</div>
          <div className="text-xs text-slate-500 mt-1">{inheritedVisibility?'Ereditate dal ruolo':'Filtrate manualmente per questo utente'}</div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-gray-700">Persone sopra di lui</div>
            <div className="text-xs text-gray-500">Puoi indicare più responsabili se la persona lavora su più linee aziendali.</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {managerOptions.length===0?<span className="text-sm text-gray-400">Nessun responsabile disponibile</span>:managerOptions.map(option=>(
            <button type="button" key={option.value} onClick={()=>toggleManager(option.value)} className={`px-3 py-1.5 rounded-full text-sm border transition ${(f.manager_user_ids||[]).includes(option.value)?'bg-navy-600 text-white border-navy-600':'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{option.label}</button>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-gray-700">Mansioni aziendali</div>
            <div className="text-xs text-gray-500">Ogni persona può coprire più mansioni tra Amministrazione, Tecnico e Commerciale come manager o operativo.</div>
          </div>
          <Btn s="sm" icon={<IcPlus s={14}/>} onClick={addAssignment}>Aggiungi mansione</Btn>
        </div>
        {(f.mansioni_json||[]).map((mansione,index)=><div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Inp label="Titolo mansione" value={mansione.title||''} onChange={v=>updateAssignment(index,'title',v)} placeholder="Back office clienti / Tecnico installazioni"/>
            <Sel label="Area" value={mansione.area||'commerciale'} onChange={v=>updateAssignment(index,'area',v)} options={MANSIONE_AREA_OPTIONS}/>
            <Sel label="Livello" value={mansione.level||'operativo'} onChange={v=>updateAssignment(index,'level',v)} options={MANSIONE_LEVEL_OPTIONS}/>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Descrizione</label>
            <textarea value={mansione.description||''} onChange={e=>updateAssignment(index,'description',e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400" placeholder="Responsabilità e perimetro di questa mansione."/>
          </div>
          {(f.mansioni_json||[]).length>1?<div className="flex justify-end"><Btn s="sm" v="danger" onClick={()=>removeAssignment(index)}>Rimuovi</Btn></div>:null}
        </div>)}
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 space-y-3">
        <div>
          <div className="text-sm font-medium text-gray-700">Visibilità e personalizzazione</div>
          <div className="text-xs text-gray-500">Rendi chiaro cosa vede davvero l’utente: se non selezioni nulla eredita tutto dal ruolo, se selezioni una o più sezioni limiti la sua esperienza a quelle aree compatibili con il ruolo.</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-white bg-white/80 px-4 py-4 space-y-3">
            <div className="text-sm font-semibold text-slate-700">Sezioni base del ruolo</div>
            <div className="flex flex-wrap gap-2">
              {roleVisibleSections.map(section=><span key={section} className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">{USER_VISIBLE_SECTION_OPTIONS.find(item=>item.value===section)?.label||section}</span>)}
            </div>
          </div>
          <div className="rounded-2xl border border-white bg-white/80 px-4 py-4 space-y-3">
            <div className="text-sm font-semibold text-slate-700">Anteprima finale utente</div>
            <div className="flex flex-wrap gap-2">
              {effectiveVisibleSections.map(section=><span key={section} className="text-[11px] px-2 py-1 rounded-full bg-blue-50 text-blue-700">{USER_VISIBLE_SECTION_OPTIONS.find(item=>item.value===section)?.label||section}</span>)}
            </div>
            <div className={`text-xs ${inheritedVisibility?'text-emerald-600':'text-amber-600'}`}>{inheritedVisibility?'Nessun filtro manuale: l’utente eredita le sezioni del ruolo.':'Filtro manuale attivo: la preview mostra esattamente cosa vedrà l’utente.'}</div>
          </div>
        </div>
        <div className="space-y-3">
          {USER_VISIBLE_SECTION_GROUPS.map(group=>{
            const options=USER_VISIBLE_SECTION_OPTIONS.filter(option=>group.sections.includes(option.value)&&roleVisibleSections.includes(option.value));
            if(!options.length)return null;
            return(
              <div key={group.id} className="rounded-2xl border border-white bg-white/80 px-4 py-4 space-y-3">
                <div className="text-sm font-semibold text-slate-700">{group.label}</div>
                <div className="flex flex-wrap gap-2">
                  {options.map(option=>(
                    <button type="button" key={option.value} onClick={()=>toggleVisibleSection(option.value)} className={`px-3 py-1.5 rounded-full text-sm border transition ${(f.visible_sections||[]).includes(option.value)?'bg-navy-600 text-white border-navy-600':'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{option.label}</button>
                  ))}
                </div>
              </div>
            );
          })}
          {!roleVisibleSections.length?<div className="text-sm text-slate-400">Questo ruolo non ha sezioni configurate.</div>:null}
        </div>
      </div>
      {f.ruolo==='rivenditore'?<div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Regioni visibili</div>
          <div className="flex flex-wrap gap-2">
            {regionOptions.length===0?<span className="text-sm text-gray-400">Nessuna regione disponibile</span>:regionOptions.map(region=>(
              <button type="button" key={region} onClick={()=>toggleTerritory('allowed_regioni',region)} className={`px-3 py-1.5 rounded-full text-sm border transition ${parseStoredList(f.allowed_regioni).includes(region)?'bg-navy-600 text-white border-navy-600':'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{S(region)}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Province visibili</div>
          <div className="flex flex-wrap gap-2">
            {provinceOptions.length===0?<span className="text-sm text-gray-400">Nessuna provincia disponibile</span>:provinceOptions.map(province=>(
              <button type="button" key={province} onClick={()=>toggleTerritory('allowed_province',province)} className={`px-3 py-1.5 rounded-full text-sm border transition ${parseStoredList(f.allowed_province).includes(province)?'bg-accent-400 text-white border-accent-400':'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{S(province)}</button>
            ))}
          </div>
        </div>
      </div>:null}
      <div><label className="block text-sm font-medium text-gray-600 mb-1">Note ruolo</label>
        <textarea value={f.mansioni||''} onChange={e=>u('mansioni',e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400" placeholder="Note aggiuntive o indicazioni organizzative."/></div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 space-y-3">
        <div>
          <div className="text-sm font-medium text-gray-700">Calendario personale</div>
          <div className="text-xs text-gray-500">Dati del calendario Google personale dell’utente. I task assegnati con data e ora verranno pubblicati in questo calendario tramite feed.</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Inp label="Email Google Calendar" value={f.google_calendar_email||''} onChange={v=>u('google_calendar_email',v)} placeholder="nome.cognome@gmail.com"/>
          <label className="flex items-center gap-3 px-3 py-2 border rounded-lg self-end bg-white">
            <input type="checkbox" checked={!!f.google_calendar_enabled} onChange={e=>u('google_calendar_enabled',e.target.checked)} className="accent-accent-400"/>
            <span className="text-sm text-gray-700">Calendario personale attivo</span>
          </label>
        </div>
        {user?.google_calendar_token&&f.google_calendar_enabled?<div>
          <div className="text-xs text-gray-500 mb-2">Link calendario personale</div>
          <input readOnly value={calendarFeedUrl(user.google_calendar_token)} className="w-full px-3 py-2 border rounded-lg text-sm bg-white text-slate-600"/>
        </div>:null}
      </div>
      <Inp label={user?'Nuova password (facoltativa)':'Password manuale (facoltativa)'} type={showPassword?'text':'password'} value={f.password} onChange={v=>u('password',v)} placeholder={user?'Lascia vuoto per non cambiare':'Lascia vuoto: l’utente la imposterà dal link email'}/>
      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={showPassword} onChange={e=>setShowPassword(e.target.checked)} className="accent-accent-400"/>
        <span>Mostra password</span>
      </label>
      {user&&<Sel label="Stato" value={f.attivo} onChange={v=>u('attivo',parseInt(v))} options={[{value:1,label:'Attivo'},{value:0,label:'Disattivo'}]}/>}
      <div className="flex justify-between gap-3 pt-2">
        <div>
          {user?<Btn v="danger" onClick={deleteCurrentUser}>Elimina utente</Btn>:null}
        </div>
        <div className="flex gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva</Btn>
        </div>
      </div>
    </div></Modal>);
}

function AgentPermissionsModal({user,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const[items,setItems]=useState([]);
  const[search,setSearch]=useState('');
  const[loading,setLoading]=useState(true);
  const[saveLoading,setSaveLoading]=useState(false);
  const isDealer=String(user?.ruolo||'').trim().toLowerCase()==='rivenditore';
  const subjectLabel=isDealer?'rivenditore':'commerciale';

  useEffect(()=>{
    let mounted=true;
    api('agent_permissions','GET',null,{user_id:user.id})
      .then(d=>{if(mounted)setItems(d.data||[])})
      .catch(e=>showToast(e.message,'error'))
      .finally(()=>{if(mounted)setLoading(false)});
    return()=>{mounted=false};
  },[user.id]);

  const filtered=useMemo(()=>{
    if(!search)return items;
    const s=search.toLowerCase();
    return items.filter(i=>(i.nome||'').toLowerCase().includes(s)||(i.sku||'').toLowerCase().includes(s)||(i.categoria||'').toLowerCase().includes(s));
  },[items,search]);

  const updateRow=(id,key,value)=>setItems(prev=>prev.map(item=>{
    if(item.id!==id)return item;
    const next={...item,[key]:value?1:0};
    if(key==='can_sell'&&value)next.can_view=1;
    if(key==='can_view'&&!value)next.can_sell=0;
    return next;
  }));

  const applyBulk=mode=>setItems(prev=>prev.map(item=>{
    if(mode==='none')return{...item,can_view:0,can_sell:0};
    if(mode==='view')return{...item,can_view:1};
    return{...item,can_view:1,can_sell:1};
  }));

  const save=async()=>{
    setSaveLoading(true);
    try{
      await api('agent_permissions_update','PUT',{user_id:user.id,permissions:items.map(i=>({articolo_id:i.id,can_view:i.can_view,can_sell:i.can_sell}))});
      showToast('Permessi catalogo aggiornati');
      onSaved();
    }catch(e){showToast(e.message,'error')}
    finally{setSaveLoading(false)}
  };

  return(<Modal title={`Permessi Catalogo · ${user.nome||user.username}`} onClose={onClose} wide>
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        {isDealer
          ?'Qui decidi quali articoli il rivenditore puo vedere e vendere. Se non salvi ancora alcun filtro, il rivenditore continua a vedere il listino completo.'
          :'Qui decidi quali articoli il commerciale puo vedere e vendere.'}
      </div>
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="relative flex-1"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca articolo..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/></div>
        <div className="flex gap-2 flex-wrap">
          <Btn s="sm" v="secondary" onClick={()=>applyBulk('none')}>Blocca tutto</Btn>
          <Btn s="sm" v="secondary" onClick={()=>applyBulk('view')}>Vede tutto</Btn>
          <Btn s="sm" onClick={()=>applyBulk('sell')}>Vende tutto</Btn>
        </div>
      </div>
      {loading?<Loading/>:
      <div className="overflow-x-auto border rounded-xl"><table className="w-full text-sm">
        <thead><tr className="bg-navy-600 text-white">
          <th className="px-3 py-2.5 text-left">SKU</th>
          <th className="px-3 py-2.5 text-left">Articolo</th>
          <th className="px-3 py-2.5 text-left">Categoria</th>
          <th className="px-3 py-2.5 text-center">Regola</th>
          <th className="px-3 py-2.5 text-right">Listino</th>
          <th className="px-3 py-2.5 text-left">Condizioni</th>
          <th className="px-3 py-2.5 text-center">Vede</th>
          <th className="px-3 py-2.5 text-center">Vende</th>
        </tr></thead>
        <tbody>{filtered.map((item,i)=>(
          <tr key={item.id} className={`border-b ${i%2?'bg-gray-50/50':''}`}>
            <td className="px-3 py-2">{S(item.sku)}</td>
            <td className="px-3 py-2">{S(item.nome)}</td>
            <td className="px-3 py-2">{S(item.categoria)}</td>
            <td className="px-3 py-2 text-center"><span className="text-xs bg-navy-50 text-navy-600 px-2 py-1 rounded-full">{S(item.discount_rule_code||'-')}</span></td>
            <td className="px-3 py-2 text-right">{fmt(item.prezzo_listino)}</td>
            <td className="px-3 py-2 text-xs text-gray-600">
              <div>{S(item.discount_rule_rivenditore||'Da definire')}</div>
              <div className="text-gray-400">{S(item.discount_rule_cliente||'Da definire')}</div>
            </td>
            <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!item.can_view} onChange={e=>updateRow(item.id,'can_view',e.target.checked)} className="accent-accent-400"/></td>
            <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!item.can_sell} onChange={e=>updateRow(item.id,'can_sell',e.target.checked)} className="accent-accent-400"/></td>
          </tr>
        ))}</tbody>
      </table></div>}
      <div className="flex justify-end gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save} disabled={saveLoading}>{saveLoading?'Salvataggio...':`Salva permessi ${subjectLabel}`}</Btn>
      </div>
    </div>
  </Modal>);
}

// ========== CATALOGO ==========
function CatalogoPage(){
  const{articoli,categories,loadArticoli,showToast}=useContext(DataCtx);
  const{isAdmin,isManager}=useContext(AuthCtx);
  const{isPhone}=useDevice();
  const canView=isAdmin||isManager;
  const[listinoTipo,setListinoTipo]=useState('principale');
  const[search,setSearch]=useState('');
  const[catFilter,setCatFilter]=useState('');
  const[showForm,setShowForm]=useState(false);
  const[editArt,setEditArt]=useState(null);
  const[showImport,setShowImport]=useState(false);
  const[showCatManager,setShowCatManager]=useState(false);
  const articoliListino=useMemo(()=>articoli.filter(a=>normalizeListinoTipoClient(a.listino_tipo)===listinoTipo),[articoli,listinoTipo]);
  const categoriesForList=useMemo(()=>[...new Set(articoliListino.map(a=>a.categoria).filter(Boolean))].sort(),[articoliListino]);
  useEffect(()=>{loadArticoli().catch(()=>{})},[loadArticoli]);

  const filtered=useMemo(()=>{
    let list=articoliListino.filter(a=>!(a.nome||'').startsWith('_placeholder_categoria_'));
    if(catFilter)list=list.filter(a=>a.categoria===catFilter);
    if(search){const s=search.toLowerCase();list=list.filter(a=>(a.nome||'').toLowerCase().includes(s)||(a.sku||'').toLowerCase().includes(s))}
    return list;
  },[articoliListino,search,catFilter]);
  useEffect(()=>{if(catFilter&&!categoriesForList.includes(catFilter))setCatFilter('')},[catFilter,categoriesForList]);

  const deleteArt=async(id)=>{
    if(!confirm('Eliminare questo articolo?'))return;
    try{await api('articolo_delete','DELETE',null,{id});showToast('Articolo eliminato');loadArticoli()}catch(e){showToast(e.message,'error')}
  };
  const duplicateArt=articleDraft=>{
    const draft={...(articleDraft||{})};
    delete draft.id;
    delete draft.created_at;
    delete draft.updated_at;
    draft._isDuplicateDraft=true;
    draft.listino_tipo=normalizeListinoTipoClient(draft.listino_tipo||listinoTipo);
    draft.sku=duplicatedArticleValue(draft.sku,'COPY','-');
    draft.nome=duplicatedArticleValue(draft.nome,'Copia');
    setEditArt(draft);
    setShowForm(true);
  };

  const exportCSV=()=>{
    const cols=['sku','nome','listino_tipo','categoria','categoria_agente','um','costo_netto','margine_min','markup_riv','markup_cliente','prezzo_listino','tipo_sconto','famiglia','sconto_fornitore','attivo','descrizione_tecnica'];
    const header=cols.join(';');
    const realArticoli=articoliListino.filter(a=>!(a.nome||'').startsWith('_placeholder_categoria_'));
    const rows=realArticoli.map(a=>cols.map(c=>{
      let v=a[c]??'';
      v=String(v).replace(/"/g,'""');
      if(String(v).includes(';')||String(v).includes('"')||String(v).includes('\n'))v='"'+v+'"';
      return v;
    }).join(';'));
    const csv='\uFEFF'+header+'\n'+rows.join('\n');
    const blob=new Blob([csv],{type:'text/csv;charset=utf-8'});
    const url=URL.createObjectURL(blob);const a=document.createElement('a');
    a.href=url;a.download=`catalogo_${listinoTipo}.csv`;a.click();URL.revokeObjectURL(url);
    showToast('CSV esportato con '+realArticoli.length+' articoli');
  };

  return(<Page title="Catalogo" subtitle="Articoli e ricambi per listino e preventivi">
    <PageToolbar>
      <div className="segmented-wrap w-full">
        {LISTINO_TIPO_OPTIONS.map(option=><button key={option.value} onClick={()=>setListinoTipo(option.value)}
          className={`segmented-tab ${listinoTipo===option.value?'active':''} flex items-center gap-2 px-4 py-3 text-sm font-semibold rounded-[20px]`}>
          <span className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${listinoTipo===option.value?'bg-slate-900 text-white':'bg-white/70 text-slate-500'}`}>{option.value==='ricambi'?<IcWrench s={16}/>:<IcPkg s={16}/>}</span>
          <span>{option.label}</span>
        </button>)}
      </div>
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center w-full">
        <div className="relative flex-1"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca articolo..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/></div>
        <Sel value={catFilter} onChange={setCatFilter} options={categoriesForList} placeholder="Tutte le categorie"/>
      </div>
      {isAdmin?<div className="flex gap-2 flex-wrap w-full md:w-auto">
        <Btn v="secondary" s={isPhone?'sm':'md'} icon={<IcDl s={16}/>} onClick={exportCSV}>Esporta CSV</Btn>
        <Btn v="secondary" s={isPhone?'sm':'md'} icon={<IcUpload s={16}/>} onClick={()=>setShowImport(true)}>Importa</Btn>
        {!isPhone?<Btn v="secondary" onClick={()=>setShowCatManager(true)}>Categorie</Btn>:null}
        <Btn s={isPhone?'sm':'md'} icon={<IcPlus s={16}/>} onClick={()=>{setEditArt({listino_tipo:listinoTipo});setShowForm(true)}}>{listinoTipo==='ricambi'?'Nuovo ricambio':'Nuovo articolo'}</Btn>
      </div>:null}
    </PageToolbar>
    <Card>
      <div className="text-sm text-gray-400 mb-3">{filtered.length} {listinoTipo==='ricambi'?'ricambi':'articoli'} nel {listinoTipo==='ricambi'?'listino ricambi':'listino principale'}</div>
      {isPhone?<div className="data-list-phone">{filtered.map(a=>{const pr=calcPrezzi(a);return(
        <div key={a.id} className={`data-row-card ${!a.attivo?'opacity-50':''}`}>
          <div className="data-row-card__head">
            <div className="min-w-0">
              <div className="data-row-card__title">{S(a.nome)}</div>
              <div className="data-row-card__meta">{S(a.sku)} · {S(a.categoria)}</div>
            </div>
            <div className="text-right shrink-0"><div className="font-bold text-navy-600">{fmt(a.prezzo_listino)}</div></div>
          </div>
          <div className="catalog-row-meta">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy-50 text-navy-600">{S(a.tipo_sconto||'-')}</span>
            {canView?<span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Costo {fmt(a.costo_netto)}</span>:null}
          </div>
          {isAdmin?<div className="flex gap-2 mt-3 pt-2 border-t border-slate-100">
            <Btn s="sm" v="secondary" onClick={()=>duplicateArt(a)}>Duplica</Btn>
            <Btn s="sm" v="secondary" onClick={()=>{setEditArt(a);setShowForm(true)}}>Modifica</Btn>
            <Btn s="sm" v="danger" onClick={()=>deleteArt(a.id)}>Elimina</Btn>
          </div>:null}
        </div>)})}</div>:
      <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-navy-600 text-white">
        <th className="px-3 py-2.5 text-left">SKU</th><th className="px-3 py-2.5 text-left">Nome</th><th className="px-3 py-2.5 text-left">Categoria</th>
        <th className="px-3 py-2.5 text-center">Regola</th>
        {canView?<React.Fragment><th className="px-3 py-2.5 text-right">Costo</th><th className="px-3 py-2.5 text-right">Pr. Minimo</th><th className="px-3 py-2.5 text-right">Pr. Rivend.</th><th className="px-3 py-2.5 text-right">Pr. Consigliato</th></React.Fragment>:null}
        <th className="px-3 py-2.5 text-right">Listino</th>
        {isAdmin?<th className="px-3 py-2.5 text-center">Azioni</th>:null}
      </tr></thead><tbody>{filtered.map((a,i)=>{const pr=calcPrezzi(a);return(
        <tr key={a.id} className={`border-b hover:bg-gray-50 ${i%2?'bg-gray-50/50':''} ${!a.attivo?'opacity-40':''}`}>
          <td className="px-3 py-2">{S(a.sku)}</td>
          <td className="px-3 py-2 max-w-xs truncate">{S(a.nome)}</td>
          <td className="px-3 py-2"><div className="flex flex-wrap gap-1.5">
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{S(a.categoria)}</span>
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{listinoTipoLabel(a.listino_tipo)}</span>
          </div></td>
          <td className="px-3 py-2 text-center"><span className="text-xs bg-navy-50 text-navy-600 px-2 py-0.5 rounded-full">{S(a.tipo_sconto||'-')}</span></td>
          {canView?<React.Fragment><td className="px-3 py-2 text-right">{fmt(a.costo_netto)}</td>
            <td className="px-3 py-2 text-right text-gray-500">{fmt(pr.prezzoMinimo)}</td>
            <td className="px-3 py-2 text-right text-gray-500">{fmt(pr.prezzoRivenditore)}</td>
            <td className="px-3 py-2 text-right text-gray-500">{fmt(pr.prezzoConsigliato)}</td></React.Fragment>:null}
          <td className="px-3 py-2 text-right font-medium text-navy-600">{fmt(a.prezzo_listino)}</td>
          {isAdmin&&<td className="px-3 py-2 text-center">
            <div className="flex justify-center gap-1">
              <button onClick={()=>duplicateArt(a)} title="Duplica" className="p-1.5 hover:bg-blue-100 rounded text-gray-400 hover:text-blue-600"><IcCopy s={16}/></button>
              <button onClick={()=>{setEditArt(a);setShowForm(true)}} className="p-1.5 hover:bg-blue-100 rounded text-gray-400 hover:text-blue-600"><IcEdit s={16}/></button>
              <button onClick={()=>deleteArt(a.id)} className="p-1.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-600"><IcTrash s={16}/></button>
            </div></td>}
        </tr>)})}</tbody></table></div>}
    </Card>
    {showForm&&<ArticoloForm articolo={editArt} defaultListinoTipo={listinoTipo} onDuplicate={duplicateArt} onClose={()=>setShowForm(false)} onSaved={()=>{setShowForm(false);loadArticoli()}}/>}
    {showImport&&<ImportModal type="articoli" defaultTipo={listinoTipo} onClose={()=>setShowImport(false)} onDone={()=>{setShowImport(false);loadArticoli()}}/>}
    {showCatManager&&<CategorieManager onClose={()=>setShowCatManager(false)} onSaved={()=>{setShowCatManager(false);loadArticoli()}}/>}
  </Page>);
}

function ListinoPage(){
  const{articoli,settings,loadArticoli}=useContext(DataCtx);
  const{user}=useContext(AuthCtx);
  const{isPhone}=useDevice();
  const isRivenditore=String(user?.ruolo||'').trim().toLowerCase()==='rivenditore';
  const discountProfile=isRivenditore?'rivenditore':'cliente';
  const[listinoTipo,setListinoTipo]=useState('principale');
  const[search,setSearch]=useState('');
  const[catFilter,setCatFilter]=useState('');
  const articoliListino=useMemo(()=>articoli.filter(a=>normalizeListinoTipoClient(a.listino_tipo)===listinoTipo&&!(a.nome||'').startsWith('_placeholder_categoria_')),[articoli,listinoTipo]);
  const categoriesForList=useMemo(()=>[...new Set(articoliListino.map(a=>a.categoria).filter(Boolean))].sort(),[articoliListino]);
  const filtered=useMemo(()=>{
    let list=[...articoliListino];
    if(catFilter)list=list.filter(a=>a.categoria===catFilter);
    if(search){
      const term=search.trim().toLowerCase();
      list=list.filter(a=>(a.nome||'').toLowerCase().includes(term)||(a.sku||'').toLowerCase().includes(term)||(a.categoria||'').toLowerCase().includes(term));
    }
    return list;
  },[articoliListino,catFilter,search]);
  useEffect(()=>{if(catFilter&&!categoriesForList.includes(catFilter))setCatFilter('')},[catFilter,categoriesForList]);
  useEffect(()=>{loadArticoli().catch(()=>{})},[loadArticoli]);
  const rows=useMemo(()=>filtered.map(article=>{
    const basePrice=getArticoloBasePrice(article);
    const policy=getDiscountPolicy(article,settings,discountProfile);
    const maxDiscount=clamp(toNum(policy?.maxDiscount),0,0.95);
    return{
      ...article,
      basePrice,
      maxDiscount,
      maxDiscountLabel:maxDiscount>0?`${Math.round(maxDiscount*100)}%`:(String(policy?.note||'').toLowerCase().includes('trattativa')?'Trattativa':'0%'),
      purchasePrice:round2(basePrice*(1-maxDiscount)),
      discountRule:getDiscountRuleCode(article)||'A',
    };
  }),[filtered,settings,discountProfile]);
  const stats=useMemo(()=>{
    const totalListino=round2(rows.reduce((sum,row)=>sum+toNum(row.basePrice),0));
    const avgDiscount=rows.length?Math.round((rows.reduce((sum,row)=>sum+toNum(row.maxDiscount),0)/rows.length)*100):0;
    const avgPurchase=rows.length?round2(rows.reduce((sum,row)=>sum+toNum(row.purchasePrice),0)/rows.length):0;
    return{totalListino,avgDiscount,avgPurchase};
  },[rows]);

  return(<Page title="Listino" subtitle={isRivenditore?'Prezzi acquisto e sconti rivenditore':'Listino commerciale e sconti massimi'}>
    <div className="stat-tile-grid">
      <div className="stat-tile bg-slate-50">
        <div className="text-xs uppercase tracking-wide text-slate-400">Vista attiva</div>
        <div className="mt-2 text-xl font-bold text-navy-600">{isRivenditore?'Listino rivenditore':'Listino commerciale'}</div>
      </div>
      <div className="stat-tile bg-blue-50">
        <div className="text-xs uppercase tracking-wide text-slate-400">Articoli visibili</div>
        <div className="mt-2 text-2xl font-bold text-navy-600">{rows.length}</div>
        <div className="text-sm text-slate-500 mt-1">{listinoTipo==='ricambi'?'Listino ricambi':'Listino principale'}</div>
      </div>
      <div className="stat-tile bg-emerald-50">
        <div className="text-xs uppercase tracking-wide text-slate-400">{isRivenditore?'Prezzo medio acquisto':'Sconto medio massimo'}</div>
        <div className="mt-2 text-2xl font-bold text-navy-600">{isRivenditore?fmt(stats.avgPurchase):`${stats.avgDiscount}%`}</div>
      </div>
    </div>
    <div className="segmented-wrap">
      {LISTINO_TIPO_OPTIONS.map(option=><button key={option.value} onClick={()=>setListinoTipo(option.value)}
        className={`segmented-tab ${listinoTipo===option.value?'active':''} flex items-center gap-2 px-4 py-3 text-sm font-semibold rounded-[20px]`}>
        <span className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${listinoTipo===option.value?'bg-slate-900 text-white':'bg-white/70 text-slate-500'}`}>{option.value==='ricambi'?<IcWrench s={16}/>:<IcClipboard s={16}/>}</span>
        <span>{option.label}</span>
      </button>)}
    </div>
    <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
      <div className="flex gap-3 flex-1 max-w-2xl">
        <div className="relative flex-1"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca articolo, SKU o categoria..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/></div>
        <Sel value={catFilter} onChange={setCatFilter} options={categoriesForList} placeholder="Tutte le categorie"/>
      </div>
      <div className="text-xs text-slate-500 bg-white/70 border border-slate-200 rounded-full px-3 py-2">
        {isRivenditore?'Prezzi acquisto calcolati sul tuo profilo rivenditore':'Sconti massimi calcolati sul profilo cliente'}
      </div>
    </div>
    <Card>
      <div className="text-sm text-gray-400 mb-3">{rows.length} voci visibili nel {listinoTipo==='ricambi'?'listino ricambi':'listino principale'}</div>
      {isPhone?<div className="data-list-phone">{rows.map(a=>(
        <div key={a.id} className={`data-row-card ${!a.attivo?'opacity-50':''}`}>
          <div className="data-row-card__head">
            <div className="min-w-0">
              <div className="data-row-card__title">{S(a.nome)}</div>
              <div className="data-row-card__meta">{S(a.sku)} · {S(a.categoria||'-')}</div>
            </div>
            <div className="text-right shrink-0"><div className="font-bold text-navy-600">{fmt(a.basePrice)}</div></div>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-slate-500">Sconto max</span><span className="font-semibold">{S(a.maxDiscountLabel)}</span>
          </div>
          {isRivenditore?<div className="flex justify-between text-sm mt-1"><span className="text-slate-500">Acquisto</span><span className="font-semibold text-emerald-700">{fmt(a.purchasePrice)}</span></div>:null}
        </div>
      ))}</div>:
      <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-navy-600 text-white">
        <th className="px-3 py-2.5 text-left">SKU</th>
        <th className="px-3 py-2.5 text-left">Articolo</th>
        <th className="px-3 py-2.5 text-left">Categoria</th>
        <th className="px-3 py-2.5 text-center">Regola</th>
        <th className="px-3 py-2.5 text-right">Prezzo listino</th>
        <th className="px-3 py-2.5 text-right">Sconto massimo</th>
        {isRivenditore?<th className="px-3 py-2.5 text-right">Prezzo acquisto</th>:null}
      </tr></thead><tbody>{rows.map((a,i)=><tr key={a.id} className={`border-b hover:bg-gray-50 ${i%2?'bg-gray-50/50':''} ${!a.attivo?'opacity-40':''}`}>
        <td className="px-3 py-2 whitespace-nowrap">{S(a.sku)}</td>
        <td className="px-3 py-2 min-w-[240px]">
          <div className="font-medium text-navy-600">{S(a.nome)}</div>
          <div className="text-xs text-slate-400 mt-0.5">{listinoTipoLabel(a.listino_tipo)}</div>
        </td>
        <td className="px-3 py-2"><span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{S(a.categoria||'-')}</span></td>
        <td className="px-3 py-2 text-center"><span className="text-xs bg-navy-50 text-navy-600 px-2 py-0.5 rounded-full">{S(a.discountRule||'-')}</span></td>
        <td className="px-3 py-2 text-right font-medium text-navy-600 whitespace-nowrap">{fmt(a.basePrice)}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          <div className="font-semibold text-slate-700">{S(a.maxDiscountLabel)}</div>
        </td>
        {isRivenditore?<td className="px-3 py-2 text-right whitespace-nowrap"><span className="font-semibold text-emerald-700">{fmt(a.purchasePrice)}</span></td>:null}
      </tr>)}</tbody></table></div>}
    </Card>
  </Page>);
}

function CategorieManager({onClose,onSaved}){
  const{articoli,categories,showToast}=useContext(DataCtx);
  const[newCat,setNewCat]=useState('');
  const[renameCat,setRenameCat]=useState(null);
  const[renameVal,setRenameVal]=useState('');

  const catCounts=useMemo(()=>{
    const m={};categories.forEach(c=>{m[c]=articoli.filter(a=>a.categoria===c).length});return m;
  },[articoli,categories]);

  const addCategory=async()=>{
    const name=newCat.trim();if(!name){showToast('Nome categoria obbligatorio','error');return}
    if(categories.includes(name)){showToast('Categoria già esistente','error');return}
    try{
      await api('categoria_create','POST',{nome:name});
      showToast('Categoria creata');setNewCat('');onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  const renameCategory=async(oldName)=>{
    const newName=renameVal.trim();if(!newName){showToast('Nome obbligatorio','error');return}
    if(newName===oldName){setRenameCat(null);return}
    if(categories.includes(newName)){showToast('Categoria già esistente','error');return}
    try{
      await api('categoria_rename','PUT',{old_name:oldName,new_name:newName});
      showToast('Categoria rinominata');setRenameCat(null);onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  const deleteCategory=async(name)=>{
    const count=catCounts[name]||0;
    if(count>0){if(!confirm('Questa categoria contiene '+count+' articoli. Gli articoli rimarranno senza categoria. Continuare?'))return}
    else{if(!confirm('Eliminare la categoria "'+name+'"?'))return}
    try{
      await api('categoria_delete','DELETE',null,{nome:name});
      showToast('Categoria eliminata');onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  return(<Modal title="Gestione Categorie" onClose={onClose}>
    <div className="space-y-4">
      <div className="flex gap-2">
        <input value={newCat} onChange={e=>setNewCat(e.target.value)} placeholder="Nuova categoria..."
          className="flex-1 px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"
          onKeyDown={e=>{if(e.key==='Enter')addCategory()}}/>
        <Btn icon={<IcPlus s={16}/>} onClick={addCategory}>Aggiungi</Btn>
      </div>
      <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
        {categories.length===0?<div className="p-4 text-sm text-gray-400 text-center">Nessuna categoria</div>:
        categories.map(cat=>(
          <div key={cat} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
            {renameCat===cat?(
              <div className="flex gap-2 flex-1 mr-2">
                <input value={renameVal} onChange={e=>setRenameVal(e.target.value)} autoFocus
                  className="flex-1 px-2 py-1 border rounded text-sm outline-none focus:ring-2 focus:ring-accent-400"
                  onKeyDown={e=>{if(e.key==='Enter')renameCategory(cat);if(e.key==='Escape')setRenameCat(null)}}/>
                <Btn s="sm" v="success" onClick={()=>renameCategory(cat)}>OK</Btn>
                <Btn s="sm" v="secondary" onClick={()=>setRenameCat(null)}>X</Btn>
              </div>
            ):(
              <React.Fragment>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-navy-600">{cat}</span>
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{catCounts[cat]||0}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={()=>{setRenameCat(cat);setRenameVal(cat)}} className="p-1.5 hover:bg-blue-100 rounded text-gray-400 hover:text-blue-600"><IcEdit s={14}/></button>
                  <button onClick={()=>deleteCategory(cat)} className="p-1.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-600"><IcTrash s={14}/></button>
                </div>
              </React.Fragment>
            )}
          </div>
        ))}
      </div>
    </div>
  </Modal>);
}

function ArticoloForm({articolo,defaultListinoTipo='principale',onDuplicate,onClose,onSaved}){
  const{showToast,categories,settings}=useContext(DataCtx);
  const def={sku:'',nome:'',listino_tipo:defaultListinoTipo,categoria:'',categoria_agente:'A',um:'Nr',costo_netto:0,margine_min:0.2,markup_riv:0.1,markup_cliente:0.15,prezzo_listino:0,tipo_sconto:'C',famiglia:'',sconto_fornitore:0,attivo:1,foto:'',descrizione_tecnica:'',depliant_file:''};
  const[f,setF]=useState(articolo?{...def,...articolo}:{...def});
  const isEditing=Boolean(articolo?.id)&&!articolo?._isDuplicateDraft;
  const isDuplicateDraft=Boolean(articolo?._isDuplicateDraft);
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const pr=calcPrezzi(f);
  const discountSummary=getDiscountSummary(f,settings);
  const fotoRef=useRef();
  const deplRef=useRef();
  const[uplDep,setUplDep]=useState(false);

  const handleFoto=(e)=>{
    const file=e.target.files[0];if(!file)return;
    const reader=new FileReader();
    reader.onload=(ev)=>{
      const img=new Image();
      img.onload=()=>{
        const canvas=document.createElement('canvas');
        const max=400;let w=img.width,h=img.height;
        if(w>max||h>max){if(w>h){h=h*(max/w);w=max}else{w=w*(max/h);h=max}}
        canvas.width=w;canvas.height=h;
        canvas.getContext('2d').drawImage(img,0,0,w,h);
        u('foto',canvas.toDataURL('image/jpeg',0.8));
      };
      img.src=ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleDepliant=async(e)=>{
    const file=e.target.files[0];if(!file||!articolo)return;
    setUplDep(true);
    try{
      const reader=new FileReader();
      const b64=await new Promise((res,rej)=>{reader.onload=()=>res(reader.result.split(',')[1]);reader.onerror=rej;reader.readAsDataURL(file)});
      const d=await api('file_upload','POST',{file_data:b64,file_name:file.name,target_type:'depliant',target_id:articolo.id});
      u('depliant_file',d.filename);
      showToast('Depliant caricato');
    }catch(e){showToast(e.message,'error')}finally{setUplDep(false)}
  };

  const removeDepliant=async()=>{
    if(!articolo||!f.depliant_file)return;
    try{
      await api('file_delete','DELETE',null,{filename:f.depliant_file,target_type:'depliant',target_id:articolo.id});
      u('depliant_file','');showToast('Depliant rimosso');
    }catch(e){showToast(e.message,'error')}
  };

  const save=async()=>{
    if(!f.nome){showToast('Nome obbligatorio','error');return}
    try{
      const body={...f};
      delete body._isDuplicateDraft;
      if(!body.prezzo_listino)body.prezzo_listino=pr.prezzoConsigliato;
      if(isEditing)await api('articolo_update','PUT',body,{id:articolo.id});
      else await api('articolo_create','POST',body);
      showToast(isEditing?'Articolo aggiornato':isDuplicateDraft?'Copia articolo creata':'Articolo creato');onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  return(<Modal title={isEditing?'Modifica Articolo':isDuplicateDraft?'Duplica Articolo':'Nuovo Articolo'} onClose={onClose} wide>
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Foto</label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center cursor-pointer hover:border-accent-400 transition relative"
            style={{minHeight:'140px'}} onClick={()=>fotoRef.current?.click()}>
            {f.foto?<img src={f.foto} className="w-full h-auto rounded object-contain" style={{maxHeight:'160px'}}/>:
              <div className="flex flex-col items-center justify-center py-6 text-gray-400"><IcImage s={32}/><span className="text-xs mt-1">Clicca per caricare</span></div>}
            <input ref={fotoRef} type="file" accept="image/*" onChange={handleFoto} className="hidden"/>
          </div>
          {f.foto&&<button onClick={(e)=>{e.stopPropagation();u('foto','')}} className="text-xs text-red-500 mt-1 hover:underline">Rimuovi foto</button>}
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Inp label="SKU" value={f.sku} onChange={v=>u('sku',v)}/>
            <Inp label="Nome" value={f.nome} onChange={v=>u('nome',v)} required className="col-span-2"/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Sel label="Listino" value={normalizeListinoTipoClient(f.listino_tipo)} onChange={v=>u('listino_tipo',v)} options={LISTINO_TIPO_OPTIONS}/>
            <Sel label="Categoria" value={f.categoria} onChange={v=>u('categoria',v)} options={[...categories,{value:'__new',label:'+ Nuova...'}]} placeholder="Seleziona"/>
            <Sel label="Regola Sconto" value={f.tipo_sconto||''} onChange={v=>{u('tipo_sconto',v);if(['A','B','C','D'].includes(v))u('categoria_agente',v)}} options={DISCOUNT_RULE_OPTIONS}/>
            <Sel label="UM" value={f.um} onChange={v=>u('um',v)} options={['Nr','Kg','kg','Lt','Mt','Set','Conf','Ore','Km']}/>
            {articolo&&<div><label className="block text-sm font-medium text-gray-600 mb-1">Depliant PDF</label>
              {f.depliant_file?
                <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg"><IcClip s={16} c="text-green-600"/><span className="text-sm text-green-700 flex-1 truncate">{S(f.depliant_file)}</span>
                  <button onClick={removeDepliant} className="text-red-500 hover:text-red-700"><IcTrash s={14}/></button></div>
                :<div><Btn v="secondary" s="sm" icon={<IcUpload s={14}/>} onClick={()=>deplRef.current?.click()} disabled={uplDep}>{uplDep?'Caricamento...':'Carica PDF'}</Btn>
                  <input ref={deplRef} type="file" accept=".pdf" onChange={handleDepliant} className="hidden"/></div>}
            </div>}
          </div>
        </div>
      </div>
      <div><label className="block text-sm font-medium text-gray-600 mb-1">Descrizione Tecnica</label>
        <textarea value={f.descrizione_tecnica||''} onChange={e=>u('descrizione_tecnica',e.target.value)} rows={3} placeholder="Inserisci specifiche tecniche, caratteristiche..."
          className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/></div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Inp label="Costo Netto" type="number" value={f.costo_netto} onChange={v=>u('costo_netto',v)}/>
        <Inp label="Margine Min %" type="number" value={Math.round(f.margine_min*1000)/10} onChange={v=>u('margine_min',v/100)}/>
        <Inp label="Markup Riv %" type="number" value={Math.round(f.markup_riv*1000)/10} onChange={v=>u('markup_riv',v/100)}/>
        <Inp label="Markup Cliente %" type="number" value={Math.round(f.markup_cliente*1000)/10} onChange={v=>u('markup_cliente',v/100)}/>
        <Inp label="Prezzo Listino" type="number" value={f.prezzo_listino} onChange={v=>u('prezzo_listino',v)}/>
      </div>
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-navy-600 mb-3">Prezzi calcolati automaticamente</h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div><div className="text-xs text-gray-500">Prezzo Minimo</div><div className="text-lg font-bold text-gray-700">{fmt(pr.prezzoMinimo)}</div></div>
          <div><div className="text-xs text-gray-500">Prezzo Rivenditore</div><div className="text-lg font-bold text-gray-700">{fmt(pr.prezzoRivenditore)}</div></div>
          <div><div className="text-xs text-gray-500">Prezzo Consigliato</div><div className="text-lg font-bold text-accent-400">{fmt(pr.prezzoConsigliato)}</div></div>
        </div>
        <div className="mt-3 text-xs text-blue-700 text-center">{`Rivenditore: ${S(discountSummary.dealer)} · Cliente: ${S(discountSummary.client)}`}</div>
        {!f.prezzo_listino&&<p className="text-xs text-center text-blue-600 mt-2">Prezzo listino non impostato: verra' usato il prezzo consigliato</p>}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        {isEditing&&onDuplicate?<Btn v="secondary" icon={<IcCopy s={16}/>} onClick={()=>onDuplicate(f)}>Duplica</Btn>:null}
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva</Btn></div>
    </div></Modal>);
}

// ========== IMPORT MODAL (Excel) ==========
function ClienteImportEditModal({value,onClose,onSave}){
  const[f,setF]=useState({...value});
  const u=(k,v)=>setF(prev=>({...prev,[k]:v}));
  return(<Modal title="Modifica contatto importato" onClose={onClose}>
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Ragione sociale" value={f.azienda||''} onChange={v=>u('azienda',v)}/>
        <Inp label="Nome" value={f.nome||''} onChange={v=>u('nome',v)}/>
        <Inp label="Cognome / Referente" value={f.cognome||''} onChange={v=>u('cognome',v)}/>
        <Inp label="Email" value={f.email||''} onChange={v=>u('email',v)}/>
        <Inp label="Telefono" value={f.telefono||''} onChange={v=>u('telefono',v)}/>
        <Inp label="Cellulare" value={f.cellulare||''} onChange={v=>u('cellulare',v)}/>
        <Inp label="Fax" value={f.fax||''} onChange={v=>u('fax',v)}/>
        <Inp label="Indirizzo" value={f.via||''} onChange={v=>u('via',v)}/>
        <Inp label="Città" value={f.citta||''} onChange={v=>u('citta',v)}/>
        <Inp label="CAP" value={f.cap||''} onChange={v=>u('cap',v)}/>
        <Inp label="Provincia" value={f.provincia||''} onChange={v=>u('provincia',v)}/>
        <Inp label="Regione" value={f.regione||''} onChange={v=>u('regione',v)}/>
        <ClientCategoryPicker label="Categorie merceologiche" value={f.categoria_merceologica||''} onChange={v=>u('categoria_merceologica',v)}/>
        <Inp label="Dealer" value={f.dealer||''} onChange={v=>u('dealer',v)}/>
        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={yesNoFlagToBool(f.cliente_mimaki)} onChange={e=>u('cliente_mimaki',boolToYesNo(e.target.checked))} className="w-4 h-4 accent-accent-500"/><span>Cliente Mimaki</span></label>
        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={yesNoFlagToBool(f.cliente_mastertek)} onChange={e=>u('cliente_mastertek',boolToYesNo(e.target.checked))} className="w-4 h-4 accent-accent-500"/><span>Cliente Mastertek</span></label>
        <Inp label="Prodotto Mimaki" value={f.prodotto_mimaki||''} onChange={v=>u('prodotto_mimaki',v)}/>
        <Inp label="Numero seriale" value={f.numero_seriale||''} onChange={v=>u('numero_seriale',v)}/>
        <Inp label="P.IVA" value={f.piva||''} onChange={v=>u('piva',v)}/>
        <Inp label="Codice fiscale" value={f.cf||''} onChange={v=>u('cf',v)}/>
        <Inp label="SDI" value={f.sdi||''} onChange={v=>u('sdi',v)}/>
        <Inp label="PEC" value={f.pec||''} onChange={v=>u('pec',v)}/>
        <Inp label="Fonte" value={f.fonte||''} onChange={v=>u('fonte',v)}/>
        <Inp label="Pagamento" value={f.pagamento||''} onChange={v=>u('pagamento',v)}/>
        <Sel label="Tipo" value={f.tipo||'cliente'} onChange={v=>u('tipo',v)} options={[
          {value:'cliente',label:'Cliente'},
          {value:'potenziale',label:'Potenziale'},
          {value:'rivenditore',label:'Rivenditore'},
        ]}/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Note</label>
        <textarea value={f.note||''} onChange={e=>u('note',e.target.value)} rows={4}
          className="w-full px-4 py-3 border rounded-2xl text-sm outline-none focus:ring-2 focus:ring-accent-400"/>
      </div>
      <div className="flex justify-end gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" onClick={()=>onSave(f)}>Salva modifiche</Btn>
      </div>
    </div>
  </Modal>);
}

function ImportModal({type,defaultTipo,onClose,onDone}){
  const{showToast}=useContext(DataCtx);
  const[workbook,setWorkbook]=useState(null);
  const[sheetNames,setSheetNames]=useState([]);
  const[selectedSheet,setSelectedSheet]=useState('');
  const[data,setData]=useState(null);
  const[preview,setPreview]=useState(null);
  const[loading,setLoading]=useState(false);
  const[progress,setProgress]=useState('');
  const[mapping,setMapping]=useState({});
  const[replaceAll,setReplaceAll]=useState(type==='articoli');
  const[batchTipo,setBatchTipo]=useState(defaultTipo||'cliente');
  const[review,setReview]=useState(null);
  const[editReviewIndex,setEditReviewIndex]=useState(null);
  const fileRef=useRef();

  const targetFields=type==='articoli'
    ?[{key:'sku',label:'SKU'},{key:'nome',label:'Nome'},{key:'listino_tipo',label:'Listino'},{key:'categoria',label:'Categoria'},{key:'um',label:'UM'},{key:'categoria_agente',label:'Classe Agente'},{key:'tipo_sconto',label:'Regola Sconto'},{key:'famiglia',label:'Famiglia'},{key:'costo_netto',label:'Costo Netto'},{key:'prezzo_listino',label:'Prezzo Listino'},{key:'margine_min',label:'Margine Min %'},{key:'markup_riv',label:'Markup Riv %'},{key:'markup_cliente',label:'Markup Cli %'},{key:'sconto_fornitore',label:'Sc. Fornitore'},{key:'attivo',label:'Attivo'}]
    :[{key:'azienda',label:'Ragione Sociale'},{key:'piva',label:'P.IVA'},{key:'cf',label:'Codice Fiscale'},{key:'fatturato_annuo',label:'Fatturato annuo'},{key:'numero_dipendenti',label:'N. dipendenti'},{key:'categoria_merceologica',label:'Categoria'},{key:'via',label:'Indirizzo'},{key:'cap',label:'CAP'},{key:'citta',label:'Città'},{key:'provincia',label:'Provincia'},{key:'regione',label:'Regione'},{key:'telefono',label:'Telefono'},{key:'fax',label:'Fax'},{key:'cellulare',label:'Cellulare'},{key:'email',label:'Email'},{key:'sito_web',label:'Sito Web'},{key:'referente',label:'Referente'},{key:'dealer',label:'Dealer'},{key:'cliente_mimaki',label:'Cliente Mimaki'},{key:'cliente_mastertek',label:'Cliente Mastertek'},{key:'prodotto_mimaki',label:'Prodotto Mimaki'},{key:'numero_seriale',label:'Numero Seriale'},{key:'fonte',label:'Fonte'},{key:'nome',label:'Nome'},{key:'cognome',label:'Cognome/Referente'},{key:'nazione',label:'Nazione'},{key:'sdi',label:'SDI'},{key:'pec',label:'PEC'},{key:'pagamento',label:'Pagamento'},{key:'contratto_assistenza',label:'Contratto assistenza (0/1)'},{key:'contratto_assistenza_tipo',label:'Tipo contratto'},{key:'contratto_assistenza_scadenza',label:'Scadenza contratto'},{key:'contratto_assistenza_note',label:'Note contratto'},{key:'note',label:'Note'}];
  const CLIENTI_IMPORT_BATCH=400;
  const [bulkEnrichMode,setBulkEnrichMode]=useState(false);

  const autoMapColumns=(cols)=>{
    const autoMap={};
    const aliases={
      azienda:['ragionesociale','ragione','azienda','company','denominazione'],
      nome:['nome','name','firstname'],
      cognome:['cognome','referente','contatto','contact'],
      email:['email','mail','posta'],
      telefono:['telefono','tel','phone','cellulare'],
      via:['indirizzo','via','address'],
      citta:['citta','città','city','comune'],
      cap:['cap','zip','postale'],
      provincia:['provincia','prov','province'],
      regione:['regione','region'],
      categoria_merceologica:['categoriamerceologica','categoriamerceologica','merceologica','settoremerceologico','settore','industry','categoria_cliente'],
      fax:['fax'],
      cellulare:['cellulare','cell','mobile'],
      sito_web:['sitoweb','web','homepage','home_page','website'],
      referente:['referente','contatto','primarycontact','contact'],
      dealer:['dealer','agente','reseller'],
      cliente_mimaki:['clientemimaki','cliente_mimaki'],
      cliente_mastertek:['clientemastertek','cliente_mastertek'],
      prodotto_mimaki:['prodottomimaki','prodotto','product','ownedproduct'],
      numero_seriale:['numeroseriale','serialnumber','seriale'],
      fonte:['fonte','source'],
      piva:['piva','p.iva','partitaiva','vat'],
      cf:['cf','codicefiscale','taxcode'],
      nazione:['nazione','country'],
      sdi:['sdi','codedestinatariofatt.elettr.','codicedestinatariofatt.elettr.','cod.destinatariofatt.elettr.','codicedestinatario','codicedestinatariofatturazione'],
      pec:['pec'],
      pagamento:['pagamento','payment'],
      fatturato_annuo:['fatturato','fatturatoannuo','fatturato_annuo','revenue','turnover','ricavi'],
      numero_dipendenti:['dipendenti','numerodipendenti','numero_dipendenti','employees','headcount','addetti'],
      contratto_assistenza:['contrattoassistenza','contratto_assistenza','assistenzaattiva'],
      contratto_assistenza_tipo:['tipocontratto','contratto_assistenza_tipo','tipoassistenza'],
      contratto_assistenza_scadenza:['scadenzacontratto','contratto_assistenza_scadenza','scadenzaassistenza'],
      contratto_assistenza_note:['notecontratto','contratto_assistenza_note'],
      note:['note','notes','osservazioni'],
      sku:['sku','codice','code'],
      categoria:['categoria','category','tipo'],
      listino_tipo:['listino','listinotipo','tipo_listino','tipolistino','ricambi','spareparts'],
      um:['um','u.m','unit','misura'],
      categoria_agente:['categoriaagente','classeagente','fasciaagente','classe','abc','abcd'],
      tipo_sconto:['tiposconto','regolasconto','sconto','classeprezzo'],
      famiglia:['famiglia','family','linea'],
      costo_netto:['costonetto','costo','cost'],
      prezzo_listino:['prezzolistino','listino','prezzo','price'],
      sconto_fornitore:['scontofornitore','scfornitore','scontonitore','fornitore'],
      attivo:['attivo','active'],
    };
    targetFields.forEach(tf=>{
      const al=aliases[tf.key]||[tf.key];
      const match=cols.find(c=>{
        const norm=c.toLowerCase().replace(/[_\s.\-]/g,'');
        return al.some(a=>norm.includes(a.replace(/[_\s.\-]/g,'')));
      });
      if(match)autoMap[tf.key]=match;
    });
    return autoMap;
  };

  const handleFile=(e)=>{
    const file=e.target.files[0];if(!file)return;
    const reader=new FileReader();
    reader.onload=async(evt)=>{
      await ensureXlsx();
      const wb=XLSX.read(evt.target.result,{type:'array'});
      setWorkbook(wb);
      setSheetNames(wb.SheetNames);
      loadSheet(wb,wb.SheetNames[0]);
    };
    reader.readAsArrayBuffer(file);
  };

  const loadSheet=(wb,sheetName)=>{
    const ws=wb.Sheets[sheetName];
    const json=XLSX.utils.sheet_to_json(ws,{defval:''});
    setSelectedSheet(sheetName);
    setData(json);
    setPreview(json.slice(0,5));
    setReview(null);
    setEditReviewIndex(null);
    if(json.length>0)setMapping(autoMapColumns(Object.keys(json[0])));
    else setMapping({});
  };

  const resetImport=()=>{
    setWorkbook(null);
    setData(null);
    setPreview(null);
    setSheetNames([]);
    setSelectedSheet('');
    setReview(null);
    setEditReviewIndex(null);
    setProgress('');
  };

  const mappedRows=useMemo(()=>{
    if(!data||!data.length)return[];
    return data.map(row=>{
      const obj={};
      targetFields.forEach(tf=>{
        const col=mapping[tf.key];
        if(col)obj[tf.key]=row[col]??'';
      });
      if(type==='articoli'&&!obj.listino_tipo)obj.listino_tipo=defaultTipo||'principale';
      if(type==='clienti'&&!obj.tipo)obj.tipo=batchTipo;
      return obj;
    }).filter(obj=>obj.nome||obj.azienda);
  },[data,mapping,targetFields,type,defaultTipo,batchTipo]);

  const analyzeClientiImport=async()=>{
    if(!mappedRows.length)return;
    setLoading(true);
    const allRows=[];
    let duplicates=0;
    try{
      for(let offset=0;offset<mappedRows.length;offset+=CLIENTI_IMPORT_BATCH){
        const chunk=mappedRows.slice(offset,offset+CLIENTI_IMPORT_BATCH);
        setProgress(`Analisi ${Math.min(offset+chunk.length,mappedRows.length)} / ${mappedRows.length}...`);
        const d=await api('clienti_import_preview','POST',{clienti:chunk,tipo:batchTipo});
        (d.data?.rows||[]).forEach((item,idx)=>{
          const isDup=!!item.duplicate;
          if(isDup)duplicates++;
          allRows.push({
            ...item,
            index:offset+idx,
            duplicate_action:bulkEnrichMode&&isDup?'merge':(isDup?safeDuplicateDefault(item.suggested_action):'insert'),
          });
        });
      }
      setReview({rows:allRows,summary:{total:allRows.length,duplicates,new_records:allRows.length-duplicates}});
      setProgress('');
      showToast(bulkEnrichMode
        ?`Pronto: ${duplicates} anagrafiche da aggiornare, ${allRows.length-duplicates} nuove`
        :`Analisi: ${duplicates} duplicati su ${allRows.length} righe`);
    }catch(e){
      setProgress('');
      showToast(e.message,'error');
    }finally{
      setLoading(false);
    }
  };

  const safeDuplicateDefault=value=>CLIENTI_DUPLICATE_ACTION_OPTIONS.some(item=>item.value===value)?value:'merge';
  const updateReviewAction=(index,value)=>{
    setReview(prev=>prev?({...prev,rows:prev.rows.map((row,rowIndex)=>rowIndex===index?({...row,duplicate_action:value}):row)}):prev);
  };
  const updateReviewRow=(index,rowValue)=>{
    setReview(prev=>prev?({...prev,rows:prev.rows.map((row,rowIndex)=>rowIndex===index?({...row,row:rowValue}):row)}):prev);
  };

  const doImport=async()=>{
    if(type==='clienti'&&!review){
      analyzeClientiImport();
      return;
    }
    if(type==='articoli'&&(!data||data.length===0))return;
    if(type==='clienti'&&(!review?.rows?.length))return;
    setLoading(true);
    try{
      const action=type==='articoli'?'articoli_import':'clienti_import';
      if(type==='articoli'){
        const d=await api(action,'POST',{articoli:mappedRows,replace_all:replaceAll});
        showToast('Importati '+(d.imported||mappedRows.length)+' articoli'+(d.updated?' ('+d.updated+' aggiornati)':''));
      }else{
        let imported=0,updated=0,skipped=0;
        const rows=review.rows;
        for(let offset=0;offset<rows.length;offset+=CLIENTI_IMPORT_BATCH){
          const chunk=rows.slice(offset,offset+CLIENTI_IMPORT_BATCH).map(item=>({
            row:item.row,
            existing_id:item.duplicate?.existing_id||0,
            duplicate_action:item.duplicate?item.duplicate_action:'insert',
          }));
          setProgress(`Importazione ${Math.min(offset+chunk.length,rows.length)} / ${rows.length}...`);
          const d=await api(action,'POST',{clienti:chunk,tipo:batchTipo});
          imported+=d.imported||0;
          updated+=d.updated||0;
          skipped+=d.skipped||0;
        }
        setProgress('');
        showToast(`Import completato: ${imported} nuovi, ${updated} aggiornati, ${skipped} scartati`);
      }
      onDone();
    }catch(e){setProgress('');showToast(e.message,'error')}finally{setLoading(false)}
  };

  const duplicatesCount=review?.summary?.duplicates||0;
  const reviewRows=review?.rows||[];

  return(<Modal title={`Importa ${type==='articoli'?'Articoli':'Contatti'} da Excel`} onClose={onClose} wide>
    <div className="space-y-4">
      {!workbook?(
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <IcUpload s={36} c="mx-auto text-gray-400 mb-3"/>
          <p className="text-gray-600 mb-4">Trascina un file Excel (.xlsx, .xls, .csv) oppure</p>
          <Btn onClick={()=>fileRef.current?.click()} icon={<IcUpload s={16}/>}>Seleziona file</Btn>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="hidden"/>
        </div>
      ):(
        <React.Fragment>
          {sheetNames.length>1?<div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Foglio del file</label>
            <div className="flex flex-wrap gap-2">{sheetNames.map(sn=>(
              <button key={sn} onClick={()=>loadSheet(workbook,sn)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${selectedSheet===sn?'bg-navy-600 text-white border-navy-600':'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>{sn}</button>
            ))}</div>
          </div>:null}
          {type==='clienti'&&<div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm font-medium text-gray-600">Importa come:</label>
              <div className="flex gap-2">
                <button onClick={()=>setBatchTipo('cliente')} className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${batchTipo==='cliente'?'bg-accent-400 text-white border-accent-400':'bg-white text-gray-600 border-gray-200'}`}>Clienti</button>
                <button onClick={()=>setBatchTipo('potenziale')} className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${batchTipo==='potenziale'?'bg-amber-500 text-white border-amber-500':'bg-white text-gray-600 border-gray-200'}`}>Potenziali</button>
                <button onClick={()=>setBatchTipo('rivenditore')} className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${batchTipo==='rivenditore'?'bg-navy-600 text-white border-navy-600':'bg-white text-gray-600 border-gray-200'}`}>Rivenditori</button>
              </div>
            </div>
            <label className="flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 cursor-pointer">
              <input type="checkbox" checked={bulkEnrichMode} onChange={e=>setBulkEnrichMode(e.target.checked)} className="w-4 h-4 mt-0.5 accent-accent-500"/>
              <span><strong>Aggiornamento massivo</strong> — unisci automaticamente i duplicati (ideale per 20.000+ anagrafiche). Nel file metti P.IVA (o chiave univoca) + colonne da completare. I campi vuoti non cancellano dati già presenti.</span>
            </label>
          </div>}
          <div className="bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg">
            Trovate {data?data.length:0} righe{sheetNames.length>1?' nel foglio "'+selectedSheet+'"':''}. Mappa le colonne:</div>
          {data&&data.length>0?<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {targetFields.map(tf=>(
              <Sel key={tf.key} label={tf.label} value={mapping[tf.key]||''} onChange={v=>setMapping(p=>({...p,[tf.key]:v}))}
                options={Object.keys(data[0]).map(c=>({value:c,label:c}))} placeholder="-- Non importare --"/>
            ))}
          </div>:null}
          {preview&&preview.length>0?<div className="overflow-x-auto"><table className="w-full text-xs border"><thead><tr className="bg-gray-100">
            {Object.keys(preview[0]).map(k=><th key={k} className="px-2 py-1 text-left">{k}</th>)}</tr></thead>
            <tbody>{preview.map((r,i)=><tr key={i} className="border-t">
              {Object.values(r).map((v,j)=><td key={j} className="px-2 py-1 truncate max-w-[150px]">{String(v)}</td>)}</tr>)}</tbody></table>
            <p className="text-xs text-gray-400 mt-1">Anteprima prime 5 righe</p></div>:null}
          {type==='articoli'&&<div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <input type="checkbox" id="replaceAll" checked={replaceAll} onChange={e=>setReplaceAll(e.target.checked)} className="accent-accent-400"/>
            <label htmlFor="replaceAll" className="text-sm text-amber-800">Sostituisci catalogo esistente (elimina tutti gli articoli e importa solo quelli del file)</label>
          </div>}
          {type==='clienti'&&review?<div className="space-y-4 rounded-3xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-navy-600">Revisione duplicati e normalizzazione</div>
                <div className="text-xs text-slate-500 mt-1">{review.summary?.total||0} righe analizzate · {duplicatesCount} possibili duplicati · {review.summary?.new_records||0} nuove anagrafiche</div>
              </div>
              <Btn v="secondary" s="sm" onClick={()=>setReview(null)}>Torna alla mappatura</Btn>
            </div>
            {duplicatesCount===0?<div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">Nessun duplicato trovato. I contatti verranno importati come nuove anagrafiche normalizzate.</div>:null}
            {bulkEnrichMode&&duplicatesCount>0?<div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">Modalità massiva attiva: <strong>{duplicatesCount}</strong> contatti esistenti verranno aggiornati con i dati del file (merge). L’elenco dettagliato duplicati è nascosto per performance.</div>:null}
            {!bulkEnrichMode&&reviewRows.filter(item=>item.duplicate).map((item,index)=>{
              const existing=item.duplicate?.existing||{};
              const reviewIndex=reviewRows.findIndex(row=>row.index===item.index);
              return <div key={item.index} className="rounded-3xl border border-slate-200 bg-white p-4 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-navy-600">{item.row.azienda||[item.row.nome,item.row.cognome].filter(Boolean).join(' ')||`Riga ${item.index+1}`}</div>
                    <div className="text-xs text-slate-500 mt-1">Duplicato rilevato per: {item.duplicate?.matched_label||'Anagrafica'} · azione suggerita: {clientiImportActionLabel(item.suggested_action)}</div>
                  </div>
                  <div className="w-full lg:w-[260px]">
                    <Sel label="Azione" value={item.duplicate_action||'merge'} onChange={v=>updateReviewAction(reviewIndex,v)} options={CLIENTI_DUPLICATE_ACTION_OPTIONS}/>
                  </div>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Contatto esistente</div>
                    <div className="font-semibold text-slate-700">{existing.azienda||[existing.nome,existing.cognome].filter(Boolean).join(' ')||'Contatto esistente'}</div>
                    <div className="text-sm text-slate-500 mt-2 space-y-1">
                      <div>{[existing.email,existing.telefono].filter(Boolean).join(' · ')||'Nessun recapito'}</div>
                      <div>{[existing.citta,normalizeProvincia(existing.provincia),existing.regione].filter(Boolean).join(' · ')||'Nessuna località'}</div>
                      <div>{existing.categoria_merceologica||'Categoria non indicata'}</div>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs uppercase tracking-wide text-slate-400">Nuovo contatto importato</div>
                      <button onClick={()=>setEditReviewIndex(reviewIndex)} className="text-xs text-accent-500 hover:text-accent-700 font-medium">Modifica dati</button>
                    </div>
                    <div className="font-semibold text-slate-700 mt-2">{item.row.azienda||[item.row.nome,item.row.cognome].filter(Boolean).join(' ')||'Nuovo contatto'}</div>
                    <div className="text-sm text-slate-500 mt-2 space-y-1">
                      <div>{[item.row.email,item.row.telefono].filter(Boolean).join(' · ')||'Nessun recapito'}</div>
                      <div>{[item.row.citta,normalizeProvincia(item.row.provincia),item.row.regione].filter(Boolean).join(' · ')||'Nessuna località'}</div>
                      <div>{item.row.categoria_merceologica||'Categoria non indicata'}</div>
                    </div>
                  </div>
                </div>
              </div>;
            })}
          </div>:null}
          {progress?<div className="bg-blue-50 text-blue-700 text-sm px-4 py-2 rounded-lg text-center">{progress}</div>:null}
          <div className="flex justify-end gap-3">
            <Btn v="secondary" onClick={resetImport}>Cambia file</Btn>
            <Btn v="success" icon={<IcCheck s={16}/>} onClick={doImport} disabled={loading||(!data||data.length===0)}>
              {loading?'Importo...':type==='clienti'?(review?'Conferma importazione':'Analizza duplicati'):'Importa'}
            </Btn></div>
        </React.Fragment>
      )}
      {type==='clienti'&&editReviewIndex!==null&&reviewRows[editReviewIndex]?<ClienteImportEditModal
        value={reviewRows[editReviewIndex].row}
        onClose={()=>setEditReviewIndex(null)}
        onSave={nextRow=>{updateReviewRow(editReviewIndex,nextRow);setEditReviewIndex(null);}}
      />:null}
    </div></Modal>);
}

// ========== SCHEDA CLIENTE ==========
const STAMPANTE_TIPOLOGIE_OPTIONS=[
  {value:'eco_solvente',label:'Eco-solvente'},{value:'uv',label:'UV'},{value:'latex',label:'Latex'},
  {value:'sublimazione',label:'Sublimazione'},{value:'plotter_taglio',label:'Plotter taglio'},
  {value:'dtf',label:'DTF'},{value:'dtg',label:'DTG'},{value:'flex',label:'Flex'},{value:'altro',label:'Altro'},
];
const POTENZIALE_PRIORITA_OPTIONS=[{value:'alta',label:'Alta'},{value:'media',label:'Media'},{value:'bassa',label:'Bassa'}];
const CONTRATTO_TIPO_OPTIONS=['5 ore','10 ore','20 ore'].map(v=>({value:v,label:v}));
const fmtOreContratto=ore=>{
  const n=Number(ore||0);
  if(!Number.isFinite(n))return '0 h';
  const r=Math.round(n*100)/100;
  return Math.abs(r-Math.round(r))<0.01?`${Math.round(r)} h`:`${r.toFixed(2).replace('.',',')} h`;
};
const oreDaMinuti=min=>Math.round(Math.max(0,Number(min)||0)/60*100)/100;
const stampanteTipologiaLabel=key=>STAMPANTE_TIPOLOGIE_OPTIONS.find(o=>o.value===key)?.label||key||'—';
const prioritaPotenzialeClass=p=>p==='alta'?'bg-rose-100 text-rose-700':p==='media'?'bg-amber-100 text-amber-700':'bg-slate-100 text-slate-600';

function ClienteStampanteSchedaModal({cliente,stampante,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const def={cliente_id:String(cliente?.id||''),tipologia:'',marca:'',modello:'',numero_seriale:'',ubicazione:'',note:'',attiva:1};
  const[f,setF]=useState(stampante?{...def,...stampante,cliente_id:String(stampante.cliente_id||cliente?.id||''),attiva:stampante.attiva!==0?1:0}:{...def});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const save=async()=>{
    if(!String(f.modello||'').trim()){showToast('Modello obbligatorio','error');return}
    const payload={cliente_id:parseInt(f.cliente_id)||null,tipologia:f.tipologia||'',marca:(f.marca||'').trim(),modello:(f.modello||'').trim(),numero_seriale:(f.numero_seriale||'').trim(),ubicazione:(f.ubicazione||'').trim(),note:(f.note||'').trim(),attiva:f.attiva?1:0};
    try{
      if(stampante?.id)await api('cliente_stampante_update','PUT',payload,{id:stampante.id});
      else await api('cliente_stampante_create','POST',payload);
      showToast(stampante?'Stampante aggiornata':'Stampante aggiunta');onSaved();
    }catch(e){showToast(e.message||'Errore','error')}
  };
  return(<Modal title={stampante?'Modifica stampante':'Aggiungi stampante'} onClose={onClose}>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Sel label="Tipologia" value={f.tipologia||''} onChange={v=>u('tipologia',v)} options={STAMPANTE_TIPOLOGIE_OPTIONS} placeholder="Seleziona tipologia"/>
      <Inp label="Marca" value={f.marca||''} onChange={v=>u('marca',v)} placeholder="Epson, Mimaki, HP..."/>
      <Inp label="Modello" value={f.modello||''} onChange={v=>u('modello',v)} required/>
      <Inp label="Numero seriale" value={f.numero_seriale||''} onChange={v=>u('numero_seriale',v)}/>
      <Inp label="Ubicazione" value={f.ubicazione||''} onChange={v=>u('ubicazione',v)} placeholder="Reparto, piano..."/>
      <label className="flex items-center gap-2 text-sm text-slate-700 md:col-span-2"><input type="checkbox" checked={!!f.attiva} onChange={e=>u('attiva',e.target.checked?1:0)} className="w-4 h-4 accent-accent-500"/> Stampante attiva (in uso)</label>
      <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-600 mb-1">Note</label><textarea value={f.note||''} onChange={e=>u('note',e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm"/></div>
    </div>
    <div className="flex justify-end gap-2 mt-4"><Btn v="secondary" onClick={onClose}>Annulla</Btn><Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva</Btn></div>
  </Modal>);
}

function ClientePotenzialeModal({cliente,potenziale,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const def={cliente_id:String(cliente?.id||''),tipologia:'',marca:'',modello:'',note:'',priorita:'media'};
  const[f,setF]=useState(potenziale?{...def,...potenziale,cliente_id:String(potenziale.cliente_id||cliente?.id||'')}:{...def});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const save=async()=>{
    if(!String(f.modello||'').trim()){showToast('Modello potenziale obbligatorio','error');return}
    const payload={cliente_id:parseInt(f.cliente_id)||null,tipologia:f.tipologia||'',marca:(f.marca||'').trim(),modello:(f.modello||'').trim(),note:(f.note||'').trim(),priorita:f.priorita||'media'};
    try{
      if(potenziale?.id)await api('cliente_potenziale_update','PUT',payload,{id:potenziale.id});
      else await api('cliente_potenziale_create','POST',payload);
      showToast(potenziale?'Potenziale aggiornato':'Potenziale aggiunto');onSaved();
    }catch(e){showToast(e.message||'Errore','error')}
  };
  return(<Modal title={potenziale?'Modifica potenzialità':'Nuova potenzialità stampante'} onClose={onClose}>
    <p className="text-sm text-slate-500 mb-3">Indica per quali modelli nuovi questo cliente è un target commerciale (es. uscita nuova macchina).</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Sel label="Tipologia" value={f.tipologia||''} onChange={v=>u('tipologia',v)} options={STAMPANTE_TIPOLOGIE_OPTIONS} placeholder="Tipologia"/>
      <Sel label="Priorità" value={f.priorita||'media'} onChange={v=>u('priorita',v)} options={POTENZIALE_PRIORITA_OPTIONS}/>
      <Inp label="Marca" value={f.marca||''} onChange={v=>u('marca',v)}/>
      <Inp label="Modello target" value={f.modello||''} onChange={v=>u('modello',v)} placeholder="es. SureColor S80600"/>
      <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-600 mb-1">Note</label><textarea value={f.note||''} onChange={e=>u('note',e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm"/></div>
    </div>
    <div className="flex justify-end gap-2 mt-4"><Btn v="secondary" onClick={onClose}>Annulla</Btn><Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva</Btn></div>
  </Modal>);
}

function ClienteVisitaModal({cliente,visita,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const def={cliente_id:String(cliente?.id||''),data_visita:today(),persone_contatto:'',descrizione:'',todo_testo:'',todo_scadenza:''};
  const[f,setF]=useState(visita?{...def,...visita,cliente_id:String(visita.cliente_id||cliente?.id||'')}:{...def});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const save=async()=>{
    const payload={cliente_id:parseInt(f.cliente_id)||null,data_visita:f.data_visita,persone_contatto:(f.persone_contatto||'').trim(),descrizione:(f.descrizione||'').trim(),todo_testo:(f.todo_testo||'').trim(),todo_scadenza:f.todo_scadenza||''};
    try{
      if(visita?.id)await api('cliente_visita_update','PUT',payload,{id:visita.id});
      else await api('cliente_visita_create','POST',payload);
      showToast(visita?'Visita aggiornata':'Visita registrata');onSaved();
    }catch(e){showToast(e.message||'Errore','error')}
  };
  return(<Modal title={visita?'Modifica visita':'Vistato — nuova visita'} onClose={onClose} wide>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Inp label="Data visita" type="date" value={f.data_visita||''} onChange={v=>u('data_visita',v)}/>
      <Inp label="Persone incontrate" value={f.persone_contatto||''} onChange={v=>u('persone_contatto',v)} placeholder="Nome referente, ruolo..."/>
      <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-600 mb-1">Descrizione evento</label><textarea value={f.descrizione||''} onChange={e=>u('descrizione',e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm"/></div>
      <div className="md:col-span-2 border-t pt-3"><div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Follow-up</div></div>
      <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-600 mb-1">To-do</label><textarea value={f.todo_testo||''} onChange={e=>u('todo_testo',e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Richiamare, inviare preventivo, demo..."/></div>
      <Inp label="Entro il (scadenza to-do)" type="date" value={f.todo_scadenza||''} onChange={v=>u('todo_scadenza',v)}/>
    </div>
    <div className="flex justify-end gap-2 mt-4"><Btn v="secondary" onClick={onClose}>Annulla</Btn><Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva visita</Btn></div>
  </Modal>);
}

function ClienteSchedaPanel({clienteId,onBack,onEditAnagrafica,onOpenPreventivo,embedded=false}){
  const{showToast,isAdmin,isManager}=useContext(AuthCtx);
  const{calcolaRiepilogo}=useContext(DataCtx);
  const{isPhone}=useDevice();
  const[scheda,setScheda]=useState(null);
  const[tab,setTab]=useState('panoramica');
  const[loading,setLoading]=useState(true);
  const[editStampante,setEditStampante]=useState(null);
  const[showStampante,setShowStampante]=useState(false);
  const[editPotenziale,setEditPotenziale]=useState(null);
  const[showPotenziale,setShowPotenziale]=useState(false);
  const[editVisita,setEditVisita]=useState(null);
  const[showVisita,setShowVisita]=useState(false);
  const[savingContratto,setSavingContratto]=useState(false);
  const[fattLookupBusy,setFattLookupBusy]=useState(false);
  const[preventiviTotalsLoading,setPreventiviTotalsLoading]=useState(false);
  const[assistenzaLoading,setAssistenzaLoading]=useState(false);
  const[assistenzaLoaded,setAssistenzaLoaded]=useState(false);
  const showToastRef=useRef(showToast);
  showToastRef.current=showToast;
  const loadPreventiviTotals=useCallback(async()=>{
    setPreventiviTotalsLoading(true);
    try{
      const d=await api('cliente_scheda_preventivi_preview','GET',null,{id:clienteId});
      const payload=d.data||{};
      setScheda(prev=>{
        if(!prev)return prev;
        return{...prev,preventivi:payload.preventivi||prev.preventivi,preventivi_stats:payload.preventivi_stats||prev.preventivi_stats};
      });
    }catch(e){showToast(e.message||'Calcolo totali preventivi non riuscito','error')}
    finally{setPreventiviTotalsLoading(false)}
  },[clienteId,showToast]);
  const loadAssistenza=useCallback(async()=>{
    if(assistenzaLoaded)return;
    setAssistenzaLoading(true);
    try{
      const d=await api('cliente_scheda_assistenza','GET',null,{id:clienteId});
      const payload=d.data||{};
      setScheda(prev=>{
        if(!prev)return prev;
        return{
          ...prev,
          interventi_tecnici:payload.interventi_tecnici||[],
          interventi_tecnici_stats:payload.interventi_tecnici_stats||prev.interventi_tecnici_stats,
          preventivi_assistenza:payload.preventivi_assistenza||[],
          preventivi_assistenza_stats:payload.preventivi_assistenza_stats||prev.preventivi_assistenza_stats,
        };
      });
      setAssistenzaLoaded(true);
    }catch(e){showToast(e.message||'Errore caricamento assistenza','error')}
    finally{setAssistenzaLoading(false)}
  },[clienteId,showToast,assistenzaLoaded]);
  const load=useCallback(async({withPreventiviTotals=false}={})=>{
    setLoading(true);
    try{
      const d=await api('cliente_scheda','GET',null,{id:clienteId});
      const data=d.data||null;
      if(!data||!data.cliente){
        setScheda(null);
        showToast('Scheda cliente non disponibile','error');
        return false;
      }
      setScheda(data);
      if(withPreventiviTotals)loadPreventiviTotals();
      return true;
    }catch(e){
      setScheda(null);
      showToast(e.message||'Errore caricamento scheda','error');
      return false;
    }finally{setLoading(false)}
  },[clienteId,showToast,loadPreventiviTotals]);
  const runFatturatoLookup=async()=>{
    setFattLookupBusy(true);
    try{
      await api('cliente_fatturato_lookup','POST',{cliente_id:clienteId});
      showToast('Fatturato aggiornato da fonti online');
      await load();
    }catch(e){showToast(e.message||'Ricerca fatturato non riuscita','error')}
    finally{setFattLookupBusy(false)}
  };
  useEffect(()=>{
    setScheda(null);
    setTab('panoramica');
    setPreventiviTotalsLoading(false);
    setAssistenzaLoaded(false);
    setAssistenzaLoading(false);
    let cancelled=false;
    (async()=>{
      setLoading(true);
      try{
        const d=await api('cliente_scheda','GET',null,{id:clienteId});
        if(cancelled)return;
        const data=d.data||null;
        if(!data||!data.cliente){
          setScheda(null);
          showToastRef.current('Scheda cliente non disponibile','error');
          return;
        }
        setScheda(data);
      }catch(e){
        if(!cancelled){
          setScheda(null);
          showToastRef.current(e.message||'Errore caricamento scheda','error');
        }
      }finally{
        if(!cancelled)setLoading(false);
      }
    })();
    return()=>{cancelled=true};
  },[clienteId]);
  useEffect(()=>{
    if(tab==='preventivi'&&!scheda?.preventivi_stats?.valori_calcolati)loadPreventiviTotals();
  },[tab,scheda?.preventivi_stats?.valori_calcolati,loadPreventiviTotals]);
  useEffect(()=>{
    if(tab==='assistenza')loadAssistenza();
  },[tab,loadAssistenza]);
  const c=scheda?.cliente||{};
  const saveContratto=async(fields)=>{
    setSavingContratto(true);
    try{
      await api('cliente_update','PUT',{...fields,contratto_assistenza:fields.contratto_assistenza?1:0},{id:clienteId});
      showToast(fields.contratto_assistenza?'Contratto ore aggiornato (riporto ore residue al rinnovo)':'Contratto assistenza disattivato');
      await load();
    }catch(e){showToast(e.message||'Errore','error')}finally{setSavingContratto(false)}
  };
  const deleteStampante=async id=>{if(!confirm('Eliminare questa stampante?'))return;try{await api('cliente_stampante_delete','DELETE',null,{id});showToast('Eliminata');load()}catch(e){showToast(e.message||'Errore','error')}};
  const deletePotenziale=async id=>{if(!confirm('Eliminare questa potenzialità?'))return;try{await api('cliente_potenziale_delete','DELETE',null,{id});showToast('Eliminato');load()}catch(e){showToast(e.message||'Errore','error')}};
  const deleteVisita=async id=>{if(!confirm('Eliminare questa visita?'))return;try{await api('cliente_visita_delete','DELETE',null,{id});showToast('Eliminata');load()}catch(e){showToast(e.message||'Errore','error')}};
  const assistenzaTabCount=assistenzaLoaded
    ?(scheda?.interventi_tecnici?.length||0)
    :(scheda?.interventi_tecnici_stats?.totals?.totale??scheda?.preventivi_assistenza_stats?.totale??0);
  const tabs=[{k:'panoramica',l:'Panoramica'},{k:'stampanti',l:'Stampanti',n:scheda?.stampanti?.length},{k:'potenziale',l:'Potenzialità',n:scheda?.potenziali?.length},{k:'visite',l:'Visite',n:scheda?.visite?.length},{k:'preventivi',l:'Preventivi',n:scheda?.preventivi_stats?.totale},{k:'assistenza',l:'Assistenza tecnica',n:assistenzaTabCount}];
  const visiteCal=useMemo(()=>{
    const items=(scheda?.visite||[]).filter(v=>v.todo_scadenza);
    return items.sort((a,b)=>(a.todo_scadenza||'').localeCompare(b.todo_scadenza||''));
  },[scheda?.visite]);
  if(loading&&!scheda)return<Loading/>;
  if(!scheda)return<Empty icon={<IcUsers s={36}/>} title="Scheda non disponibile" action={<Btn onClick={onBack}>Torna all'elenco</Btn>}/>;
  const ps=scheda.preventivi_stats||{};
  const valoriPreventiviPronti=!!ps.valori_calcolati;
  const fmtValorePreventivo=v=>{
    if(v!=null&&v!=='')return fmt(v);
    return preventiviTotalsLoading?'…':'—';
  };
  const contrattoAttivo=!!c.contratto_assistenza;
  const contrattoOre=scheda?.contratto_assistenza||{};
  const contrattoAttivoOre=contrattoOre?.attivo||null;
  const fattLk=scheda?.fatturato_lookup||{};
  const fattStorico=fattLk.storico||[];
  const fattTrend=fattLk.trend||{};
  const fattReport=fattLk.report||{};
  const fattConfigured=!!fattLk.configured;
  const fattDir=fattTrend.direction||'neutral';
  const fattPct=Math.abs(Number(fattTrend.variazione_pct||0));
  const schedaActions=<div className="flex flex-wrap gap-2">
      <Btn v="secondary" s={isPhone?'sm':'md'} icon={<IcEdit s={16}/>} onClick={()=>onEditAnagrafica(c)}>Anagrafica</Btn>
      <Btn s={isPhone?'sm':'md'} icon={<IcPlus s={16}/>} onClick={()=>{setEditVisita(null);setShowVisita(true)}}>Vistato</Btn>
      {!isPhone?<Btn v="secondary" onClick={()=>{setEditStampante(null);setShowStampante(true)}}>+ Stampante</Btn>:null}
    </div>;
  const schedaContent=<>
    {!embedded&&onBack?<button type="button" onClick={onBack} className="text-sm text-accent-500 hover:text-accent-700 -mt-2 mb-1">← Elenco clienti</button>:null}
    {embedded&&onBack?<button type="button" onClick={onBack} className="text-sm text-accent-500 hover:text-accent-700 mb-2">← Torna all'elenco</button>:null}
    <div className="flex flex-wrap gap-2 mb-2">
      {contrattoAttivo?<span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">Contratto {contrattoAttivoOre?.pacchetto_label||c.contratto_assistenza_tipo||'assistenza'}{contrattoAttivoOre?` · ${fmtOreContratto(contrattoAttivoOre.ore_residue)} residue`:''}{c.contratto_assistenza_scadenza?` · scad. ${fmtDate(c.contratto_assistenza_scadenza)}`:''}</span>:<span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">Senza contratto assistenza</span>}
      {c.contratto_in_scadenza?<span className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-medium">Contratto in scadenza</span>:null}
      {(c.stampanti_count||0)>0?<span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">{c.stampanti_count} stampanti</span>:null}
      {(c.potenziali_count||0)>0?<span className="text-xs px-2.5 py-1 rounded-full bg-violet-50 text-violet-700">{c.potenziali_count} potenziali</span>:null}
    </div>
    <TabBarScroll tabs={tabs} active={tab} onChange={setTab}/>

    {tab==='panoramica'?<div className="page-stack">
      <Card title="Fatturato aziendale & andamento" action={<Btn s="sm" icon={<IcSearch s={15}/>} onClick={runFatturatoLookup} disabled={fattLookupBusy||!fattConfigured}>{fattLookupBusy?'Ricerca...':'Cerca fatturato online'}</Btn>}>
        {!fattConfigured?<p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">Abilita la ricerca in <strong>Impostazioni → Ricerca fatturato online</strong> (consigliato: <em>Siti pubblici gratuiti</em>, senza API a pagamento).</p>:null}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs text-slate-500">Ultimo anno</div><div className="text-lg font-bold text-navy-600">{fattTrend.anno_ultimo||'—'}</div></div>
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs text-slate-500">Fatturato ultimo</div><div className="text-lg font-bold text-navy-600">{fattTrend.fatturato_ultimo>0?fmt(fattTrend.fatturato_ultimo):'—'}</div></div>
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs text-slate-500">Anno precedente</div><div className="text-lg font-bold text-navy-600">{fattTrend.anno_precedente||'—'}</div></div>
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs text-slate-500">Variazione YoY</div>
                <div className={`text-lg font-bold flex items-center gap-1 ${fattDir==='up'?'text-emerald-600':fattDir==='down'?'text-rose-600':'text-slate-600'}`}>
                  <span className="text-xl leading-none">{fattDir==='up'?'↑':fattDir==='down'?'↓':'→'}</span>
                  {fattTrend.anno_precedente?`${fattPct.toFixed(1)}%`:'—'}
                </div>
              </div>
            </div>
            {fattStorico.length===0?<p className="text-sm text-slate-500">Nessuno storico salvato. Usa la ricerca online per importare i bilanci annuali (richiede ragione sociale e/o P.IVA).</p>:
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600"><tr><th className="text-left px-3 py-2">Anno</th><th className="text-right px-3 py-2">Fatturato</th><th className="text-right px-3 py-2">Dipendenti</th></tr></thead>
                <tbody>{[...fattStorico].sort((a,b)=>b.anno-a.anno).map(row=>{
                  const prev=fattStorico.find(r=>r.anno===row.anno-1);
                  const delta=prev&&prev.fatturato>0?((row.fatturato-prev.fatturato)/prev.fatturato)*100:null;
                  return<tr key={row.anno} className="border-t border-slate-100"><td className="px-3 py-2 font-medium">{row.anno}</td><td className="px-3 py-2 text-right font-semibold">{fmt(row.fatturato)}</td>
                    <td className="px-3 py-2 text-right">{row.dipendenti>0?row.dipendenti:'—'}{delta!=null?<span className={`ml-2 text-xs ${delta>=0?'text-emerald-600':'text-rose-600'}`}>{delta>=0?'↑':'↓'}{Math.abs(delta).toFixed(1)}%</span>:null}</td></tr>;
                })}</tbody>
              </table>
            </div>}
          </div>
          <div className={`rounded-2xl border p-4 ${fattDir==='up'?'border-emerald-200 bg-emerald-50':fattDir==='down'?'border-rose-200 bg-rose-50':'border-slate-200 bg-slate-50'}`}>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Report andamento</div>
            <div className={`text-3xl font-bold mb-2 ${fattDir==='up'?'text-emerald-700':fattDir==='down'?'text-rose-700':'text-slate-700'}`}>
              {fattDir==='up'?'↑ Crescita':fattDir==='down'?'↓ Calo':'→ Stabile'}
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{S(fattReport.sintesi||'Esegui la ricerca per generare il report.')}</p>
            {fattReport.match_denominazione||fattReport.match_piva?<div className="mt-3 text-xs text-slate-500 border-t border-slate-200/80 pt-3">
              {fattReport.match_denominazione?<div>Match: {S(fattReport.match_denominazione)}</div>:null}
              {fattReport.match_piva?<div>P.IVA: {S(fattReport.match_piva)}</div>:null}
            </div>:null}
            {fattTrend.cagr_pct!=null&&fattStorico.length>=3?<div className="mt-2 text-xs text-slate-600">CAGR medio: <strong>{Number(fattTrend.cagr_pct).toFixed(1)}%</strong></div>:null}
          </div>
        </div>
      </Card>
      <div className="grid-responsive-2 xl:grid-cols-3 gap-6">
      <Card title="Profilo commerciale"><div className="space-y-3 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Fatturato annuo (scheda)</span><strong>{c.fatturato_annuo>0?fmt(c.fatturato_annuo):'—'}</strong></div>
        <div className="flex justify-between"><span className="text-slate-500">N. dipendenti</span><strong>{c.numero_dipendenti>0?c.numero_dipendenti:'—'}</strong></div>
        <div className="flex justify-between"><span className="text-slate-500">Categoria</span><strong>{clientiNormalizeMerceologia(c.categoria_merceologica)}</strong></div>
        <div className="flex justify-between"><span className="text-slate-500">Preventivi</span><strong>{ps.totale||0}</strong></div>
        <div className="flex justify-between"><span className="text-slate-500">Valore offerte</span><strong>{valoriPreventiviPronti?fmt(ps.valore_totale||0):fmtValorePreventivo(ps.valore_totale)}</strong></div>
        <div className="flex justify-between"><span className="text-slate-500">Accettati</span><strong className="text-emerald-700">{valoriPreventiviPronti?fmt(ps.valore_accettato||0):fmtValorePreventivo(ps.valore_accettato)}</strong></div>
      </div></Card>
      <Card title="Contratto assistenza (ore)"><div className="space-y-3">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={contrattoAttivo} onChange={e=>saveContratto({contratto_assistenza:e.target.checked?1:0,contratto_assistenza_tipo:c.contratto_assistenza_tipo||'10 ore',contratto_assistenza_scadenza:c.contratto_assistenza_scadenza||'',contratto_assistenza_note:c.contratto_assistenza_note||''})} disabled={savingContratto}/> Contratto assistenza attivo</label>
        <Sel label="Pacchetto ore" value={c.contratto_assistenza_tipo||''} onChange={v=>saveContratto({contratto_assistenza:1,contratto_assistenza_tipo:v,contratto_assistenza_scadenza:c.contratto_assistenza_scadenza||'',contratto_assistenza_note:c.contratto_assistenza_note||''})} options={CONTRATTO_TIPO_OPTIONS} placeholder="Seleziona pacchetto"/>
        <Inp label="Scadenza contratto" type="date" value={c.contratto_assistenza_scadenza||''} onChange={v=>saveContratto({contratto_assistenza:1,contratto_assistenza_tipo:c.contratto_assistenza_tipo||'',contratto_assistenza_scadenza:v,contratto_assistenza_note:c.contratto_assistenza_note||''})}/>
        {contrattoAttivoOre?<div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-slate-600">Ore incluse pacchetto</span><strong>{fmtOreContratto(contrattoAttivoOre.ore_incluse)}</strong></div>
          {contrattoAttivoOre.ore_riportate>0?<div className="flex justify-between"><span className="text-slate-600">Riporto precedente</span><strong>+{fmtOreContratto(contrattoAttivoOre.ore_riportate)}</strong></div>:null}
          <div className="flex justify-between text-emerald-900"><span>Ore residue</span><strong className="text-lg">{fmtOreContratto(contrattoAttivoOre.ore_residue)}</strong></div>
          <div className="text-xs text-slate-500">Valido fino al {fmtDate(contrattoAttivoOre.data_fine)} · al rinnovo le ore non consumate vengono sommate al nuovo pacchetto</div>
        </div>:contrattoAttivo?<p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Salva pacchetto e scadenza per attivare il contatore ore.</p>:null}
        {(contrattoOre.movimenti||[]).length>0?<div className="pt-2 border-t border-slate-100"><div className="text-xs font-semibold text-slate-500 uppercase mb-2">Ultimi movimenti</div><div className="space-y-1 max-h-36 overflow-y-auto">{(contrattoOre.movimenti||[]).slice(0,6).map(m=><div key={m.id} className="text-xs flex justify-between gap-2"><span className="text-slate-600 truncate">{m.tipo==='utilizzo_intervento'?'Intervento':m.tipo==='riporto_rinnovo'?'Riporto rinnovo':m.tipo}{m.intervento_id?` #${m.intervento_id}`:''}</span><span className={m.ore_delta<0?'text-rose-700':'text-emerald-700'}>{m.ore_delta>0?'+':''}{fmtOreContratto(m.ore_delta)} → {fmtOreContratto(m.ore_residue_dopo)}</span></div>)}</div></div>:null}
        <div><label className="block text-sm font-medium text-gray-600 mb-1">Note contratto</label><textarea defaultValue={c.contratto_assistenza_note||''} onBlur={e=>saveContratto({contratto_assistenza:contrattoAttivo?1:0,contratto_assistenza_tipo:c.contratto_assistenza_tipo||'',contratto_assistenza_scadenza:c.contratto_assistenza_scadenza||'',contratto_assistenza_note:e.target.value})} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm"/></div>
      </div></Card>
      <Card title="Calendario follow-up visite">{visiteCal.length===0?<p className="text-sm text-slate-400">Nessun to-do con scadenza dalle visite.</p>:<div className="space-y-2 max-h-64 overflow-y-auto">{visiteCal.map(v=><div key={v.id} className={`rounded-xl border px-3 py-2 text-sm ${v.todo_scadenza<today()?'border-rose-200 bg-rose-50':'border-slate-200'}`}>
        <div className="font-medium text-navy-600">{fmtDate(v.todo_scadenza)} · {S(v.todo_testo)}</div>
        <div className="text-xs text-slate-500">Visita {fmtDate(v.data_visita)}{v.persone_contatto?` · ${S(v.persone_contatto)}`:''}</div>
      </div>)}      </div>}</Card>
    </div></div>:null}

    {tab==='stampanti'?<Card title="Parco stampanti installato" action={<Btn s="sm" onClick={()=>{setEditStampante(null);setShowStampante(true)}}>Aggiungi stampante</Btn>}>
      {(scheda.stampanti||[]).length===0?<Empty icon={<IcPkg s={32}/>} title="Nessuna stampante" sub="Registra le macchine presenti presso il cliente." action={<Btn onClick={()=>{setEditStampante(null);setShowStampante(true)}}>Aggiungi stampante</Btn>}/>:
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{(scheda.stampanti||[]).map(s=><div key={s.id} className={`rounded-2xl border p-4 ${s.attiva? 'border-slate-200 bg-white':'border-slate-100 bg-slate-50 opacity-75'}`}>
        <div className="flex justify-between gap-2"><div><div className="font-semibold text-navy-600">{S(s.modello)}</div><div className="text-xs text-slate-500">{stampanteTipologiaLabel(s.tipologia)}{s.marca?` · ${S(s.marca)}`:''}</div></div>
          <div className="flex gap-1"><button onClick={()=>{setEditStampante(s);setShowStampante(true)}} className="p-1.5 hover:bg-blue-50 rounded"><IcEdit s={15}/></button><button onClick={()=>deleteStampante(s.id)} className="p-1.5 hover:bg-red-50 rounded"><IcTrash s={15}/></button></div></div>
        <div className="text-xs text-slate-500 mt-2 space-y-0.5">{s.numero_seriale?<div>S/N: {S(s.numero_seriale)}</div>:null}{s.ubicazione?<div>{S(s.ubicazione)}</div>:null}{s.note?<div className="italic">{S(s.note)}</div>:null}</div>
      </div>)}</div>}
    </Card>:null}

    {tab==='potenziale'?<Card title="Potenzialità commerciale" action={<Btn s="sm" onClick={()=>{setEditPotenziale(null);setShowPotenziale(true)}}>Aggiungi modello target</Btn>}>
      <p className="text-sm text-slate-500 mb-4">Elenco modelli/stampanti per cui questo cliente è considerato potenziale (utile quando esce una novità).</p>
      {(scheda.potenziali||[]).length===0?<Empty title="Nessuna potenzialità" sub="Aggiungi i modelli di interesse commerciale."/>:<div className="divide-y">{(scheda.potenziali||[]).map(p=><div key={p.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
        <div><div className="font-semibold text-navy-600">{S(p.modello)}</div><div className="text-xs text-slate-500">{stampanteTipologiaLabel(p.tipologia)}{p.marca?` · ${S(p.marca)}`:''}</div>{p.note?<div className="text-sm text-slate-600 mt-1">{S(p.note)}</div>:null}</div>
        <div className="flex items-center gap-2"><span className={`text-xs px-2 py-1 rounded-full font-medium ${prioritaPotenzialeClass(p.priorita)}`}>{p.priorita}</span><Btn s="sm" v="secondary" onClick={()=>{setEditPotenziale(p);setShowPotenziale(true)}}>Modifica</Btn><Btn s="sm" v="danger" onClick={()=>deletePotenziale(p.id)}>Elimina</Btn></div>
      </div>)}</div>}
    </Card>:null}

    {tab==='visite'?<Card title="Diario visite (Vistato)" action={<Btn s="sm" onClick={()=>{setEditVisita(null);setShowVisita(true)}}>Nuova visita</Btn>}>
      {(scheda.visite||[]).length===0?<Empty title="Nessuna visita" sub="Usa Vistato per registrare incontri e follow-up."/>:<div className="space-y-4">{(scheda.visite||[]).map(v=><div key={v.id} className="rounded-2xl border border-slate-200 p-4">
        <div className="flex flex-wrap justify-between gap-2 mb-2"><div><span className="font-semibold text-navy-600">{fmtDate(v.data_visita)}</span>{v.persone_contatto?<span className="text-sm text-slate-500 ml-2">con {S(v.persone_contatto)}</span>:null}</div>
          <div className="flex gap-1"><Btn s="sm" v="secondary" onClick={()=>{setEditVisita(v);setShowVisita(true)}}>Modifica</Btn><Btn s="sm" v="danger" onClick={()=>deleteVisita(v.id)}>Elimina</Btn></div></div>
        {v.descrizione?<p className="text-sm text-slate-700 whitespace-pre-wrap">{S(v.descrizione)}</p>:null}
        {v.todo_testo?<div className={`mt-3 rounded-lg px-3 py-2 text-sm ${v.todo_scadenza&&v.todo_scadenza<today()?'bg-rose-50 text-rose-800':'bg-amber-50 text-amber-900'}`}><strong>To-do:</strong> {S(v.todo_testo)}{v.todo_scadenza?<span className="block text-xs mt-1">Entro {fmtDate(v.todo_scadenza)}</span>:null}</div>:null}
        <div className="text-xs text-slate-400 mt-2">{v.user_nome?`Registrato da ${S(v.user_nome)}`:''}</div>
      </div>)}</div>}
    </Card>:null}

    {tab==='preventivi'?<div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[{l:'Totale',v:ps.totale},{l:'Bozze',v:ps.bozza},{l:'Inviate',v:ps.inviata},{l:'Accettate',v:ps.accettata},{l:'Valore totale',v:valoriPreventiviPronti?fmt(ps.valore_totale||0):fmtValorePreventivo(ps.valore_totale),raw:1}].map((item,i)=><div key={i} className="rounded-xl border border-slate-200 bg-white p-3 text-center"><div className="text-lg font-bold text-navy-600">{item.raw?item.v:item.v||0}</div><div className="text-xs text-slate-500">{item.l}</div></div>)}
      {preventiviTotalsLoading?<p className="text-xs text-slate-500">Calcolo totali preventivi in corso…</p>:null}
      </div>
      <Card title="Elenco preventivi">{(scheda.preventivi||[]).length===0?<Empty title="Nessun preventivo"/>:
      <div className="divide-y">{(scheda.preventivi||[]).map(p=><div key={p.id} className="py-3 flex flex-wrap items-center justify-between gap-2 cursor-pointer hover:bg-slate-50 px-2 rounded-lg" onClick={()=>onOpenPreventivo&&onOpenPreventivo(p.id)}>
        <div><div className="font-mono font-semibold text-navy-600">{S(p.codice)}</div><div className="text-xs text-slate-500">{fmtDate(p.data)} · {S(p.agente||p.owner_nome||'')}</div></div>
        <div className="flex items-center gap-3"><StatoBadge stato={p.stato}/><div className="text-right"><div className="font-bold">{p.totale_fattura!=null?fmt(p.totale_fattura):fmtValorePreventivo(null)}</div>{(isAdmin||isManager)&&p.imponibile>0?<div className="text-xs text-slate-400">Margine {fmt((p.margine_pct||0)*100,'pct')}</div>:null}</div></div>
      </div>)}</div>}
      </Card>
    </div>:null}

    {tab==='assistenza'?assistenzaLoading&&!assistenzaLoaded?<Loading/>:<div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[{l:'Attività tecniche',v:scheda?.interventi_tecnici_stats?.totals?.attivita_totali||0},{l:'Ore totali',v:`${Number(scheda?.interventi_tecnici_stats?.totals?.ore_totali||0).toFixed(1)}h`},{l:'Ricavo consuntivo',v:fmt(scheda?.interventi_tecnici_stats?.totals?.ricavo_consuntivo||0),raw:1},{l:'Preventivi assistenza',v:scheda?.preventivi_assistenza_stats?.totale||0}].map((item,i)=><div key={i} className="rounded-xl border border-slate-200 bg-white p-3 text-center"><div className="text-lg font-bold text-navy-600">{item.raw?item.v:item.v||0}</div><div className="text-xs text-slate-500">{item.l}</div></div>)}
      </div>
      <Card title="Storico interventi tecnici">
        {(scheda.interventi_tecnici||[]).length===0?<Empty title="Nessun intervento tecnico"/>:
        <div className="space-y-3">{(scheda.interventi_tecnici||[]).slice(0,20).map(item=><div key={item.id} className="rounded-2xl border border-slate-200 p-4 bg-white/80">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">{S(item.codice)}</span>
            <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">{assistenzaCategoriaLabel(item.categoria_attivita)}</span>
            <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">{assistenzaStatoLabel(item.stato_attivita)}</span>
          </div>
          <div className="font-semibold text-navy-600">{S(item.titolo||item.problematica||'Attività tecnica')}</div>
          <div className="text-sm text-slate-500 mt-1">{fmtDate(item.data_pianificata||item.data_intervento)}{item.ora_inizio_prevista||item.orario_intervento?` · ${S(item.ora_inizio_prevista||item.orario_intervento)}`:''} · {S(item.tecnico_nome||item.tecnico_user_nome||item.tecnico_username||'-')}</div>
          <div className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{S(item.problematica||'-')}</div>
          <div className="text-xs text-slate-400 mt-2">{`Ore: ${Number((item.tempo_minuti||0)/60).toFixed(1)}h · Ricavo: ${fmt(item.ricavo_consuntivo||0)} · ${assistenzaFatturabileLabel(item.fatturabile)}`}</div>
        </div>)}</div>}
      </Card>
      <Card title="Storico preventivi assistenza">
        {(scheda.preventivi_assistenza||[]).length===0?<Empty title="Nessun preventivo assistenza"/>:
        <div className="divide-y">{(scheda.preventivi_assistenza||[]).map(item=><div key={item.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-semibold text-navy-600">{S(item.codice)}</div>
            <div className="text-xs text-slate-500">{fmtDate(item.data_preventivo||String(item.created_at||'').slice(0,10))}{item.data_prevista_intervento?` · Previsto ${fmtDate(item.data_prevista_intervento)}`:''}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2 py-1 rounded-full ${String(item.stato||'bozza').toLowerCase()==='confermato'?'bg-green-50 text-green-700':'bg-slate-100 text-slate-600'}`}>{String(item.stato||'bozza').toLowerCase()==='confermato'?'Confermato':'Bozza'}</span>
            <div className="font-bold">{fmt(item.totale||0)}</div>
          </div>
        </div>)}</div>}
      </Card>
    </div>:null}

    {showStampante?<ClienteStampanteSchedaModal cliente={c} stampante={editStampante} onClose={()=>setShowStampante(false)} onSaved={()=>{setShowStampante(false);load()}}/>:null}
    {showPotenziale?<ClientePotenzialeModal cliente={c} potenziale={editPotenziale} onClose={()=>setShowPotenziale(false)} onSaved={()=>{setShowPotenziale(false);load()}}/>:null}
    {showVisita?<ClienteVisitaModal cliente={c} visita={editVisita} onClose={()=>setShowVisita(false)} onSaved={()=>{setShowVisita(false);load()}}/>:null}
  </>;
  if(embedded){
    return(<div className="clienti-detail-panel">
      <div className="clienti-detail-panel__head">
        <h2 className="text-lg font-bold text-navy-600 truncate">{S(c.azienda||c.nome||'Cliente')}</h2>
        <p className="text-xs text-slate-500 truncate">{[c.citta,normalizeProvincia(c.provincia),c.regione].filter(Boolean).join(' · ')}</p>
        {schedaActions}
      </div>
      {schedaContent}
    </div>);
  }
  return(<Page title={S(c.azienda||c.nome||'Cliente')} subtitle={[c.citta,normalizeProvincia(c.provincia),c.regione].filter(Boolean).join(' · ')} actions={schedaActions}>
    {schedaContent}
  </Page>);
}

const ClienteSchedaPage=ClienteSchedaPanel;

function useClientiList(apiParams,{enabled=true,progressive=true}={}){
  const[rows,setRows]=useState([]);
  const[total,setTotal]=useState(0);
  const[counts,setCounts]=useState({cliente:0,potenziale:0,rivenditore:0,totale:0});
  const[loading,setLoading]=useState(false);
  const[error,setError]=useState(null);
  const requestIdRef=useRef(0);
  const paramsKey=useMemo(()=>JSON.stringify(apiParams||{}),[apiParams]);
  const reload=useCallback(async()=>{
    const requestId=++requestIdRef.current;
    setLoading(true);
    setError(null);
    try{
      const rawParams=JSON.parse(paramsKey);
      const params={light:'1',enrich:'0',counts:'0',...rawParams};
      const targetTipo=String(rawParams?.tipo||'').trim();
      const canProgress=progressive&&(!targetTipo||targetTipo==='all');
      if(canProgress){
        const previewParams={...params,tipo:'cliente'};
        const preview=await api('clienti','GET',null,previewParams);
        if(requestId!==requestIdRef.current)return;
        const previewList=preview.data||[];
        setRows(previewList);
        setTotal(Number(preview.meta?.total??previewList.length)||0);
      }
      const d=await api('clienti','GET',null,params);
      if(requestId!==requestIdRef.current)return;
      const list=d.data||[];
      setRows(list);
      setTotal(Number(d.meta?.total??list.length)||0);
      if(d.meta?.counts)setCounts(d.meta.counts);
      else setCounts(prev=>prev?.totale?prev:{cliente:0,potenziale:0,rivenditore:0,totale:Number(d.meta?.total??list.length)||0});
    }catch(e){
      if(requestId!==requestIdRef.current)return;
      setRows([]);
      setTotal(0);
      setCounts({cliente:0,potenziale:0,rivenditore:0,totale:0});
      setError(e?.message||'Errore caricamento clienti');
    }finally{
      if(requestId===requestIdRef.current)setLoading(false);
    }
  },[paramsKey,progressive]);
  useEffect(()=>{
    if(!enabled)return;
    reload();
  },[enabled,reload]);
  return{rows,total,counts,loading,error,reload,setRows};
}

const emptyClientiAnalysisStats=()=>({
  total_records:0,
  counts:{cliente:0,potenziale:0,rivenditore:0,totale:0},
  kpi:{totale:0,clientiCount:0,rivenditoriCount:0,potenzialiCount:0,categorieCount:0,regioniCount:0},
  sections:{merceologiche:[],tipo:[],regioni:[],province:[],citta:[],pagamenti:[]},
});

// ========== CLIENTI ==========
function ClientiCleanupModal({onClose,onDone}){
  const{showToast}=useContext(DataCtx);
  const[loading,setLoading]=useState(true);
  const[groups,setGroups]=useState([]);
  const[confidenceFilter,setConfidenceFilter]=useState('all');
  const loadGroups=async()=>{
    setLoading(true);
    try{
      const d=await api('clienti_duplicate_scan');
      setGroups(d.data?.groups||[]);
    }catch(e){showToast(e.message,'error')}
    finally{setLoading(false);}
  };
  useEffect(()=>{loadGroups()},[]);
  const mergeGroup=async(group)=>{
    const contacts=group.contacts||[];
    if(contacts.length<2)return;
    const master=contacts[0];
    const duplicateIds=contacts.slice(1).map(item=>item.id);
    if(!confirm(`Unire ${contacts.length} anagrafiche mantenendo come principale "${master.azienda||[master.nome,master.cognome].filter(Boolean).join(' ')||'contatto'}"?`))return;
    try{
      await api('clienti_duplicate_merge','POST',{master_id:master.id,duplicate_ids:duplicateIds});
      showToast('Duplicati uniti');
      loadGroups();
    }catch(e){showToast(e.message,'error')}
  };
  const mergeAllGroups=async()=>{
    if(!filteredGroups.length)return;
    if(!confirm(`Unire automaticamente tutti i ${filteredGroups.length} gruppi di duplicati attualmente visibili?`))return;
    setLoading(true);
    try{
      for(const group of filteredGroups){
        const contacts=group.contacts||[];
        if(contacts.length<2)continue;
        const master=contacts[0];
        const duplicateIds=contacts.slice(1).map(item=>item.id);
        await api('clienti_duplicate_merge','POST',{master_id:master.id,duplicate_ids:duplicateIds});
      }
      showToast('Tutti i gruppi duplicati sono stati uniti');
      await loadGroups();
    }catch(e){
      showToast(e.message,'error');
    }finally{
      setLoading(false);
    }
  };
  const filteredGroups=useMemo(()=>{
    if(confidenceFilter==='all')return groups;
    if(confidenceFilter==='alta')return groups.filter(g=>g.confidence==='alta');
    if(confidenceFilter==='media')return groups.filter(g=>g.confidence==='media');
    if(confidenceFilter==='review')return groups.filter(g=>g.confidence==='da verificare');
    return groups;
  },[groups,confidenceFilter]);
  return(<Modal title="Pulizia database clienti" onClose={onClose} wide>
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">La scansione usa criteri forti: P.IVA, codice fiscale, azienda + email, azienda + telefono/cellulare, persona + email/telefono e azienda + indirizzo completo. Ho rimosso i confronti troppo larghi che generavano falsi positivi.</div>
      <div className="flex flex-wrap items-center gap-2">
        {[{k:'all',l:`Tutti (${groups.length})`},{k:'alta',l:`Alta (${groups.filter(g=>g.confidence==='alta').length})`},{k:'media',l:`Media (${groups.filter(g=>g.confidence==='media').length})`},{k:'review',l:`Verifica (${groups.filter(g=>g.confidence==='da verificare').length})`}].map(item=>(
          <button key={item.k} onClick={()=>setConfidenceFilter(item.k)} className={`px-3 py-2 rounded-full text-xs font-semibold border transition ${confidenceFilter===item.k?'border-slate-900 bg-slate-900 text-white':'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>{item.l}</button>
        ))}
      </div>
      {loading?<Loading/>:filteredGroups.length===0?<Empty icon={<IcUsers s={30} c="text-accent-400"/>} title="Nessun duplicato rilevato" sub="Con i criteri attivi non risultano gruppi duplicati da verificare."/>:
      <div className="space-y-4 max-h-[68vh] overflow-y-auto pr-1">{filteredGroups.map(group=><div key={group.group_key} className="rounded-3xl border border-slate-200 bg-white p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="flex items-center flex-wrap gap-2">
              <div className="text-sm font-semibold text-navy-600">Possibile duplicato: {group.matched_label||'Anagrafica'}</div>
              <span className={`text-[11px] px-2 py-1 rounded-full ${group.confidence==='alta'?'bg-emerald-100 text-emerald-700':group.confidence==='media'?'bg-amber-100 text-amber-700':'bg-slate-100 text-slate-600'}`}>{group.confidence==='alta'?'Affidabilità alta':group.confidence==='media'?'Affidabilità media':'Da verificare'}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">{group.contacts.length} contatti coinvolti · criterio: {group.matched_label||'anagrafica'} · punteggio {group.score||0}</div>
          </div>
          <Btn s="sm" onClick={()=>mergeGroup(group)}>Unisci gruppo</Btn>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">{group.contacts.map((item,idx)=><div key={item.id} className={`rounded-2xl border p-4 ${idx===0?'border-emerald-200 bg-emerald-50':'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="font-semibold text-slate-700">{item.azienda||[item.nome,item.cognome].filter(Boolean).join(' ')||`Contatto ${item.id}`}</div>
            {idx===0?<span className="text-xs px-2 py-1 rounded-full bg-emerald-600 text-white">Principale</span>:null}
          </div>
          <div className="text-sm text-slate-500 mt-2 space-y-1">
            <div>{[item.email,item.telefono||item.cellulare].filter(Boolean).join(' · ')||'Nessun recapito'}</div>
            <div>{[item.via,item.cap,item.citta,normalizeProvincia(item.provincia),item.regione].filter(Boolean).join(', ')||'Nessun indirizzo'}</div>
            <div>{item.categoria_merceologica||'Categoria non indicata'}</div>
          </div>
        </div>)}</div>
      </div>)}</div>}
      <div className="flex justify-end gap-3">
        <Btn v="secondary" onClick={onClose}>Chiudi</Btn>
        <Btn v="secondary" onClick={loadGroups}>Aggiorna scansione</Btn>
        {filteredGroups.length>0?<Btn v="secondary" onClick={mergeAllGroups}>Unisci tutti</Btn>:null}
        <Btn onClick={onDone}>Fatto</Btn>
      </div>
    </div>
  </Modal>);
}

function ClientiPage(){
  const{loadClienti,invalidateClientiCache,showToast,settings}=useContext(DataCtx);
  const{isAdmin}=useContext(AuthCtx);
  const{isPhone,isTablet}=useDevice();
  const[viewMode,setViewMode]=useState('elenco');
  const[search,setSearch]=useState('');
  const[tipoFilter,setTipoFilter]=useState('cliente');
  const[cittaFilter,setCittaFilter]=useState('');
  const[provFilter,setProvFilter]=useState('');
  const[regFilter,setRegFilter]=useState('');
  const[catMercFilter,setCatMercFilter]=useState('');
  const[pagFilter,setPagFilter]=useState('');
  const[stampanteTipFilter,setStampanteTipFilter]=useState('');
  const[stampanteModelFilter,setStampanteModelFilter]=useState('');
  const[potenzialeModelFilter,setPotenzialeModelFilter]=useState('');
  const[contrattoFilter,setContrattoFilter]=useState('');
  const[fatturatoMinFilter,setFatturatoMinFilter]=useState('');
  const[schedaClienteId,setSchedaClienteId]=useState(null);
  const[potenzialiSearchQ,setPotenzialiSearchQ]=useState('');
  const[potenzialiSearchRes,setPotenzialiSearchRes]=useState([]);
  const[showPotenzialiSearch,setShowPotenzialiSearch]=useState(false);
  const[showForm,setShowForm]=useState(false);
  const[editCl,setEditCl]=useState(null);
  const[formDefaultTipo,setFormDefaultTipo]=useState('cliente');
  const[showImport,setShowImport]=useState(false);
  const[importDefaultTipo,setImportDefaultTipo]=useState('cliente');
  const[transformCliente,setTransformCliente]=useState(null);
  const[showCleanup,setShowCleanup]=useState(false);
  const[showCategoryMerge,setShowCategoryMerge]=useState(false);
  const[showToolsMenu,setShowToolsMenu]=useState(false);
  const[analysisVisibility,setAnalysisVisibility]=useState({merceologiche:true,tipo:true,regioni:true,province:true,citta:true,pagamenti:true});
  const[analysisStats,setAnalysisStats]=useState(()=>emptyClientiAnalysisStats());
  const[analysisStatsLoading,setAnalysisStatsLoading]=useState(false);
  const[analysisReloadKey,setAnalysisReloadKey]=useState(0);
  const[countsLoading,setCountsLoading]=useState(false);
  const[countsError,setCountsError]=useState('');
  const[counts,setCounts]=useState({cliente:0,potenziale:0,rivenditore:0,totale:0});
  const[page,setPage]=useState(1);
  const[debouncedSearch,setDebouncedSearch]=useState('');
  const PER_PAGE=Math.max(20,Math.min(200,parseInt(settings?.performance_client_page_size,10)||50));
  const masterDetail=!isPhone;
  const useMasterDetailLayout=masterDetail&&viewMode==='elenco';
  useEffect(()=>{const t=setTimeout(()=>setDebouncedSearch(search.trim()),400);return()=>clearTimeout(t)},[search]);
  const schedaApiFilters=useMemo(()=>{
    const f={};
    if(stampanteTipFilter)f.stampante_tipologia=stampanteTipFilter;
    if(stampanteModelFilter.trim())f.stampante_modello=stampanteModelFilter.trim();
    if(potenzialeModelFilter.trim())f.potenziale_modello=potenzialeModelFilter.trim();
    if(contrattoFilter==='attivo')f.contratto_assistenza='1';
    if(contrattoFilter==='scadenza')f.contratto_in_scadenza='1';
    if(fatturatoMinFilter&&toNum(fatturatoMinFilter)>0)f.fatturato_min=String(toNum(fatturatoMinFilter));
    return f;
  },[stampanteTipFilter,stampanteModelFilter,potenzialeModelFilter,contrattoFilter,fatturatoMinFilter]);
  const baseClientiApiParams=useMemo(()=>{
    const f={...schedaApiFilters};
    if(debouncedSearch)f.q=debouncedSearch;
    if(tipoFilter!=='all')f.tipo=tipoFilter;
    if(cittaFilter)f.citta=cittaFilter;
    if(provFilter)f.provincia=provFilter;
    if(regFilter)f.regione=regFilter;
    if(catMercFilter)f.categoria_merceologica=catMercFilter;
    if(pagFilter)f.pagamento=pagFilter;
    return f;
  },[schedaApiFilters,debouncedSearch,tipoFilter,cittaFilter,provFilter,regFilter,catMercFilter,pagFilter]);
  const listApiParams=useMemo(()=>({
    ...baseClientiApiParams,
    limit:String(PER_PAGE),
    offset:String((page-1)*PER_PAGE),
    enrich:'0',
    light:'1',
  }),[baseClientiApiParams,page,PER_PAGE]);
  const analisiApiParams=useMemo(()=>{
    return{
      ...baseClientiApiParams,
      limit:'1',
      offset:'0',
      enrich:'0',
      light:'1',
    };
  },[baseClientiApiParams]);
  const statsApiParams=useMemo(()=>({...baseClientiApiParams}),[baseClientiApiParams]);
  const elencoList=useClientiList(listApiParams,{enabled:viewMode==='elenco',progressive:true});
  const analisiList=useClientiList(analisiApiParams,{enabled:viewMode==='analisi',progressive:false});
  const listState=viewMode==='analisi'?analisiList:elencoList;
  const{rows:pageList,total:clientiTotal,loading:clientiLoading,error:listError,reload:reloadList}=listState;
  const countClienti=counts.cliente??0;
  const countPotenziali=counts.potenziale??0;
  const countRivenditori=counts.rivenditore??0;
  const countTutti=counts.totale??clientiTotal;
  useEffect(()=>{if(listError)showToast(listError,'error')},[listError,showToast]);
  useEffect(()=>{
    let cancelled=false;
    setCountsLoading(true);
    setCountsError('');
    api('clienti_counts','GET',null,statsApiParams)
      .then(resp=>{
        if(cancelled)return;
        setCounts(resp.data||{cliente:0,potenziale:0,rivenditore:0,totale:0});
      })
      .catch(err=>{
        if(cancelled)return;
        setCountsError(err.message||'Errore conteggi clienti');
      })
      .finally(()=>{
        if(cancelled)return;
        setCountsLoading(false);
      });
    return()=>{cancelled=true};
  },[statsApiParams,showToast]);
  useEffect(()=>{if(countsError)showToast(countsError,'error')},[countsError,showToast]);
  useEffect(()=>{
    if(viewMode!=='analisi')return;
    let cancelled=false;
    setAnalysisStatsLoading(true);
    api('clienti_stats','GET',null,statsApiParams)
      .then(resp=>{
        if(cancelled)return;
        setAnalysisStats(resp.data||emptyClientiAnalysisStats());
      })
      .catch(err=>{
        if(cancelled)return;
        setAnalysisStats(emptyClientiAnalysisStats());
        showToast(err.message||'Errore caricamento analisi clienti','error');
      })
      .finally(()=>{
        if(cancelled)return;
        setAnalysisStatsLoading(false);
      });
    return()=>{cancelled=true};
  },[viewMode,statsApiParams,analysisReloadKey,showToast]);
  const runPotenzialiSearch=async()=>{
    const q=potenzialiSearchQ.trim();
    if(!q){showToast('Indica il modello da cercare','error');return}
    try{const d=await api('cliente_scheda_potenziali_search','GET',null,{modello:q});setPotenzialiSearchRes(d.data||[]);setShowPotenzialiSearch(true)}catch(e){showToast(e.message||'Errore ricerca','error')}
  };

  const filterRows=elencoList.rows.length?elencoList.rows:pageList;
  const regioni=useMemo(()=>uniqueTextValues(filterRows.map(c=>c.regione)),[filterRows]);
  const province=useMemo(()=>uniqueTextValues(filterRows.map(c=>normalizeProvincia(c.provincia))),[filterRows]);
  const citta=useMemo(()=>uniqueTextValues(filterRows.map(c=>c.citta)),[filterRows]);
  const categorieMerceologiche=useMemo(()=>uniqueTextValues(filterRows.flatMap(c=>clientiParseMerceologie(c.categoria_merceologica))),[filterRows]);
  const pagamenti=useMemo(()=>uniqueTextValues(filterRows.map(c=>c.pagamento)),[filterRows]);

  useEffect(()=>{setPage(1)},[debouncedSearch,tipoFilter,cittaFilter,provFilter,regFilter,catMercFilter,pagFilter,viewMode,stampanteTipFilter,stampanteModelFilter,potenzialeModelFilter,contrattoFilter,fatturatoMinFilter]);
  useEffect(()=>{setShowToolsMenu(false)},[viewMode,showImport,showCleanup,showCategoryMerge,showForm]);
  const totalPages=viewMode==='analisi'?1:Math.max(1,Math.ceil((clientiTotal||0)/PER_PAGE));
  const reloadClienti=useCallback(async()=>{
    await reloadList();
    setAnalysisReloadKey(key=>key+1);
    try{
      const resp=await api('clienti_counts','GET',null,statsApiParams);
      setCounts(resp.data||{cliente:0,potenziale:0,rivenditore:0,totale:0});
    }catch(_e){}
    loadClienti({enrich:'0',light:'1',force:true}).catch(()=>{});
  },[reloadList,loadClienti,statsApiParams]);

  const deleteCl=async(id)=>{
    if(!confirm('Eliminare questo contatto?'))return;
    try{await api('cliente_delete','DELETE',null,{id});invalidateClientiCache();showToast('Contatto eliminato');reloadClienti()}catch(e){showToast(e.message,'error')}
  };

  const promoteCl=async(c)=>{
    try{await api('cliente_update','PUT',{tipo:'cliente'},{id:c.id});invalidateClientiCache();showToast('Promosso a cliente');reloadClienti()}catch(e){showToast(e.message,'error')}
  };

  const promoteDealer=async(c)=>{
    if(!confirm(`Trasformare "${clienteDisplayName(c)}" in rivenditore?`))return;
    try{
      await api('cliente_transform','POST',{cliente_id:c.id,mode:'rivenditore'});
      invalidateClientiCache();
      showToast('Cliente trasformato in rivenditore');
      reloadClienti();
    }catch(e){showToast(e.message,'error')}
  };

  const hasFilters=cittaFilter||provFilter||regFilter||catMercFilter||pagFilter||stampanteTipFilter||stampanteModelFilter||potenzialeModelFilter||contrattoFilter||fatturatoMinFilter;
  const clearFilters=()=>{setCittaFilter('');setProvFilter('');setRegFilter('');setCatMercFilter('');setPagFilter('');setStampanteTipFilter('');setStampanteModelFilter('');setPotenzialeModelFilter('');setContrattoFilter('');setFatturatoMinFilter('')};
  const closeSchedaCliente=useCallback(()=>{setSchedaClienteId(null)},[]);
  const selectCliente=id=>{
    setSchedaClienteId(id);
  };
  const analyticsKpi=analysisStats?.kpi||emptyClientiAnalysisStats().kpi;
  const statsMerceologiche=analysisStats?.sections?.merceologiche||[];
  const statsRegioni=analysisStats?.sections?.regioni||[];
  const statsProvince=analysisStats?.sections?.province||[];
  const statsCitta=analysisStats?.sections?.citta||[];
  const statsPagamenti=analysisStats?.sections?.pagamenti||[];
  const statsTipo=analysisStats?.sections?.tipo||[];
  const analysisTotalRecords=Number(analysisStats?.total_records??analyticsKpi.totale??0)||0;

  const exportClientiCsv=async()=>{
    const cols=[
      {label:'ID',value:'id'},
      {label:'Tipo',value:row=>normalizeClienteTipo(row.tipo)},
      {label:'Azienda',value:'azienda'},
      {label:'Nome',value:'nome'},
      {label:'Cognome',value:'cognome'},
      {label:'Email',value:'email'},
      {label:'Telefono',value:'telefono'},
      {label:'Cellulare',value:'cellulare'},
      {label:'Fax',value:'fax'},
      {label:'Referente',value:'referente'},
      {label:'Indirizzo',value:'via'},
      {label:'Citta',value:'citta'},
      {label:'CAP',value:'cap'},
      {label:'Provincia',value:row=>normalizeProvincia(row.provincia)},
      {label:'Regione',value:'regione'},
      {label:'Categoria merceologica',value:row=>clientiNormalizeMerceologia(row.categoria_merceologica)},
      {label:'Dealer',value:'dealer'},
      {label:'Cliente Mimaki',value:'cliente_mimaki'},
      {label:'Cliente Mastertek',value:'cliente_mastertek'},
      {label:'Prodotto Mimaki',value:'prodotto_mimaki'},
      {label:'Numero seriale',value:'numero_seriale'},
      {label:'Fonte',value:'fonte'},
      {label:'P.IVA',value:'piva'},
      {label:'Codice fiscale',value:'cf'},
      {label:'SDI',value:'sdi'},
      {label:'PEC',value:'pec'},
      {label:'Pagamento',value:'pagamento'},
      {label:'Note',value:'note'},
    ];
    try{
      const d=await api('clienti','GET',null,{...baseClientiApiParams,limit:'0',offset:'0',light:'1',enrich:'0'});
      const rows=d.data||[];
      clientiDownloadCsv(`clienti_${today()}.csv`,cols,rows);
      showToast(`CSV clienti esportato con ${rows.length} record`);
    }catch(e){showToast(e.message,'error')}
  };

  const exportAnalisiCsv=()=>{
    const rows=[
      ...statsMerceologiche.map(item=>({ambito:'Categorie merceologiche',label:item.label,valore:item.value})),
      ...statsRegioni.map(item=>({ambito:'Regioni',label:item.label,valore:item.value})),
      ...statsProvince.map(item=>({ambito:'Province',label:item.label,valore:item.value})),
      ...statsCitta.map(item=>({ambito:'Citta',label:item.label,valore:item.value})),
      ...statsPagamenti.map(item=>({ambito:'Pagamenti',label:item.label,valore:item.value})),
      ...statsTipo.map(item=>({ambito:'Tipologia contatto',label:item.label,valore:item.value})),
    ];
    clientiDownloadCsv(`analisi_clienti_${today()}.csv`,[
      {label:'Ambito',value:'ambito'},
      {label:'Voce',value:'label'},
      {label:'Valore',value:'valore'},
    ],rows);
    showToast('CSV analisi clienti esportato');
  };
  const exportAnalisiPdf=async()=>{
    try{
      await exportClientiAnalyticsPdf({
        settings,
        filters:{
          tipo:tipoFilter==='all'?'Tutti':tipoFilter==='rivenditore'?'Rivenditori':tipoFilter==='potenziale'?'Potenziali':'Clienti',
          regione:regFilter,
          provincia:provFilter,
          citta:cittaFilter,
          categoria:catMercFilter,
          pagamento:pagFilter,
          search,
        },
        kpi:analyticsKpi,
        totalRecords:analysisTotalRecords,
        sections:{
          merceologiche:analysisVisibility.merceologiche?statsMerceologiche:[],
          tipo:analysisVisibility.tipo?statsTipo:[],
          regioni:analysisVisibility.regioni?statsRegioni:[],
          province:analysisVisibility.province?statsProvince:[],
          citta:analysisVisibility.citta?statsCitta:[],
          pagamenti:analysisVisibility.pagamenti?statsPagamenti:[],
        },
      });
      showToast('PDF report clienti generato');
    }catch(e){showToast(e.message,'error')}
  };
  const runSeedBackfill=async()=>{
    if(!confirm('Vuoi riallineare il database clienti con il database sorgente interno di Complevia? Verranno aggiornate categorie merceologiche, flag Mimaki/Mastertek e dati mancanti dove disponibili.'))return;
    try{
      const d=await api('clienti_seed_backfill','POST',{});
      const total=d.data?.total_updated||0;
      const geo=d.data?.geo_updated||0;
      const meta=d.data?.metadata_updated||0;
      invalidateClientiCache();
      await reloadClienti();
      showToast(`Riallineamento completato: ${total} aggiornamenti (${meta} metadati, ${geo} geo)`);
    }catch(e){showToast(e.message,'error')}
  };
  const toggleAnalysisBlock=key=>setAnalysisVisibility(prev=>({...prev,[key]:!prev[key]}));
  const toolsActions=[
    {key:'export',label:'Esporta CSV clienti',icon:<IcDl s={16}/>,onClick:()=>{setShowToolsMenu(false);exportClientiCsv();}},
    viewMode==='analisi'?{key:'export-analysis-csv',label:'Esporta analisi CSV',icon:<IcChart s={16}/>,onClick:()=>{setShowToolsMenu(false);exportAnalisiCsv();}}:null,
    viewMode==='analisi'?{key:'export-analysis-pdf',label:'Esporta PDF report',icon:<IcFile s={16}/>,onClick:()=>{setShowToolsMenu(false);exportAnalisiPdf();}}:null,
    isAdmin?{key:'import',label:'Importa Excel',icon:<IcUpload s={16}/>,onClick:()=>{setShowToolsMenu(false);setImportDefaultTipo(tipoFilter==='potenziale'?'potenziale':tipoFilter==='rivenditore'?'rivenditore':'cliente');setShowImport(true);}}:null,
    isAdmin?{key:'backfill',label:'Riallinea database',icon:<IcCogStack s={16}/>,onClick:()=>{setShowToolsMenu(false);runSeedBackfill();}}:null,
    isAdmin?{key:'cleanup',label:'Pulizia database',icon:<IcUsers s={16}/>,onClick:()=>{setShowToolsMenu(false);setShowCleanup(true);}}:null,
    isAdmin?{key:'merge-categories',label:'Unisci categorie',icon:<IcLayers s={16}/>,onClick:()=>{setShowToolsMenu(false);setShowCategoryMerge(true);}}:null,
  ].filter(Boolean);

  const rowClass=c=>schedaClienteId===c.id?' is-selected':'';
  const renderClienteRowPhone=c=>{
    const tipo=normalizeClienteTipo(c.tipo);
    const isPot=tipo==='potenziale';
    const isDealer=tipo==='rivenditore';
    return(
      <div key={c.id} className={`data-row-card${rowClass(c)}`} onClick={()=>selectCliente(c.id)}>
        <div className="data-row-card__head">
          <div className="min-w-0">
            <div className="data-row-card__title">{S(c.azienda||c.nome||'N/D')}</div>
            <div className="data-row-card__meta">{[c.citta,normalizeProvincia(c.provincia)].filter(Boolean).join(' · ')}</div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-semibold shrink-0 ${isPot?'bg-amber-100 text-amber-800':isDealer?'bg-navy-100 text-navy-800':'bg-slate-100 text-slate-700'}`}>{isPot?'Potenziale':isDealer?'Rivenditore':'Cliente'}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {c.contratto_assistenza?<span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Contratto</span>:null}
          {(c.stampanti_count||0)>0?<span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{c.stampanti_count} stamp.</span>:null}
        </div>
        <div className="data-row-card__foot">
          <span className="text-sm font-semibold text-navy-600">{clientiNormalizeMerceologia(c.categoria_merceologica)}</span>
          <Btn s="sm" v="secondary" onClick={e=>{e.stopPropagation();selectCliente(c.id)}}>Apri</Btn>
        </div>
      </div>
    );
  };
  const renderClienteRowCompact=c=>{
    const tipo=normalizeClienteTipo(c.tipo);
    const isPot=tipo==='potenziale';
    const isDealer=tipo==='rivenditore';
    const title=clienteDisplayName(c);
    const meta=[c.citta,normalizeProvincia(c.provincia)].filter(Boolean).join(' · ');
    return(
      <div key={c.id} className={`clienti-list-row clienti-list-row--compact${rowClass(c)}`} onClick={()=>selectCliente(c.id)}>
        <div className="clienti-list-row__main">
          <div className="clienti-list-row__title" title={title}>{title}</div>
          {meta?<div className="clienti-list-row__meta">{meta}</div>:null}
        </div>
        <span className={`clienti-list-row__badge ${isPot?'is-pot':isDealer?'is-dealer':'is-cliente'}`}>{isPot?'Pot.':isDealer?'Riv.':'Cli.'}</span>
      </div>
    );
  };
  const renderClienteRowDesktop=c=>{
    const tipo=normalizeClienteTipo(c.tipo);
    const isPot=tipo==='potenziale';
    const isDealer=tipo==='rivenditore';
    return(
      <div key={c.id} className={`flex items-center gap-4 py-3 px-2 hover:bg-gray-50 rounded-lg group cursor-pointer clienti-list-row${rowClass(c)}`} onClick={()=>selectCliente(c.id)}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isPot?'bg-amber-50':isDealer?'bg-navy-50':'bg-accent-50'}`}>
          <span className={`font-bold text-sm ${isPot?'text-amber-600':isDealer?'text-navy-600':'text-accent-400'}`}>{(c.azienda||c.nome||'?')[0].toUpperCase()}</span></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-navy-600 truncate">{c.azienda||c.nome||'N/D'}</div>
            {isPot?<span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">Potenziale</span>:null}
            {isDealer?<span className="text-xs bg-navy-100 text-navy-700 px-2 py-0.5 rounded-full whitespace-nowrap">Rivenditore</span>:null}
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full whitespace-nowrap">{clientiNormalizeMerceologia(c.categoria_merceologica)}</span>
            {c.contratto_assistenza?<span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Contratto</span>:null}
            {(c.stampanti_count||0)>0?<span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{c.stampanti_count} stamp.</span>:null}
          </div>
          <div className="text-sm text-gray-500 truncate">{[c.nome,c.cognome].filter(Boolean).join(' ')}{c.citta?(' · '+S(c.citta)):''}{c.provincia?(' ('+S(normalizeProvincia(c.provincia)||c.provincia)+')'):''}{c.regione?(' · '+S(c.regione)):''}</div>
        </div>
        <div className="hidden md:flex flex-col items-end gap-0.5 min-w-[180px]">
          {c.email?<div className="text-xs text-gray-600 truncate max-w-[180px]">{S(c.email)}</div>:null}
          {c.telefono?<div className="text-xs text-gray-500">{S(c.telefono)}</div>:null}
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition shrink-0" onClick={e=>e.stopPropagation()}>
          {isPot?<button onClick={()=>promoteCl(c)} title="Promuovi a cliente" className="p-1.5 hover:bg-green-100 rounded text-gray-400 hover:text-green-600"><IcCheck s={16}/></button>:null}
          {!isPot&&!isDealer&&isAdmin?<button onClick={()=>promoteDealer(c)} title="Trasforma in rivenditore" className="p-1.5 hover:bg-cyan-100 rounded text-gray-400 hover:text-cyan-600"><IcUsers s={16}/></button>:null}
          {isAdmin?<button onClick={()=>setTransformCliente(c)} title="Trasforma / crea accesso" className="p-1.5 hover:bg-violet-100 rounded text-gray-400 hover:text-violet-600"><IcCopy s={16}/></button>:null}
          <button onClick={()=>selectCliente(c.id)} title="Apri scheda" className="p-1.5 hover:bg-slate-100 rounded text-gray-400 hover:text-navy-600"><IcFile s={16}/></button>
          <button onClick={()=>{setEditCl(c);setShowForm(true)}} className="p-1.5 hover:bg-blue-100 rounded text-gray-400 hover:text-blue-600"><IcEdit s={16}/></button>
          {isAdmin?<button onClick={()=>deleteCl(c.id)} className="p-1.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-600"><IcTrash s={16}/></button>:null}
        </div>
      </div>
    );
  };

  const elencoListCard=viewMode==='elenco'?<Card>
      <div className="text-sm text-gray-400 mb-3">{clientiLoading?'Caricamento...':`${clientiTotal} contatti`}{hasFilters?' (filtrati)':''}{totalPages>1?` · pagina ${page}/${totalPages}`:''}</div>
      {clientiLoading&&pageList.length===0?<Loading/>:null}
      {!clientiLoading&&pageList.length===0?(
        <Empty
          icon={<IcUsers s={36} c="text-accent-400"/>}
          title={clientiTotal===0&&!hasFilters?'Nessun contatto disponibile':'Nessun contatto trovato'}
          sub={clientiTotal===0&&!hasFilters?'Il caricamento non ha restituito dati o non hai contatti assegnati.':'Prova a cambiare tab oppure ad azzerare i filtri.'}
          action={hasFilters?<Btn v="secondary" onClick={clearFilters}>Azzera filtri</Btn>:null}
        />
      ):isPhone?<div className="data-list-phone">{pageList.map(renderClienteRowPhone)}</div>:<div className={masterDetail&&viewMode==='elenco'?'clienti-list-compact':'divide-y'}>{pageList.map(masterDetail&&viewMode==='elenco'?renderClienteRowCompact:renderClienteRowDesktop)}</div>}
      {totalPages>1?<div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t">
        <Btn s="sm" v="secondary" onClick={()=>setPage(1)} disabled={page===1}>«</Btn>
        <Btn s="sm" v="secondary" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>‹</Btn>
        <span className="text-sm text-gray-500 px-2">{'Pagina '+page+' di '+totalPages}</span>
        <Btn s="sm" v="secondary" onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>›</Btn>
        <Btn s="sm" v="secondary" onClick={()=>setPage(totalPages)} disabled={page===totalPages}>»</Btn>
      </div>:null}
    </Card>:null;

  return(<>
    {isPhone&&schedaClienteId?<ClienteSchedaPanel clienteId={schedaClienteId} onBack={closeSchedaCliente} onEditAnagrafica={cl=>{setEditCl(cl);setShowForm(true)}}/>:
    <Page title="Clienti" subtitle="Anagrafiche, potenziali e rivenditori">
    <PageToolbar className="flex-col items-stretch gap-3">
      <div className="segmented-wrap w-full">
        {[{key:'elenco',label:'Elenco clienti',icon:<IcUsers s={16}/>},{key:'analisi',label:'Analisi clienti',icon:<IcChart s={16}/> }].map(item=><SegmentedNavButton key={item.key} active={viewMode===item.key} onClick={()=>setViewMode(item.key)} icon={item.icon} label={item.label}/>)}
      </div>
      <div className="tipo-tabs-scroll no-scrollbar">
        {[{k:'cliente',l:'Clienti',n:countClienti},{k:'rivenditore',l:'Rivenditori',n:countRivenditori},{k:'potenziale',l:'Potenziali',n:countPotenziali},{k:'all',l:'Tutti',n:countTutti}].map(t=>(
          <button key={t.k} onClick={()=>setTipoFilter(t.k)}
            className={`text-sm font-medium border-b-2 transition ${tipoFilter===t.k?'border-accent-400 text-accent-400':'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.l}<span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{countsLoading?'…':t.n}</span>
          </button>
        ))}
      </div>
    </PageToolbar>

    <div className="flex flex-col md:flex-row gap-3 justify-between">
      <div className="relative flex-1 max-w-md"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca per nome, citta, provincia, regione, email o categoria..."
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/></div>
      <div className="flex gap-2 flex-wrap">
        <div className="relative">
          <Btn v="secondary" icon={<IcSettings s={16}/>} onClick={()=>setShowToolsMenu(v=>!v)}>Impostazioni database</Btn>
          {showToolsMenu?<div className="absolute right-0 mt-2 z-20 w-[280px] rounded-3xl border border-slate-200 bg-white shadow-[0_20px_45px_rgba(15,23,42,.14)] p-2">
            {toolsActions.map(action=><button key={action.key} onClick={action.onClick} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-slate-700 hover:bg-slate-50 text-left">
              <span className="w-9 h-9 rounded-full bg-slate-100 inline-flex items-center justify-center text-slate-500 shrink-0">{action.icon}</span>
              <span>{action.label}</span>
            </button>)}
          </div>:null}
        </div>
        <Btn v="secondary" icon={<IcPlus s={16}/>} onClick={()=>{setEditCl(null);setFormDefaultTipo('potenziale');setShowForm(true)}}>Nuovo Potenziale</Btn>
        <Btn v="secondary" icon={<IcPlus s={16}/>} onClick={()=>{setEditCl(null);setFormDefaultTipo('rivenditore');setShowForm(true)}}>Nuovo Rivenditore</Btn>
        <Btn icon={<IcPlus s={16}/>} onClick={()=>{setEditCl(null);setFormDefaultTipo('cliente');setShowForm(true)}}>Nuovo Cliente</Btn>
      </div>
    </div>

    <PageToolbar className="flex-wrap">
    {/* Filtri avanzati */}
    <div className="flex flex-wrap gap-3 items-center w-full">
      <Sel value={regFilter} onChange={setRegFilter} options={regioni.map(r=>({value:r,label:r}))} placeholder="Regione" className="min-w-[140px]"/>
      <Sel value={provFilter} onChange={setProvFilter} options={province.map(p=>({value:p,label:p}))} placeholder="Provincia" className="min-w-[130px]"/>
      <Sel value={cittaFilter} onChange={setCittaFilter} options={citta.map(c=>({value:c,label:c}))} placeholder="Città" className="min-w-[150px]"/>
      <Sel value={catMercFilter} onChange={setCatMercFilter} options={categorieMerceologiche.map(item=>({value:item,label:item}))} placeholder="Categoria merceologica" className="min-w-[200px]"/>
      <Sel value={pagFilter} onChange={setPagFilter} options={pagamenti.map(p=>({value:p,label:p}))} placeholder="Pagamento" className="min-w-[160px]"/>
      {hasFilters?<button onClick={clearFilters} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"><IcX s={14}/>Azzera filtri</button>:null}
    </div>
    </PageToolbar>
    <Card title="Ricerca per tipologia stampante / potenziale">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <Sel label="Tipologia stampante installata" value={stampanteTipFilter} onChange={setStampanteTipFilter} options={STAMPANTE_TIPOLOGIE_OPTIONS} placeholder="Tutte"/>
        <Inp label="Modello stampante installata" value={stampanteModelFilter} onChange={setStampanteModelFilter} placeholder="es. SC-S80600"/>
        <Inp label="Potenziale per modello" value={potenzialeModelFilter} onChange={setPotenzialeModelFilter} placeholder="Target commerciale"/>
        <Sel label="Contratto assistenza" value={contrattoFilter} onChange={setContrattoFilter} options={[{value:'',label:'Tutti'},{value:'attivo',label:'Con contratto'},{value:'scadenza',label:'In scadenza (30 gg)'}]} placeholder="Contratto"/>
        <Inp label="Fatturato minimo (€)" type="number" value={fatturatoMinFilter} onChange={setFatturatoMinFilter}/>
      </div>
      <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">
        <Inp label="Cerca clienti potenziali per nuovo modello" value={potenzialiSearchQ} onChange={setPotenzialiSearchQ} placeholder="Nome modello in uscita" className="flex-1 min-w-[220px]"/>
        <div className="flex items-end"><Btn v="secondary" onClick={runPotenzialiSearch}>Trova potenziali</Btn></div>
      </div>
    </Card>

    <div className={`clienti-master-detail${useMasterDetailLayout?'':' clienti-master-detail--stack'}${schedaClienteId&&useMasterDetailLayout?' has-selection':''}`}>
      <div className="clienti-master-detail__list">
        {elencoListCard}
        {viewMode==='analisi'?(analysisStatsLoading&&!analysisTotalRecords?<Card><Loading/></Card>:<div className="clienti-analisi-panel space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_.8fr] gap-6">
        <Card title="Lettura della base clienti" action={<span className="text-xs text-slate-400">{analysisTotalRecords} record nella vista attiva</span>}>
          <div className="stat-tile-grid">
        {[
          {label:'Totale contatti',value:analyticsKpi.totale,sub:'Dopo i filtri',bg:'bg-slate-50'},
          {label:'Clienti',value:analyticsKpi.clientiCount,sub:'Anagrafiche cliente',bg:'bg-blue-50'},
          {label:'Rivenditori',value:analyticsKpi.rivenditoriCount,sub:'Partner attivi',bg:'bg-indigo-50'},
          {label:'Potenziali',value:analyticsKpi.potenzialiCount,sub:'Lead presenti',bg:'bg-amber-50'},
          {label:'Categorie',value:analyticsKpi.categorieCount,sub:'Settori merceologici',bg:'bg-violet-50'},
          {label:'Regioni',value:analyticsKpi.regioniCount,sub:'Copertura territoriale',bg:'bg-emerald-50'},
        ].map((item,idx)=><div key={idx} className={`stat-tile ${item.bg}`}>
          <div className="text-2xl font-bold text-navy-600">{item.value}</div>
          <div className="text-xs uppercase tracking-[0.16em] text-slate-400 mt-2">{item.label}</div>
          <div className="text-xs text-slate-500 mt-1">{item.sub}</div>
        </div>)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Categoria dominante</div>
              <div className="font-semibold text-navy-600">{statsMerceologiche[0]?.label||'Nessun dato'}</div>
              <div className="text-sm text-slate-500 mt-2">{statsMerceologiche[0]?`${statsMerceologiche[0].value} contatti segmentati`:'Completa o riallinea le categorie per avere una lettura utile'}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Regione principale</div>
              <div className="font-semibold text-navy-600">{statsRegioni[0]?.label||'Nessun dato'}</div>
              <div className="text-sm text-slate-500 mt-2">{statsRegioni[0]?`${statsRegioni[0].value} anagrafiche`:'Nessuna concentrazione geografica disponibile'}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Indicazione operativa</div>
              <div className="font-semibold text-navy-600">{statsMerceologiche.length>1?'Segmenta per settore':'Rafforza la classificazione'}</div>
              <div className="text-sm text-slate-500 mt-2">{statsMerceologiche.length>1?'Puoi costruire campagne, assegnazioni e report per settore con maggiore precisione.':'Prima di fare analisi commerciali conviene completare le categorie mancanti.'}</div>
            </div>
          </div>
        </Card>
        <Card title="Blocchi del report">
          <div className="grid grid-cols-2 gap-2">
            {[
              {key:'merceologiche',label:'Categorie'},
              {key:'tipo',label:'Tipologia'},
              {key:'regioni',label:'Regioni'},
              {key:'province',label:'Province'},
              {key:'citta',label:'Città'},
              {key:'pagamenti',label:'Pagamenti'},
            ].map(item=><button key={item.key} onClick={()=>toggleAnalysisBlock(item.key)}
              className={`px-4 py-3 rounded-2xl text-sm font-medium border ${analysisVisibility[item.key]?'bg-navy-600 text-white border-navy-600':'bg-white text-slate-500 border-slate-200'}`}>{item.label}</button>)}
          </div>
          <div className="text-xs text-slate-400 mt-3">Attiva solo i blocchi che vuoi mostrare nel report PDF o durante le riunioni.</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {analysisVisibility.merceologiche?<Card title="Distribuzione categoria merceologica">
          <AssistenzaBarList items={statsMerceologiche} empty="Nessuna categoria merceologica disponibile." colorClass="bg-indigo-500"/>
        </Card>:null}
        {analysisVisibility.tipo?<Card title="Distribuzione tipologia contatto">
          <AssistenzaBarList items={statsTipo} empty="Nessuna tipologia disponibile." colorClass="bg-cyan-500"/>
        </Card>:null}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {analysisVisibility.regioni?<Card title="Regioni più presenti">
          <AssistenzaBarList items={statsRegioni} empty="Nessuna regione registrata." colorClass="bg-emerald-500"/>
        </Card>:null}
        {analysisVisibility.province?<Card title="Province più presenti">
          <AssistenzaBarList items={statsProvince} empty="Nessuna provincia registrata." colorClass="bg-sky-500"/>
        </Card>:null}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {analysisVisibility.citta?<Card title="Citta più presenti">
          <AssistenzaBarList items={statsCitta} empty="Nessuna citta registrata." colorClass="bg-amber-400"/>
        </Card>:null}
        {analysisVisibility.pagamenti?<Card title="Metodi di pagamento">
          <AssistenzaBarList items={statsPagamenti} empty="Nessun dato pagamento disponibile." colorClass="bg-rose-400"/>
        </Card>:null}
      </div>

    </div>):null}
      </div>
      {useMasterDetailLayout?<div className="clienti-master-detail__detail">
        {schedaClienteId?<ClienteSchedaPanel embedded clienteId={schedaClienteId} onBack={closeSchedaCliente} onEditAnagrafica={cl=>{setEditCl(cl);setShowForm(true)}}/>:
        <div className="clienti-detail-placeholder">
          <Empty icon={<IcUsers s={32} c="text-accent-400"/>} title="Seleziona un contatto" sub="Scegli un cliente dall'elenco per aprire la scheda completa."/>
        </div>}
      </div>:null}
    </div>
  </Page>}
    {showForm&&<ClienteForm cliente={editCl} defaultTipo={formDefaultTipo} onClose={()=>setShowForm(false)} onSaved={()=>{invalidateClientiCache();setShowForm(false);reloadClienti();if(schedaClienteId&&editCl?.id)setSchedaClienteId(editCl.id)}}/>}
    {showPotenzialiSearch?<Modal title={`Potenziali per «${S(potenzialiSearchQ)}»`} onClose={()=>setShowPotenzialiSearch(false)} wide>
      {potenzialiSearchRes.length===0?<Empty title="Nessun cliente" sub="Nessuna potenzialità registrata per questo modello."/>:
      <div className="divide-y max-h-[60vh] overflow-y-auto">{potenzialiSearchRes.map(row=><div key={row.id} className="py-3 flex flex-wrap justify-between gap-2 cursor-pointer hover:bg-slate-50 px-2 rounded-lg" onClick={()=>{setShowPotenzialiSearch(false);setSchedaClienteId(row.cliente_id)}}>
        <div><div className="font-semibold text-navy-600">{S(row.cliente_azienda||row.cliente_nome)}</div><div className="text-sm text-slate-500">{S(row.modello)} · {stampanteTipologiaLabel(row.tipologia)} · priorità {row.priorita}</div></div>
        <Btn s="sm">Apri scheda</Btn>
      </div>)}</div>}
    </Modal>:null}
    {showImport&&<ImportModal type="clienti" defaultTipo={importDefaultTipo} onClose={()=>setShowImport(false)} onDone={()=>{invalidateClientiCache();setShowImport(false);reloadClienti()}}/>}
    {transformCliente?<ClienteTransformModal cliente={transformCliente} onClose={()=>setTransformCliente(null)} onSaved={()=>{invalidateClientiCache();setTransformCliente(null);reloadClienti()}}/>:null}
    {showCleanup?<ClientiCleanupModal onClose={()=>setShowCleanup(false)} onDone={()=>{invalidateClientiCache();setShowCleanup(false);reloadClienti()}}/>:null}
    {showCategoryMerge?<ClientCategoryMergeModal onClose={()=>setShowCategoryMerge(false)} onSaved={()=>{invalidateClientiCache();setShowCategoryMerge(false);reloadClienti();}}/>:null}
  </>);
}

const SPESA_CATEGORY_OPTIONS=['Carburante','Pedaggi','Parcheggi','Pranzi e cene','Hotel','Taxi e mezzi','Materiale demo','Campionature','Fiera / evento','Regali cliente','Rappresentanza','Trasferta','Altro'].map(value=>({value,label:value}));
const SPESA_PAYMENT_OPTIONS=[
  {value:'contanti',label:'Contanti'},
  {value:'carta_personale',label:'Carta personale'},
  {value:'carta_aziendale',label:'Carta aziendale'},
  {value:'bonifico',label:'Bonifico'},
  {value:'paypal',label:'PayPal'},
  {value:'satispay',label:'Satispay'},
  {value:'altro',label:'Altro'},
];
const SPESA_STATUS_OPTIONS=[
  {value:'da_rimborsare',label:'Da rimborsare'},
  {value:'rimborsata',label:'Rimborsata'},
  {value:'annullata',label:'Annullata'},
];
const spesaStatusLabel=value=>SPESA_STATUS_OPTIONS.find(item=>item.value===value)?.label||'Da rimborsare';
const spesaStatusClass=value=>value==='rimborsata'?'bg-emerald-100 text-emerald-700':value==='annullata'?'bg-slate-100 text-slate-600':'bg-amber-100 text-amber-700';
const expenseMatchKey=row=>{
  const actor=String(row?.user_id||'');
  const date=String(row?.data_spesa||'').trim();
  const amount=(Math.round((parseFloat(row?.importo)||0)*100)/100).toFixed(2);
  const merchant=String(row?.esercente||row?.descrizione||'').trim().toLowerCase().replace(/\s+/g,' ');
  return [actor,date,amount,merchant].join('|');
};
const monthStartDate=()=>{
  const d=new Date();
  return`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
};

function SpesaModal({row,clienti,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const def={data_spesa:today(),categoria:'',descrizione:'',esercente:'',importo:'',valuta:'EUR',metodo_pagamento:'',stato_rimborso:'da_rimborsare',cliente_id:'',km:'',note:'',scontrino_file:'',scontrino_original_name:'',pagamento_file:'',pagamento_original_name:''};
  const[f,setF]=useState(row?{...def,...row,cliente_id:row.cliente_id?String(row.cliente_id):'',importo:toNum(row.importo||0),km:toNum(row.km||0)}:def);
  const[uploading,setUploading]=useState('');
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const uploadAttachment=async(kind,file)=>{
    if(!file)return;
    setUploading(kind);
    try{
      const reader=new FileReader();
      const b64=await new Promise((res,rej)=>{reader.onload=()=>res(String(reader.result||'').split(',')[1]||'');reader.onerror=rej;reader.readAsDataURL(file)});
      const data=await api('spesa_file_upload','POST',{file_data:b64,file_name:file.name});
      u(`${kind}_file`,data.filename||'');
      u(`${kind}_original_name`,data.original_name||file.name||'');
      showToast(kind==='scontrino'?'Scontrino caricato':'Ricevuta pagamento caricata');
    }catch(e){showToast(e.message,'error')}
    finally{setUploading('')}
  };
  const save=async()=>{
    if(!String(f.descrizione||'').trim()){showToast('Descrizione obbligatoria','error');return}
    if(!(parseFloat(f.importo)||0)){showToast('Importo obbligatorio','error');return}
    const payload={
      data_spesa:f.data_spesa||today(),
      categoria:f.categoria||'',
      descrizione:(f.descrizione||'').trim(),
      esercente:(f.esercente||'').trim(),
      importo:parseFloat(f.importo)||0,
      valuta:f.valuta||'EUR',
      metodo_pagamento:f.metodo_pagamento||'',
      stato_rimborso:f.stato_rimborso||'da_rimborsare',
      cliente_id:parseInt(f.cliente_id)||null,
      km:parseFloat(f.km)||0,
      note:(f.note||'').trim(),
      scontrino_file:f.scontrino_file||'',
      scontrino_original_name:f.scontrino_original_name||'',
      pagamento_file:f.pagamento_file||'',
      pagamento_original_name:f.pagamento_original_name||'',
    };
    try{
      if(row?.id)await api('spesa_update','PUT',payload,{id:row.id});
      else await api('spesa_create','POST',payload);
      showToast(row?.id?'Spesa aggiornata':'Spesa salvata');
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };
  return(<Modal title={row?.id?'Modifica spesa':'Nuova spesa'} onClose={onClose} wide>
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Inp label="Data spesa" type="date" value={f.data_spesa||''} onChange={v=>u('data_spesa',v)}/>
        <Sel label="Categoria" value={f.categoria||''} onChange={v=>u('categoria',v)} options={SPESA_CATEGORY_OPTIONS} placeholder="Seleziona categoria"/>
        <Inp label="Importo" type="number" value={f.importo} onChange={v=>u('importo',v)}/>
        <Inp label="Esercente / fornitore" value={f.esercente||''} onChange={v=>u('esercente',v)}/>
        <Inp label="Descrizione" value={f.descrizione||''} onChange={v=>u('descrizione',v)}/>
        <Sel label="Metodo pagamento" value={f.metodo_pagamento||''} onChange={v=>u('metodo_pagamento',v)} options={SPESA_PAYMENT_OPTIONS} placeholder="Seleziona metodo"/>
        <Sel label="Stato rimborso" value={f.stato_rimborso||'da_rimborsare'} onChange={v=>u('stato_rimborso',v)} options={SPESA_STATUS_OPTIONS}/>
        <Sel label="Cliente collegato" value={f.cliente_id||''} onChange={v=>u('cliente_id',v)} options={(clienti||[]).map(c=>({value:String(c.id),label:clienteDisplayName(c)}))} placeholder="Nessuno"/>
        <Inp label="KM" type="number" value={f.km} onChange={v=>u('km',v)}/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Note</label>
        <textarea value={f.note||''} onChange={e=>u('note',e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[{key:'scontrino',label:'Scontrino'},{key:'pagamento',label:'Ricevuta pagamento'}].map(item=><Card key={item.key} title={item.label}>
          <div className="space-y-3">
            {f[`${item.key}_file`]?<div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2">
              <IcClip s={15} c="text-emerald-600"/>
              <span className="text-sm text-emerald-700 flex-1 truncate">{S(f[`${item.key}_original_name`]||f[`${item.key}_file`])}</span>
              <Btn s="sm" v="secondary" onClick={()=>downloadStoredFile(f[`${item.key}_file`],f[`${item.key}_original_name`]||'allegato')}>Scarica</Btn>
              <Btn s="sm" v="danger" onClick={()=>{u(`${item.key}_file`,'');u(`${item.key}_original_name`,'')}}>Rimuovi</Btn>
            </div>:<div className="text-sm text-gray-400">Nessun allegato caricato.</div>}
            <label className="inline-flex">
              <input type="file" accept="image/*,.pdf" capture="environment" className="hidden" onChange={e=>uploadAttachment(item.key,e.target.files?.[0])}/>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium text-slate-700 hover:border-accent-300 cursor-pointer">{uploading===item.key?'Caricamento...':'Carica foto o PDF'}</span>
            </label>
          </div>
        </Card>)}
      </div>
      <div className="flex justify-end gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva</Btn>
      </div>
    </div>
  </Modal>);
}

function SpesePage(){
  const{user,isAdmin,isManager}=useContext(AuthCtx);
  const{showToast}=useContext(DataCtx);
  const{isPhone}=useDevice();
  const[rows,setRows]=useState([]);
  const[clienti,setClienti]=useState([]);
  const[users,setUsers]=useState([]);
  const[from,setFrom]=useState(monthStartDate());
  const[to,setTo]=useState(today());
  const[userFilter,setUserFilter]=useState('');
  const[statusFilter,setStatusFilter]=useState('');
  const[search,setSearch]=useState('');
  const[loading,setLoading]=useState(true);
  const[showModal,setShowModal]=useState(false);
  const[editRow,setEditRow]=useState(null);

  const refresh=useCallback(async()=>{
    setLoading(true);
    try{
      const requests=[
        api('spese','GET',null,{date_from:from,date_to:to,user_id:userFilter||'',stato_rimborso:statusFilter||''}),
        api('spese_clienti'),
        (isAdmin||isManager)?api('spese_users'):Promise.resolve({data:[]}),
      ];
      const[speseRes,clientiRes,usersRes]=await Promise.all(requests);
      setRows(speseRes.data||[]);
      setClienti(clientiRes.data||[]);
      setUsers(usersRes.data||[]);
    }catch(e){showToast(e.message,'error')}
    finally{setLoading(false)}
  },[from,to,userFilter,statusFilter,isAdmin,isManager,showToast]);
  useEffect(()=>{refresh()},[refresh]);

  const duplicateKeys=useMemo(()=>{
    const counts={};
    rows.forEach(row=>{const key=expenseMatchKey(row);counts[key]=(counts[key]||0)+1;});
    return counts;
  },[rows]);
  const filteredRows=useMemo(()=>{
    const needle=String(search||'').trim().toLowerCase();
    if(!needle)return rows;
    return rows.filter(row=>[
      row.descrizione,row.esercente,row.categoria,row.note,row.user_nome,row.user_username,row.cliente_azienda
    ].filter(Boolean).some(value=>String(value).toLowerCase().includes(needle)));
  },[rows,search]);
  const stats=useMemo(()=>{
    const total=filteredRows.reduce((sum,row)=>sum+toNum(row.importo),0);
    const pending=filteredRows.filter(row=>row.stato_rimborso==='da_rimborsare').reduce((sum,row)=>sum+toNum(row.importo),0);
    const duplicates=filteredRows.filter(row=>duplicateKeys[expenseMatchKey(row)]>1).length;
    return{total,pending,duplicates,count:filteredRows.length};
  },[filteredRows,duplicateKeys]);
  const exportRows=useMemo(()=>filteredRows.map(row=>({
    Data:fmtDate(row.data_spesa),
    Utente:S(row.user_nome||row.user_username||''),
    Categoria:S(row.categoria||''),
    Descrizione:S(row.descrizione||''),
    Esercente:S(row.esercente||''),
    Cliente:S(row.cliente_azienda||''),
    Importo:toNum(row.importo),
    Valuta:S(row.valuta||'EUR'),
    MetodoPagamento:S(row.metodo_pagamento||''),
    StatoRimborso:spesaStatusLabel(row.stato_rimborso),
    KM:toNum(row.km),
    Scontrino:row.scontrino_file?'SI':'NO',
    RicevutaPagamento:row.pagamento_file?'SI':'NO',
    Note:S(row.note||''),
  })),[filteredRows]);
  const exportCsv=()=>{
    const headers=Object.keys(exportRows[0]||{});
    const lines=[headers.join(';'),...exportRows.map(row=>headers.map(key=>{
      const value=String(row[key]??'').replace(/"/g,'""');
      return /[;"\n]/.test(value)?`"${value}"`:value;
    }).join(';'))];
    downloadBlob(new Blob([lines.join('\n')],{type:'text/csv;charset=utf-8;'}),`nota_spese_${from}_${to}.csv`);
  };
  const exportXls=async()=>{
    await ensureXlsx();
    if(!window.XLSX){showToast('Libreria Excel non disponibile','error');return}
    const wb=window.XLSX.utils.book_new();
    const ws=window.XLSX.utils.json_to_sheet(exportRows);
    window.XLSX.utils.book_append_sheet(wb,ws,'Nota spese');
    window.XLSX.writeFile(wb,`nota_spese_${from}_${to}.xlsx`);
  };
  const removeExpense=async(id)=>{
    if(!confirm('Eliminare questa spesa?'))return;
    try{await api('spesa_delete','DELETE',null,{id});showToast('Spesa eliminata');refresh();}catch(e){showToast(e.message,'error')}
  };

  if(loading)return<Loading/>;

  return(<Page title="Nota spese" subtitle="Scontrini, rimborsi ed export per periodo" actions={
    <div className="flex gap-2 flex-wrap">
      {!isPhone?<Btn v="secondary" icon={<IcDl s={16}/>} onClick={exportCsv} disabled={!exportRows.length}>CSV</Btn>:null}
      {!isPhone?<Btn v="secondary" icon={<IcDl s={16}/>} onClick={exportXls} disabled={!exportRows.length}>Excel</Btn>:null}
      <Btn s={isPhone?'sm':'md'} icon={<IcPlus s={16}/>} onClick={()=>{setEditRow(null);setShowModal(true)}}>Nuova spesa</Btn>
    </div>
  }>
    <div className="stat-tile-grid">
      {[{label:'Spese nel periodo',value:stats.count,bg:'bg-slate-50'},{label:'Totale',value:fmt(stats.total),bg:'bg-blue-50'},{label:'Da rimborsare',value:fmt(stats.pending),bg:'bg-amber-50'},{label:'Possibili doppi',value:stats.duplicates,bg:'bg-rose-50'}].map((item,idx)=><div key={idx} className={`stat-tile ${item.bg}`}>
        <div className="text-xs text-gray-500">{item.label}</div>
        <div className="text-2xl font-bold text-navy-600">{item.value}</div>
      </div>)}
    </div>

    <PageToolbar>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 w-full">
        <Inp label="Da" type="date" value={from} onChange={setFrom}/>
        <Inp label="A" type="date" value={to} onChange={setTo}/>
        {(isAdmin||isManager)?<Sel label="Utente" value={userFilter} onChange={setUserFilter} options={users.map(item=>({value:String(item.id),label:S(item.nome||item.username)}))} placeholder="Tutti"/>:null}
        <Sel label="Stato rimborso" value={statusFilter} onChange={setStatusFilter} options={SPESA_STATUS_OPTIONS} placeholder="Tutti"/>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-600 mb-1">Ricerca</label>
          <div className="relative"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Descrizione, esercente, cliente..." className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/>
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-500 w-full">Flusso: inserisci spesa, allega scontrino/ricevuta, verifica doppi, esporta periodo.</div>
    </PageToolbar>

    {filteredRows.length===0?<Empty icon={<IcCard s={34} c="text-accent-400"/>} title="Nessuna spesa trovata" sub="Prova a cambiare il periodo oppure inserisci una nuova nota spesa."/>:
    <Card title="Elenco spese" action={<span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600">{filteredRows.length} righe</span>}>
      {isPhone?<div className="data-list-phone">{filteredRows.map(row=>{
        const possibleDuplicate=duplicateKeys[expenseMatchKey(row)]>1;
        return(<div key={row.id} className="data-row-card">
          <div className="data-row-card__head">
            <div className="min-w-0">
              <div className="data-row-card__title">{S(row.descrizione||'Spesa')}</div>
              <div className="data-row-card__meta">{fmtDate(row.data_spesa)} · {fmt(row.importo)}</div>
            </div>
            <span className={`text-[10px] px-2 py-1 rounded-full shrink-0 ${spesaStatusClass(row.stato_rimborso)}`}>{spesaStatusLabel(row.stato_rimborso)}</span>
          </div>
          <div className="text-xs text-slate-500">{[row.categoria,row.esercente,row.cliente_azienda].filter(Boolean).join(' · ')}</div>
          {possibleDuplicate?<span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 inline-block mt-1">Possibile doppio</span>:null}
          <div className="flex gap-2 mt-3 pt-2 border-t"><Btn s="sm" v="secondary" onClick={()=>{setEditRow(row);setShowModal(true)}}>Apri</Btn><Btn s="sm" v="danger" onClick={()=>removeExpense(row.id)}>Elimina</Btn></div>
        </div>)})}</div>:
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px] text-sm">
          <thead><tr className="bg-navy-600 text-white">
            <th className="px-3 py-2.5 text-left">Data</th>
            {(isAdmin||isManager)?<th className="px-3 py-2.5 text-left">Utente</th>:null}
            <th className="px-3 py-2.5 text-left">Spesa</th>
            <th className="px-3 py-2.5 text-left">Cliente</th>
            <th className="px-3 py-2.5 text-right">Importo</th>
            <th className="px-3 py-2.5 text-left">Pagamento</th>
            <th className="px-3 py-2.5 text-left">Allegati</th>
            <th className="px-3 py-2.5 text-right"></th>
          </tr></thead>
          <tbody>{filteredRows.map((row,i)=>{
            const possibleDuplicate=duplicateKeys[expenseMatchKey(row)]>1;
            return(<tr key={row.id} className={`border-b ${i%2?'bg-gray-50/40':''}`}>
              <td className="px-3 py-2 align-top">{fmtDate(row.data_spesa)}</td>
              {(isAdmin||isManager)?<td className="px-3 py-2 align-top">{S(row.user_nome||row.user_username||'')}</td>:null}
              <td className="px-3 py-2 align-top">
                <div className="font-medium text-navy-600">{S(row.descrizione||'Spesa')}</div>
                <div className="text-xs text-gray-500 mt-1">{[row.categoria,row.esercente,row.km?`${toNum(row.km)} km`:'' ].filter(Boolean).join(' · ')}</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className={`text-[11px] px-2 py-1 rounded-full ${spesaStatusClass(row.stato_rimborso)}`}>{spesaStatusLabel(row.stato_rimborso)}</span>
                  {possibleDuplicate?<span className="text-[11px] px-2 py-1 rounded-full bg-rose-100 text-rose-700">Possibile doppio</span>:null}
                </div>
                {row.note?<div className="text-xs text-gray-400 mt-2 line-clamp-2">{S(row.note)}</div>:null}
              </td>
              <td className="px-3 py-2 align-top">{row.cliente_azienda?<span className="text-navy-600 font-medium">{S(row.cliente_azienda)}</span>:<span className="text-gray-400">Non collegato</span>}</td>
              <td className="px-3 py-2 align-top text-right font-semibold text-navy-600">{fmt(row.importo)}</td>
              <td className="px-3 py-2 align-top">
                <div>{S(row.metodo_pagamento||'-')}</div>
                <div className="text-xs text-gray-400">{S(row.valuta||'EUR')}</div>
              </td>
              <td className="px-3 py-2 align-top">
                <div className="flex flex-col gap-2">
                  {row.scontrino_file?<Btn s="sm" v="secondary" icon={<IcClip s={14}/>} onClick={()=>downloadStoredFile(row.scontrino_file,row.scontrino_original_name||'scontrino')}>Scontrino</Btn>:null}
                  {row.pagamento_file?<Btn s="sm" v="secondary" icon={<IcClip s={14}/>} onClick={()=>downloadStoredFile(row.pagamento_file,row.pagamento_original_name||'ricevuta-pagamento')}>Pagamento</Btn>:null}
                  {!row.scontrino_file&&!row.pagamento_file?<span className="text-xs text-gray-400">Nessun allegato</span>:null}
                </div>
              </td>
              <td className="px-3 py-2 align-top">
                <div className="flex gap-2 justify-end">
                  <Btn s="sm" v="secondary" onClick={()=>{setEditRow(row);setShowModal(true)}}>Apri</Btn>
                  <Btn s="sm" v="danger" onClick={()=>removeExpense(row.id)}>Elimina</Btn>
                </div>
              </td>
            </tr>);
          })}</tbody>
        </table>
      </div>}
    </Card>}

    {showModal?<SpesaModal row={editRow} clienti={clienti} onClose={()=>setShowModal(false)} onSaved={()=>{setShowModal(false);refresh();}}/>:null}
  </Page>);
}

const STIMA_CONSUMI_DEFAULTS={titolo:'Stima consumi annuale',macchina_modello:'',cliente_id:'',grammi_m2:12,velocita_m2_h:18,larghezza_supporto_m:'',velocita_metri_minuto:'',utilizzo_percent:70,giorni_lavorativi_anno:220,ore_giorno:8,articolo_id:'',costo_inchiostro_unitario:'',prezzo_vendita_unitario:'',litri_per_kg:1,sconto_inchiostro_percent:0,note:''};
const STIMA_CONSUMI_ROI_DEFAULTS={investimento_macchina:'',costo_supporti_mq:'',costo_manodopera_ora:'',setup_minuti_per_lavoro:'5',scarto_percent:'0',ripetizioni_mese:'20'};
const newConsumiLavoroRow=()=>({id:`l${Date.now()}_${Math.random().toString(36).slice(2,6)}`,descrizione:'',tipo:'pezzo',quantita:'1',larghezza_m:'',altezza_m:'',metri_lineari:'',prezzo_vendita_m2:'',note:''});
const fmtConsumiNum=(v,d=2)=>{const n=Number(v);return Number.isFinite(n)?n.toLocaleString('it-IT',{minimumFractionDigits:0,maximumFractionDigits:d}):'-'};
const fmtConsumiEuro=v=>`€ ${fmtConsumiNum(v,2)}`;
const fmtConsumiPct=v=>{const n=Number(v);return Number.isFinite(n)?`${(n*100).toLocaleString('it-IT',{maximumFractionDigits:1})}%`:'-'};
const consumiArticleFromListino=a=>{
  const costo=toNum(a?.costo_netto);
  const pr=calcPrezzi(a||{});
  const vendita=toNum(a?.prezzo_listino)||pr.prezzoConsigliato||0;
  const margineUnit=vendita-costo;
  return{
    id:a.id,sku:a.sku||'',nome:a.nome||'',categoria:a.categoria||'',listino_tipo:a.listino_tipo||'',um:a.um||'kg',
    costo_unitario:costo,prezzo_vendita_unitario:vendita,margine_unitario:margineUnit,
    margine_percent:vendita>0?margineUnit/vendita:0,prezzo_listino:vendita,
    is_inchiostro_hint:/inchiostro|ink|colore|color/i.test(`${a.nome||''} ${a.famiglia||''} ${a.categoria||''}`),
  };
};

function ConsumiReportTile({label,value,sub,highlight}){
  return(<div className={`rounded-xl border p-3 ${highlight?'border-accent-300 bg-accent-50':'border-gray-200 bg-white'}`}>
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-lg font-bold ${highlight?'text-accent-600':'text-navy-600'}`}>{value}</div>
    {sub?<div className="text-xs text-gray-400">{sub}</div>:null}
  </div>);
}

function ConsumiConfrontoCapacita({confronto,reportAnnuale}){
  if(!confronto&&!reportAnnuale)return null;
  return(<Card title="Confronto capacità macchina">
    {confronto?.messaggio_sintesi?<p className="text-sm text-navy-700 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 mb-4">{S(confronto.messaggio_sintesi)}</p>:null}
    <div className="stat-tile-grid">
      {reportAnnuale?<ConsumiReportTile label="m² annui macchina" value={fmtConsumiNum(reportAnnuale.mq_annui,0)} sub={`${fmtConsumiNum(reportAnnuale.ore_stampa_effettive,0)} h stampa`}/>:null}
      {confronto?.mq_annui_scenario!=null?<ConsumiReportTile label="m² scenario annualizzato" value={fmtConsumiNum(confronto.mq_annui_scenario,0)} sub={confronto.pct_capacita_mq!=null?`${fmtConsumiNum(confronto.pct_capacita_mq,0)}% capacità`:''}/>:null}
      {confronto?.margine_m2_lavori!=null?<ConsumiReportTile label="Margine lavori / m²" value={fmtConsumiEuro(confronto.margine_m2_lavori)}/>:null}
      {confronto?.margine_m2_inchiostro_indicativo!=null?<ConsumiReportTile label="Margine inchiostro / m²" value={fmtConsumiEuro(confronto.margine_m2_inchiostro_indicativo)}/>:null}
      {reportAnnuale?<ConsumiReportTile label="Margine annuo inchiostro" value={fmtConsumiEuro(reportAnnuale.margine_annua)} sub={fmtConsumiPct(reportAnnuale.margine_annua_percent)}/>:null}
    </div>
  </Card>);
}

function ConsumiRoiReport({reportRoi,isPhone,onPdf}){
  if(!reportRoi)return null;
  const t=reportRoi.totali||{};
  const sc=reportRoi.scenario||{};
  const roi=reportRoi.roi||{};
  return(<Card title="Report lavori & ROI" action={<div className="flex gap-2 no-print">{onPdf?<Btn s={isPhone?'sm':'md'} icon={<IcFile s={16}/>} onClick={onPdf}>PDF cliente</Btn>:null}</div>}>
    <div className="stat-tile-grid mb-4">
      <ConsumiReportTile label="m² mix" value={fmtConsumiNum(t.mq,2)}/>
      <ConsumiReportTile label="Ore produzione" value={fmtConsumiNum(t.ore,2)}/>
      <ConsumiReportTile label="Costo totale" value={fmtConsumiEuro(t.costo_totale)}/>
      <ConsumiReportTile label="Ricavo" value={fmtConsumiEuro(t.ricavo)}/>
      <ConsumiReportTile label="Margine mix" value={fmtConsumiEuro(t.margine)} sub={`${fmtConsumiEuro(t.margine_m2)}/m²`} highlight/>
      <ConsumiReportTile label="Margine mensile" value={fmtConsumiEuro(sc.margine_mensile)} sub={`× ${fmtConsumiNum(sc.ripetizioni_mese,1)} ripetizioni/mese`}/>
      <ConsumiReportTile label="Payback investimento" value={roi.payback_mesi!=null?`${fmtConsumiNum(roi.payback_mesi,1)} mesi`:'—'} sub={roi.investimento_macchina>0?`Investimento ${fmtConsumiEuro(roi.investimento_macchina)}`:''} highlight/>
    </div>
    {(reportRoi.righe||[]).length===0?<Empty title="Nessun lavoro" sub="Aggiungi almeno una riga lavoro."/>:<div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs text-gray-500 border-b"><th className="py-2 pr-2">Lavoro</th><th className="py-2 pr-2">m²</th><th className="py-2 pr-2">Min</th><th className="py-2 pr-2">Costo</th><th className="py-2 pr-2">Ricavo</th><th className="py-2">Margine</th></tr></thead><tbody>{(reportRoi.righe||[]).map((r,i)=><tr key={i} className="border-b border-gray-100"><td className="py-2 pr-2"><div className="font-medium text-navy-600">{S(r.descrizione||`Lavoro ${i+1}`)}</div><div className="text-xs text-gray-400">{r.tipo==='metri'?`${fmtConsumiNum(r.metri_lineari,2)} m × ${fmtConsumiNum(r.larghezza_m||reportRoi.macchina?.larghezza_supporto_m,2)} m`:`${fmtConsumiNum(r.larghezza_m,2)}×${fmtConsumiNum(r.altezza_m,2)} m × ${fmtConsumiNum(r.quantita,0)}`}</div></td><td className="py-2 pr-2">{fmtConsumiNum(r.mq,2)}</td><td className="py-2 pr-2">{fmtConsumiNum(r.minuti_totali,1)}</td><td className="py-2 pr-2">{fmtConsumiEuro(r.costo_totale)}</td><td className="py-2 pr-2">{fmtConsumiEuro(r.ricavo)}</td><td className="py-2 font-semibold text-navy-600">{fmtConsumiEuro(r.margine)}</td></tr>)}</tbody></table></div>}
  </Card>);
}

function ConsumiLavoriEditor({lavori,onChange,isPhone}){
  const update=(id,patch)=>onChange((lavori||[]).map(row=>row.id===id?{...row,...patch}:row));
  const remove=id=>onChange((lavori||[]).filter(row=>row.id!==id));
  const add=()=>onChange([...(lavori||[]),newConsumiLavoroRow()]);
  return(<Card title="Lavori da stampare" action={<Btn s={isPhone?'sm':'md'} icon={<IcPlus s={16}/>} onClick={add}>Aggiungi riga</Btn>}>
    {(lavori||[]).length===0?<Empty title="Nessun lavoro" sub="Aggiungi pezzi o metri lineari con prezzo al m²."/>:<div className="space-y-3">{(lavori||[]).map((row,i)=><div key={row.id} className="rounded-xl border border-gray-200 p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
      <Inp label={`Descrizione #${i+1}`} value={row.descrizione} onChange={v=>update(row.id,{descrizione:v})}/>
      <div><label className="block text-sm font-medium text-gray-600 mb-1">Tipo</label>
        <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={row.tipo} onChange={e=>update(row.id,{tipo:e.target.value})}>
          <option value="pezzo">Pezzo (L × H)</option>
          <option value="metri">Metri lineari</option>
        </select></div>
      <Inp label="Quantità" type="number" value={row.quantita} onChange={v=>update(row.id,{quantita:v})}/>
      <Inp label="Prezzo vendita (€/m²)" type="number" value={row.prezzo_vendita_m2} onChange={v=>update(row.id,{prezzo_vendita_m2:v})}/>
      {row.tipo==='metri'?<React.Fragment>
        <Inp label="Metri lineari" type="number" value={row.metri_lineari} onChange={v=>update(row.id,{metri_lineari:v})}/>
        <Inp label="Larghezza bobina (m)" type="number" value={row.larghezza_m} onChange={v=>update(row.id,{larghezza_m:v})} placeholder="Vuoto = larghezza macchina"/>
      </React.Fragment>:<React.Fragment>
        <Inp label="Larghezza (m)" type="number" value={row.larghezza_m} onChange={v=>update(row.id,{larghezza_m:v})}/>
        <Inp label="Altezza (m)" type="number" value={row.altezza_m} onChange={v=>update(row.id,{altezza_m:v})}/>
      </React.Fragment>}
      <div className="md:col-span-2 flex justify-end"><Btn s="sm" v="danger" onClick={()=>remove(row.id)}>Rimuovi</Btn></div>
    </div>)}</div>}
  </Card>);
}

async function exportConsumiRoiPdf({settings,form,reportRoi,reportAnnuale,clienteNome}){
  const filename=sanitizePdfFilename(form.titolo||'simulazione-roi','simulazione-roi');
  const righe=reportRoi?.righe||[];
  const t=reportRoi?.totali||{};
  const sc=reportRoi?.scenario||{};
  const roi=reportRoi?.roi||{};
  const conf=reportRoi?.confronto_capacita||{};
  await createAssistenzaPdf(filename,async({drawStandardHeader,drawTextLines,drawInfoGrid,drawTable,gap,drawRule,colors})=>{
    await drawStandardHeader({
      settings,
      clientName:clienteNome||'',
      docLabel:'Simulazione lavori & ROI',
      code:form.macchina_modello||'',
      dateText:fmtDate(today()),
      extraText:form.titolo||'',
    });
    drawTextLines('Riepilogo economico',{size:13,font:'bold'});
    gap(6);
    drawInfoGrid([
      {label:'m² mix',value:fmtConsumiNum(t.mq,2)},
      {label:'Margine mix',value:fmtConsumiEuro(t.margine)},
      {label:'Margine mensile',value:fmtConsumiEuro(sc.margine_mensile)},
      {label:'Payback',value:roi.payback_mesi!=null?`${fmtConsumiNum(roi.payback_mesi,1)} mesi`:'n.d.'},
      {label:'Investimento',value:fmtConsumiEuro(roi.investimento_macchina)},
      {label:'Ricavo mix',value:fmtConsumiEuro(t.ricavo)},
    ]);
    gap(10);
    if(righe.length){
      drawTextLines('Dettaglio lavori',{size:12,font:'bold'});
      gap(4);
      drawTable(
        ['Lavoro','m²','Min','Costo','Ricavo','Margine'],
        righe.map(r=>[
          String(r.descrizione||'-'),
          fmtConsumiNum(r.mq,2),
          fmtConsumiNum(r.minuti_totali,1),
          fmtConsumiEuro(r.costo_totale),
          fmtConsumiEuro(r.ricavo),
          fmtConsumiEuro(r.margine),
        ]),
        [0.28,0.1,0.1,0.14,0.14,0.14]
      );
    }
    if(conf?.messaggio_sintesi){
      gap(8);
      drawRule();
      drawTextLines('Capacità macchina',{size:12,font:'bold'});
      gap(4);
      drawTextLines(conf.messaggio_sintesi,{size:10});
    }
    if(reportAnnuale?.mq_annui){
      gap(6);
      drawInfoGrid([
        {label:'m² annui macchina',value:fmtConsumiNum(reportAnnuale.mq_annui,0)},
        {label:'Margine annuo inchiostro',value:fmtConsumiEuro(reportAnnuale.margine_annua)},
      ]);
    }
    gap(12);
    drawTextLines('Documento indicativo a scopo commerciale. I valori dipendono da parametri, listini e volumi reali.',{size:8,color:colors?.muted});
  });
}

function StimaConsumiPage(){
  const{clienti,showToast,activeArticles,loadArticoli,settings}=useContext(DataCtx);
  const{isPhone}=useDevice();
  const[activeTab,setActiveTab]=useState('annuale');
  const[form,setForm]=useState({...STIMA_CONSUMI_DEFAULTS,...STIMA_CONSUMI_ROI_DEFAULTS});
  const[lavori,setLavori]=useState([newConsumiLavoroRow()]);
  const[articoliApi,setArticoliApi]=useState([]);
  const[catalogoQ,setCatalogoQ]=useState('');
  const[report,setReport]=useState(null);
  const[reportRoi,setReportRoi]=useState(null);
  const[reportAnnualeRoi,setReportAnnualeRoi]=useState(null);
  const[stime,setStime]=useState([]);
  const[loading,setLoading]=useState(false);
  const[filterCliente,setFilterCliente]=useState('');
  const u=useCallback((k,v)=>setForm(prev=>({...prev,[k]:v})),[]);
  const loadArticoliApi=useCallback(async(q='')=>{try{const d=await api('consumi_articoli','GET',null,q?{q}:{});setArticoliApi(d.data||[])}catch(e){showToast(e.message||'Errore catalogo','error')}},[showToast]);
  const loadStime=useCallback(async()=>{try{const params=filterCliente?{cliente_id:filterCliente}:{};const d=await api('consumi_stime','GET',null,params);setStime(d.data||[])}catch(e){showToast(e.message||'Errore storico','error')}},[filterCliente,showToast]);
  useEffect(()=>{if(!activeArticles?.length)loadArticoli().catch(()=>{})},[activeArticles?.length,loadArticoli]);
  useEffect(()=>{loadArticoliApi(catalogoQ)},[catalogoQ,loadArticoliApi]);
  useEffect(()=>{loadStime()},[loadStime]);
  const articoliListino=useMemo(()=>{
    const fromCtx=(activeArticles||[]).filter(a=>a.attivo!==0&&normalizeListinoTipoClient(a.listino_tipo)!=='ricambi').map(consumiArticleFromListino);
    const source=fromCtx.length?fromCtx:(articoliApi||[]);
    const q=String(catalogoQ||'').trim().toLowerCase();
    if(!q)return source;
    return source.filter(a=>`${a.nome} ${a.sku} ${a.categoria}`.toLowerCase().includes(q));
  },[activeArticles,articoliApi,catalogoQ]);
  const selectedArticolo=useMemo(()=>articoliListino.find(a=>String(a.id)===String(form.articolo_id)),[articoliListino,form.articolo_id]);
  const consumiDaVelocitaLineare=useMemo(()=>{
    const w=toNum(form.larghezza_supporto_m),vm=toNum(form.velocita_metri_minuto);
    if(w<=0||vm<=0)return null;
    const m2min=w*vm;
    const m2h=m2min*60;
    return{m2Min:m2min,m2H:m2h};
  },[form.larghezza_supporto_m,form.velocita_metri_minuto]);
  const onSelectArticolo=id=>{
    const art=articoliListino.find(a=>String(a.id)===String(id));
    setForm(prev=>({
      ...prev,
      articolo_id:id,
      costo_inchiostro_unitario:art?String(art.costo_unitario??''):prev.costo_inchiostro_unitario,
      prezzo_vendita_unitario:art?String(art.prezzo_vendita_unitario??''):prev.prezzo_vendita_unitario,
    }));
  };
  const buildPayload=(tipoOverride='')=>{
    const tipo=tipoOverride||activeTab;
    const lavoriPayload=(lavori||[]).map(row=>({
      descrizione:row.descrizione||'',
      tipo:row.tipo||'pezzo',
      quantita:toNum(row.quantita)||1,
      larghezza_m:toNum(row.larghezza_m),
      altezza_m:toNum(row.altezza_m),
      metri_lineari:toNum(row.metri_lineari),
      prezzo_vendita_m2:toNum(row.prezzo_vendita_m2),
      note:row.note||'',
    }));
    return{
      ...form,
      tipo_stima:tipo,
      cliente_id:form.cliente_id?Number(form.cliente_id):0,
      articolo_id:form.articolo_id?Number(form.articolo_id):0,
      grammi_m2:toNum(form.grammi_m2),
      velocita_m2_h:toNum(form.velocita_m2_h),
      larghezza_supporto_m:toNum(form.larghezza_supporto_m),
      velocita_metri_minuto:toNum(form.velocita_metri_minuto),
      utilizzo_percent:toNum(form.utilizzo_percent),
      giorni_lavorativi_anno:parseInt(form.giorni_lavorativi_anno,10)||0,
      ore_giorno:toNum(form.ore_giorno),
      sconto_inchiostro_percent:toNum(form.sconto_inchiostro_percent),
      costo_inchiostro_unitario:toNum(form.costo_inchiostro_unitario),
      prezzo_vendita_unitario:toNum(form.prezzo_vendita_unitario),
      litri_per_kg:toNum(form.litri_per_kg)||1,
      investimento_macchina:toNum(form.investimento_macchina),
      costo_supporti_mq:toNum(form.costo_supporti_mq),
      costo_manodopera_ora:toNum(form.costo_manodopera_ora),
      setup_minuti_per_lavoro:toNum(form.setup_minuti_per_lavoro),
      scarto_percent:toNum(form.scarto_percent),
      ripetizioni_mese:toNum(form.ripetizioni_mese)||1,
      lavori:lavoriPayload,
    };
  };
  const calcola=async()=>{setLoading(true);try{const d=await api('consumi_calcola','POST',buildPayload('annuale'));setReport(d.data?.report||null);showToast('Report aggiornato')}catch(e){showToast(e.message||'Errore calcolo','error')}finally{setLoading(false)}};
  const calcolaRoi=async()=>{setLoading(true);try{const d=await api('consumi_roi_calcola','POST',buildPayload('roi'));setReportRoi(d.data?.report_roi||null);setReportAnnualeRoi(d.data?.report_annuale||null);setReport(d.data?.report_annuale||null);showToast('Simulazione aggiornata')}catch(e){showToast(e.message||'Errore calcolo','error')}finally{setLoading(false)}};
  const salva=async()=>{
    const isRoi=activeTab==='roi';
    if(isRoi&&!reportRoi){showToast('Esegui prima la simulazione','error');return}
    if(!isRoi&&!report){showToast('Esegui prima il calcolo','error');return}
    setLoading(true);
    try{
      await api('consumi_stima_save','POST',buildPayload());
      showToast('Stima salvata');
      loadStime();
    }catch(e){showToast(e.message||'Errore salvataggio','error')}
    finally{setLoading(false)}
  };
  const caricaStima=row=>{
    const inputs=row.inputs||{};
    const tipo=row.tipo_stima||inputs.tipo_stima||'annuale';
    setActiveTab(tipo==='roi'?'roi':'annuale');
    setForm({
      ...STIMA_CONSUMI_DEFAULTS,...STIMA_CONSUMI_ROI_DEFAULTS,...inputs,
      cliente_id:String(row.cliente_id||inputs.cliente_id||''),
      titolo:row.titolo||inputs.titolo||(tipo==='roi'?'Simulazione lavori & ROI':STIMA_CONSUMI_DEFAULTS.titolo),
      macchina_modello:row.macchina_modello||inputs.macchina_modello||'',
      articolo_id:String(inputs.articolo_id||''),
      costo_inchiostro_unitario:String(inputs.costo_inchiostro_unitario??inputs.costo_unitario??''),
      prezzo_vendita_unitario:String(inputs.prezzo_vendita_unitario??inputs.prezzo_unitario_override??''),
      larghezza_supporto_m:String(inputs.larghezza_supporto_m??''),
      velocita_metri_minuto:String(inputs.velocita_metri_minuto??''),
      investimento_macchina:String(inputs.investimento_macchina??''),
      costo_supporti_mq:String(inputs.costo_supporti_mq??''),
      costo_manodopera_ora:String(inputs.costo_manodopera_ora??''),
      setup_minuti_per_lavoro:String(inputs.setup_minuti_per_lavoro??STIMA_CONSUMI_ROI_DEFAULTS.setup_minuti_per_lavoro),
      scarto_percent:String(inputs.scarto_percent??''),
      ripetizioni_mese:String(inputs.ripetizioni_mese??STIMA_CONSUMI_ROI_DEFAULTS.ripetizioni_mese),
    });
    const loadedLavori=Array.isArray(inputs.lavori)&&inputs.lavori.length
      ?inputs.lavori.map((job,i)=>({...newConsumiLavoroRow(),...job,id:job.id||`l${i}`}))
      :[newConsumiLavoroRow()];
    setLavori(loadedLavori);
    if(tipo==='roi'){
      setReportRoi(row.report_roi||row.report||null);
      setReportAnnualeRoi(row.report||null);
      setReport(row.report||null);
    }else{
      setReport(row.report||null);
      setReportRoi(null);
      setReportAnnualeRoi(null);
    }
    showToast('Stima caricata');
  };
  const exportRoiPdf=async()=>{
    if(!reportRoi){showToast('Calcola prima la simulazione','error');return}
    try{
      const brand={...INITIAL_BRAND_SETTINGS,...(settings||{})};
      await exportConsumiRoiPdf({
        settings:brand,
        form,
        reportRoi,
        reportAnnuale:reportAnnualeRoi||report,
        clienteNome:clienteLabel(form.cliente_id),
      });
      showToast('PDF generato');
    }catch(e){showToast(e.message||'Errore PDF','error')}
  };
  const eliminaStima=async id=>{if(!window.confirm('Eliminare questa stima?'))return;try{await api('consumi_stima_delete','DELETE',null,{id});showToast('Stima eliminata');loadStime()}catch(e){showToast(e.message||'Errore','error')}};
  const clienteLabel=id=>{const c=(clienti||[]).find(x=>String(x.id)===String(id));return c?.azienda||c?.nome||`Cliente #${id}`};
  const marginePreview=toNum(form.prezzo_vendita_unitario)>0?((toNum(form.prezzo_vendita_unitario)-toNum(form.costo_inchiostro_unitario))/toNum(form.prezzo_vendita_unitario)):0;
  const sharedHeader=<div className="grid grid-cols-1 md:grid-cols-3 gap-3 no-print mb-4">
    <Inp label="Titolo" value={form.titolo} onChange={v=>u('titolo',v)}/>
    <div><label className="block text-sm font-medium text-gray-600 mb-1">Cliente</label>
      <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.cliente_id} onChange={e=>u('cliente_id',e.target.value)}>
        <option value="">— Nessun cliente —</option>
        {(clienti||[]).map(c=><option key={c.id} value={c.id}>{S(c.azienda||c.nome||`#${c.id}`)}</option>)}
      </select></div>
    <Inp label="Modello macchina" value={form.macchina_modello} onChange={v=>u('macchina_modello',v)}/>
  </div>;
  return(<Page title="Stima consumi" subtitle="Inchiostro annuale, simulazione lavori e ROI stampante">
    <div className="flex flex-wrap gap-2 no-print mb-4">
      <button type="button" onClick={()=>setActiveTab('annuale')} className={`px-4 py-2 rounded-full text-sm font-medium ${activeTab==='annuale'?'bg-accent-400 text-white shadow-md':'bg-white border border-slate-200 text-gray-600'}`}>Inchiostro annuale</button>
      <button type="button" onClick={()=>setActiveTab('roi')} className={`px-4 py-2 rounded-full text-sm font-medium ${activeTab==='roi'?'bg-accent-400 text-white shadow-md':'bg-white border border-slate-200 text-gray-600'}`}>Lavori & ROI</button>
    </div>
    {sharedHeader}
    {activeTab==='annuale'?<React.Fragment>
    <p className="text-sm text-gray-500 no-print -mt-2 mb-4">Costo e vendita dal listino. Con larghezza (m) e m/min i m²/h sono calcolati automaticamente (larghezza × m/min × 60).</p>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 no-print">
      <Card title="Parametri produzione"><div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Inp label="Consumo inchiostro (g/m²)" type="number" value={form.grammi_m2} onChange={v=>u('grammi_m2',v)}/>
        <Inp label="Velocità stampa (m²/h)" type="number" value={form.velocita_m2_h} onChange={v=>u('velocita_m2_h',v)} placeholder={consumiDaVelocitaLineare?'Ignorato se usi larghezza + m/min':''}/>
        <div className="md:col-span-2 text-xs font-medium text-navy-500 pt-1 border-t border-gray-100 mt-1">Alternativa single-pass (velocità lineare)</div>
        <Inp label="Larghezza supporto (m)" type="number" value={form.larghezza_supporto_m} onChange={v=>u('larghezza_supporto_m',v)} placeholder="es. 1,60"/>
        <Inp label="Velocità avanzamento (m/min)" type="number" value={form.velocita_metri_minuto} onChange={v=>u('velocita_metri_minuto',v)} placeholder="metri lineari al minuto"/>
        {consumiDaVelocitaLineare?<div className="md:col-span-2 text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded-lg px-3 py-2">
          Produzione nominale: <strong>{fmtConsumiNum(consumiDaVelocitaLineare.m2Min,3)} m²/min</strong> → <strong>{fmtConsumiNum(consumiDaVelocitaLineare.m2H,2)} m²/h</strong> (usati nel report al posto del campo m²/h).
        </div>:<div className="md:col-span-2 text-xs text-gray-400">Compila entrambi (larghezza e m/min) per calcolare automaticamente i m²/h; altrimenti vale solo «Velocità stampa (m²/h)».</div>}
        <Inp label="Utilizzo macchina (%)" type="number" value={form.utilizzo_percent} onChange={v=>u('utilizzo_percent',v)}/>
        <Inp label="Giorni lavorativi / anno" type="number" value={form.giorni_lavorativi_anno} onChange={v=>u('giorni_lavorativi_anno',v)}/>
        <Inp label="Ore lavoro / giorno" type="number" value={form.ore_giorno} onChange={v=>u('ore_giorno',v)}/>
        <Inp label="Sconto su vendita inchiostro (%)" type="number" value={form.sconto_inchiostro_percent} onChange={v=>u('sconto_inchiostro_percent',v)}/>
        <Inp label="Litri per kg" type="number" value={form.litri_per_kg} onChange={v=>u('litri_per_kg',v)} placeholder="1 se prezzo al kg"/>
      </div></Card>
      <Card title="Listino / prezzi inchiostro"><div className="space-y-3">
        <Inp label="Cerca nel listino" value={catalogoQ} onChange={setCatalogoQ} placeholder="Nome, SKU, categoria, inchiostro..."/>
        <div><label className="block text-sm font-medium text-gray-600 mb-1">Articolo dal catalogo</label>
          <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.articolo_id} onChange={e=>onSelectArticolo(e.target.value)}>
            <option value="">— Inserimento manuale costo e vendita —</option>
            {articoliListino.map(a=><option key={a.id} value={a.id}>{S(a.nome)} — costo {fmtConsumiEuro(a.costo_unitario)} · vendita {fmtConsumiEuro(a.prezzo_vendita_unitario)}/{S(a.um||'kg')}{a.is_inchiostro_hint?' ★':''}</option>)}
          </select>
          <div className="text-xs text-gray-400 mt-1">{articoliListino.length} articoli dal listino visibile</div>
        </div>
        {selectedArticolo?<div className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1">
          <div><strong>{S(selectedArticolo.nome)}</strong> {selectedArticolo.sku?`(${S(selectedArticolo.sku)})`:''}</div>
          <div>Costo acquisto: <strong>{fmtConsumiEuro(selectedArticolo.costo_unitario)}</strong> / {S(selectedArticolo.um)}</div>
          <div>Prezzo vendita listino: <strong>{fmtConsumiEuro(selectedArticolo.prezzo_vendita_unitario)}</strong> / {S(selectedArticolo.um)}</div>
          <div>Margine unitario: <strong>{fmtConsumiEuro(selectedArticolo.margine_unitario)}</strong> ({fmtConsumiPct(selectedArticolo.margine_percent)})</div>
        </div>:null}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t pt-3">
          <Inp label="Costo inchiostro (acquisto)" type="number" value={form.costo_inchiostro_unitario} onChange={v=>u('costo_inchiostro_unitario',v)} placeholder="€ per kg o litro"/>
          <Inp label="Prezzo vendita inchiostro" type="number" value={form.prezzo_vendita_unitario} onChange={v=>u('prezzo_vendita_unitario',v)} placeholder="€ per kg o litro"/>
        </div>
        {!form.articolo_id&&toNum(form.prezzo_vendita_unitario)>0?<div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Margine stimato manuale: <strong>{fmtConsumiPct(marginePreview)}</strong> ({fmtConsumiEuro(toNum(form.prezzo_vendita_unitario)-toNum(form.costo_inchiostro_unitario))} / unità)</div>:null}
        <Inp label="Note" value={form.note} onChange={v=>u('note',v)}/>
        <div className="flex flex-wrap gap-2 pt-2"><Btn onClick={calcola} disabled={loading}>{loading?'Calcolo…':'Calcola report'}</Btn><Btn v="success" onClick={salva} disabled={loading||!report}>Salva stima</Btn></div>
      </div></Card>
    </div>
    {report?<Card title="Report annuale" action={<Btn className="no-print" s={isPhone?'sm':'md'} icon={<IcPrint s={16}/>} onClick={()=>window.print()}>Stampa</Btn>}>
      <div className="stat-tile-grid">
        {report.velocita_modalita==='lineare'?<>
          <ConsumiReportTile label="Larghezza supporto" value={`${fmtConsumiNum(report.larghezza_supporto_m,2)} m`} sub={`${fmtConsumiNum(report.velocita_metri_minuto,2)} m/min`}/>
          <ConsumiReportTile label="Superficie / minuto" value={`${fmtConsumiNum(report.m2_minuto_nominali,3)} m²/min`}/>
          <ConsumiReportTile label="Velocità effettiva" value={`${fmtConsumiNum(report.velocita_m2_h_effettiva,2)} m²/h`} sub="da lineare × larghezza"/>
        </>:<>
          <ConsumiReportTile label="Velocità effettiva" value={`${fmtConsumiNum(Number(report.velocita_m2_h_effettiva)||(toNum(report.ore_stampa_effettive)>0?toNum(report.mq_annui)/toNum(report.ore_stampa_effettive):0)||toNum(report.velocita_m2_h_form),2)} m²/h`} sub="immissione diretta m²/h"/>
        </>}
        <ConsumiReportTile label="m² annui" value={fmtConsumiNum(report.mq_annui,2)}/>
        <ConsumiReportTile label="Kg inchiostro" value={fmtConsumiNum(report.kg_inchiostro,3)}/>
        <ConsumiReportTile label={`Qty (${report.unita_consumo})`} value={fmtConsumiNum(report.quantita_fatturata,3)}/>
        <ConsumiReportTile label="Costo acquisto annuo" value={fmtConsumiEuro(report.costo_acquisto_annuo)}/>
        <ConsumiReportTile label="Ricavo vendita lordo" value={fmtConsumiEuro(report.ricavo_vendita_lordo)}/>
        <ConsumiReportTile label={`Sconto vendita ${fmtConsumiNum(report.sconto_inchiostro_percent,0)}%`} value={fmtConsumiEuro(report.risparmio_sconto)}/>
        <ConsumiReportTile label="Ricavo vendita netto" value={fmtConsumiEuro(report.ricavo_vendita_netto)}/>
        <ConsumiReportTile label="Margine annuo" value={fmtConsumiEuro(report.margine_annua)} sub={fmtConsumiPct(report.margine_annua_percent)} highlight/>
        <ConsumiReportTile label="Margine / unità" value={fmtConsumiEuro(report.margine_unitario)} sub={`${fmtConsumiEuro(report.costo_unitario)} → ${fmtConsumiEuro(report.prezzo_vendita_unitario)}`}/>
        <ConsumiReportTile label="Ricavo / m²" value={fmtConsumiEuro(report.ricavo_m2)}/>
        <ConsumiReportTile label="Costo acquisto / m²" value={fmtConsumiEuro(report.costo_m2)}/>
      </div>
    </Card>:<Empty icon={<IcChart s={36} c="text-accent-400"/>} title="Nessun report" sub="Inserisci i parametri e calcola."/>}
    </React.Fragment>:<React.Fragment>
    <p className="text-sm text-gray-500 no-print -mt-2 mb-4">Inserisci lavori a pezzo o a metri lineari con prezzo al m². Il sistema calcola tempi, costi reali e payback dell&apos;investimento, confrontando anche la capacità annua della macchina.</p>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 no-print">
      <Card title="Macchina & investimento"><div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Inp label="Investimento stampante (€)" type="number" value={form.investimento_macchina} onChange={v=>u('investimento_macchina',v)}/>
        <Inp label="Costo supporto (€/m²)" type="number" value={form.costo_supporti_mq} onChange={v=>u('costo_supporti_mq',v)}/>
        <Inp label="Costo manodopera (€/ora)" type="number" value={form.costo_manodopera_ora} onChange={v=>u('costo_manodopera_ora',v)}/>
        <Inp label="Setup per lavoro (min)" type="number" value={form.setup_minuti_per_lavoro} onChange={v=>u('setup_minuti_per_lavoro',v)}/>
        <Inp label="Scarto / spreco (%)" type="number" value={form.scarto_percent} onChange={v=>u('scarto_percent',v)}/>
        <Inp label="Ripetizioni scenario / mese" type="number" value={form.ripetizioni_mese} onChange={v=>u('ripetizioni_mese',v)} placeholder="Quante volte ripeti il mix al mese"/>
        <Inp label="Consumo inchiostro (g/m²)" type="number" value={form.grammi_m2} onChange={v=>u('grammi_m2',v)}/>
        <Inp label="Larghezza supporto (m)" type="number" value={form.larghezza_supporto_m} onChange={v=>u('larghezza_supporto_m',v)}/>
        <Inp label="Velocità avanzamento (m/min)" type="number" value={form.velocita_metri_minuto} onChange={v=>u('velocita_metri_minuto',v)}/>
        <Inp label="Velocità stampa (m²/h)" type="number" value={form.velocita_m2_h} onChange={v=>u('velocita_m2_h',v)} placeholder={consumiDaVelocitaLineare?'Opzionale se usi m/min':''}/>
        {consumiDaVelocitaLineare?<div className="md:col-span-2 text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded-lg px-3 py-2">Velocità effettiva: <strong>{fmtConsumiNum(consumiDaVelocitaLineare.m2H,2)} m²/h</strong></div>:null}
        <Inp label="Utilizzo macchina (%)" type="number" value={form.utilizzo_percent} onChange={v=>u('utilizzo_percent',v)}/>
        <Inp label="Giorni lavorativi / anno" type="number" value={form.giorni_lavorativi_anno} onChange={v=>u('giorni_lavorativi_anno',v)}/>
        <Inp label="Ore lavoro / giorno" type="number" value={form.ore_giorno} onChange={v=>u('ore_giorno',v)}/>
      </div></Card>
      <Card title="Listino inchiostro"><div className="space-y-3">
        <Inp label="Cerca nel listino" value={catalogoQ} onChange={setCatalogoQ} placeholder="Nome, SKU..."/>
        <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.articolo_id} onChange={e=>onSelectArticolo(e.target.value)}>
          <option value="">— Costo inchiostro manuale —</option>
          {articoliListino.map(a=><option key={a.id} value={a.id}>{S(a.nome)} — {fmtConsumiEuro(a.costo_unitario)}/{S(a.um||'kg')}</option>)}
        </select>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Inp label="Costo inchiostro" type="number" value={form.costo_inchiostro_unitario} onChange={v=>u('costo_inchiostro_unitario',v)}/>
          <Inp label="Litri per kg" type="number" value={form.litri_per_kg} onChange={v=>u('litri_per_kg',v)}/>
        </div>
        <div className="flex flex-wrap gap-2 pt-2"><Btn onClick={calcolaRoi} disabled={loading}>{loading?'Calcolo…':'Calcola simulazione'}</Btn><Btn v="success" onClick={salva} disabled={loading||!reportRoi}>Salva</Btn></div>
      </div></Card>
    </div>
    <div className="no-print"><ConsumiLavoriEditor lavori={lavori} onChange={setLavori} isPhone={isPhone}/></div>
    <ConsumiRoiReport reportRoi={reportRoi} isPhone={isPhone} onPdf={exportRoiPdf}/>
    <ConsumiConfrontoCapacita confronto={reportRoi?.confronto_capacita} reportAnnuale={reportAnnualeRoi||report}/>
    {!reportRoi?<Empty icon={<IcChart s={36} c="text-accent-400"/>} title="Nessuna simulazione" sub="Aggiungi lavori e calcola."/>:null}
    </React.Fragment>}
    <Card title="Stime salvate" action={<select className="border border-gray-200 rounded-lg px-2 py-1 text-sm no-print" value={filterCliente} onChange={e=>setFilterCliente(e.target.value)}>
      <option value="">Tutti i clienti</option>{(clienti||[]).map(c=><option key={c.id} value={c.id}>{S(c.azienda||c.nome)}</option>)}
    </select>}>
      {stime.length===0?<p className="text-sm text-gray-400 text-center py-6">Nessuna stima salvata</p>:<div className="divide-y">{stime.map(row=>{
        const isRoiRow=(row.tipo_stima||'')==='roi';
        const margineLabel=isRoiRow
          ?`margine mensile ${fmtConsumiEuro(row.report_roi?.scenario?.margine_mensile??row.report?.report_roi?.scenario?.margine_mensile)}`
          :`margine ${fmtConsumiEuro(row.report?.margine_annua)} (${fmtConsumiPct(row.report?.margine_annua_percent)})`;
        return(<div key={row.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
          <div><div className="font-semibold text-navy-600 flex items-center gap-2 flex-wrap">{S(row.titolo)}<span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${isRoiRow?'bg-violet-100 text-violet-700':'bg-slate-100 text-slate-600'}`}>{isRoiRow?'ROI':'Annuale'}</span></div><div className="text-xs text-gray-500">{row.cliente_azienda||clienteLabel(row.cliente_id)} · {margineLabel}</div></div>
          <div className="flex gap-2"><Btn s="sm" onClick={()=>caricaStima(row)}>Apri</Btn><Btn s="sm" v="danger" onClick={()=>eliminaStima(row.id)}>Elimina</Btn></div>
        </div>);
      })}</div>}
    </Card>
  </Page>);
}

function ClienteTransformModal({cliente,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const displayName=clienteDisplayName(cliente);
  const autoUsername=useMemo(()=>{
    const emailPrefix=String(cliente?.email||'').trim().split('@')[0]||'';
    const company=normalizeToken(cliente?.azienda||'').replace(/[^a-z0-9]+/g,'');
    const person=[cliente?.nome,cliente?.cognome].map(v=>normalizeToken(v).replace(/[^a-z0-9]+/g,'')).filter(Boolean).join('.');
    return(emailPrefix||person||company||`utente${cliente?.id||''}`).slice(0,40);
  },[cliente]);
  const[f,setF]=useState({
    mode:normalizeClienteTipo(cliente?.tipo)==='rivenditore'?'rivenditore_user':'agente_user',
    username:autoUsername,
    nome:(cliente?.nome||cliente?.azienda||'').trim(),
    email:(cliente?.email||'').trim(),
    send_credentials_email:true,
    password:'',
  });
  const[result,setResult]=useState(null);
  const savingRef=useRef(false);
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  useEffect(()=>{
    setF({
      mode:normalizeClienteTipo(cliente?.tipo)==='rivenditore'?'rivenditore_user':'agente_user',
      username:autoUsername,
      nome:(cliente?.nome||cliente?.azienda||'').trim(),
      email:(cliente?.email||'').trim(),
      send_credentials_email:true,
      password:'',
    });
    setResult(null);
  },[cliente,autoUsername]);
  const needsCredentials=f.mode!=='rivenditore';
  const save=async()=>{
    if(savingRef.current)return;
    if(needsCredentials&&!String(f.username||'').trim()){showToast('Username obbligatorio','error');return}
    if(needsCredentials&&f.send_credentials_email&&!String(f.email||'').trim()){showToast('Inserisci una email per inviare il link di attivazione','error');return}
    savingRef.current=true;
    try{
      const payload={
        cliente_id:cliente.id,
        mode:f.mode,
        username:String(f.username||'').trim(),
        nome:String(f.nome||'').trim(),
        email:String(f.email||'').trim(),
        send_credentials_email:f.send_credentials_email?1:0,
        password:String(f.password||'').trim(),
      };
      const d=await api('cliente_transform','POST',payload);
      if(d.success){
        if(d.data?.password||d.data?.username)setResult(d.data);
        if(d.data?.email_sent){
          showToast(`${d.message||'Trasformazione completata'} · Email inviata`);
        }else if(f.send_credentials_email&&needsCredentials){
          showToast(`${d.message||'Trasformazione completata'}${d.data?.email_error?` · Email non inviata: ${d.data.email_error}`:''}`,'error');
        }else{
          showToast(d.message||'Trasformazione completata');
        }
        if(!d.data?.password)onSaved();
      }
    }catch(e){showToast(e.message,'error')}
    finally{savingRef.current=false}
  };
  return(<Modal title="Trasforma cliente" onClose={onClose}>
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="text-xs uppercase tracking-wide text-slate-400">Contatto selezionato</div>
        <div className="font-semibold text-navy-600">{displayName}</div>
        <div className="text-xs text-slate-500 mt-1">{[cliente?.email,cliente?.telefono].filter(Boolean).join(' · ')||'Nessun recapito registrato'}</div>
      </div>
      <Sel label="Operazione" value={f.mode} onChange={v=>u('mode',v)} options={[
        {value:'rivenditore',label:'Trasforma in rivenditore'},
        {value:'agente_user',label:'Crea accesso agente'},
        {value:'rivenditore_user',label:'Crea accesso rivenditore'},
      ]}/>
      {needsCredentials?<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Username" value={f.username} onChange={v=>u('username',v)} placeholder="utente.demo"/>
        <Inp label="Nome visualizzato" value={f.nome} onChange={v=>u('nome',v)} placeholder="Mario Rossi"/>
        <Inp label="Email utente" type="email" value={f.email} onChange={v=>u('email',v)} placeholder="utente@azienda.it"/>
        <Inp label="Password iniziale" value={f.password} onChange={v=>u('password',v)} placeholder="Lascia vuoto se vuoi usare solo il link di attivazione"/>
      </div>:<div className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-800">Il contatto verrà spostato tra i rivenditori senza creare un nuovo accesso utente.</div>}
      {needsCredentials?<label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <input type="checkbox" checked={!!f.send_credentials_email} onChange={e=>u('send_credentials_email',e.target.checked)} className="w-4 h-4 text-accent-500 rounded"/>
        <span>Invia email con link di attivazione e istruzioni</span>
      </label>:null}
      {result?<div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 space-y-1">
        <div className="text-sm font-semibold text-green-800">Accesso creato</div>
        <div className="text-sm text-green-700">Username: <strong>{S(result.username||'-')}</strong></div>
        {result.manual_password_set?<div className="text-sm text-green-700">Password: <strong>impostata manualmente durante la creazione</strong></div>:<div className="text-sm text-green-700">Password: <strong>l’utente la imposterà dal link ricevuto</strong></div>}
        <div className="text-xs text-green-700">{result.email_sent?'Email di accesso inviata correttamente.':(result.manual_password_set?'Comunica all’utente la password che hai appena impostato manualmente.':'Se non hai inviato l’email, invia un nuovo link di attivazione o completa la configurazione manualmente.')}</div>
      </div>:null}
      <div className="flex justify-end gap-3">
        {result?<Btn v="secondary" onClick={onSaved}>Chiudi</Btn>:<React.Fragment>
          <Btn v="secondary" onClick={onClose}>Annulla</Btn>
          <Btn v="success" onClick={save}>{needsCredentials?'Crea accesso':'Trasforma'}</Btn>
        </React.Fragment>}
      </div>
    </div>
  </Modal>);
}

function AssistenzaStampanteModal({cliente,stampante,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const def={cliente_id:String(cliente?.id||''),tipologia:'',marca:'',modello:'',numero_seriale:'',ubicazione:'',note:'',attiva:1};
  const[f,setF]=useState(stampante?{...def,...stampante,cliente_id:String(stampante.cliente_id||cliente?.id||''),attiva:stampante.attiva!==0?1:0}:{...def});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const save=async()=>{
    if(!f.cliente_id){showToast('Cliente obbligatorio','error');return}
    if(!String(f.modello||'').trim()){showToast('Inserisci il modello stampante','error');return}
    try{
      const payload={cliente_id:parseInt(f.cliente_id)||null,tipologia:f.tipologia||'',marca:(f.marca||'').trim(),modello:(f.modello||'').trim(),numero_seriale:(f.numero_seriale||'').trim(),ubicazione:(f.ubicazione||'').trim(),note:(f.note||'').trim(),attiva:f.attiva?1:0};
      if(stampante?.id)await api('assistenza_stampante_update','PUT',payload,{id:stampante.id});
      else await api('assistenza_stampante_create','POST',payload);
      showToast(stampante?'Stampante aggiornata':'Stampante registrata');
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };
  return(<Modal title={stampante?'Modifica stampante cliente':'Nuova stampante cliente'} onClose={onClose} wide>
    <div className="space-y-4">
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Cliente</div>
        <div className="font-semibold text-navy-600">{clienteDisplayName(cliente)}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Sel label="Tipologia" value={f.tipologia||''} onChange={v=>u('tipologia',v)} options={STAMPANTE_TIPOLOGIE_OPTIONS} placeholder="Tipologia"/>
        <Inp label="Marca" value={f.marca||''} onChange={v=>u('marca',v)}/>
        <Inp label="Modello / Stampante" value={f.modello||''} onChange={v=>u('modello',v)} placeholder="Mimaki CJV150" required/>
        <Inp label="Numero seriale" value={f.numero_seriale||''} onChange={v=>u('numero_seriale',v)} placeholder="SN-..."/>
        <Inp label="Ubicazione" value={f.ubicazione||''} onChange={v=>u('ubicazione',v)} placeholder="Produzione, showroom, reparto 1..."/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Note stampante</label>
        <textarea value={f.note||''} onChange={e=>u('note',e.target.value)} rows={3} className="w-full px-4 py-3 border rounded-2xl text-sm outline-none focus:ring-2 focus:ring-accent-400" placeholder="Dettagli utili su configurazione, accessori o installazione"/>
      </div>
      <div className="flex justify-end gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva stampante</Btn>
      </div>
    </div>
  </Modal>);
}

function AssistenzaPreventivoModal({preventivo,intervento,cliente,stampanti,activeArticles,ricambiArticles,settings,onClose,onSaved,onConfirmed}){
  const{showToast}=useContext(DataCtx);
  const[generatingPdf,setGeneratingPdf]=useState(false);
  const[confirming,setConfirming]=useState(false);
  const[ricambioSearch,setRicambioSearch]=useState('');
  const kmArticleName=(settings?.assistenza_km_articolo||'KM').trim();
  const oreArticleName=(settings?.assistenza_ore_viaggio_articolo||'ORE VIAGGIO').trim();
  const findArticlePrice=name=>{
    const exact=(activeArticles||[]).find(article=>String(article.nome||'').trim().toLowerCase()===String(name||'').trim().toLowerCase());
    if(exact)return toNum(exact.prezzo_listino||calcPrezzi(exact).prezzoConsigliato);
    const fallback=(activeArticles||[]).find(article=>String(article.nome||'').toLowerCase().includes(String(name||'').toLowerCase()));
    return fallback?toNum(fallback.prezzo_listino||calcPrezzi(fallback).prezzoConsigliato):0;
  };
  const kmUnit=findArticlePrice(kmArticleName);
  const oreUnit=findArticlePrice(oreArticleName);
  const def={data_preventivo:today(),data_prevista_intervento:intervento?.data_intervento||today(),orario_previsto_intervento:intervento?.orario_intervento||'',stampante_id:String(intervento?.stampante_id||''),km_qta:0,km_prezzo_unitario:kmUnit,ore_viaggio_qta:0,ore_viaggio_prezzo_unitario:oreUnit,ore_manodopera_qta:0,manodopera_prezzo_unitario:0,num_tecnici:1,stato:'bozza',note:'',righe:[{descrizione:'',qta:1,prezzo_unitario:0}]};
  const[f,setF]=useState(preventivo?{...def,...preventivo,righe:(preventivo.righe||[]).length?(preventivo.righe||[]).map(row=>({descrizione:row.descrizione||'',qta:row.qta||1,prezzo_unitario:row.prezzo_unitario||0})):def.righe}:{...def});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const updateRow=(idx,key,val)=>setF(p=>({...p,righe:(p.righe||[]).map((row,rowIdx)=>rowIdx===idx?{...row,[key]:val}:row)}));
  const addRow=()=>setF(p=>({...p,righe:[...(p.righe||[]),{descrizione:'',qta:1,prezzo_unitario:0}]}));
  const removeRow=idx=>setF(p=>({...p,righe:(p.righe||[]).filter((_,rowIdx)=>rowIdx!==idx)}));
  const addRicambioRow=article=>setF(p=>({...p,righe:[...(p.righe||[]),{descrizione:article?.nome||'',qta:1,prezzo_unitario:toNum(article?.prezzo_listino||calcPrezzi(article||{}).prezzoConsigliato)}]}));
  const filteredRicambi=useMemo(()=>{
    const base=(ricambiArticles||[]).filter(article=>article.attivo);
    if(!ricambioSearch.trim())return base.slice(0,30);
    const term=ricambioSearch.trim().toLowerCase();
    return base.filter(article=>(article.nome||'').toLowerCase().includes(term)||(article.sku||'').toLowerCase().includes(term)||(article.categoria||'').toLowerCase().includes(term)).slice(0,30);
  },[ricambiArticles,ricambioSearch]);
  const preview=useMemo(()=>{
    const righe=(f.righe||[]).map((row,idx)=>({...row,idx,totale:round2(toNum(row.qta)*toNum(row.prezzo_unitario))})).filter(row=>String(row.descrizione||'').trim()||toNum(row.qta)||toNum(row.prezzo_unitario));
    const totaleKm=round2(toNum(f.km_qta)*toNum(f.km_prezzo_unitario));
    const totaleOre=round2(toNum(f.ore_viaggio_qta)*toNum(f.ore_viaggio_prezzo_unitario));
    const oreManodoperaTotali=round2(toNum(f.ore_manodopera_qta)*Math.max(1,toNum(f.num_tecnici)||1));
    const totaleManodopera=round2(oreManodoperaTotali*toNum(f.manodopera_prezzo_unitario));
    const totaleManuale=round2(righe.reduce((sum,row)=>sum+toNum(row.totale),0));
    return{righe,totaleKm,totaleOre,oreManodoperaTotali,totaleManodopera,totaleManuale,totale:round2(totaleKm+totaleOre+totaleManodopera+totaleManuale)};
  },[f]);

  const buildQuotePayload=()=>({
    intervento_id:intervento?.id,
    data_preventivo:f.data_preventivo||today(),
    data_prevista_intervento:f.data_prevista_intervento||'',
    orario_previsto_intervento:f.orario_previsto_intervento||'',
    stampante_id:parseInt(f.stampante_id)||null,
    km_qta:toNum(f.km_qta),
    km_prezzo_unitario:toNum(f.km_prezzo_unitario),
    ore_viaggio_qta:toNum(f.ore_viaggio_qta),
    ore_viaggio_prezzo_unitario:toNum(f.ore_viaggio_prezzo_unitario),
    ore_manodopera_qta:toNum(f.ore_manodopera_qta),
    manodopera_prezzo_unitario:toNum(f.manodopera_prezzo_unitario),
    num_tecnici:Math.max(1,parseInt(f.num_tecnici)||1),
    note:(f.note||'').trim(),
    righe:(f.righe||[]).map(row=>({descrizione:(row.descrizione||'').trim(),qta:toNum(row.qta),prezzo_unitario:toNum(row.prezzo_unitario)})).filter(row=>row.descrizione),
  });
  const buildQuoteSnapshot=extra=>({
    ...preventivo,
    ...f,
    ...extra,
    righe:preview.righe,
    totale_km:preview.totaleKm,
    totale_ore_viaggio:preview.totaleOre,
    totale_manodopera:preview.totaleManodopera,
    ore_manodopera_totali:preview.oreManodoperaTotali,
    totale_manuale:preview.totaleManuale,
    totale:preview.totale,
    km_prezzo_unitario:toNum(f.km_prezzo_unitario),
    ore_viaggio_prezzo_unitario:toNum(f.ore_viaggio_prezzo_unitario),
    ore_manodopera_qta:toNum(f.ore_manodopera_qta),
    manodopera_prezzo_unitario:toNum(f.manodopera_prezzo_unitario),
    num_tecnici:Math.max(1,parseInt(f.num_tecnici)||1),
    km_articolo_nome:kmArticleName,
    ore_viaggio_articolo_nome:oreArticleName,
    stampante_id:parseInt(f.stampante_id)||null,
    stampante_modello:(stampanti||[]).find(item=>String(item.id)===String(f.stampante_id))?.modello||intervento?.stampante_modello||intervento?.macchina_modello||preventivo?.stampante_modello||'',
    stampante_numero_seriale:(stampanti||[]).find(item=>String(item.id)===String(f.stampante_id))?.numero_seriale||intervento?.stampante_numero_seriale||intervento?.numero_seriale||preventivo?.stampante_numero_seriale||'',
  });
  const persistQuote=async({closeAfterSave=true,notify=true}={})=>{
    const payload=buildQuotePayload();
    let responseData={};
    if(preventivo?.id){
      await api('assistenza_preventivo_update','PUT',payload,{id:preventivo.id});
    }else{
      const created=await api('assistenza_preventivo_create','POST',payload);
      responseData=created?.data||{};
    }
    const savedQuote=buildQuoteSnapshot({
      id:responseData.id||preventivo?.id,
      codice:responseData.codice||preventivo?.codice||'',
      tecnico_nome:preventivo?.tecnico_nome||intervento?.tecnico_nome||'',
      intervento_id:intervento?.id,
      data_preventivo:payload.data_preventivo,
    });
    if(notify)showToast(preventivo?'Preventivo assistenza tecnica aggiornato':'Preventivo assistenza tecnica creato');
    if(closeAfterSave)onSaved();
    return savedQuote;
  };
  const save=async()=>{
    try{
      await persistQuote();
    }catch(e){showToast(e.message,'error')}
  };
  const confirmQuote=async()=>{
    setConfirming(true);
    try{
      const savedQuote=await persistQuote({closeAfterSave:false,notify:false});
      const resp=await api('assistenza_preventivo_confirm','PUT',{}, {id:savedQuote.id});
      showToast('Preventivo confermato. La scheda intervento e ora pronta per la lavorazione');
      onConfirmed?.(resp?.data?.intervento_id||intervento?.id||savedQuote.intervento_id);
    }catch(e){showToast(e.message,'error')}
    finally{setConfirming(false)}
  };
  const exportPdf=async()=>{
    setGeneratingPdf(true);
    try{
      const savedQuote=await persistQuote({closeAfterSave:false,notify:false});
      await exportAssistenzaPreventivoPdf(savedQuote,cliente,intervento,settings);
      showToast('PDF preventivo generato');
      onSaved();
    }catch(e){showToast(`Errore PDF: ${e.message}`,'error')}
    finally{setGeneratingPdf(false)}
  };

  return(<Modal title={preventivo?'Modifica preventivo assistenza tecnica':'Nuovo preventivo assistenza tecnica'} onClose={onClose} wide>
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Cliente</div>
          <div className="font-semibold text-navy-600">{clienteDisplayName(cliente)}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Intervento</div>
          <div className="font-semibold text-navy-600">{S(intervento?.codice||'')}</div>
          <div className="text-sm text-slate-500 mt-1">{S(intervento?.titolo||'Intervento tecnico')}</div>
        </div>
        <Inp label="Data preventivo" type="date" value={f.data_preventivo||''} onChange={v=>u('data_preventivo',v)}/>
      </div>

      <Card title="Programmazione intervento">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <Sel label="Stampante da servire" value={String(f.stampante_id||'')} onChange={v=>u('stampante_id',v)} options={(stampanti||[]).map(item=>({value:String(item.id),label:`${S(item.modello||'Stampante')} · ${S(item.numero_seriale||'senza seriale')}`}))} placeholder="Seleziona stampante"/>
          <Inp label="Data prevista" type="date" value={f.data_prevista_intervento||''} onChange={v=>u('data_prevista_intervento',v)}/>
          <Inp label="Orario previsto" type="time" value={f.orario_previsto_intervento||''} onChange={v=>u('orario_previsto_intervento',v)}/>
          <Inp label="Numero tecnici" type="number" value={f.num_tecnici||1} onChange={v=>u('num_tecnici',v)}/>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Stato</div>
            <div className="font-semibold text-navy-600">{String(f.stato||preventivo?.stato||'bozza').toLowerCase()==='confermato'?'Confermato':'Bozza'}</div>
          </div>
        </div>
      </Card>

      <Card title="Voci da listino">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-navy-600">{S(kmArticleName||'KM')}</div>
                <div className="text-xs text-slate-500">Prezzo modificabile manualmente</div>
              </div>
              <div className="w-24">
                <Inp label="KM" type="number" value={f.km_qta||''} onChange={v=>u('km_qta',v)} />
              </div>
            </div>
            <div className="mt-3">
              <Inp label="Costo KM" type="number" value={f.km_prezzo_unitario||''} onChange={v=>u('km_prezzo_unitario',v)}/>
            </div>
          </div>
          <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-navy-600">{S(oreArticleName||'Ore viaggio')}</div>
                <div className="text-xs text-slate-500">Prezzo modificabile manualmente</div>
              </div>
              <div className="w-24">
                <Inp label="Ore viaggio" type="number" value={f.ore_viaggio_qta||''} onChange={v=>u('ore_viaggio_qta',v)} />
              </div>
            </div>
            <div className="mt-3">
              <Inp label="Costo ore viaggio" type="number" value={f.ore_viaggio_prezzo_unitario||''} onChange={v=>u('ore_viaggio_prezzo_unitario',v)}/>
            </div>
          </div>
          <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50">
            <div className="grid grid-cols-2 gap-3">
              <Inp label="Ore manodopera" type="number" value={f.ore_manodopera_qta||''} onChange={v=>u('ore_manodopera_qta',v)}/>
              <Inp label="Prezzo manodopera" type="number" value={f.manodopera_prezzo_unitario||''} onChange={v=>u('manodopera_prezzo_unitario',v)}/>
            </div>
            <div className="mt-3 text-xs text-slate-500">{`Totale ore uomo conteggiate: ${S(preview.oreManodoperaTotali||0)} (${S(f.num_tecnici||1)} tecnici)`}</div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6">
        <Card title="Righe manuali" action={<Btn s="sm" icon={<IcPlus s={14}/>} onClick={addRow}>Aggiungi riga</Btn>}>
          <div className="space-y-3">
            {(f.righe||[]).map((row,idx)=><div key={idx} className="grid grid-cols-1 md:grid-cols-[minmax(0,1.6fr)_120px_140px_40px] gap-3 border border-slate-200 rounded-2xl p-3">
              <Inp label="Descrizione" value={row.descrizione||''} onChange={v=>updateRow(idx,'descrizione',v)} placeholder="Ricambio, manodopera, consumo, verifica..."/>
              <Inp label="Qta" type="number" value={row.qta||''} onChange={v=>updateRow(idx,'qta',v)}/>
              <Inp label="Prezzo unitario" type="number" value={row.prezzo_unitario||''} onChange={v=>updateRow(idx,'prezzo_unitario',v)}/>
              <div className="flex items-end justify-end">
                <button onClick={()=>removeRow(idx)} className="p-2 text-gray-400 hover:text-red-600"><IcTrash s={16}/></button>
              </div>
            </div>)}
          </div>
        </Card>
        <Card title="Listino ricambi" action={<span className="text-xs text-slate-400">{(ricambiArticles||[]).length} ricambi</span>}>
          <div className="space-y-3">
            <div className="relative">
              <IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input value={ricambioSearch} onChange={e=>setRicambioSearch(e.target.value)} placeholder="Cerca ricambio per nome, SKU o categoria"
                className="field-shell w-full pl-10 pr-4 py-3 rounded-2xl text-sm outline-none transition"/>
            </div>
            <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
              {filteredRicambi.length===0?<div className="text-sm text-slate-400">Nessun ricambio trovato.</div>:filteredRicambi.map(article=><button key={article.id} onClick={()=>addRicambioRow(article)} className="w-full text-left border border-slate-200 rounded-2xl px-3 py-3 hover:border-accent-300 hover:bg-accent-50/40 transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy-600 truncate">{S(article.nome||'Ricambio')}</div>
                    <div className="text-xs text-slate-500 mt-1">{[article.sku||'',article.categoria||''].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-slate-700">{fmt(article.prezzo_listino||calcPrezzi(article).prezzoConsigliato)}</div>
                    <div className="text-[11px] text-accent-600 mt-1">Aggiungi</div>
                  </div>
                </div>
              </button>)}
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Note</label>
          <textarea value={f.note||''} onChange={e=>u('note',e.target.value)} rows={5} className="w-full px-4 py-3 border rounded-2xl text-sm outline-none focus:ring-2 focus:ring-accent-400" placeholder="Condizioni, tempistiche, esclusioni, note operative..."/>
        </div>
        <Card title="Totali">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">KM</span><span>{fmt(preview.totaleKm)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Ore viaggio</span><span>{fmt(preview.totaleOre)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Manodopera prevista</span><span>{fmt(preview.totaleManodopera)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Ore uomo totali</span><span>{S(preview.oreManodoperaTotali||0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Righe manuali</span><span>{fmt(preview.totaleManuale)}</span></div>
            <div className="border-t pt-3 flex justify-between text-lg font-bold"><span>Totale</span><span className="text-navy-600">{fmt(preview.totale)}</span></div>
          </div>
        </Card>
      </div>

      <div className="flex justify-end gap-3">
        <Btn v="secondary" icon={<IcDl s={16}/>} onClick={exportPdf}>{generatingPdf?'Generazione PDF...':'Genera PDF'}</Btn>
        <Btn v="secondary" icon={<IcCheck s={16}/>} onClick={confirmQuote}>{confirming?'Conferma in corso...':'Conferma e apri scheda'}</Btn>
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva preventivo</Btn>
      </div>
    </div>
  </Modal>);
}

function AssistenzaInterventoModal({intervento,clienti,stampanti,defaultClientId,onClose,onSaved,onManagePrinters}){
  const{user}=useContext(AuthCtx);
  const{showToast,settings}=useContext(DataCtx);
  const def={
    cliente_id:defaultClientId?String(defaultClientId):'',
    stampante_id:'',
    categoria_attivita:'assistenza',
    stato_attivita:'completata',
    data_intervento:today(),
    orario_intervento:'',
    data_pianificata:today(),
    ora_inizio_prevista:'',
    ora_fine_prevista:'',
    ora_inizio_effettiva:'',
    ora_fine_effettiva:'',
    modalita:'remoto',
    titolo:'',
    problematica:'',
    attivita_svolta:'',
    esito:'',
    tempo_minuti:0,
    tempo_viaggio_minuti:0,
    tempo_lavoro_minuti:0,
    tempo_supporto_minuti:0,
    km_trasferta:0,
    fatturabile:'si',
    fonte_ricavo:'',
    ricavo_previsto:0,
    ricavo_consuntivo:0,
    costo_previsto:0,
    costo_consuntivo:0,
    luogo_intervento:'',
    priorita:'media',
    esito_commerciale:'',
    materiali_usati:'',
    macchina_modello:'',
    numero_seriale:'',
    scheda_tecnica_file:'',
    scheda_tecnica_original_name:'',
    test_ugelli_file:'',
    test_ugelli_original_name:'',
    cliente_email_firma:'',
    firma_cliente_nome:'',
    firma_cliente_status:'unsigned',
    firma_cliente_method:'',
    firma_cliente_signed_at:'',
    firma_cliente_signature_data:'',
    firma_cliente_otp_expires_at:'',
  };
  const[f,setF]=useState(intervento?{...def,...intervento,cliente_id:String(intervento.cliente_id||defaultClientId||'')}:{...def});
  const[savedInterventoId,setSavedInterventoId]=useState(intervento?.id||null);
  const[contrattoCliente,setContrattoCliente]=useState(null);
  const[selectedClient,setSelectedClient]=useState(clienti.find(c=>String(c.id)===String(intervento?.cliente_id||defaultClientId||''))||null);
  const selectedPrinter=useMemo(()=>stampanti.find(item=>String(item.id)===String(f.stampante_id||''))||null,[stampanti,f.stampante_id]);
  const[otpCode,setOtpCode]=useState('');
  const[otpSending,setOtpSending]=useState(false);
  const[otpVerifying,setOtpVerifying]=useState(false);
  const[signatureSaving,setSignatureSaving]=useState(false);
  const signatureCanvasRef=useRef(null);
  const signatureDrawingRef=useRef(false);
  const signatureHasStrokeRef=useRef(false);
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const currentInterventoId=savedInterventoId||intervento?.id||null;
  useEffect(()=>{
    setSelectedClient(clienti.find(c=>String(c.id)===String(f.cliente_id||''))||null);
  },[clienti,f.cliente_id]);
  useEffect(()=>{
    if(!selectedPrinter)return;
    setF(prev=>({
      ...prev,
      macchina_modello:selectedPrinter.modello||prev.macchina_modello||'',
      numero_seriale:selectedPrinter.numero_seriale||prev.numero_seriale||'',
    }));
  },[selectedPrinter]);
  useEffect(()=>{
    if(!selectedClient)return;
    setF(prev=>({
      ...prev,
      cliente_email_firma:prev.cliente_email_firma||selectedClient.email||'',
      firma_cliente_nome:prev.firma_cliente_nome||([selectedClient.nome,selectedClient.cognome].filter(Boolean).join(' ').trim()||selectedClient.azienda||''),
    }));
  },[selectedClient]);
  useEffect(()=>{
    const travel=parseInt(f.tempo_viaggio_minuti)||0;
    const work=parseInt(f.tempo_lavoro_minuti)||0;
    const support=parseInt(f.tempo_supporto_minuti)||0;
    const total=Math.max(0,travel)+Math.max(0,work)+Math.max(0,support);
    if(total>0&&parseInt(f.tempo_minuti)!==total){
      setF(prev=>({...prev,tempo_minuti:total}));
    }
  },[f.tempo_viaggio_minuti,f.tempo_lavoro_minuti,f.tempo_supporto_minuti]);
  useEffect(()=>{
    const cid=parseInt(f.cliente_id)||0;
    if(!cid){setContrattoCliente(null);return;}
    let cancelled=false;
    (async()=>{
      try{
        const d=await api('cliente_contratto_assistenza','GET',null,{cliente_id:cid});
        if(!cancelled)setContrattoCliente(d.data||null);
      }catch(e){
        if(!cancelled)setContrattoCliente(null);
      }
    })();
    return()=>{cancelled=true};
  },[f.cliente_id,currentInterventoId,f.ore_contratto_residue]);
  const contrattoAttivoModal=contrattoCliente?.attivo||f.contratto_cliente_attivo||intervento?.contratto_cliente_attivo||null;
  const oreImpiegateModal=oreDaMinuti(f.tempo_minuti||0);
  const oreGiaAddebitate=Number(f.ore_contratto_utilizzate||0);
  const oreResiduePreview=contrattoAttivoModal?Math.round((Number(contrattoAttivoModal.ore_residue||0)+oreGiaAddebitate-oreImpiegateModal)*100)/100:null;

  const prepareSignatureCanvas=()=>{
    const canvas=signatureCanvasRef.current;
    if(!canvas)return null;
    canvas.width=800;
    canvas.height=220;
    const ctx=canvas.getContext('2d');
    if(!ctx)return null;
    ctx.fillStyle='#ffffff';
    ctx.fillRect(0,0,canvas.width,canvas.height);
    ctx.lineCap='round';
    ctx.lineJoin='round';
    ctx.lineWidth=2.4;
    ctx.strokeStyle='#0f172a';
    return ctx;
  };

  useEffect(()=>{
    const ctx=prepareSignatureCanvas();
    if(!ctx)return;
    if(f.firma_cliente_signature_data){
      signatureHasStrokeRef.current=true;
      const img=new Image();
      img.onload=()=>{
        ctx.fillStyle='#ffffff';
        ctx.fillRect(0,0,800,220);
        ctx.drawImage(img,0,0,800,220);
      };
      img.src=f.firma_cliente_signature_data;
    }else{
      signatureHasStrokeRef.current=false;
    }
  },[f.firma_cliente_signature_data]);

  const signaturePoint=evt=>{
    const canvas=signatureCanvasRef.current;
    if(!canvas)return null;
    const rect=canvas.getBoundingClientRect();
    const source=(evt.touches&&evt.touches[0])||(evt.changedTouches&&evt.changedTouches[0])||evt;
    if(!source)return null;
    return{
      x:(source.clientX-rect.left)*(canvas.width/rect.width),
      y:(source.clientY-rect.top)*(canvas.height/rect.height),
    };
  };
  const beginSignature=evt=>{
    const canvas=signatureCanvasRef.current;
    const ctx=canvas?canvas.getContext('2d'):null;
    const point=signaturePoint(evt);
    if(!ctx||!point)return;
    evt.preventDefault?.();
    signatureDrawingRef.current=true;
    signatureHasStrokeRef.current=true;
    ctx.beginPath();
    ctx.moveTo(point.x,point.y);
  };
  const moveSignature=evt=>{
    if(!signatureDrawingRef.current)return;
    const canvas=signatureCanvasRef.current;
    const ctx=canvas?canvas.getContext('2d'):null;
    const point=signaturePoint(evt);
    if(!ctx||!point)return;
    evt.preventDefault?.();
    ctx.lineTo(point.x,point.y);
    ctx.stroke();
  };
  const endSignature=evt=>{
    if(!signatureDrawingRef.current)return;
    evt.preventDefault?.();
    signatureDrawingRef.current=false;
  };
  const clearSignatureCanvas=()=>{
    prepareSignatureCanvas();
    signatureHasStrokeRef.current=false;
  };

  const uploadAttachment=async(prefix,file)=>{
    if(!file)return;
    try{
      const reader=new FileReader();
      const b64=await new Promise((res,rej)=>{reader.onload=()=>res(String(reader.result||'').split(',')[1]||'');reader.onerror=rej;reader.readAsDataURL(file)});
      const d=await api('assistenza_file_upload','POST',{file_data:b64,file_name:file.name});
      u(`${prefix}_file`,d.filename||'');
      u(`${prefix}_original_name`,d.original_name||file.name);
      showToast('Allegato caricato');
    }catch(e){showToast(e.message,'error')}
  };

  const removeAttachment=async(prefix)=>{
    const filename=f[`${prefix}_file`];
    if(!filename)return;
    try{
      await api('assistenza_file_delete','DELETE',null,{filename});
      u(`${prefix}_file`,'');
      u(`${prefix}_original_name`,'');
      showToast('Allegato rimosso');
    }catch(e){showToast(e.message,'error')}
  };

  const payloadFromForm=()=>({
    cliente_id:parseInt(f.cliente_id)||null,
    stampante_id:parseInt(f.stampante_id)||null,
    categoria_attivita:f.categoria_attivita||'assistenza',
    stato_attivita:f.stato_attivita||'completata',
    data_intervento:f.data_intervento||today(),
    orario_intervento:f.orario_intervento||'',
    data_pianificata:f.data_pianificata||'',
    ora_inizio_prevista:f.ora_inizio_prevista||'',
    ora_fine_prevista:f.ora_fine_prevista||'',
    ora_inizio_effettiva:f.ora_inizio_effettiva||'',
    ora_fine_effettiva:f.ora_fine_effettiva||'',
    modalita:f.modalita||'remoto',
    titolo:(f.titolo||'').trim(),
    problematica:(f.problematica||'').trim(),
    attivita_svolta:(f.attivita_svolta||'').trim(),
    esito:(f.esito||'').trim(),
    tempo_minuti:parseInt(f.tempo_minuti)||0,
    tempo_viaggio_minuti:parseInt(f.tempo_viaggio_minuti)||0,
    tempo_lavoro_minuti:parseInt(f.tempo_lavoro_minuti)||0,
    tempo_supporto_minuti:parseInt(f.tempo_supporto_minuti)||0,
    km_trasferta:parseFloat(f.km_trasferta)||0,
    fatturabile:f.fatturabile||'si',
    fonte_ricavo:(f.fonte_ricavo||'').trim(),
    ricavo_previsto:parseFloat(f.ricavo_previsto)||0,
    ricavo_consuntivo:parseFloat(f.ricavo_consuntivo)||0,
    costo_previsto:parseFloat(f.costo_previsto)||0,
    costo_consuntivo:parseFloat(f.costo_consuntivo)||0,
    luogo_intervento:(f.luogo_intervento||'').trim(),
    priorita:f.priorita||'media',
    esito_commerciale:(f.esito_commerciale||'').trim(),
    materiali_usati:(f.materiali_usati||'').trim(),
    macchina_modello:(f.macchina_modello||'').trim(),
    numero_seriale:(f.numero_seriale||'').trim(),
    scheda_tecnica_file:f.scheda_tecnica_file||'',
    scheda_tecnica_original_name:f.scheda_tecnica_original_name||'',
    test_ugelli_file:f.test_ugelli_file||'',
    test_ugelli_original_name:f.test_ugelli_original_name||'',
    cliente_email_firma:(f.cliente_email_firma||'').trim(),
    firma_cliente_nome:(f.firma_cliente_nome||'').trim(),
  });

  const ensureSaved=async(showSavedToast=false)=>{
    const payload=payloadFromForm();
    if(!payload.cliente_id){showToast('Seleziona un cliente','error');return null}
    if(!payload.problematica){showToast('Inserisci la problematica','error');return null}
    if(currentInterventoId){
      const resp=await api('assistenza_intervento_update','PUT',payload,{id:currentInterventoId});
      const row=resp?.data||resp;
      if(row&&typeof row==='object')setF(prev=>({...prev,...row}));
      if(row?.contratto_avviso)showToast(row.contratto_avviso,'error');
      if(showSavedToast)showToast('Intervento aggiornato');
      return currentInterventoId;
    }
    const created=await api('assistenza_intervento_create','POST',payload);
    const row=created?.data||created;
    const newId=row?.id||created?.id||null;
    if(newId){
      setSavedInterventoId(newId);
      if(row&&typeof row==='object')setF(prev=>({...prev,...row,id:newId,codice:row.codice||prev.codice}));
      if(row?.contratto_avviso)showToast(row.contratto_avviso,'error');
      if(showSavedToast)showToast(row?.ore_contratto_utilizzate>0?'Intervento registrato · ore contratto aggiornate':'Intervento registrato');
      return newId;
    }
    return null;
  };

  const save=async(closeAfter=true)=>{
    try{
      const id=await ensureSaved(true);
      if(!id)return;
      if(closeAfter)onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  const sendOtp=async()=>{
    try{
      setOtpSending(true);
      const id=await ensureSaved(false);
      if(!id)return;
      const resp=await api('assistenza_intervento_send_otp','POST',{cliente_email_firma:(f.cliente_email_firma||'').trim(),firma_cliente_nome:(f.firma_cliente_nome||'').trim()},{id});
      const row=resp?.data||resp;
      if(row)setF(prev=>({...prev,...row}));
      showToast('Codice OTP inviato al cliente');
    }catch(e){showToast(e.message,'error')}
    finally{setOtpSending(false)}
  };

  const verifyOtp=async()=>{
    try{
      if(!otpCode.trim()){showToast('Inserisci il codice OTP','error');return}
      setOtpVerifying(true);
      const id=await ensureSaved(false);
      if(!id)return;
      const resp=await api('assistenza_intervento_verify_otp','POST',{otp_code:otpCode.trim(),cliente_email_firma:(f.cliente_email_firma||'').trim(),firma_cliente_nome:(f.firma_cliente_nome||'').trim()},{id});
      const row=resp?.data||resp;
      if(row)setF(prev=>({...prev,...row}));
      setOtpCode('');
      showToast('Firma OTP confermata');
    }catch(e){showToast(e.message,'error')}
    finally{setOtpVerifying(false)}
  };

  const saveDrawSignature=async()=>{
    try{
      const canvas=signatureCanvasRef.current;
      if(!canvas||!signatureHasStrokeRef.current){showToast('Fai firmare il cliente nel riquadro prima di salvare','error');return}
      setSignatureSaving(true);
      const id=await ensureSaved(false);
      if(!id)return;
      const dataUrl=canvas.toDataURL('image/png');
      const resp=await api('assistenza_intervento_sign_draw','POST',{firma_cliente_signature_data:dataUrl,cliente_email_firma:(f.cliente_email_firma||'').trim(),firma_cliente_nome:(f.firma_cliente_nome||'').trim()},{id});
      const row=resp?.data||resp;
      if(row)setF(prev=>({...prev,...row}));
      showToast('Firma su schermo salvata');
    }catch(e){showToast(e.message,'error')}
    finally{setSignatureSaving(false)}
  };

  const resetSignature=async()=>{
    if(!currentInterventoId){
      clearSignatureCanvas();
      setF(prev=>({...prev,firma_cliente_status:'unsigned',firma_cliente_method:'',firma_cliente_signed_at:'',firma_cliente_signature_data:'',firma_cliente_otp_expires_at:''}));
      setOtpCode('');
      return;
    }
    if(!confirm('Resettare la firma cliente su questa scheda?'))return;
    try{
      const resp=await api('assistenza_intervento_signature_reset','PUT',{}, {id:currentInterventoId});
      const row=resp?.data||resp;
      clearSignatureCanvas();
      if(row)setF(prev=>({...prev,...row}));
      setOtpCode('');
      showToast('Firma cliente resettata');
    }catch(e){showToast(e.message,'error')}
  };

  const signatureSignedLabel=assistenzaFirmaStatusLabel(f.firma_cliente_status||'unsigned',f.firma_cliente_method||'');
  const signatureClass=assistenzaFirmaStatusClass(f.firma_cliente_status||'unsigned',f.firma_cliente_method||'');

  return(<Modal title={currentInterventoId?'Modifica intervento assistenza':'Nuovo intervento assistenza'} onClose={onClose} wide>
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Sel label="Cliente" value={f.cliente_id} onChange={v=>u('cliente_id',v)} options={clienti.map(c=>({value:String(c.id),label:clienteDisplayName(c)}))} placeholder="Seleziona cliente" className="md:col-span-2"/>
        <Inp label="Data intervento" type="date" value={f.data_intervento||''} onChange={v=>u('data_intervento',v)}/>
        <Inp label="Orario" type="time" value={f.orario_intervento||''} onChange={v=>u('orario_intervento',v)}/>
        <Sel label="Modalità" value={f.modalita||'remoto'} onChange={v=>u('modalita',v)} options={ASSISTENZA_MODALITA}/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Sel label="Categoria attività" value={f.categoria_attivita||'assistenza'} onChange={v=>u('categoria_attivita',v)} options={ASSISTENZA_CATEGORIE}/>
        <Sel label="Stato attività" value={f.stato_attivita||'completata'} onChange={v=>u('stato_attivita',v)} options={ASSISTENZA_STATI}/>
        <Sel label="Priorità" value={f.priorita||'media'} onChange={v=>u('priorita',v)} options={ASSISTENZA_PRIORITA}/>
        <Inp label="Luogo intervento" value={f.luogo_intervento||''} onChange={v=>u('luogo_intervento',v)} placeholder="Sede cliente, showroom, officina..."/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Inp label="Data pianificata" type="date" value={f.data_pianificata||''} onChange={v=>u('data_pianificata',v)}/>
        <Inp label="Inizio previsto" type="time" value={f.ora_inizio_prevista||''} onChange={v=>u('ora_inizio_prevista',v)}/>
        <Inp label="Fine prevista" type="time" value={f.ora_fine_prevista||''} onChange={v=>u('ora_fine_prevista',v)}/>
        <Inp label="Inizio effettivo" type="time" value={f.ora_inizio_effettiva||''} onChange={v=>u('ora_inizio_effettiva',v)}/>
        <Inp label="Fine effettiva" type="time" value={f.ora_fine_effettiva||''} onChange={v=>u('ora_fine_effettiva',v)}/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-4 items-end">
        <Sel label="Stampante cliente" value={f.stampante_id||''} onChange={v=>u('stampante_id',v)} options={stampanti.map(item=>({value:String(item.id),label:`${S(item.modello||'Stampante')} · ${S(item.numero_seriale||'senza seriale')}`}))} placeholder="Seleziona stampante registrata"/>
        <Btn v="secondary" onClick={onManagePrinters}>Gestisci stampanti</Btn>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Inp label="Tecnico" value={intervento?.tecnico_nome||user?.nome||user?.username||''} onChange={()=>{}} disabled className="md:col-span-2"/>
        <Inp label="Tempo impiegato (min)" type="number" value={f.tempo_minuti||0} onChange={v=>u('tempo_minuti',v)}/>
        <Inp label="Titolo intervento" value={f.titolo||''} onChange={v=>u('titolo',v)} placeholder="Sostituzione testina, pulizia, diagnosi..."/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Inp label="Min. lavoro" type="number" value={f.tempo_lavoro_minuti||0} onChange={v=>u('tempo_lavoro_minuti',v)}/>
        <Inp label="Min. viaggio" type="number" value={f.tempo_viaggio_minuti||0} onChange={v=>u('tempo_viaggio_minuti',v)}/>
        <Inp label="Min. supporto" type="number" value={f.tempo_supporto_minuti||0} onChange={v=>u('tempo_supporto_minuti',v)}/>
        <Inp label="KM trasferta" type="number" value={f.km_trasferta||0} onChange={v=>u('km_trasferta',v)}/>
        <Sel label="Fatturabilità" value={f.fatturabile||'si'} onChange={v=>u('fatturabile',v)} options={ASSISTENZA_FATTURABILITA}/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Inp label="Fonte ricavo" value={f.fonte_ricavo||''} onChange={v=>u('fonte_ricavo',v)} placeholder="Contratto, extra, demo, interno..."/>
        <Inp label="Ricavo previsto" type="number" value={f.ricavo_previsto||0} onChange={v=>u('ricavo_previsto',v)}/>
        <Inp label="Ricavo consuntivo" type="number" value={f.ricavo_consuntivo||0} onChange={v=>u('ricavo_consuntivo',v)}/>
        <Inp label="Costo previsto" type="number" value={f.costo_previsto||0} onChange={v=>u('costo_previsto',v)}/>
        <Inp label="Costo consuntivo" type="number" value={f.costo_consuntivo||0} onChange={v=>u('costo_consuntivo',v)}/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Esito commerciale / follow-up</label>
        <textarea value={f.esito_commerciale||''} onChange={e=>u('esito_commerciale',e.target.value)} rows={2} className="w-full px-4 py-3 border rounded-2xl text-sm outline-none focus:ring-2 focus:ring-accent-400" placeholder="Supporto al commerciale, demo riuscita, approfondimenti richiesti..."/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Macchina / Modello" value={f.macchina_modello||''} onChange={v=>u('macchina_modello',v)} placeholder="Mimaki CJV150"/>
        <Inp label="Numero seriale" value={f.numero_seriale||''} onChange={v=>u('numero_seriale',v)} placeholder="SN-..."/>
      </div>
      {selectedClient?<div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
        <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Cartella cliente</div>
        <div className="font-semibold text-navy-600">{clienteDisplayName(selectedClient)}</div>
        <div className="text-sm text-slate-500 mt-1">{[selectedClient.via,selectedClient.cap,selectedClient.citta,normalizeProvincia(selectedClient.provincia),selectedClient.regione].filter(Boolean).join(' · ')}</div>
        <div className="text-sm text-slate-500">{[selectedClient.telefono,selectedClient.email].filter(Boolean).join(' · ')}</div>
      </div>:null}
      {contrattoAttivoModal?<div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
        <div className="text-xs uppercase tracking-wide text-emerald-800 font-semibold mb-2">Contratto assistenza · {contrattoAttivoModal.pacchetto_label||'ore'}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-slate-500 block text-xs">Ore residue attuali</span><strong className="text-emerald-900">{fmtOreContratto(contrattoAttivoModal.ore_residue)}</strong></div>
          <div><span className="text-slate-500 block text-xs">Ore questo intervento</span><strong>{fmtOreContratto(oreImpiegateModal)}</strong></div>
          <div><span className="text-slate-500 block text-xs">Ore residue dopo salvataggio</span><strong className={oreResiduePreview!=null&&oreResiduePreview<0?'text-rose-700':'text-navy-700'}>{oreResiduePreview!=null?fmtOreContratto(oreResiduePreview):'—'}</strong></div>
          <div><span className="text-slate-500 block text-xs">Scadenza</span><strong>{fmtDate(contrattoAttivoModal.data_fine)}</strong></div>
        </div>
        <p className="text-xs text-slate-600 mt-2">Al salvataggio le ore impiegate vengono scalate automaticamente dal contratto. Compare anche in firma elettronica e PDF.</p>
      </div>:f.cliente_id?<p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">Nessun contratto ore attivo per questo cliente (attivalo dalla scheda cliente).</p>:null}
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Problematica</label>
        <textarea value={f.problematica||''} onChange={e=>u('problematica',e.target.value)} rows={4} className="w-full px-4 py-3 border rounded-2xl text-sm outline-none focus:ring-2 focus:ring-accent-400" placeholder="Descrivi il problema segnalato dal cliente"/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Attività svolta</label>
        <textarea value={f.attivita_svolta||''} onChange={e=>u('attivita_svolta',e.target.value)} rows={4} className="w-full px-4 py-3 border rounded-2xl text-sm outline-none focus:ring-2 focus:ring-accent-400" placeholder="Spiega cosa è stato fatto durante l'intervento"/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Esito intervento</label>
        <textarea value={f.esito||''} onChange={e=>u('esito',e.target.value)} rows={3} className="w-full px-4 py-3 border rounded-2xl text-sm outline-none focus:ring-2 focus:ring-accent-400" placeholder="Esito finale, macchina ripristinata, attività da programmare..."/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Materiale utilizzato</label>
        <textarea value={f.materiali_usati||''} onChange={e=>u('materiali_usati',e.target.value)} rows={3} className="w-full px-4 py-3 border rounded-2xl text-sm outline-none focus:ring-2 focus:ring-accent-400" placeholder="Ricambi, solventi, tubi, inchiostro, accessori, consumo materiali..."/>
      </div>
      <Card title="Firma cliente" action={<span className={`text-xs px-3 py-1 rounded-full border ${signatureClass}`}>{signatureSignedLabel}</span>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Inp label="Nome firmatario" value={f.firma_cliente_nome||''} onChange={v=>u('firma_cliente_nome',v)} placeholder="Nome e cognome cliente"/>
          <Inp label="Email firma / OTP" type="email" value={f.cliente_email_firma||''} onChange={v=>u('cliente_email_firma',v)} placeholder="cliente@azienda.it"/>
        </div>
        {contrattoAttivoModal&&(oreImpiegateModal>0||oreResiduePreview!=null)?<div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
          <div className="font-semibold text-navy-600 mb-1">Riepilogo ore contratto (visibile al cliente in firma)</div>
          <div className="flex flex-wrap gap-4">
            <span><span className="text-slate-500">Impiegate:</span> <strong>{fmtOreContratto(oreImpiegateModal)}</strong></span>
            <span><span className="text-slate-500">Residue dopo intervento:</span> <strong className={oreResiduePreview!=null&&oreResiduePreview<0?'text-rose-700':''}>{oreResiduePreview!=null?fmtOreContratto(oreResiduePreview):'—'}</strong></span>
          </div>
        </div>:null}
        <div className="text-xs text-slate-500 mt-3">
          {currentInterventoId?'Puoi usare OTP via email oppure la firma diretta sullo schermo.':'Salva prima la scheda, poi potrai inviare OTP o raccogliere la firma sul display.'}
        </div>
        <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50">
            <div className="flex items-center gap-2 text-navy-600 font-semibold"><IcMail s={16}/>Firma con OTP via email</div>
            <div className="text-sm text-slate-500 mt-2">Il cliente riceve un codice e la scheda viene firmata quando lo inserisci qui.</div>
            <div className="flex gap-2 flex-wrap mt-4">
              <Btn v="secondary" onClick={sendOtp} disabled={otpSending||!f.cliente_email_firma}>{otpSending?'Invio OTP...':'Invia codice OTP'}</Btn>
              <Btn v="ghost" onClick={resetSignature}>Reset firma</Btn>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-3 mt-4 items-end">
              <Inp label="Codice ricevuto" value={otpCode} onChange={v=>setOtpCode(v)} placeholder="Inserisci il codice a 6 cifre"/>
              <Btn v="success" onClick={verifyOtp} disabled={otpVerifying||!otpCode}>{otpVerifying?'Verifica...':'Conferma codice'}</Btn>
            </div>
            {f.firma_cliente_status==='pending'&&f.firma_cliente_otp_expires_at?<div className="text-xs text-amber-700 mt-3">Codice attivo fino al {fmtDateTime(f.firma_cliente_otp_expires_at)}</div>:null}
            {f.firma_cliente_status==='signed'&&f.firma_cliente_method==='otp'?<div className="text-xs text-green-700 mt-3">Firma OTP confermata {f.firma_cliente_signed_at?`il ${fmtDateTime(f.firma_cliente_signed_at)}`:''}</div>:null}
          </div>
          <div className="border border-slate-200 rounded-2xl p-4 bg-white">
            <div className="flex items-center gap-2 text-navy-600 font-semibold"><IcEdit s={16}/>Firma sullo schermo</div>
            <div className="text-sm text-slate-500 mt-2">Il cliente può firmare con dito, pennino, mouse o trackpad.</div>
            <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white overflow-hidden">
              <canvas
                ref={signatureCanvasRef}
                className="w-full h-[220px] touch-none bg-white"
                onMouseDown={beginSignature}
                onMouseMove={moveSignature}
                onMouseUp={endSignature}
                onMouseLeave={endSignature}
                onTouchStart={beginSignature}
                onTouchMove={moveSignature}
                onTouchEnd={endSignature}
              />
            </div>
            <div className="flex gap-2 flex-wrap mt-4">
              <Btn v="secondary" onClick={clearSignatureCanvas}>Pulisci riquadro</Btn>
              <Btn v="success" onClick={saveDrawSignature} disabled={signatureSaving}>{signatureSaving?'Salvataggio firma...':'Salva firma su schermo'}</Btn>
            </div>
            {f.firma_cliente_status==='signed'&&f.firma_cliente_method==='draw'?<div className="text-xs text-emerald-700 mt-3">Firma grafica acquisita {f.firma_cliente_signed_at?`il ${fmtDateTime(f.firma_cliente_signed_at)}`:''}</div>:null}
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          {prefix:'scheda_tecnica',title:'Scheda tecnica intervento'},
          {prefix:'test_ugelli',title:'Test ugelli'},
        ].map(item=><div key={item.prefix} className="border border-slate-200 rounded-2xl p-4 bg-slate-50">
          <div className="font-semibold text-navy-600 mb-3">{item.title}</div>
          {f[`${item.prefix}_file`]?
            <div className="space-y-2">
              <div className="text-sm text-slate-600 break-all">{S(f[`${item.prefix}_original_name`]||f[`${item.prefix}_file`])}</div>
              <div className="flex gap-2 flex-wrap">
                <Btn s="sm" v="secondary" onClick={()=>downloadStoredFile(f[`${item.prefix}_file`],f[`${item.prefix}_original_name`]||'')}>Scarica</Btn>
                <Btn s="sm" v="danger" onClick={()=>removeAttachment(item.prefix)}>Rimuovi</Btn>
              </div>
            </div>:
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100">
              <IcUpload s={16}/>Carica file
              <input type="file" className="hidden" onChange={e=>uploadAttachment(item.prefix,e.target.files?.[0])}/>
            </label>}
        </div>)}
      </div>
      <div className="flex justify-end gap-3 flex-wrap">
        {currentInterventoId?<Btn v="secondary" icon={<IcDl s={16}/>} onClick={async()=>{try{await exportAssistenzaInterventoPdf({...intervento,...f,id:currentInterventoId,tecnico_nome:intervento?.tecnico_nome||user?.nome||user?.username||''},selectedClient,settings);showToast('PDF scheda intervento generato')}catch(e){showToast(`Errore PDF: ${e.message}`,'error')}}}>Genera PDF</Btn>:null}
        {!currentInterventoId?<Btn v="secondary" onClick={()=>save(false)}>Salva e continua firma</Btn>:null}
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={()=>save(true)}>{currentInterventoId?'Salva intervento':'Salva e chiudi'}</Btn>
      </div>
    </div>
  </Modal>);
}

function AssistenzaPage(){
  const{activeArticles,ricambiArticles,settings}=useContext(DataCtx);
  const{showToast}=useContext(DataCtx);
  const{user,isAdmin,isManager}=useContext(AuthCtx);
  const{isPhone}=useDevice();
  const[section,setSection]=useState('clienti');
  const[clienti,setClienti]=useState([]);
  const[search,setSearch]=useState('');
  const[selectedClientId,setSelectedClientId]=useState('');
  const[interventi,setInterventi]=useState([]);
  const[allInterventi,setAllInterventi]=useState([]);
  const[stampanti,setStampanti]=useState([]);
  const[preventiviIntervento,setPreventiviIntervento]=useState([]);
  const[dashboardPreventivi,setDashboardPreventivi]=useState([]);
  const[assistenzaStats,setAssistenzaStats]=useState({totals:{},by_category:[],by_technician:[],by_client:[],by_billing:[]});
  const[clientsLoading,setClientsLoading]=useState(true);
  const[workspaceLoading,setWorkspaceLoading]=useState(false);
  const[dashboardLoading,setDashboardLoading]=useState(false);
  const[showModal,setShowModal]=useState(false);
  const[editIntervento,setEditIntervento]=useState(null);
  const[showPrinterModal,setShowPrinterModal]=useState(false);
  const[editStampante,setEditStampante]=useState(null);
  const[showQuoteModal,setShowQuoteModal]=useState(false);
  const[editQuote,setEditQuote]=useState(null);
  const[quoteIntervento,setQuoteIntervento]=useState(null);
  const[calendarMonth,setCalendarMonth]=useState(()=>new Date(new Date().getFullYear(),new Date().getMonth(),1));
  const skipSelectedClientReloadRef=useRef('');
  const dashboardLoadedRef=useRef(false);

  const loadClientFolders=useCallback(async()=>{
    setClientsLoading(true);
    try{
      const d=await api('assistenza_clienti');
      const rows=d.data||[];
      setClienti(rows);
      return rows;
    }finally{
      setClientsLoading(false);
    }
  },[]);

  const loadInterventi=useCallback(async(clienteId)=>{
    const d=await api('assistenza_interventi','GET',null,clienteId?{cliente_id:clienteId}:{});
    setInterventi(d.data||[]);
  },[]);

  const loadStampanti=useCallback(async(clienteId)=>{
    if(!clienteId){setStampanti([]);return}
    const d=await api('assistenza_stampanti','GET',null,{cliente_id:clienteId});
    setStampanti(d.data||[]);
  },[]);

  const loadPreventiviIntervento=useCallback(async(clienteId)=>{
    if(!clienteId){setPreventiviIntervento([]);return}
    const d=await api('assistenza_preventivi','GET',null,{cliente_id:clienteId});
    setPreventiviIntervento(d.data||[]);
  },[]);
  const loadDashboardData=useCallback(async()=>{
    setDashboardLoading(true);
    try{
      const[dashQuotes,allRows,statsRows]=await Promise.all([
        api('assistenza_preventivi_dashboard'),
        api('assistenza_interventi'),
        api('assistenza_stats'),
      ]);
      setDashboardPreventivi(dashQuotes.data||[]);
      setAllInterventi(allRows.data||[]);
      setAssistenzaStats(statsRows.data||{totals:{},by_category:[],by_technician:[],by_client:[],by_billing:[]});
      dashboardLoadedRef.current=true;
    }finally{
      setDashboardLoading(false);
    }
  },[]);

  const loadClientWorkspace=useCallback(async(targetClientId)=>{
    if(!targetClientId){
      setInterventi([]);
      setStampanti([]);
      setPreventiviIntervento([]);
      return;
    }
    setWorkspaceLoading(true);
    try{
      await Promise.all([loadInterventi(targetClientId),loadStampanti(targetClientId),loadPreventiviIntervento(targetClientId)]);
    }catch(e){showToast(e.message,'error')}
    finally{setWorkspaceLoading(false)}
  },[loadInterventi,loadPreventiviIntervento,loadStampanti,showToast]);

  const refresh=useCallback(async(targetClientId='')=>{
    try{
      const rows=await loadClientFolders();
      const nextId=String(targetClientId||selectedClientId||rows[0]?.id||'');
      skipSelectedClientReloadRef.current=nextId;
      setSelectedClientId(nextId);
      if(nextId){
        loadClientWorkspace(nextId);
      }
      if(section!=='clienti' || dashboardLoadedRef.current){
        loadDashboardData().catch(e=>showToast(e.message,'error'));
      }
    }catch(e){showToast(e.message,'error')}
  },[loadClientFolders,loadDashboardData,loadClientWorkspace,selectedClientId,section,showToast]);

  useEffect(()=>{
    refresh();
  },[]);

  const filteredClienti=useMemo(()=>{
    const s=search.trim().toLowerCase();
    if(!s)return clienti;
    return clienti.filter(c=>{
      const hay=[c.azienda,c.nome,c.cognome,c.citta,c.provincia,c.regione,c.email,c.telefono].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(s);
    });
  },[clienti,search]);

  useEffect(()=>{
    if(selectedClientId&&filteredClienti.some(c=>String(c.id)===String(selectedClientId)))return;
    if(filteredClienti[0])setSelectedClientId(String(filteredClienti[0].id));
  },[filteredClienti,selectedClientId]);

  useEffect(()=>{
    if(!selectedClientId)return;
    if(skipSelectedClientReloadRef.current===String(selectedClientId)){
      skipSelectedClientReloadRef.current='';
      return;
    }
    loadClientWorkspace(selectedClientId).catch(e=>showToast(e.message,'error'));
  },[selectedClientId,loadClientWorkspace,showToast]);

  useEffect(()=>{
    if(['dashboard','agenda','statistiche'].includes(section) && !dashboardLoadedRef.current && !dashboardLoading){
      loadDashboardData().catch(e=>showToast(e.message,'error'));
    }
  },[section,dashboardLoading,loadDashboardData,showToast]);

  const selectedClient=clienti.find(c=>String(c.id)===String(selectedClientId))||null;
  const totalInterventi=interventi.length;
  const totalMinutes=interventi.reduce((sum,item)=>sum+(parseInt(item.tempo_minuti)||0),0);
  const totalInterventiAll=allInterventi.length;
  const quoteMapByIntervento=useMemo(()=>{
    const map={};
    preventiviIntervento.forEach(item=>{
      const key=String(item.intervento_id||'0');
      if(!map[key])map[key]=[];
      map[key].push(item);
    });
    return map;
  },[preventiviIntervento]);
  const dashboardOpenQuotes=useMemo(()=>dashboardPreventivi.filter(item=>String(item.stato||'bozza').toLowerCase()!=='confermato'),[dashboardPreventivi]);
  const dashboardConfirmedQuotes=useMemo(()=>dashboardPreventivi.filter(item=>String(item.stato||'bozza').toLowerCase()==='confermato'),[dashboardPreventivi]);
  const upcomingPlannedEvents=useMemo(()=>{
    const merged=allInterventi
      .filter(item=>String(item.data_pianificata||item.data_intervento||'').trim())
      .map(item=>({
        id:item.id,
        cliente_id:item.cliente_id,
        date:String(item.data_pianificata||item.data_intervento||'').slice(0,10),
        time:String(item.ora_inizio_prevista||item.orario_intervento||'').slice(0,5),
        title:item.titolo||item.problematica||'Intervento tecnico',
        clientName:clienteDisplayName(item),
        machine:item.stampante_modello||item.macchina_modello||'',
        category:item.categoria_attivita,
        kind:'intervento',
      }))
      .sort((a,b)=>`${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
    return merged;
  },[allInterventi]);
  const calendarEvents=useMemo(()=>{
    const eventsFromInterventi=allInterventi
      .filter(item=>String(item.data_pianificata||item.data_intervento||'').trim())
      .map(item=>({
        id:`int-${item.id}`,
        cliente_id:item.cliente_id,
        date:String(item.data_pianificata||item.data_intervento||'').slice(0,10),
        time:String(item.ora_inizio_prevista||item.orario_intervento||'').slice(0,5),
        title:item.titolo||item.problematica||assistenzaCategoriaLabel(item.categoria_attivita),
        clientName:clienteDisplayName(item),
        machine:item.stampante_modello||item.macchina_modello||'',
        kind:'intervento',
      }));
    const eventsFromQuotes=dashboardConfirmedQuotes
      .filter(item=>String(item.data_prevista_intervento||'').trim())
      .map(item=>({
        id:`quote-${item.id}`,
        cliente_id:item.cliente_id,
        date:String(item.data_prevista_intervento||'').slice(0,10),
        time:String(item.orario_previsto_intervento||'').slice(0,5),
        title:item.stampante_modello?`Intervento ${item.stampante_modello}`:(item.intervento_titolo||'Intervento pianificato'),
        clientName:clienteDisplayName(item),
        machine:item.stampante_modello||'',
        kind:'confermato',
      }));
    return [...eventsFromInterventi,...eventsFromQuotes];
  },[allInterventi,dashboardConfirmedQuotes]);
  const statsProblematiche=useMemo(()=>{
    const map={};
    allInterventi.forEach(item=>{
      const key=assistenzaNormalizeIssue(item.problematica||'');
      map[key]=(map[key]||0)+1;
    });
    return Object.entries(map).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value).slice(0,8);
  },[allInterventi]);
  const statsMacchine=useMemo(()=>{
    const map={};
    allInterventi.forEach(item=>{
      const key=S(item.stampante_modello||item.macchina_modello||'Macchina non indicata');
      map[key]=(map[key]||0)+1;
    });
    return Object.entries(map).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value).slice(0,8);
  },[allInterventi]);
  const statsAziende=useMemo(()=>{
    const map={};
    allInterventi.forEach(item=>{
      const key=clienteDisplayName(item);
      map[key]=(map[key]||0)+1;
    });
    return Object.entries(map).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value).slice(0,8);
  },[allInterventi]);
  const statsModalita=useMemo(()=>{
    const map={};
    allInterventi.forEach(item=>{
      const key=assistenzaModeLabel(item.modalita);
      map[key]=(map[key]||0)+1;
    });
    return Object.entries(map).map(([label,value])=>({label,value})).sort((a,b)=>b.value-a.value);
  },[allInterventi]);

  const deleteIntervento=async(item)=>{
    if(!confirm('Eliminare questo intervento di assistenza?'))return;
    try{
      await api('assistenza_intervento_delete','DELETE',null,{id:item.id});
      showToast('Intervento eliminato');
      refresh(selectedClientId);
    }catch(e){showToast(e.message,'error')}
  };

  const deleteStampante=async(item)=>{
    if(!confirm('Eliminare questa stampante cliente?'))return;
    try{
      await api('assistenza_stampante_delete','DELETE',null,{id:item.id});
      showToast('Stampante eliminata');
      refresh(selectedClientId);
    }catch(e){showToast(e.message,'error')}
  };

  const deleteQuote=async(item)=>{
    if(!confirm('Eliminare questo preventivo assistenza tecnica?'))return;
    try{
      await api('assistenza_preventivo_delete','DELETE',null,{id:item.id});
      showToast('Preventivo assistenza tecnica eliminato');
      refresh(selectedClientId);
    }catch(e){showToast(e.message,'error')}
  };
  const openConfirmedIntervento=async(interventoId)=>{
    setShowQuoteModal(false);
    try{
      const d=await api('assistenza_interventi','GET',null,{cliente_id:selectedClientId});
      const rows=d.data||[];
      setInterventi(rows);
      await loadPreventiviIntervento(selectedClientId);
      const found=rows.find(item=>String(item.id)===String(interventoId));
      if(found){
        setEditIntervento(found);
        setShowModal(true);
      }
    }catch(e){showToast(e.message,'error')}
  };
  const createTechnicalQuote=async()=>{
    if(!selectedClientId){
      showToast('Seleziona prima un cliente','error');
      return;
    }
    try{
      const created=await api('assistenza_intervento_create','POST',{
        cliente_id:parseInt(selectedClientId),
        data_intervento:today(),
        orario_intervento:'',
        modalita:'remoto',
        titolo:'Preventivo assistenza tecnica',
        problematica:'Preventivo tecnico da definire',
        attivita_svolta:'',
        esito:'',
        tempo_minuti:0,
        materiali_usati:'',
        stampante_id:null,
        macchina_modello:'',
        numero_seriale:'',
      });
      const result=await api('assistenza_interventi','GET',null,{cliente_id:selectedClientId});
      const rows=result.data||[];
      setInterventi(rows);
      const createdItem=rows.find(item=>String(item.id)===String(created?.data?.id))||{
        id:created?.data?.id,
        codice:created?.data?.codice,
        cliente_id:selectedClientId,
        data_intervento:today(),
        modalita:'remoto',
        titolo:'Preventivo assistenza tecnica',
        problematica:'Preventivo tecnico da definire',
        attivita_svolta:'',
        esito:'',
        tempo_minuti:0,
        materiali_usati:'',
      };
      setQuoteIntervento(createdItem);
      setEditQuote(null);
      setShowQuoteModal(true);
      showToast('Scheda tecnica creata. Ora completa il preventivo e genera il PDF');
    }catch(e){showToast(e.message,'error')}
  };
  const openClientWorkspace=item=>{
    const clientId=String(item?.cliente_id||'');
    if(!clientId)return;
    setSection('clienti');
    setSelectedClientId(clientId);
  };

  if(clientsLoading&&!clienti.length)return<Loading/>;
  const totalClientFolders=clienti.length;
  const totalTechnicalQuotes=preventiviIntervento.length;
  const confirmedTechnicalQuotes=preventiviIntervento.filter(item=>String(item.stato||'bozza').toLowerCase()==='confermato').length;
  const pendingSignatures=interventi.filter(item=>String(item.firma_cliente_status||'unsigned')!=='signed').length;
  const totalPendingSignaturesAll=allInterventi.filter(item=>String(item.firma_cliente_status||'unsigned')!=='signed').length;
  const statTotals=assistenzaStats?.totals||{};

  return(<Page title="Assistenza" subtitle="Dashboard operativa, agenda e preventivi tecnici" actions={
    <div className="flex gap-2 flex-wrap">
      <Btn v="secondary" s={isPhone?'sm':'md'} onClick={()=>refresh(selectedClientId)}>Aggiorna</Btn>
      {!isPhone?<Btn v="secondary" icon={<IcClipboard s={16}/>} onClick={createTechnicalQuote} disabled={!selectedClient}>Nuovo preventivo tecnico</Btn>:null}
      <Btn s={isPhone?'sm':'md'} icon={<IcPlus s={isPhone?16:18}/>} onClick={()=>{setEditIntervento(null);setShowModal(true)}} disabled={!selectedClient}>Nuovo intervento</Btn>
    </div>
  }>
    <TabBarScroll tabs={assistenzaSectionTabs.map(t=>({k:t.id,l:t.label}))} active={section} onChange={setSection}/>

    {section==='dashboard'?<React.Fragment>
      <div className="stat-tile-grid">
        {[
          {label:'Clienti assistenza',value:totalClientFolders,bg:'bg-slate-50'},
          {label:'Attività tecniche',value:statTotals.attivita_totali||totalInterventiAll,bg:'bg-blue-50'},
          {label:'Ricavi assistenza',value:fmt(statTotals.ricavo_consuntivo||0),bg:'bg-amber-50'},
          {label:'Ore tecniche',value:`${Number(statTotals.ore_totali||0).toFixed(1)}h`,bg:'bg-emerald-50'},
        ].map((item,idx)=><div key={idx} className={`stat-tile ${item.bg}`}>
          <div className="text-xs text-gray-500">{item.label}</div>
          <div className="text-2xl font-bold text-navy-600">{item.value}</div>
        </div>)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><div className="text-xs text-gray-500">Ore lavoro</div><div className="text-2xl font-bold text-navy-600">{Number(statTotals.ore_lavoro||0).toFixed(1)}h</div></Card>
        <Card><div className="text-xs text-gray-500">Ore viaggio</div><div className="text-2xl font-bold text-navy-600">{Number(statTotals.ore_viaggio||0).toFixed(1)}h</div></Card>
        <Card><div className="text-xs text-gray-500">Ore supporto/demo</div><div className="text-2xl font-bold text-navy-600">{Number(statTotals.ore_supporto||0).toFixed(1)}h</div></Card>
        <Card><div className="text-xs text-gray-500">Margine consuntivo</div><div className="text-2xl font-bold text-navy-600">{fmt(statTotals.margine_consuntivo||0)}</div></Card>
      </div>

      {dashboardLoading&&!dashboardLoadedRef.current?<Card><div className="text-sm text-slate-500">Sto sincronizzando dashboard, statistiche e agenda tecnica…</div></Card>:null}
      <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_1fr] gap-6">
        <Card title="Preventivi Assistenza Aperti" action={<span className="text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-700">{dashboardOpenQuotes.length} da seguire</span>}>
          {dashboardOpenQuotes.length===0?<Empty icon={<IcClipboard s={28} c="text-accent-400"/>} title="Nessun preventivo aperto" sub="Non ci sono preventivi tecnici in bozza da seguire in questo momento."/>:
          <div className="space-y-3">{dashboardOpenQuotes.slice(0,12).map(item=><div key={item.id} className="border border-slate-200 rounded-2xl p-4 bg-white/80 min-w-0">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-navy-600">{S(item.codice)}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700">Bozza</span>
                </div>
                <div className="text-sm text-slate-600 mt-1 break-words"><strong>{clienteDisplayName(item)}</strong>{item.stampante_modello?` · ${S(item.stampante_modello)}`:''}</div>
                <div className="text-xs text-slate-400 mt-1 break-words">
                  {[item.data_prevista_intervento?`Previsto ${fmtDate(item.data_prevista_intervento)}`:'',item.orario_previsto_intervento?`ore ${S(item.orario_previsto_intervento)}`:'',`Totale ${fmt(item.totale||0)}`].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap shrink-0">
                <Btn s="sm" v="secondary" onClick={()=>openClientWorkspace(item)}>Apri cliente</Btn>
              </div>
            </div>
          </div>)}</div>}
        </Card>

        <Card title="Agenda Interventi" action={<span className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-700">{upcomingPlannedEvents.filter(item=>item.date>=today()).length} programmati</span>}>
          <div className="space-y-3">
            {upcomingPlannedEvents.filter(item=>item.date>=today()).slice(0,10).length===0?<div className="text-sm text-gray-400">Nessun intervento pianificato nei prossimi giorni.</div>:
              upcomingPlannedEvents.filter(item=>item.date>=today()).slice(0,10).map(item=><button key={`${item.id}-${item.date}`} onClick={()=>openClientWorkspace(item)} className="w-full text-left border rounded-2xl p-4 hover:border-accent-300 bg-white/80">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy-600 break-words">{S(item.title)}</div>
                    <div className="text-sm text-slate-600 mt-1 break-words">{item.clientName}</div>
                    <div className="text-xs text-slate-400 mt-1">{fmtTaskDue(item.date,item.time)}{item.machine?` · ${S(item.machine)}`:''}</div>
                  </div>
                </div>
              </button>)
            }
          </div>
        </Card>
      </div>

      <AssistenzaCalendar
        events={calendarEvents}
        monthDate={calendarMonth}
        onPrev={()=>setCalendarMonth(prev=>assistenzaAddMonths(prev,-1))}
        onNext={()=>setCalendarMonth(prev=>assistenzaAddMonths(prev,1))}
        onSelectClient={openClientWorkspace}
      />
    </React.Fragment>:null}

    {section==='agenda'?<div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-6">
        <AssistenzaCalendar
          events={calendarEvents}
          monthDate={calendarMonth}
          onPrev={()=>setCalendarMonth(prev=>assistenzaAddMonths(prev,-1))}
          onNext={()=>setCalendarMonth(prev=>assistenzaAddMonths(prev,1))}
          onSelectClient={openClientWorkspace}
        />
        <Card title="Appuntamenti Tecnici" action={<span className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-700">{upcomingPlannedEvents.filter(item=>item.date>=today()).length} pianificati</span>}>
          <div className="space-y-3">
            {upcomingPlannedEvents.filter(item=>item.date>=today()).length===0?<div className="text-sm text-gray-400">Nessun appuntamento pianificato.</div>:
              upcomingPlannedEvents.filter(item=>item.date>=today()).slice(0,18).map(item=><button key={`${item.id}-${item.date}-${item.time}`} onClick={()=>openClientWorkspace(item)} className="w-full text-left border rounded-2xl p-4 hover:border-accent-300 bg-white/80">
                <div className="font-semibold text-navy-600 break-words">{S(item.title)}</div>
                <div className="text-sm text-slate-600 mt-1">{item.clientName}</div>
                <div className="text-xs text-slate-400 mt-1">{fmtTaskDue(item.date,item.time)}{item.machine?` · ${S(item.machine)}`:''}{item.category?` · ${assistenzaCategoriaLabel(item.category)}`:''}</div>
              </button>)
            }
          </div>
        </Card>
      </div>
      <Card title="Calendario Personale Tecnico">
        <div className="text-sm text-slate-600">
          Gli appuntamenti pianificati vengono pubblicati anche nel feed calendario personale del tecnico. Se il tecnico ha attivato Google Calendar nel proprio profilo, può sottoscrivere il feed e vedere automaticamente gli appuntamenti anche sul calendario personale.
        </div>
      </Card>
    </div>:null}

    {section==='clienti'?<div className="assistenza-layout">
      <Card title="Clienti Assistenza" action={<span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600">{filteredClienti.length} visibili</span>} className="h-full">
        {filteredClienti.length===0?<Empty icon={<IcUsers s={30} c="text-accent-400"/>} title="Nessuna cartella cliente" sub="Non ci sono clienti visibili per l'assistenza."/>:
        <React.Fragment>
          <div className="relative mb-4"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input
              value={search}
              onChange={e=>setSearch(e.target.value)}
              placeholder="Cerca cliente, città o zona..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"
            />
          </div>
          <div className="text-sm text-gray-400 mb-3">
            {search.trim()?`${filteredClienti.length} risultati trovati`:`${filteredClienti.length} clienti disponibili in assistenza`}
          </div>
          <div className="divide-y assistenza-client-list">{filteredClienti.map(cliente=>{
          const active=String(selectedClientId)===String(cliente.id);
          return <button key={cliente.id} onClick={()=>setSelectedClientId(String(cliente.id))} className={`w-full text-left px-2 py-3 rounded-xl transition group ${active?'bg-accent-50':'hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-bold text-navy-600 truncate">{clienteDisplayName(cliente)}</span>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${active?'bg-accent-400 text-white':'bg-slate-100 text-slate-600'}`}>{cliente.assistenza_count||0} interventi</span>
                </div>
                <div className="text-sm text-gray-600 truncate">{[cliente.citta,normalizeProvincia(cliente.provincia),cliente.regione].filter(Boolean).join(' · ')||'Nessuna zona indicata'}</div>
                <div className="text-xs text-gray-400 mt-1 truncate">
                  {cliente.assistenza_last_at?`Ultimo intervento ${fmtDate(String(cliente.assistenza_last_at).slice(0,10))}`:'Nessun intervento registrato'}
                </div>
              </div>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${active?'bg-accent-400':'bg-slate-200 group-hover:bg-slate-300'}`}/>
            </div>
          </button>;
        })}</div>
        </React.Fragment>}
      </Card>

      {!selectedClient?<Empty icon={<IcFile s={34} c="text-accent-400"/>} title="Seleziona una cartella cliente" sub="Scegli un cliente dall'elenco per vedere il diario assistenza."/>:
      <div className="space-y-6">
        {workspaceLoading?<Card><div className="text-sm text-slate-500">Sto caricando rapidamente diario, stampanti e preventivi assistenza del cliente…</div></Card>:null}
        <Card title="Panoramica Cliente" action={<div className="flex gap-2 flex-wrap"><span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600">{totalInterventi} interventi</span><span className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-700">{fmtMinutes(totalMinutes)}</span><span className="text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700">{confirmedTechnicalQuotes} preventivi confermati</span></div>}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-400">Cliente</div>
              <div className="font-semibold text-navy-600 mt-1 break-words">{clienteDisplayName(selectedClient)}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-400">Contatti</div>
              <div className="text-sm text-slate-600 mt-1 break-words">{[selectedClient.telefono,selectedClient.email].filter(Boolean).join(' · ')||'-'}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-400">Indirizzo</div>
              <div className="text-sm text-slate-600 mt-1 break-words">{[selectedClient.via,selectedClient.cap,selectedClient.citta,normalizeProvincia(selectedClient.provincia),selectedClient.regione].filter(Boolean).join(', ')||'-'}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-400">P.IVA / CF</div>
              <div className="text-sm text-slate-600 mt-1 break-words">{[selectedClient.piva,selectedClient.cf].filter(Boolean).join(' · ')||'-'}</div>
            </div>
          </div>
        </Card>

        <Card title="Stampanti cliente" action={<Btn s="sm" icon={<IcPlus s={14}/>} onClick={()=>{setEditStampante(null);setShowPrinterModal(true)}}>Nuova stampante</Btn>}>
          {stampanti.length===0?<Empty icon={<IcPkg s={28} c="text-accent-400"/>} title="Nessuna stampante registrata" sub="Aggiungi le stampanti del cliente per attribuire correttamente gli interventi." action={<Btn onClick={()=>{setEditStampante(null);setShowPrinterModal(true)}}>Aggiungi stampante</Btn>}/>:
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">{stampanti.map(item=><div key={item.id} className="border border-slate-200 rounded-2xl p-4 bg-white/75 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-navy-600 break-words">{S(item.modello||'Stampante')}</div>
                <div className="text-sm text-slate-500 mt-1 break-words">{`Seriale: ${S(item.numero_seriale||'-')}`}</div>
                {item.ubicazione?<div className="text-xs text-slate-400 mt-1 break-words">{S(item.ubicazione)}</div>:null}
              </div>
              <div className="flex gap-2">
                <Btn s="sm" v="secondary" onClick={()=>{setEditStampante(item);setShowPrinterModal(true)}}>Modifica</Btn>
                <Btn s="sm" v="danger" onClick={()=>deleteStampante(item)}>Elimina</Btn>
              </div>
            </div>
            {item.note?<div className="text-sm text-slate-600 mt-3 whitespace-pre-wrap">{S(item.note)}</div>:null}
          </div>)}</div>}
        </Card>

        <Card title="Diario interventi" action={<div className="flex gap-2 flex-wrap"><span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600">{interventi.length} schede</span><span className="text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-700">{pendingSignatures} firme aperte</span><Btn s="sm" icon={<IcPlus s={14}/>} onClick={()=>{setEditIntervento(null);setShowModal(true)}}>Nuovo intervento</Btn></div>}>
          {interventi.length===0?<Empty icon={<IcFile s={30} c="text-accent-400"/>} title="Nessun intervento registrato" sub="Apri la prima scheda assistenza per questo cliente e inizia il diario tecnico." action={<Btn onClick={()=>{setEditIntervento(null);setShowModal(true)}}>Crea intervento</Btn>}/>:
          <div className="space-y-4">{interventi.map(item=>{
            const editable=isAdmin||isManager||(parseInt(item.tecnico_user_id)||0)===(parseInt(user?.id)||0);
            const relatedQuotes=quoteMapByIntervento[String(item.id)]||[];
            const signatureLabel=assistenzaFirmaStatusLabel(item.firma_cliente_status||'unsigned',item.firma_cliente_method||'');
            const signatureClass=assistenzaFirmaStatusClass(item.firma_cliente_status||'unsigned',item.firma_cliente_method||'');
            return <div key={item.id} className="border border-slate-200 rounded-[26px] p-4 md:p-5 bg-white/75 min-w-0">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">{S(item.codice)}</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">{assistenzaModeLabel(item.modalita)}</span>
                    <span className={`text-xs px-2 py-1 rounded-full border ${signatureClass}`}>{signatureLabel}</span>
                    <span className="text-xs text-slate-400">{fmtDate(item.data_intervento||String(item.created_at||'').slice(0,10))}</span>
                  </div>
                  <div className="font-semibold text-navy-600 mt-2 break-words">{S(item.titolo||'Intervento tecnico')}</div>
                  <div className="text-sm text-slate-500 mt-1 break-words">{`Tecnico: ${S(item.tecnico_nome||item.tecnico_user_nome||item.tecnico_username||'-')}${item.orario_intervento?` · Ore ${S(item.orario_intervento)}`:''} · Tempo: ${fmtMinutes(item.tempo_minuti||0)}`}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Btn s="sm" icon={<IcClipboard s={14}/>} onClick={()=>{setQuoteIntervento(item);setEditQuote(null);setShowQuoteModal(true)}}>Preventivo tecnico</Btn>
                  <Btn s="sm" v="secondary" icon={<IcDl s={14}/>} onClick={async()=>{try{await exportAssistenzaInterventoPdf(item,selectedClient,settings);showToast('PDF scheda intervento generato')}catch(e){showToast(`Errore PDF: ${e.message}`,'error')}}}>PDF scheda</Btn>
                  {editable?<Btn s="sm" v="secondary" icon={<IcEdit s={14}/>} onClick={()=>{setEditIntervento(item);setShowModal(true)}}>Modifica</Btn>:null}
                  {editable?<Btn s="sm" v="danger" icon={<IcTrash s={14}/>} onClick={()=>deleteIntervento(item)}>Elimina</Btn>:null}
                </div>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Problematica</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{S(item.problematica||'-')}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Attività svolta</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{S(item.attivita_svolta||'-')}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Esito</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{S(item.esito||'-')}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Materiale utilizzato</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{S(item.materiali_usati||'-')}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Macchina</div>
                  <div className="text-sm text-slate-700 break-words">{S(item.stampante_modello||item.macchina_modello||'-')}</div>
                  <div className="text-xs text-slate-500 mt-2">{`Seriale: ${S(item.stampante_numero_seriale||item.numero_seriale||'-')}`}</div>
                  {item.stampante_ubicazione?<div className="text-xs text-slate-400 mt-1 break-words">{S(item.stampante_ubicazione)}</div>:null}
                </div>
              </div>
              <div className="mt-4 border-t pt-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="text-sm font-semibold text-navy-600">Preventivi assistenza tecnica</div>
                  <Btn s="sm" v="secondary" icon={<IcPlus s={14}/>} onClick={()=>{setQuoteIntervento(item);setEditQuote(null);setShowQuoteModal(true)}}>Nuovo preventivo</Btn>
                </div>
                {relatedQuotes.length===0?<div className="text-sm text-slate-400">Nessun preventivo assistenza tecnica collegato a questa scheda.</div>:
                <div className="space-y-3">{relatedQuotes.map(quote=><div key={quote.id} className="border border-slate-200 rounded-2xl p-3 bg-slate-50">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-navy-600 break-words">{S(quote.codice)}</div>
                        <span className={`text-xs px-2 py-1 rounded-full ${String(quote.stato||'bozza').toLowerCase()==='confermato'?'bg-green-50 text-green-700':'bg-slate-100 text-slate-600'}`}>{String(quote.stato||'bozza').toLowerCase()==='confermato'?'Confermato':'Bozza'}</span>
                      </div>
                      <div className="text-sm text-slate-500 mt-1 break-words">{`${fmtDate(quote.data_preventivo||String(quote.created_at||'').slice(0,10))} · Totale ${fmt(quote.totale||0)}`}</div>
                      <div className="text-xs text-slate-400 mt-1 break-words">{`KM: ${S(quote.km_qta||0)} · Ore viaggio: ${S(quote.ore_viaggio_qta||0)} · Manodopera: ${S(quote.ore_manodopera_qta||0)}h x ${S(quote.num_tecnici||1)} tecnici · Altre righe: ${(quote.righe||[]).length}`}</div>
                      {(quote.data_prevista_intervento||quote.orario_previsto_intervento||quote.stampante_modello)?<div className="text-xs text-slate-400 mt-1 break-words">{[quote.data_prevista_intervento?`Previsto ${fmtDate(quote.data_prevista_intervento)}`:'',quote.orario_previsto_intervento?`ore ${S(quote.orario_previsto_intervento)}`:'',quote.stampante_modello?`Stampante ${S(quote.stampante_modello)}`:''].filter(Boolean).join(' · ')}</div>:null}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Btn s="sm" v="secondary" icon={<IcDl s={14}/>} onClick={async()=>{try{await exportAssistenzaPreventivoPdf(quote,selectedClient,item,settings);showToast('PDF preventivo generato')}catch(e){showToast(`Errore PDF: ${e.message}`,'error')}}}>Genera PDF</Btn>
                      <Btn s="sm" v="secondary" onClick={()=>{setEditIntervento(item);setShowModal(true)}}>Scheda intervento</Btn>
                      <Btn s="sm" v="secondary" onClick={()=>{setQuoteIntervento(item);setEditQuote(quote);setShowQuoteModal(true)}}>Modifica</Btn>
                      <Btn s="sm" v="danger" onClick={()=>deleteQuote(quote)}>Elimina</Btn>
                    </div>
                  </div>
                </div>)}</div>}
              </div>
              {(item.scheda_tecnica_file||item.test_ugelli_file)?<div className="mt-4 flex flex-wrap gap-2">
                {item.scheda_tecnica_file?<Btn s="sm" v="secondary" icon={<IcDl s={14}/>} onClick={()=>downloadStoredFile(item.scheda_tecnica_file,item.scheda_tecnica_original_name||'scheda-tecnica')}>{`Scheda tecnica${item.scheda_tecnica_original_name?` · ${item.scheda_tecnica_original_name}`:''}`}</Btn>:null}
                {item.test_ugelli_file?<Btn s="sm" v="secondary" icon={<IcDl s={14}/>} onClick={()=>downloadStoredFile(item.test_ugelli_file,item.test_ugelli_original_name||'test-ugelli')}>{`Test ugelli${item.test_ugelli_original_name?` · ${item.test_ugelli_original_name}`:''}`}</Btn>:null}
              </div>:null}
            </div>;
          })}</div>}
        </Card>
      </div>}
    </div>:null}

    {section==='statistiche'?<div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {label:'Attività totali',value:statTotals.attivita_totali||totalInterventiAll,color:'text-navy-600'},
          {label:'Ricavo consuntivo',value:fmt(statTotals.ricavo_consuntivo||0),color:'text-emerald-700'},
          {label:'Ore totali',value:`${Number(statTotals.ore_totali||0).toFixed(1)}h`,color:'text-amber-700'},
          {label:'Margine',value:fmt(statTotals.margine_consuntivo||0),color:'text-blue-700'},
        ].map((item,idx)=><Card key={idx}>
          <div className="text-xs text-gray-500">{item.label}</div>
          <div className={`text-2xl font-bold ${item.color}`}>{item.value}</div>
        </Card>)}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card title="Attività per Categoria">
          <AssistenzaBarList items={(assistenzaStats.by_category||[]).slice(0,8)} empty="Non ci sono ancora categorie analizzabili." colorClass="bg-fuchsia-500"/>
        </Card>
        <Card title="Carico Tecnici (ore)">
          <AssistenzaBarList items={(assistenzaStats.by_technician||[]).slice(0,8).map(item=>({label:item.label,value:Number(item.ore||0).toFixed(1)}))} empty="Non ci sono ancora tecnici analizzabili." colorClass="bg-indigo-500"/>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card title="Problematiche Ricorrenti">
          <AssistenzaBarList items={statsProblematiche} empty="Non ci sono ancora problematiche analizzabili." colorClass="bg-amber-400"/>
        </Card>
        <Card title="Macchine con Più Interventi">
          <AssistenzaBarList items={statsMacchine} empty="Non ci sono ancora dati sulle macchine." colorClass="bg-cyan-500"/>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card title="Aziende con Più Richieste">
          <AssistenzaBarList items={statsAziende} empty="Non ci sono ancora aziende con storico assistenza." colorClass="bg-blue-500"/>
        </Card>
        <Card title="Fatturabilità Attività">
          <AssistenzaBarList items={assistenzaStats.by_billing||statsModalita} empty="Non ci sono ancora modalità registrate." colorClass="bg-emerald-500"/>
        </Card>
      </div>

      <Card title="Lettura Strategica">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Problema più ricorrente</div>
            <div className="font-semibold text-navy-600">{statsProblematiche[0]?.label||'Nessun dato'}</div>
            <div className="text-slate-500 mt-2">{statsProblematiche[0]?`${statsProblematiche[0].value} interventi collegati`:'Servono più dati per una lettura affidabile'}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Macchina più critica</div>
            <div className="font-semibold text-navy-600">{statsMacchine[0]?.label||'Nessun dato'}</div>
            <div className="text-slate-500 mt-2">{statsMacchine[0]?`${statsMacchine[0].value} interventi registrati`:'Servono più dati macchina per analizzare trend'}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Azienda più seguita</div>
            <div className="font-semibold text-navy-600">{statsAziende[0]?.label||'Nessun dato'}</div>
            <div className="text-slate-500 mt-2">{statsAziende[0]?`${statsAziende[0].value} interventi nello storico`:'Le statistiche compariranno con i primi interventi'}</div>
          </div>
        </div>
      </Card>
    </div>:null}

    {showModal?<AssistenzaInterventoModal intervento={editIntervento} clienti={clienti} stampanti={stampanti} defaultClientId={selectedClientId} onClose={()=>setShowModal(false)} onManagePrinters={()=>{setShowModal(false);setEditStampante(null);setShowPrinterModal(true)}} onSaved={()=>{setShowModal(false);refresh(selectedClientId)}}/>:null}
    {showPrinterModal&&selectedClient?<AssistenzaStampanteModal cliente={selectedClient} stampante={editStampante} onClose={()=>setShowPrinterModal(false)} onSaved={()=>{setShowPrinterModal(false);refresh(selectedClientId)}}/>:null}
    {showQuoteModal&&quoteIntervento&&selectedClient?<AssistenzaPreventivoModal preventivo={editQuote} intervento={quoteIntervento} cliente={selectedClient} stampanti={stampanti} activeArticles={activeArticles} ricambiArticles={ricambiArticles} settings={settings} onClose={()=>setShowQuoteModal(false)} onSaved={()=>{setShowQuoteModal(false);refresh(selectedClientId)}} onConfirmed={openConfirmedIntervento}/>:null}
  </Page>);
}

function ClienteForm({cliente,defaultTipo,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const def={azienda:'',nome:'',cognome:'',email:'',telefono:'',fax:'',cellulare:'',via:'',citta:'',cap:'',provincia:'',regione:'',categoria_merceologica:'',sito_web:'',referente:'',dealer:'',cliente_mimaki:'',cliente_mastertek:'',prodotto_mimaki:'',numero_seriale:'',fonte:'',piva:'',cf:'',nazione:'Italia',sdi:'',pec:'',pagamento:'BONIFICO 30GG',tipo:defaultTipo||'cliente',note:'',fatturato_annuo:'',numero_dipendenti:'',contratto_assistenza:0,contratto_assistenza_tipo:'',contratto_assistenza_scadenza:'',contratto_assistenza_note:''};
  const[f,setF]=useState(cliente?{...def,...cliente,tipo:cliente.tipo||'cliente',provincia:normalizeProvincia(cliente.provincia),fatturato_annuo:cliente.fatturato_annuo??'',numero_dipendenti:cliente.numero_dipendenti??'',contratto_assistenza:cliente.contratto_assistenza?1:0}:{...def});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const save=async()=>{
    try{
      if(cliente)await api('cliente_update','PUT',f,{id:cliente.id});
      else await api('cliente_create','POST',f);
      showToast(cliente?'Contatto aggiornato':'Contatto creato');onSaved();
    }catch(e){showToast(e.message,'error')}
  };
  const entityLabel=f.tipo==='potenziale'?'Potenziale':f.tipo==='rivenditore'?'Rivenditore':'Cliente';
  const title=cliente?'Modifica '+entityLabel:'Nuovo '+entityLabel;
  return(<Modal title={title} onClose={onClose} wide>
    <div className="space-y-4">
      <div className="flex gap-2">
        <button onClick={()=>u('tipo','cliente')} className={`px-4 py-2 rounded-lg text-sm font-medium border ${f.tipo==='cliente'?'bg-accent-400 text-white border-accent-400':'bg-white text-gray-600 border-gray-200'}`}>Cliente</button>
        <button onClick={()=>u('tipo','rivenditore')} className={`px-4 py-2 rounded-lg text-sm font-medium border ${f.tipo==='rivenditore'?'bg-navy-600 text-white border-navy-600':'bg-white text-gray-600 border-gray-200'}`}>Rivenditore</button>
        <button onClick={()=>u('tipo','potenziale')} className={`px-4 py-2 rounded-lg text-sm font-medium border ${f.tipo==='potenziale'?'bg-amber-500 text-white border-amber-500':'bg-white text-gray-600 border-gray-200'}`}>Potenziale</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Inp label="Azienda" value={f.azienda} onChange={v=>u('azienda',v)}/>
        <Inp label="Nome" value={f.nome} onChange={v=>u('nome',v)}/>
        <Inp label="Cognome" value={f.cognome} onChange={v=>u('cognome',v)}/>
        <Inp label="Email" value={f.email} onChange={v=>u('email',v)}/>
        <Inp label="Telefono" value={f.telefono} onChange={v=>u('telefono',v)}/>
        <Inp label="Cellulare" value={f.cellulare||''} onChange={v=>u('cellulare',v)}/>
        <Inp label="Fax" value={f.fax||''} onChange={v=>u('fax',v)}/>
        <Inp label="P.IVA" value={f.piva} onChange={v=>u('piva',v)}/>
        <Inp label="Codice Fiscale" value={f.cf} onChange={v=>u('cf',v)}/>
        <Inp label="Referente" value={f.referente||''} onChange={v=>u('referente',v)}/>
        <Inp label="Indirizzo" value={f.via} onChange={v=>u('via',v)}/>
        <Inp label="Città" value={f.citta} onChange={v=>u('citta',v)}/>
        <Inp label="CAP" value={f.cap} onChange={v=>u('cap',v)}/>
        <Inp label="Provincia" value={f.provincia} onChange={v=>u('provincia',normalizeProvincia(v))} placeholder="MO"/>
        <Inp label="Regione" value={f.regione} onChange={v=>u('regione',v)}/>
        <Inp label="Nazione" value={f.nazione||''} onChange={v=>u('nazione',v)}/>
        <ClientCategoryPicker label="Categorie merceologiche" value={f.categoria_merceologica||''} onChange={v=>u('categoria_merceologica',v)}/>
        <Inp label="Dealer" value={f.dealer||''} onChange={v=>u('dealer',v)}/>
        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={yesNoFlagToBool(f.cliente_mimaki)} onChange={e=>u('cliente_mimaki',boolToYesNo(e.target.checked))} className="w-4 h-4 accent-accent-500"/><span>Cliente Mimaki</span></label>
        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700"><input type="checkbox" checked={yesNoFlagToBool(f.cliente_mastertek)} onChange={e=>u('cliente_mastertek',boolToYesNo(e.target.checked))} className="w-4 h-4 accent-accent-500"/><span>Cliente Mastertek</span></label>
        <Inp label="Prodotto Mimaki" value={f.prodotto_mimaki||''} onChange={v=>u('prodotto_mimaki',v)}/>
        <Inp label="Numero seriale" value={f.numero_seriale||''} onChange={v=>u('numero_seriale',v)}/>
        <Inp label="Sito web" value={f.sito_web||''} onChange={v=>u('sito_web',v)}/>
        <Inp label="Fonte" value={f.fonte||''} onChange={v=>u('fonte',v)}/>
        <Inp label="SDI" value={f.sdi||''} onChange={v=>u('sdi',v)}/>
        <Inp label="PEC" value={f.pec||''} onChange={v=>u('pec',v)}/>
        <Sel label="Pagamento" value={f.pagamento} onChange={v=>u('pagamento',v)} options={['BONIFICO 30GG','BONIFICO 60GG','RIBA 30GG','RIBA 60GG','NOLEGGIO OPERATIVO','LEASING','CONTANTI']}/>
        <Inp label="Fatturato annuo stimato (€)" type="number" value={f.fatturato_annuo} onChange={v=>u('fatturato_annuo',v)}/>
        <Inp label="N. dipendenti" type="number" value={f.numero_dipendenti} onChange={v=>u('numero_dipendenti',v)}/>
        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 md:col-span-2"><input type="checkbox" checked={!!f.contratto_assistenza} onChange={e=>u('contratto_assistenza',e.target.checked?1:0)} className="w-4 h-4 accent-accent-500"/><span>Contratto assistenza attivo</span></label>
        <Sel label="Tipo contratto" value={f.contratto_assistenza_tipo||''} onChange={v=>u('contratto_assistenza_tipo',v)} options={CONTRATTO_TIPO_OPTIONS} placeholder="Tipo"/>
        <Inp label="Scadenza contratto" type="date" value={f.contratto_assistenza_scadenza||''} onChange={v=>u('contratto_assistenza_scadenza',v)}/>
        <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-600 mb-1">Note contratto</label><textarea value={f.contratto_assistenza_note||''} onChange={e=>u('contratto_assistenza_note',e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm"/></div>
      </div>
      <div><label className="block text-sm font-medium text-gray-600 mb-1">Note</label>
        <textarea value={f.note} onChange={e=>u('note',e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/></div>
      <div className="flex justify-end gap-3 pt-2">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva</Btn></div>
    </div></Modal>);
}

// ========== PREVENTIVI ==========
function PreventiviPage({onOpenPrint}){
  const{user,isAdmin,isManager}=useContext(AuthCtx);
  const{preventivi,clienti,getCliente,calcolaRiepilogo,loadPreventivi,loadClienti,loadArticoli,showToast}=useContext(DataCtx);
  const{isPhone}=useDevice();
  const[currentPrevId,setCurrentPrevId]=useState(null);
  const[search,setSearch]=useState('');
  const[statoFilter,setStatoFilter]=useState('all');
  useEffect(()=>{
    loadPreventivi({includeRighe:false}).catch(()=>{});
    loadClienti({enrich:'0',light:'1'}).catch(()=>{});
  },[loadPreventivi,loadClienti]);
  useEffect(()=>{
    if(!currentPrevId)return;
    loadArticoli().catch(()=>{});
  },[currentPrevId,loadArticoli]);

  const createNew=async(clienteId)=>{
    try{
      const body={cliente_id:clienteId||null,agente:user?.nome||'',data:today(),stato:'bozza'};
      const existingCodes=preventivi.map(p=>p.codice);
      if(cplvcrmOfflineActive()){
        body.codice=cplvcrmGenerateCodiceOfferta(user,existingCodes);
        const d=await cplvcrmQueueOfflineMutation('preventivo_create','POST',body);
        await loadPreventivi();
        setCurrentPrevId(d.data.id);
        showToast(`Offerta ${S(d.data.codice)} salvata in locale. Sincronizzazione quando torna la rete.`);
        return;
      }
      const d=await api('preventivo_create','POST',body);
      await loadPreventivi();
      if(clienteId)loadClienti(); // reflect potenziale→cliente promotion
      setCurrentPrevId(d.data.id);
    }catch(e){showToast(e.message,'error')}
  };

  const deletePrev=async(id)=>{
    const target=preventivi.find(item=>item.id===id);
    if(target&&!canDeletePreventivo(user,target)){
      showToast('Puoi eliminare solo le tue offerte in bozza, oppure serve un admin','error');
      return;
    }
    if(!confirm('Eliminare questo preventivo?'))return;
    try{await api('preventivo_delete','DELETE',null,{id});showToast('Preventivo eliminato');loadPreventivi();
      if(currentPrevId===id)setCurrentPrevId(null)}catch(e){showToast(e.message,'error')}
  };

  const duplicatePrev=async(id)=>{
    try{
      const created=await api('preventivo_duplicate','POST',null,{id});
      showToast(`Offerta duplicata: ${S(created?.data?.codice||'nuovo codice generato')}`);
      await loadPreventivi();
      setCurrentPrevId(created.data.id);
    }catch(e){showToast(e.message,'error')}
  };

  // ALL hooks MUST be called before any conditional return
  const filtered=useMemo(()=>{
    let list=[...preventivi];
    if(statoFilter!=='all')list=list.filter(p=>p.stato===statoFilter);
    if(search){const s=search.toLowerCase();list=list.filter(p=>{const cl=getCliente(p.cliente_id);
      return p.codice?.toLowerCase().includes(s)||(cl?.azienda||'').toLowerCase().includes(s)||(cl?.nome||'').toLowerCase().includes(s)||(p.agente||'').toLowerCase().includes(s)})}
    return list.sort((a,b)=>(b.updated_at||b.data||'').localeCompare(a.updated_at||a.data||''));
  },[preventivi,search,statoFilter,getCliente]);

  // If viewing a specific preventivo, show editor
  if(currentPrevId){
    return<PreventivoEditor prevId={currentPrevId} onBack={()=>setCurrentPrevId(null)} onNew={createNew} onDelete={deletePrev} onDuplicate={duplicatePrev} onOpenPrint={onOpenPrint}/>;
  }

  // List view
  return(<Page title="Preventivi" subtitle="Gestione offerte commerciali" actions={<Btn s={isPhone?'md':'lg'} icon={<IcPlus s={18}/>} onClick={()=>createNew()}>Nuovo</Btn>}>
    <PageToolbar className="!p-3 md:!p-4">
      <div className="relative flex-1 min-w-[200px]"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca preventivo..."
          className="field-shell w-full pl-10 pr-4 py-3 rounded-[22px] text-sm outline-none"/></div>
      <div className={`flex gap-2 ${isPhone?'flex-wrap w-full':'flex-wrap'}`}>
        {[{id:'all',l:'Tutti'},{id:'bozza',l:'Bozze'},{id:'inviata',l:'Inviate'},{id:'accettata',l:'Accettate'},{id:'rifiutata',l:'Rifiutate'}].map(t=>(
          <button key={t.id} onClick={()=>setStatoFilter(t.id)}
            className={`cplv-filter-pill ${statoFilter===t.id?'is-active':''}`}>{t.l}</button>))}
      </div>
    </PageToolbar>
    {filtered.length===0?(
      <Empty icon={<IcFile s={36} c="text-accent-400"/>} title={preventivi.length===0?'Crea il tuo primo preventivo':'Nessun risultato'}
        sub={preventivi.length===0?'Genera automaticamente un codice univoco':'Prova a modificare i filtri'}
        action={preventivi.length===0&&<Btn s="lg" icon={<IcPlus s={20}/>} onClick={()=>createNew()}>Nuovo Preventivo</Btn>}/>
    ):(
      <Card>
        <div className="text-sm text-gray-400 mb-3">{filtered.length} preventiv{filtered.length===1?'o':'i'}</div>
        {isPhone?<div className="data-list-phone">{filtered.map(p=>{const cl=getCliente(p.cliente_id);const r=calcolaRiepilogo(p);return(
          <div key={p.id} className="data-row-card" onClick={()=>setCurrentPrevId(p.id)}>
            <div className="data-row-card__head">
              <div className="min-w-0"><div className="data-row-card__title font-mono">{S(p.codice)}</div><div className="data-row-card__meta">{S(cl?.azienda||cl?.nome||'Senza cliente')} · {fmtDate(p.data)}</div></div>
              <StatoBadge stato={p.stato}/>
            </div>
            <div className="data-row-card__foot"><span className="font-bold text-navy-600">{fmt(r?.totaleFattura||0)}</span><span className="text-xs text-slate-500">{S(p.agente||'')}</span></div>
          </div>)})}</div>:
        <div className="divide-y">{filtered.map(p=>{const cl=getCliente(p.cliente_id);const r=calcolaRiepilogo(p);return(
          <div key={p.id} className="flex items-center gap-4 py-3 hover:bg-gray-50 px-2 rounded-lg cursor-pointer group" onClick={()=>setCurrentPrevId(p.id)}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="font-bold text-navy-600 font-mono">{p.codice}</span><StatoBadge stato={p.stato}/>
                <span className="text-xs text-gray-400">{fmtDate(p.data)}</span></div>
              <div className="text-sm text-gray-600 truncate">
                {cl?<React.Fragment><strong>{S(cl.azienda||cl.nome)}</strong>{cl.azienda&&cl.nome?` - ${S(cl.nome)}`:''}</React.Fragment>:<span className="italic text-gray-400">Nessun cliente</span>}
                {p.agente?<span className="text-gray-400 ml-2">| {S(p.agente)}</span>:null}</div></div>
            <div className="text-right shrink-0"><div className="font-bold text-navy-600">{fmt(r?.totaleFattura||0)}</div>
              {(isAdmin||isManager)&&r&&r.imponibile>0&&<div className="text-xs text-gray-400">Margine {fmt(r?.marginePct||0,'pct')}</div>}</div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
              <button onClick={e=>{e.stopPropagation();duplicatePrev(p.id)}} title="Duplica" className="p-1.5 hover:bg-blue-100 rounded text-gray-400 hover:text-blue-600"><IcCopy s={16}/></button>
              {canDeletePreventivo(user,p)?<button onClick={e=>{e.stopPropagation();deletePrev(p.id)}} title="Elimina" className="p-1.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-600"><IcTrash s={16}/></button>:null}</div>
          </div>)})}</div>}
      </Card>
    )}
  </Page>);
}

function OpportunityForm({opportunity,salesUsers,onClose,onSaved}){
  const{user,isAdmin,isManager}=useContext(AuthCtx);
  const{clienti,preventivi,showToast}=useContext(DataCtx);
  const privileged=isAdmin||isManager;
  const def={titolo:'',cliente_id:'',user_id:user?.id||'',preventivo_id:'',fase:'lead',valore_stimato:0,probabilita:10,data_chiusura_prevista:today(),motivo_perso:'',concorrente:'',note:''};
  const[f,setF]=useState(opportunity?{
    ...def,
    ...opportunity,
    cliente_id:opportunity.cliente_id?String(opportunity.cliente_id):'',
    user_id:opportunity.user_id?String(opportunity.user_id):String(user?.id||''),
    preventivo_id:opportunity.preventivo_id?String(opportunity.preventivo_id):'',
    probabilita:Math.round(toNum(opportunity.probabilita)*100),
  }:{...def});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));

  const save=async()=>{
    if(!f.titolo.trim()){showToast('Titolo opportunità obbligatorio','error');return}
    if(f.fase==='chiuso_perso'&&!String(f.motivo_perso||'').trim()){showToast('Motivo di perdita obbligatorio','error');return}
    const payload={
      titolo:f.titolo.trim(),
      cliente_id:parseInt(f.cliente_id)||null,
      user_id:privileged?(parseInt(f.user_id)||user?.id):user?.id,
      preventivo_id:parseInt(f.preventivo_id)||null,
      fase:f.fase,
      valore_stimato:toNum(f.valore_stimato),
      probabilita:clamp(toNum(f.probabilita)/100,0,1),
      data_chiusura_prevista:f.data_chiusura_prevista||null,
      motivo_perso:(f.motivo_perso||'').trim(),
      concorrente:(f.concorrente||'').trim(),
      note:(f.note||'').trim(),
    };
    try{
      if(opportunity)await api('opportunity_update','PUT',payload,{id:opportunity.id});
      else await api('opportunity_create','POST',payload);
      showToast(opportunity?'Opportunità aggiornata':'Opportunità creata');
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  return(<Modal title={opportunity?'Modifica Opportunità':'Nuova Opportunità'} onClose={onClose} wide>
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Titolo opportunità" value={f.titolo} onChange={v=>u('titolo',v)} required/>
        <Sel label="Fase" value={f.fase} onChange={v=>u('fase',v)} options={OPPORTUNITY_PHASES.map(p=>({value:p.id,label:p.label}))}/>
        <Sel label="Cliente" value={f.cliente_id} onChange={v=>u('cliente_id',v)}
          options={clienti.map(c=>({value:String(c.id),label:S(c.azienda||c.nome||'Senza nome')}))} placeholder="Seleziona cliente"/>
        {privileged?<Sel label="Commerciale assegnato" value={f.user_id} onChange={v=>u('user_id',v)}
          options={salesUsers.map(s=>({value:String(s.id),label:S(s.nome||s.username)}))} placeholder="Seleziona commerciale"/>:null}
        <Inp label="Valore stimato" type="number" value={f.valore_stimato} onChange={v=>u('valore_stimato',v)}/>
        <Inp label="Probabilità %" type="number" value={f.probabilita} onChange={v=>u('probabilita',clamp(toNum(v),0,100))}/>
        <Inp label="Chiusura prevista" type="date" value={f.data_chiusura_prevista||''} onChange={v=>u('data_chiusura_prevista',v)}/>
        <Sel label="Preventivo collegato" value={f.preventivo_id} onChange={v=>u('preventivo_id',v)}
          options={preventivi.map(p=>({value:String(p.id),label:S(p.codice)}))} placeholder="Nessun preventivo"/>
        <Inp label="Concorrente" value={f.concorrente} onChange={v=>u('concorrente',v)}/>
        {f.fase==='chiuso_perso'?<Inp label="Motivo di perdita" value={f.motivo_perso} onChange={v=>u('motivo_perso',v)} required/>:null}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Note</label>
        <textarea value={f.note||''} onChange={e=>u('note',e.target.value)} rows={4}
          className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/>
      </div>
      <div className="flex justify-end gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva</Btn>
      </div>
    </div>
  </Modal>);
}

function PipelinePage(){
  const{user,isAdmin,isManager}=useContext(AuthCtx);
  const privileged=isAdmin||isManager;
  const{opportunita,opportunityStats,clienti,loadOpportunita,loadOpportunityStats,loadClienti,showToast}=useContext(DataCtx);
  const[search,setSearch]=useState('');
  const[assigneeFilter,setAssigneeFilter]=useState('all');
  const[showForm,setShowForm]=useState(false);
  const[editOpp,setEditOpp]=useState(null);
  const[draggedId,setDraggedId]=useState(null);
  const[salesUsers,setSalesUsers]=useState([]);

  useEffect(()=>{
    if(!privileged)return;
    api('sales_users').then(d=>setSalesUsers(d.data||[])).catch(()=>{});
  },[privileged]);
  useEffect(()=>{
    loadOpportunita().catch(()=>{});
    loadOpportunityStats().catch(()=>{});
    loadClienti({enrich:'0',light:'1'}).catch(()=>{});
  },[loadOpportunita,loadOpportunityStats,loadClienti]);

  const refresh=async()=>{
    await Promise.all([loadOpportunita(),loadOpportunityStats()]);
  };

  const filtered=useMemo(()=>{
    let list=[...opportunita];
    if(search){
      const s=search.toLowerCase();
      list=list.filter(o=>(o.titolo||'').toLowerCase().includes(s)||(o.codice||'').toLowerCase().includes(s)||(o.cliente_azienda||o.cliente_nome||'').toLowerCase().includes(s));
    }
    if(privileged&&assigneeFilter!=='all')list=list.filter(o=>String(o.user_id)===assigneeFilter);
    return list;
  },[opportunita,search,assigneeFilter,privileged]);

  const grouped=useMemo(()=>{
    const map=Object.fromEntries(OPPORTUNITY_PHASES.map(p=>[p.id,[]]));
    filtered.forEach(o=>{(map[o.fase]||(map[o.fase]=[])).push(o)});
    Object.values(map).forEach(list=>list.sort((a,b)=>toNum(b.valore_stimato)-toNum(a.valore_stimato)));
    return map;
  },[filtered]);

  const moveToPhase=async(opportunity,targetPhase)=>{
    try{
      let payload={fase:targetPhase};
      if(targetPhase==='chiuso_perso'){
        const reason=window.prompt('Inserisci il motivo di perdita:','');
        if(reason===null)return;
        if(!reason.trim()){showToast('Motivo di perdita obbligatorio','error');return}
        payload.motivo_perso=reason.trim();
      }
      await api('opportunity_update','PUT',payload,{id:opportunity.id});
      showToast('Fase aggiornata');
      refresh();
    }catch(e){showToast(e.message,'error')}
  };

  const deleteOpp=async(id)=>{
    if(!confirm('Eliminare questa opportunità?'))return;
    try{await api('opportunity_delete','DELETE',null,{id});showToast('Opportunità eliminata');refresh()}catch(e){showToast(e.message,'error')}
  };

  return(<Page title="Pipeline vendita" subtitle="Kanban opportunità, forecast e conversioni" actions={<Btn icon={<IcPlus s={16}/>} onClick={()=>{setEditOpp(null);setShowForm(true)}}>Nuova opportunità</Btn>}>
    {opportunityStats?<div className="stat-tile-grid">
      {[
        {label:'Attive',value:opportunityStats.totale_attive,bg:'bg-blue-50',sub:fmt(opportunityStats.valore_aperto)},
        {label:'Forecast',value:fmt(opportunityStats.forecast_pesato),bg:'bg-indigo-50',sub:'pesato'},
        {label:'Vinte',value:opportunityStats.totale_vinte,bg:'bg-green-50',sub:fmt(opportunityStats.valore_vinto)},
        {label:'Perse',value:opportunityStats.totale_perse,bg:'bg-red-50',sub:opportunityStats.motivi_persi?.[0]?.reason||'nessun motivo'},
        {label:'Totali',value:opportunityStats.totale_opportunita,bg:'bg-gray-50',sub:'pipeline completa'},
      ].map((item,i)=><div key={i} className={`stat-tile ${item.bg}`}>
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">{item.label}</div>
        <div className="text-2xl font-bold text-navy-600">{S(item.value)}</div>
        <div className="text-xs text-gray-500 mt-1">{S(item.sub)}</div>
      </div>)}
    </div>:null}

    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
      <Card title="Filtri Pipeline">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca opportunità o cliente..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/></div>
          {privileged?<Sel value={assigneeFilter} onChange={setAssigneeFilter}
            options={[{value:'all',label:'Tutti i commerciali'},...salesUsers.map(s=>({value:String(s.id),label:S(s.nome||s.username)}))]} className="min-w-[220px]"/>:null}
        </div>
      </Card>
      <Card title="Conversioni">
        <div className="space-y-2">
          {(opportunityStats?.conversioni||[]).map((c,i)=><div key={i} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
            <span className="text-gray-600">{phaseLabel(c.from)} → {phaseLabel(c.to)}</span>
            <span className="font-medium text-navy-600">{c.to_count}/{c.from_count} · {fmt(c.rate,'pct')}</span>
          </div>)}
        </div>
      </Card>
    </div>

    <div className="pipeline-kanban-scroll no-scrollbar">
      <div className="pipeline-kanban-row">
        {OPPORTUNITY_PHASES.map(phase=>{
          const column=grouped[phase.id]||[];
          const totalValue=column.reduce((sum,item)=>sum+toNum(item.valore_stimato),0);
          return(
            <div key={phase.id} className="pipeline-column"
              onDragOver={e=>e.preventDefault()}
              onDrop={e=>{
                e.preventDefault();
                const current=filtered.find(o=>o.id===draggedId);
                setDraggedId(null);
                if(current&&current.fase!==phase.id)moveToPhase(current,phase.id);
              }}>
              <div className="p-4 border-b bg-gray-50 rounded-t-2xl">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${phase.color}`}>{phase.label}</span>
                  <span className="text-xs text-gray-500">{column.length} deal</span>
                </div>
                <div className="text-sm font-semibold text-navy-600 mt-2">{fmt(totalValue)}</div>
              </div>
              <div className="p-3 space-y-3 min-h-[420px]">
                {column.length===0?<div className="text-sm text-gray-400 text-center py-10 border border-dashed rounded-xl">Nessuna opportunità</div>:column.map(item=>(
                  <div key={item.id} draggable onDragStart={()=>setDraggedId(item.id)} className="pipeline-card">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold text-navy-600">{S(item.titolo)}</div>
                        <div className="text-xs text-gray-400 font-mono">{S(item.codice)}</div>
                      </div>
                      <span className={`text-[11px] px-2 py-1 rounded-full ${phaseBadgeClass(item.fase)}`}>{phaseLabel(item.fase)}</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-2">{S(item.cliente_azienda||item.cliente_nome||'Nessun cliente')}</div>
                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-gray-500">
                      <div><span className="block text-gray-400">Valore</span><strong className="text-navy-600">{fmt(item.valore_stimato)}</strong></div>
                      <div><span className="block text-gray-400">Prob.</span><strong className="text-navy-600">{fmt(item.probabilita,'pct')}</strong></div>
                      <div><span className="block text-gray-400">Forecast</span><strong className="text-navy-600">{fmt(item.weighted_value)}</strong></div>
                      <div><span className="block text-gray-400">Chiusura</span><strong className="text-navy-600">{item.data_chiusura_prevista?fmtDate(item.data_chiusura_prevista):'-'}</strong></div>
                    </div>
                    {privileged?<div className="text-xs text-gray-500 mt-3">{'Commerciale: '+S(item.agente_nome||item.agente_username||'-')}</div>:null}
                    {item.fase==='chiuso_perso'&&item.motivo_perso?<div className="mt-2 text-xs text-red-600 bg-red-50 rounded-lg px-2 py-1">{S(item.motivo_perso)}</div>:null}
                    <div className="flex gap-2 mt-3">
                      <Btn s="sm" v="secondary" onClick={()=>{setEditOpp(item);setShowForm(true)}}>Apri</Btn>
                      <Btn s="sm" v="ghost" onClick={()=>deleteOpp(item.id)}>Elimina</Btn>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Forecast Mensile">
        {(opportunityStats?.forecast_mensile||[]).length===0?<p className="text-sm text-gray-400">Nessun forecast disponibile.</p>:
        <div className="space-y-2">{opportunityStats.forecast_mensile.map((row,i)=><div key={i} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
          <span className="text-gray-600">{S(row.mese)}</span>
          <span className="font-medium text-navy-600">{fmt(row.forecast)} <span className="text-xs text-gray-400">{`su ${fmt(row.valore)}`}</span></span>
        </div>)}</div>}
      </Card>
      <Card title="Motivi di Perdita">
        {(opportunityStats?.motivi_persi||[]).length===0?<p className="text-sm text-gray-400">Nessuna opportunità persa registrata.</p>:
        <div className="space-y-2">{opportunityStats.motivi_persi.map((row,i)=><div key={i} className="flex items-center justify-between text-sm bg-red-50 rounded-lg px-3 py-2">
          <span className="text-red-700">{S(row.reason)}</span><strong className="text-red-700">{row.count}</strong>
        </div>)}</div>}
      </Card>
    </div>

    {showForm?<OpportunityForm opportunity={editOpp} salesUsers={salesUsers} onClose={()=>setShowForm(false)} onSaved={()=>{setShowForm(false);refresh()}}/>:null}
  </Page>);
}

// ========== PREVENTIVO EDITOR ==========
function PreventivoEditor({prevId,onBack,onNew,onDelete,onDuplicate,onOpenPrint}){
  const{user,isAdmin,isManager}=useContext(AuthCtx);
  const{isPhone}=useDevice();
  const canViewMargins=isAdmin||isManager;
  const isSalesRole=effectiveMansioniRole(user?.ruolo)==='commerciale';
  const{activeArticles,sellableArticles,clienti,getCliente,settings,showToast,loadPreventivi,loadClienti,loadArticoli,previewQuote}=useContext(DataCtx);
  const[prev,setPrev]=useState(null);
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[dirty,setDirty]=useState(false);
  const[agentPreview,setAgentPreview]=useState(null);
  const[clientSearch,setClientSearch]=useState('');
  const[articleSearch,setArticleSearch]=useState({});
  const saveTimer=useRef(null);
  const previewTimer=useRef(null);
  const latestPrevRef=useRef(null);
  const lastSavedSnapshotRef=useRef('');
  const normalizePreventivoData=useCallback(data=>{
    if(!data)return data;
    const righe=Array.isArray(data.righe)?data.righe:[];
    if(!righe.length)return data;
    const normalizedRows=righe.map(row=>{
      const matchedArticle=findArticleByReference(activeArticles,row?.articolo_nome);
      if(!matchedArticle||!String(matchedArticle.nome||'').trim())return row;
      return{...row,articolo_nome:String(matchedArticle.nome||'').trim()};
    });
    return{...data,righe:normalizedRows};
  },[activeArticles]);

  const loadPrev=async()=>{
    if(String(prevId||'').startsWith('local-')){
      showToast('Questa offerta è ancora solo in locale. Attendi la sincronizzazione o creala di nuovo con la rete attiva.','error');
      onBack();
      setLoading(false);
      return;
    }
    try{const d=await api('preventivo','GET',null,{id:prevId});
      const p=normalizePreventivoData(d.data);
      if(!p.righe||p.righe.length===0)p.righe=[{articolo_nome:'',qta:0,sconto:0}];
      const snapshot=JSON.stringify(p);
      latestPrevRef.current=p;
      lastSavedSnapshotRef.current=snapshot;
      setDirty(false);
      setPrev(p)}catch(e){showToast(e.message,'error');onBack()}finally{setLoading(false)}
  };
  useEffect(()=>{loadPrev()},[prevId]);
  useEffect(()=>{
    if(!clienti.length)loadClienti({enrich:'0',light:'1'}).catch(()=>{});
    if(!activeArticles.length)loadArticoli().catch(()=>{});
  },[clienti.length,activeArticles.length,loadClienti,loadArticoli]);

  const doSave=useCallback(async(data)=>{
    const payload=normalizePreventivoData(data);
    setSaving(true);
    try{
      await api('preventivo_update','PUT',payload,{id:prevId});
      lastSavedSnapshotRef.current=JSON.stringify(payload);
      latestPrevRef.current=payload;
      setPrev(payload);
      setDirty(false);
      loadPreventivi();
    }
    catch(e){showToast('Errore salvataggio: '+e.message,'error')}
    finally{setSaving(false)}
  },[prevId,normalizePreventivoData,loadPreventivi,showToast]);

  const flushSave=useCallback(async(forceData)=>{
    const data=forceData||latestPrevRef.current;
    if(!data)return;
    const snapshot=JSON.stringify(data);
    if(snapshot===lastSavedSnapshotRef.current&&!dirty)return;
    await doSave(data);
  },[doSave,dirty]);

  useEffect(()=>{
    latestPrevRef.current=prev;
  },[prev]);

  useEffect(()=>()=>{clearTimeout(saveTimer.current);clearTimeout(previewTimer.current)},[]);

  const upd=(updates)=>{
    if(!canEditPreventivo(user,prev)){
      showToast(readOnlyMessage||'Offerta in sola lettura','error');
      return;
    }
    setPrev(p=>{const next={...p,...updates};
      latestPrevRef.current=next;
      setDirty(true);
      return next;
    });
  };

  const calcolaRigaLocale=useCallback((riga)=>{
    const art=findArticleByReference(activeArticles,riga.articolo_nome);
    if(!art||!riga.qta)return{prezzoBase:0,prezzoNetto:0,totale:0,costoUnit:0,costoRiga:0,margineEur:0,marginePct:0};
    const prezzoBase=getArticoloBasePrice(art);
    const prezzoNetto=prezzoBase*(1-(riga.sconto||0));
    const totale=Math.round(riga.qta*prezzoNetto*100)/100;
    const costoRiga=Math.round(riga.qta*toNum(art.costo_netto)*100)/100;
    const margineEur=Math.round((totale-costoRiga)*100)/100;
    const marginePct=totale>0?margineEur/totale:0;
    return{prezzoBase,prezzoNetto,totale,costoUnit:toNum(art.costo_netto),costoRiga,margineEur,marginePct};
  },[activeArticles]);

  const localSummary=useMemo(()=>{
    if(!prev)return null;
    const righeCalc=(prev.righe||[]).map(r=>({...r,calc:calcolaRigaLocale(r)}));
    const totaleRighe=righeCalc.reduce((s,r)=>s+r.calc.totale,0);
    const totaleCosto=righeCalc.reduce((s,r)=>s+r.calc.costoRiga,0);
    const imponibile=totaleRighe+(prev.trasporto||0)-(prev.trade_in||0);
    const margineLordo=imponibile-totaleCosto;
    const marginePct=imponibile>0?(imponibile-totaleCosto)/imponibile:0;
    const aliqIva=parseFloat(settings.aliquota_iva)||0.22;
    const iva=imponibile*aliqIva;const totaleFattura=imponibile+iva;
    const baseCalcolo=settings.base_calcolo_provv||'Fatturato';
    const mMin=parseFloat(settings.margine_min_provv)||0.25;const mMax=parseFloat(settings.margine_max_provv)||0.5;
    const pMin=parseFloat(settings.provv_min)||0;const pMax=parseFloat(settings.provv_max)||0.1;
    const base=baseCalcolo==='Fatturato'?imponibile:margineLordo;
    let provvPct=marginePct<mMin?pMin:marginePct>mMax?pMax:pMin+(marginePct-mMin)*(pMax-pMin)/(mMax-mMin);
    const provvEur=base*provvPct;const mbo=provvEur*0.1;
    const utileNetto=imponibile-totaleCosto-provvEur;const margineNetto=imponibile>0?utileNetto/imponibile:0;
    let rt=[];try{rt=JSON.parse(settings.rate_table||'[]')}catch(e){}
    const dk=prev.noleggio_durata||'60';let coeff=0;
    for(let i=rt.length-1;i>=0;i--){if(imponibile>=rt[i].importo){coeff=rt[i].mesi[dk]||0;break}}
    const impNol=imponibile*(1-(prev.noleggio_anticipo||0));
    const rataMensile=coeff>0?Math.round(impNol*coeff/100*100)/100:0;
    return{righeCalc,totaleRighe,totaleCosto,imponibile,margineLordo,marginePct,iva,totaleFattura,provvEur,provvPct,mbo,utileNetto,margineNetto,rataMensile,coeff};
  },[prev,settings,calcolaRigaLocale]);

  const previewPayload=useMemo(()=>{
    if(!prev)return null;
    const righeValide=Array.isArray(prev.righe)?prev.righe
      .map(r=>({
        articolo_nome:r?.articolo_nome||'',
        qta:r?.qta||0,
        sconto:r?.sconto||0,
      }))
      .filter(r=>String(r.articolo_nome||'').trim()!==''&&toNum(r.qta)>0):[];
    if(!prev.cliente_id||!righeValide.length)return null;
    return{
      cliente_id:prev.cliente_id||null,
      trasporto:prev.trasporto||0,
      trade_in:prev.trade_in||0,
      righe:righeValide
    };
  },[prev]);

  useEffect(()=>{
    if(!isSalesRole||!previewPayload){setAgentPreview(null);return}
    clearTimeout(previewTimer.current);
    previewTimer.current=setTimeout(()=>{
      previewQuote(previewPayload).then(setAgentPreview).catch(()=>setAgentPreview(null));
    },600);
    return()=>clearTimeout(previewTimer.current);
  },[isSalesRole,previewPayload,previewQuote]);

  if(loading||!prev)return<Loading/>;
  const p=prev;
  const canEditCurrentPreventivo=canEditPreventivo(user,p);
  const canDeleteCurrentPreventivo=canDeletePreventivo(user,p);
  const readOnlyMessage=!canEditCurrentPreventivo
    ?(!isPreventivoOwner(user,p)
      ?'Questa offerta è di un altro utente: può modificarla solo l\'autore oppure un admin.'
      :'Questa offerta non è più in bozza: può modificarla o cancellarla solo un admin.')
    :'';
  const r=isSalesRole?agentPreview:localSummary;
  const cliente=getCliente(p.cliente_id);
  const discountProfile=getDiscountProfileFromCliente(cliente);
  const discountProfileLabel=getDiscountProfileLabel(discountProfile);
  const isNoleggioVisible=p.mostra_noleggio!==0&&p.mostra_noleggio!=='0';
  const articleOptions=isSalesRole?sellableArticles:activeArticles;
  const clientOptions=[...clienti].sort((a,b)=>{
    const ap=a.tipo==='potenziale'?1:0,bp=b.tipo==='potenziale'?1:0;
    if(ap!==bp)return ap-bp;
    return (a.azienda||a.nome||'').localeCompare(b.azienda||b.nome||'');
  });
  const filteredClientOptions=!clientSearch.trim()?clientOptions:clientOptions.filter(c=>{
    const s=clientSearch.toLowerCase();
    return [c.azienda,c.nome,c.cognome,c.citta,c.provincia,c.email,c.telefono].filter(Boolean).some(v=>String(v).toLowerCase().includes(s));
  });
  const getRowMaxDiscount=riga=>{
    const art=findArticleByReference(activeArticles,riga?.articolo_nome);
    return getArticoloMaxDiscount(art,settings,discountProfile,toNum(riga?.qta));
  };
  const getFilteredArticleOptions=(idx)=>{
    const query=String(articleSearch[idx]||'').trim().toLowerCase();
    if(!query)return articleOptions;
    return articleOptions.filter(a=>[a.nome,a.sku,a.categoria].filter(Boolean).some(v=>String(v).toLowerCase().includes(query)));
  };
  const updRiga=(idx,k,v)=>{
    if(!canEditCurrentPreventivo){
      showToast(readOnlyMessage||'Offerta in sola lettura','error');
      return;
    }
    const righe=[...(p.righe||[])];
    const next={...righe[idx],[k]:v};
    if(isSalesRole&&(k==='articolo_nome'||k==='qta')){
      const max=getRowMaxDiscount(next);
      if(toNum(next.sconto)>max)next.sconto=max;
    }
    if(k==='sconto'&&isSalesRole){
      next.sconto=clamp(toNum(v),0,getRowMaxDiscount(next));
    }
    righe[idx]=next;
    upd({righe});
  };
  const addRiga=()=>{if(!canEditCurrentPreventivo)return;upd({righe:[...(p.righe||[]),{articolo_nome:'',qta:0,sconto:0}]})};
  const removeRiga=(idx)=>{if(!canEditCurrentPreventivo||!p.righe||p.righe.length<=1)return;upd({righe:p.righe.filter((_,i)=>i!==idx)})};
  const sogliaMarginePct=parseFloat(settings.soglia_margine_pct)||0.35;
  const colHeaders=canViewMargins
    ?['Articolo','Qta','Prezzo base','Sc.%','Netto Unit.','Totale','Costo','Margine','M.%','']
    :['Articolo','Qta','Listino','Sc.max','Sc.%','Netto Unit.','Totale',''];
  const righe=Array.isArray(p.righe)?p.righe:(p.righe?[p.righe]:[{articolo_nome:'',qta:0,sconto:0}]);
  const righeCalcDisplay=r?.righeCalc||righe.map(riga=>{
    const art=findArticleByReference(activeArticles,riga.articolo_nome);
    const policy=getDiscountPolicy(art,settings,discountProfile,toNum(riga.qta));
    return{articolo_nome:riga.articolo_nome,qta:riga.qta,sconto:riga.sconto,sconto_massimo:getRowMaxDiscount(riga),discount_rule_label:policy.label,discount_rule_note:policy.note,warning:null,calc:calcolaRigaLocale(riga)};
  });

  return(<div className={`page-stack cplv-preventivo-editor fade-in ${isPhone?'has-editor-action-bar':''}`}>
    <header className="page-header page-header--row no-print cplv-editor-header">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <button type="button" onClick={async()=>{await flushSave();onBack&&onBack();}} className="cplv-icon-btn shrink-0" title="Torna alla lista" aria-label="Indietro">
          <IcChevronLeft s={20}/>
        </button>
        <div className="min-w-0">
          <h2 className="page-title font-mono truncate">{S(p.codice)}</h2>
          <div className="flex flex-wrap items-center gap-2 mt-0.5">
            <StatoBadge stato={String(p.stato||'bozza')}/>
            <span className="text-sm text-gray-400">{fmtDate(p.data)}</span>
            {saving&&<span className="text-xs text-accent-400 animate-pulse">Salvataggio...</span>}
            {!saving&&dirty&&<span className="text-xs text-amber-500">Da salvare</span>}
            {!saving&&!dirty&&<span className="text-xs text-emerald-500">Salvato</span>}
          </div>
        </div>
      </div>
      {!isPhone?<div className="page-actions flex gap-2 flex-wrap">
        <Sel value={String(p.stato||'bozza')} onChange={v=>upd({stato:v})} options={canViewMargins?Object.entries(STATI).map(([k,sv])=>({value:k,label:sv.label})):[{value:'bozza',label:'Bozza'},{value:'inviata',label:'Inviata'}]} disabled={!canEditCurrentPreventivo}/>
        <Btn v="success" s="sm" icon={<IcCheck s={14}/>} onClick={()=>flushSave()} disabled={!canEditCurrentPreventivo||saving||!dirty}>Salva offerta</Btn>
        <Btn v="secondary" s="sm" icon={<IcCopy s={14}/>} onClick={async()=>{await flushSave();onDuplicate?.(p.id);}}>Duplica</Btn>
        <Btn v="secondary" s="sm" icon={<IcPrint s={14}/>} onClick={async()=>{await flushSave();onOpenPrint?.(p.id);}}>Stampa</Btn>
        {canDeleteCurrentPreventivo?<Btn v="danger" s="sm" icon={<IcTrash s={14}/>} onClick={()=>onDelete?.(p.id)}>Elimina</Btn>:null}
        <Btn v="secondary" s="sm" icon={<IcPlus s={14}/>} onClick={async()=>{await flushSave();onNew&&onNew();}}>Nuovo</Btn>
      </div>:null}
    </header>
    <EditorActionBar>
      <Sel className="editor-action-bar__btn min-w-[120px]" value={String(p.stato||'bozza')} onChange={v=>upd({stato:v})} options={canViewMargins?Object.entries(STATI).map(([k,sv])=>({value:k,label:sv.label})):[{value:'bozza',label:'Bozza'},{value:'inviata',label:'Inviata'}]} disabled={!canEditCurrentPreventivo}/>
      <Btn className="editor-action-bar__btn" v="success" s="sm" icon={<IcCheck s={14}/>} onClick={()=>flushSave()} disabled={!canEditCurrentPreventivo||saving||!dirty}>Salva</Btn>
      <Btn className="editor-action-bar__btn" v="secondary" s="sm" icon={<IcPrint s={14}/>} onClick={async()=>{await flushSave();onOpenPrint?.(p.id);}}>Stampa</Btn>
      <Btn className="editor-action-bar__btn" v="secondary" s="sm" icon={<IcCopy s={14}/>} onClick={async()=>{await flushSave();onDuplicate?.(p.id);}}>Duplica</Btn>
    </EditorActionBar>
    {!canEditCurrentPreventivo?<div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">{readOnlyMessage}</div>:null}

    <Card><div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Ricerca nominativo</label>
        <input value={clientSearch} onChange={e=>setClientSearch(e.target.value)} placeholder="Cerca cliente, nominativo, città..."
          disabled={!canEditCurrentPreventivo}
          className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400 mb-2"/>
        <Sel value={p.cliente_id!=null?String(p.cliente_id):''} onChange={v=>upd({cliente_id:parseInt(v)||null})}
          options={filteredClientOptions.map(c=>({value:String(c.id),label:(c.tipo==='potenziale'?'[POT] ':c.tipo==='rivenditore'?'[RIV] ':'')+S(c.azienda||c.nome||'')+(c.azienda&&c.nome?' - '+S(c.nome):'')+(c.citta?' ('+S(c.citta)+')':'')}))} placeholder="Seleziona cliente o potenziale..." disabled={!canEditCurrentPreventivo}/>
      </div>
      <Inp label="Commerciale" value={p.agente} onChange={v=>upd({agente:v})} disabled={isSalesRole||!canEditCurrentPreventivo}/>
      <Inp label="Data" type="date" value={p.data} onChange={v=>upd({data:v})} disabled={!canEditCurrentPreventivo}/>
      <div className="flex items-end">
        <label className="flex items-center gap-3 px-4 py-2 border rounded-lg bg-white w-full">
          <input type="checkbox" checked={isNoleggioVisible} onChange={e=>upd({mostra_noleggio:e.target.checked?1:0})} className="accent-accent-400" disabled={!canEditCurrentPreventivo}/>
          <div>
            <div className="text-sm font-medium text-navy-600">Mostra noleggio in offerta</div>
            <div className="text-xs text-gray-500">Se disattivato, il noleggio non compare nella stampa dell'offerta.</div>
          </div>
        </label>
      </div>
      {canViewMargins&&<div className="flex items-end">
        {r&&r.marginePct<sogliaMarginePct&&r.imponibile>0
          ?<div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-lg w-full"><IcAlert s={18}/><span className="text-sm font-medium">{'Margine '+fmt(r.marginePct,'pct')}</span></div>
          :r&&r.imponibile>0?<div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg w-full"><IcCheck s={18}/><span className="text-sm font-medium">{'Margine '+fmt(r.marginePct,'pct')}</span></div>:null}
      </div>}
    </div>
    {cliente&&<div className="mt-3 pt-3 border-t text-sm text-gray-500 flex flex-wrap gap-x-6 gap-y-1">
      {cliente.azienda?<span><strong>{S(cliente.azienda)}</strong></span>:null}{cliente.via?<span>{S(cliente.via)}</span>:null}
      {cliente.citta?<span>{S(cliente.citta)}</span>:null}{cliente.piva?<span>{'P.IVA: '+S(cliente.piva)}</span>:null}</div>}
    {cliente?<div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
      <span className="bg-navy-50 text-navy-700 px-2 py-1 rounded-full">{`Profilo sconto: ${discountProfileLabel}`}</span>
      <span className="text-gray-500">{discountProfile==='rivenditore'?'Regole sconto rivenditore attive per categoria articolo.':'Regole cliente continuativo attive; la classe B cresce in base alla quantità.'}</span>
    </div>:null}
    {isSalesRole&&r?.violations?.length?<div className="mt-3 pt-3 border-t space-y-2">
      {r.violations.map((v,i)=><div key={i} className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-lg"><IcAlert s={16}/><span className="text-sm">{S(v.message)}{v.articolo_nome?` · ${S(v.articolo_nome)}`:''}</span></div>)}
    </div>:null}
    </Card>

    <Card title="Righe Preventivo" action={canEditCurrentPreventivo?<Btn s="sm" icon={<IcPlus s={14}/>} onClick={addRiga}>Riga</Btn>:null}>
      {isPhone?<div className="data-list-phone space-y-3">{righe.map((riga,idx)=>{const rowCalc=righeCalcDisplay[idx]||{calc:{}};
          const c=rowCalc.calc||{};
          const maxDiscount=toNum(rowCalc.sconto_massimo??getRowMaxDiscount(riga));
          const rowArticleOptions=getFilteredArticleOptions(idx);
          const selectedArticle=findArticleByReference(rowArticleOptions,riga.articolo_nome)||findArticleByReference(articleOptions,riga.articolo_nome);
          return(<div key={idx} className="data-row-card">
            <div className="text-xs text-slate-400 mb-2">Riga {idx+1}</div>
            <input value={articleSearch[idx]||''} onChange={e=>setArticleSearch(prev=>({...prev,[idx]:e.target.value}))} placeholder="Cerca articolo..." disabled={!canEditCurrentPreventivo}
              className="w-full px-2 py-2 border rounded-lg text-sm mb-2 outline-none focus:ring-2 focus:ring-accent-400"/>
            <select value={String(selectedArticle?.nome||riga.articolo_nome||'')} onChange={e=>updRiga(idx,'articolo_nome',e.target.value)} disabled={!canEditCurrentPreventivo}
              className="w-full px-2 py-2 border rounded-lg text-sm bg-yellow-50 mb-3">
              <option value="">-- Seleziona --</option>{rowArticleOptions.map(a=><option key={a.id} value={String(a.nome||'')}>{S(a.nome)}</option>)}</select>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><label className="text-xs text-slate-500">Qta</label><input type="number" min="0" value={riga.qta||''} onChange={e=>updRiga(idx,'qta',parseInt(e.target.value)||0)} disabled={!canEditCurrentPreventivo} className="w-full mt-1 px-2 py-2 border rounded-lg text-right bg-yellow-50"/></div>
              <div><label className="text-xs text-slate-500">Sc.%</label><input type="number" min="0" max={Math.round(maxDiscount*100)} value={riga.sconto?(riga.sconto*100):''} onChange={e=>updRiga(idx,'sconto',(parseFloat(e.target.value)||0)/100)} disabled={!canEditCurrentPreventivo} className="w-full mt-1 px-2 py-2 border rounded-lg text-right bg-yellow-50"/></div>
              <div><span className="text-xs text-slate-500">Totale</span><div className="font-bold text-navy-600 mt-1">{fmt(c.totale)}</div></div>
              {canViewMargins?<div><span className="text-xs text-slate-500">Margine</span><div className="font-semibold mt-1">{c.marginePct>0?fmt(c.marginePct,'pct'):'-'}</div></div>:null}
            </div>
            {canEditCurrentPreventivo?<div className="mt-3 pt-2 border-t"><button onClick={()=>removeRiga(idx)} className="text-sm text-red-500 flex items-center gap-1"><IcTrash s={14}/> Rimuovi riga</button></div>:null}
          </div>)})}</div>:
      <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-navy-600 text-white">
        {colHeaders.map((h,i)=><th key={i} className="px-3 py-2.5 text-left font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{righe.map((riga,idx)=>{const rowCalc=righeCalcDisplay[idx]||{calc:{}};
          const c=rowCalc.calc||{};
          const isLow=typeof c.marginePct==='number'&&c.marginePct>0&&c.marginePct<sogliaMarginePct;
          const maxDiscount=toNum(rowCalc.sconto_massimo??getRowMaxDiscount(riga));
          const rowWarning=rowCalc.warning;
          const rowArticleOptions=getFilteredArticleOptions(idx);
          const selectedArticle=findArticleByReference(rowArticleOptions,riga.articolo_nome)||findArticleByReference(articleOptions,riga.articolo_nome);
          return(<tr key={idx} className={`border-b hover:bg-gray-50 ${idx%2?'bg-gray-50/50':''}`}>
            <td className="px-2 py-1.5">
              <input value={articleSearch[idx]||''} onChange={e=>setArticleSearch(prev=>({...prev,[idx]:e.target.value}))} placeholder="Cerca articolo..." disabled={!canEditCurrentPreventivo}
                className="w-full min-w-[240px] px-2 py-1.5 border rounded text-sm mb-2 outline-none focus:ring-2 focus:ring-accent-400"/>
              <select value={String(selectedArticle?.nome||riga.articolo_nome||'')} onChange={e=>updRiga(idx,'articolo_nome',e.target.value)} disabled={!canEditCurrentPreventivo}
                className="w-full min-w-[240px] px-2 py-1.5 border rounded text-sm bg-yellow-50 focus:ring-2 focus:ring-accent-400 outline-none">
                <option value="">-- Seleziona --</option>{rowArticleOptions.map(a=><option key={a.id} value={String(a.nome||'')}>{S(a.nome)}{a.sku?` (${S(a.sku)})`:''}</option>)}</select>
            </td>
            <td className="px-2 py-1.5"><input type="number" min="0" value={riga.qta||''} onChange={e=>updRiga(idx,'qta',parseInt(e.target.value)||0)} disabled={!canEditCurrentPreventivo}
              className="w-16 px-2 py-1.5 border rounded text-right bg-yellow-50 outline-none"/></td>
            <td className="px-3 py-1.5 text-right">{fmt(c.prezzoBase)}</td>
            {!canViewMargins?<td className="px-3 py-1.5 text-right text-navy-600 font-medium">{fmt(maxDiscount,'pct')}</td>:null}
            <td className="px-2 py-1.5"><input type="number" min="0" max={Math.round(maxDiscount*100)} step="1" value={riga.sconto?(riga.sconto*100):''} onChange={e=>updRiga(idx,'sconto',(parseFloat(e.target.value)||0)/100)} disabled={!canEditCurrentPreventivo}
              className={`w-16 px-2 py-1.5 border rounded text-right bg-yellow-50 outline-none ${rowWarning?'border-red-400 text-red-600':''}`}/></td>
            <td className="px-3 py-1.5 text-right">{fmt(c.prezzoNetto)}</td>
            <td className="px-3 py-1.5 text-right font-medium">{fmt(c.totale)}</td>
            {canViewMargins?<React.Fragment><td className="px-3 py-1.5 text-right text-gray-500">{fmt(c.costoRiga)}</td>
              <td className="px-3 py-1.5 text-right">{fmt(c.margineEur)}</td>
              <td className="px-3 py-1.5 text-right"><span className={`px-2 py-0.5 rounded text-xs font-medium ${isLow?'bg-red-100 text-red-700':c.marginePct>0?'bg-green-100 text-green-700':'text-gray-400'}`}>
                {c.marginePct>0?fmt(c.marginePct,'pct'):'-'}</span></td></React.Fragment>:null}
            <td className="px-2 py-1.5">{canEditCurrentPreventivo?<button onClick={()=>removeRiga(idx)} className="text-gray-400 hover:text-red-500"><IcTrash s={16}/></button>:null}</td>
          </tr>)})}</tbody></table></div>}
      {isSalesRole?<div className="mt-3 text-xs text-gray-500 space-y-1">
        {righeCalcDisplay.map((rowCalc,idx)=>rowCalc.articolo_nome?<div key={idx}>{`${rowCalc.articolo_nome}: ${S(rowCalc.discount_rule_label||'Da definire')} · ${S(rowCalc.discount_rule_note||'')}`}</div>:null)}
      </div>:null}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
        <Inp label="Trasporto/Montaggio" type="number" value={p.trasporto||''} onChange={v=>upd({trasporto:v})} disabled={!canEditCurrentPreventivo}/>
        <Inp label="Trade-in/Sconto" type="number" value={p.trade_in||''} onChange={v=>upd({trade_in:v})} disabled={!canEditCurrentPreventivo}/>
      </div>
    </Card>

    {(r||localSummary)?<div className={`grid grid-cols-1 ${canViewMargins?'lg:grid-cols-3':'lg:grid-cols-2'} gap-6`}>
      <Card title="Riepilogo Economico"><div className="space-y-3">
        {[['Totale righe',(r||localSummary).totaleRighe],['Trasporto',Number(p.trasporto)||0],['Trade-in',-(Number(p.trade_in)||0)]].map(([l,v],i)=>
          <div key={i} className="flex justify-between text-sm"><span className="text-gray-600">{l}</span><span>{fmt(v)}</span></div>)}
        <div className="border-t pt-3 flex justify-between font-bold text-lg"><span>Totale Imponibile</span><span className="text-navy-600">{fmt((r||localSummary).imponibile)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-gray-600">{'IVA ('+fmt(parseFloat(settings.aliquota_iva)||0.22,'pct')+')'}</span><span>{fmt((r||localSummary).iva)}</span></div>
        <div className="border-t pt-3 flex justify-between font-bold text-xl"><span>Totale Fattura</span><span className="text-accent-400">{fmt((r||localSummary).totaleFattura)}</span></div>
      </div></Card>

      <Card title="Il tuo guadagno">
        <div className="space-y-3">
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <div className="text-xs text-green-600 font-medium uppercase tracking-wider mb-1">Provvigione</div>
            <div className="text-3xl font-bold text-green-600">{fmt((r||localSummary).provvEur)}</div>
            <div className="text-xs text-gray-500 mt-1">{fmt((r||localSummary).provvPct,'pct')+' del '+(settings.base_calcolo_provv||'Fatturato').toLowerCase()}</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 text-center">
            <div className="text-xs text-blue-600 font-medium uppercase tracking-wider mb-1">MBO (Premio)</div>
            <div className="text-2xl font-bold text-blue-600">{fmt((r||localSummary).mbo)}</div>
            <div className="text-xs text-gray-500 mt-1">10% sulla provvigione</div>
          </div>
          <div className="bg-accent-50 border-2 border-accent-300 rounded-xl p-4 text-center">
            <div className="text-xs text-accent-400 font-bold uppercase tracking-wider mb-1">Totale guadagno</div>
            <div className="text-3xl font-extrabold text-accent-400">{fmt(((r||localSummary).provvEur||0)+((r||localSummary).mbo||0))}</div>
            {isSalesRole?<div className="text-xs text-gray-500 mt-1">Aggiornato in tempo reale in base agli sconti inseriti</div>:null}
          </div>
        </div>
      </Card>

      {canViewMargins?<Card title="Marginalita' dettaglio"><div className="space-y-3">
        {[['Totale Costo',r.totaleCosto,'eur'],['Margine Lordo',r.margineLordo,'eur'],['Margine %',r.marginePct,'pct'],
          ['Provvigione',r.provvEur,'eur'],['MBO',r.mbo,'eur'],['Utile Netto',r.utileNetto,'eur'],['Margine Netto',r.margineNetto,'pct']
        ].map(([l,v,t],i)=>(<div key={i} className={`flex justify-between text-sm ${String(l).includes('Netto')?'font-bold border-t pt-2':''}`}>
          <span className="text-gray-600">{l}</span><span className={v<0?'text-red-600':''}>{fmt(v,t)}</span></div>))}
      </div></Card>:null}
    </div>:null}

    <Card title="Noleggio"><div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
      <div><label className="block text-sm font-medium text-gray-600 mb-1">Anticipo %</label>
        <input type="number" min="0" max="100" value={p.noleggio_anticipo?(Number(p.noleggio_anticipo)*100):''} onChange={e=>upd({noleggio_anticipo:(parseFloat(e.target.value)||0)/100})} disabled={!canEditCurrentPreventivo}
          className="w-full px-3 py-2 border rounded-lg text-sm bg-yellow-50"/></div>
      <Sel label="Durata" value={String(p.noleggio_durata||'60')} onChange={v=>upd({noleggio_durata:v})} disabled={!canEditCurrentPreventivo}
        options={[{value:'18',label:'18 mesi'},{value:'24',label:'24 mesi'},{value:'30',label:'30 mesi'},{value:'36',label:'36 mesi'},{value:'48',label:'48 mesi'},{value:'60',label:'60 mesi'}]}/>
      <div><label className="block text-sm font-medium text-gray-600 mb-1">Coefficiente</label><div className="px-3 py-2 bg-gray-50 rounded-lg text-sm font-mono">{r?Number(r.coeff)||0:Number(localSummary?.coeff)||0}</div></div>
      <div className="bg-accent-400 text-white rounded-lg px-4 py-3 text-center"><div className="text-xs opacity-80">Rata mensile</div><div className="text-xl font-bold">{fmt(r?r.rataMensile:localSummary?.rataMensile||0)}</div></div>
    </div></Card>

    <Card title="Note"><div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {[{k:'note1',l:'Nota 1'},{k:'note2',l:'Nota 2'},{k:'note3',l:'Nota 3'},{k:'note4',l:'Nota 4'}].map(n=>(
        <div key={n.k}><label className="block text-xs text-gray-500 mb-1">{n.l}</label>
          <textarea value={String(p[n.k]||'')} onChange={e=>upd({[n.k]:e.target.value})} rows={2} disabled={!canEditCurrentPreventivo}
            className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/></div>))}</div></Card>
  </div>);
}

// ========== OFFERTA (Print) ==========
function OffertaPage(){
  const{preventivi,getCliente,activeArticles,settings}=useContext(DataCtx);
  const{isAdmin}=useContext(AuthCtx);
  const[selPrevId,setSelPrevId]=useState('');
  const prev=preventivi.find(p=>p.id===parseInt(selPrevId));
  const showNoleggio=prev?(prev.mostra_noleggio!==0&&prev.mostra_noleggio!=='0'):false;

  // Calculate for selected
  const r=useMemo(()=>{
    if(!prev)return null;
    const righeCalc=(prev.righe||[]).map(riga=>{
      const art=findArticleByReference(activeArticles,riga.articolo_nome);
      if(!art||!riga.qta)return{...riga,calc:{prezzoBase:0,prezzoNetto:0,totale:0}};
      const prezzoBase=art.prezzo_listino||calcPrezzi(art).prezzoConsigliato;
      const prezzoNetto=prezzoBase*(1-(riga.sconto||0));
      const totale=Math.round(riga.qta*prezzoNetto*100)/100;
      return{...riga,calc:{prezzoBase,prezzoNetto,totale}};
    });
    const totaleRighe=righeCalc.reduce((s,r)=>s+r.calc.totale,0);
    const imponibile=totaleRighe+(prev.trasporto||0)-(prev.trade_in||0);
    const aliqIva=parseFloat(settings.aliquota_iva)||0.22;
    const iva=imponibile*aliqIva;const totaleFattura=imponibile+iva;
    let rt=[];try{rt=JSON.parse(settings.rate_table||'[]')}catch(e){}
    const dk=prev.noleggio_durata||'60';let coeff=0;
    for(let i=rt.length-1;i>=0;i--){if(imponibile>=rt[i].importo){coeff=rt[i].mesi[dk]||0;break}}
    const impNol=imponibile*(1-(prev.noleggio_anticipo||0));
    const rataMensile=coeff>0?Math.round(impNol*coeff/100*100)/100:0;
    return{righeCalc,totaleRighe,imponibile,iva,totaleFattura,rataMensile,coeff};
  },[prev,activeArticles,settings]);

  if(!prev)return(
    <Page title="Offerta stampabile" subtitle="Anteprima documento commerciale">
      <Sel label="Seleziona preventivo" value={selPrevId} onChange={setSelPrevId}
        options={preventivi.map(p=>({value:String(p.id),label:S(p.codice)+' - '+S(getCliente(p.cliente_id)?.azienda||'N/D')}))} placeholder="-- Seleziona --" className="max-w-md"/>
      <Empty icon={<IcPrint s={36} c="text-accent-400"/>} title="Seleziona un preventivo" sub="Scegli un preventivo per generare l'offerta stampabile"/>
    </Page>);

  const cl=getCliente(prev.cliente_id);
  return(<Page title="Offerta stampabile" subtitle={S(prev.codice)} className="print-friendly">
    <PrintPageChrome settings={settings}/>
    <PageToolbar className="no-print">
      <Sel value={selPrevId} onChange={setSelPrevId}
        options={preventivi.map(p=>({value:String(p.id),label:S(p.codice)+' - '+S(getCliente(p.cliente_id)?.azienda||'N/D')}))} placeholder="-- Seleziona --" className="max-w-md"/>
      <Btn icon={<IcPrint s={16}/>} onClick={()=>window.print()}>Stampa</Btn>
    </PageToolbar>
    <div className="bg-white rounded-xl border p-8 print:p-0 print:border-0 print:shadow-none print-with-page-chrome">
      <div className="flex justify-between items-start mb-8">
        <div><h1 className="text-2xl font-bold text-navy-600">Complevia</h1>
          <p className="text-sm text-gray-500 mt-1">Soluzioni per la stampa digitale</p></div>
        <div className="text-right"><span className="text-sm text-gray-400">Preventivo</span>
          <div className="text-xl font-bold text-navy-600 font-mono">{S(prev.codice)}</div>
          <div className="text-sm text-gray-500">{fmtDate(prev.data)}</div></div>
      </div>
      {cl?<div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-2">Destinatario</h3>
        <div className="font-bold text-navy-600">{S(cl.azienda||cl.nome)}</div>
        {cl.via?<div className="text-sm text-gray-600">{S(cl.via)}</div>:null}
        {(cl.cap||cl.citta||cl.provincia)?<div className="text-sm text-gray-600">{[cl.cap,cl.citta,cl.provincia].filter(Boolean).map(String).join(' ')}</div>:null}
        {cl.piva?<div className="text-sm text-gray-500 mt-1">{'P.IVA: '+S(cl.piva)}</div>:null}
      </div>:null}
      <table className="w-full text-sm mb-6"><thead><tr className="bg-navy-600 text-white">
        <th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Descrizione</th>
        <th className="px-3 py-2 text-right">Qta</th><th className="px-3 py-2 text-right">Prezzo</th>
        <th className="px-3 py-2 text-right">Sc.%</th><th className="px-3 py-2 text-right">Totale</th></tr></thead>
        <tbody>{(r?r.righeCalc:[]).filter(rr=>rr.articolo_nome&&rr.qta>0).map((riga,i)=>(
          <tr key={i} className={`border-b ${i%2?'bg-gray-50':''}`}>
            <td className="px-3 py-2">{i+1}</td><td className="px-3 py-2">{S(riga.articolo_nome)}</td>
            <td className="px-3 py-2 text-right">{riga.qta}</td><td className="px-3 py-2 text-right">{fmt(riga.calc.prezzoBase)}</td>
            <td className="px-3 py-2 text-right">{riga.sconto?(riga.sconto*100).toFixed(0)+'%':'-'}</td>
            <td className="px-3 py-2 text-right font-medium">{fmt(riga.calc.totale)}</td></tr>))}</tbody></table>
      <div className="flex justify-end"><div className="w-72 space-y-2">
        <div className="flex justify-between text-sm"><span>Subtotale</span><span>{fmt(r.totaleRighe)}</span></div>
        {prev.trasporto>0&&<div className="flex justify-between text-sm"><span>Trasporto</span><span>{fmt(prev.trasporto)}</span></div>}
        {prev.trade_in>0&&<div className="flex justify-between text-sm"><span>Trade-in</span><span>-{fmt(prev.trade_in)}</span></div>}
        <div className="border-t pt-2 flex justify-between font-bold"><span>Imponibile</span><span>{fmt(r.imponibile)}</span></div>
        <div className="flex justify-between text-sm"><span>IVA</span><span>{fmt(r.iva)}</span></div>
        <div className="border-t pt-2 flex justify-between font-bold text-lg text-accent-400"><span>Totale</span><span>{fmt(r.totaleFattura)}</span></div>
        {showNoleggio&&r.rataMensile>0&&<div className="bg-accent-50 rounded-lg p-3 text-center mt-3">
          <div className="text-xs text-gray-500">oppure in {prev.noleggio_durata} rate da</div>
          <div className="text-lg font-bold text-accent-400">{fmt(r.rataMensile)}/mese</div></div>}
      </div></div>
      {(prev.note1||prev.note2||prev.note3||prev.note4)&&<div className="mt-8 pt-4 border-t">
        <h4 className="text-xs text-gray-400 uppercase tracking-wider mb-2">Note</h4>
        {[prev.note1,prev.note2,prev.note3,prev.note4].filter(Boolean).map((n,i)=><p key={i} className="text-sm text-gray-600">{n}</p>)}</div>}
    </div>
  </Page>);
}

// ========== BENEFICI ==========
function BeneficiPage(){
  const{preventivi,getCliente,activeArticles,settings}=useContext(DataCtx);
  const[selPrevId,setSelPrevId]=useState('');
  const prev=preventivi.find(p=>p.id===parseInt(selPrevId));

  const imp=useMemo(()=>{
    if(!prev)return 0;
    const tot=(prev.righe||[]).reduce((s,riga)=>{
      const art=findArticleByReference(activeArticles,riga.articolo_nome);
      if(!art||!riga.qta)return s;
      const prezzoBase=art.prezzo_listino||calcPrezzi(art).prezzoConsigliato;
      return s+riga.qta*prezzoBase*(1-(riga.sconto||0));
    },0);
    return tot+(prev.trasporto||0)-(prev.trade_in||0);
  },[prev,activeArticles]);

  const fiscalBenefits=useMemo(()=>calcFiscalBenefits(settings,imp),[settings,imp]);
  const bestFiscalBenefit=useMemo(()=>{
    if(!fiscalBenefits.length)return null;
    return [...fiscalBenefits].sort((a,b)=>b.saving-a.saving)[0]||null;
  },[fiscalBenefits]);

  if(!prev)return(
    <Page title="Benefici fiscali" subtitle="Agevolazioni sull'investimento">
      <Sel label="Seleziona preventivo" value={selPrevId} onChange={setSelPrevId}
        options={preventivi.map(p=>({value:String(p.id),label:S(p.codice)+' - '+S(getCliente(p.cliente_id)?.azienda||'N/D')}))} placeholder="-- Seleziona --" className="max-w-md"/>
      <Empty icon={<IcChart s={36} c="text-accent-400"/>} title="Seleziona un preventivo" sub="Scegli un preventivo per calcolare i benefici fiscali"/>
    </Page>);

  return(<Page title="Benefici fiscali" subtitle={S(prev.codice)}>
    <PageToolbar className="no-print"><Sel value={selPrevId} onChange={setSelPrevId}
      options={preventivi.map(p=>({value:String(p.id),label:S(p.codice)+' - '+S(getCliente(p.cliente_id)?.azienda||'N/D')}))} className="max-w-full md:max-w-md"/></PageToolbar>
    <div className="stat-tile-grid">
      <div className="stat-tile bg-slate-50"><div className="text-xs text-gray-500">Investimento</div><div className="text-2xl font-bold text-navy-600">{fmt(imp)}</div><p className="text-xs text-gray-500 mt-1">{S(prev.codice)}</p></div>
      <div className="stat-tile bg-green-50"><div className="text-xs text-gray-500">Migliore agevolazione</div><div className="text-2xl font-bold text-green-600">{bestFiscalBenefit?fmt(bestFiscalBenefit.saving):fmt(0)}</div><p className="text-xs text-gray-500 mt-1">{bestFiscalBenefit?S(bestFiscalBenefit.name):'—'}</p></div>
      <div className="stat-tile bg-blue-50"><div className="text-xs text-gray-500">Costo reale finale</div><div className="text-2xl font-bold text-blue-600">{bestFiscalBenefit?fmt(bestFiscalBenefit.realCost):fmt(imp)}</div></div>
    </div>
    <Card title="Riepilogo agevolazioni"><div className="space-y-3">
      <div className="flex justify-between"><span className="text-gray-600">Investimento lordo</span><span className="font-medium">{fmt(imp)}</span></div>
      {fiscalBenefits.map((benefit,idx)=><div key={benefit.id||idx} className="flex justify-between text-sm">
        <span className={bestFiscalBenefit&&bestFiscalBenefit.id===benefit.id?'text-green-700 font-medium':'text-gray-600'}>{S(benefit.name)}</span>
        <span className={bestFiscalBenefit&&bestFiscalBenefit.id===benefit.id?'font-semibold text-green-700':'font-medium text-gray-700'}>{fmt(benefit.realCost)}</span>
      </div>)}
      <div className="border-t pt-3 flex justify-between font-bold text-lg"><span>Miglior costo effettivo</span><span className="text-navy-600">{bestFiscalBenefit?fmt(bestFiscalBenefit.realCost):fmt(imp)}</span></div>
      <div className="bg-green-50 rounded-lg p-4 text-center mt-4">
        <div className="text-sm text-green-700">Risparmio totale stimato</div>
        <div className="text-2xl font-bold text-green-600">{bestFiscalBenefit?fmt(bestFiscalBenefit.saving):fmt(0)}</div>
        <div className="text-xs text-green-600 mt-1">{bestFiscalBenefit?fmt(bestFiscalBenefit.discountPct,'pct')+' dell\'investimento':'Nessuna agevolazione attiva'}</div></div>
    </div></Card>
  </Page>);
}

// ========== DOCUMENTI (Admin/Manager) ==========
function DocumentiPage({embedded=false}){
  const{documenti,loadDocumenti,showToast}=useContext(DataCtx);
  const{isAdmin}=useContext(AuthCtx);
  const[uploading,setUploading]=useState(false);
  const fileRef=useRef();

  const handleUpload=async(e)=>{
    const file=e.target.files[0];if(!file)return;
    const nome=prompt('Nome del documento:',file.name.replace(/\.[^.]+$/,''));
    if(!nome)return;
    const tipo=prompt('Tipo (contratto / noleggio / altro):','altro')||'altro';
    setUploading(true);
    try{
      const reader=new FileReader();
      const b64=await new Promise((res,rej)=>{reader.onload=()=>res(reader.result.split(',')[1]);reader.onerror=rej;reader.readAsDataURL(file)});
      await api('documento_create','POST',{file_data:b64,file_name:file.name,nome,tipo});
      showToast('Documento caricato');loadDocumenti();
    }catch(e){showToast(e.message,'error')}finally{setUploading(false);if(fileRef.current)fileRef.current.value=''}
  };

  const deleteDoc=async(id)=>{
    if(!confirm('Eliminare questo documento?'))return;
    try{await api('documento_delete','DELETE',null,{id});showToast('Documento eliminato');loadDocumenti()}catch(e){showToast(e.message,'error')}
  };

  const tipoLabel={contratto:'Contratto di vendita',noleggio:'Documenti noleggio',depliant:'Depliant',altro:'Altro'};
  const tipoColor={contratto:'bg-purple-100 text-purple-700',noleggio:'bg-orange-100 text-orange-700',depliant:'bg-sky-100 text-sky-700',altro:'bg-gray-100 text-gray-700'};

  const docList=(
    <Card>
      {documenti.length===0?<Empty icon={<IcFile s={32} c="text-slate-400"/>} title="Nessun documento" sub="Carica PDF o allegati per la stampa offerte."/>:(
      <div className="cplv-list-rows divide-y">{documenti.map(d=>(
        <div key={d.id} className="cplv-list-row flex items-center gap-4 py-3 px-2 group">
          <div className="cplv-list-row__icon w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0"><IcFile s={20} c="text-red-500"/></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900">{S(d.nome)}</div>
            <div className="text-xs text-slate-500">{S(d.original_name)}{d.created_at?' · '+fmtDate(d.created_at.substring(0,10)):''}</div>
          </div>
          <span className={`cplv-badge text-xs px-2.5 py-1 rounded-full ${tipoColor[d.tipo]||tipoColor.altro}`}>{tipoLabel[d.tipo]||S(d.tipo)}</span>
          {isAdmin?<button type="button" onClick={()=>deleteDoc(d.id)} className="cplv-icon-btn opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600" aria-label="Elimina"><IcTrash s={16}/></button>:null}
        </div>))}</div>)}
    </Card>
  );
  if(embedded)return<div className="page-stack space-y-4">{docList}</div>;
  return(
    <Page title="Documenti" subtitle="Contratti, noleggio e allegati per le offerte PDF" actions={isAdmin?<Btn icon={<IcUpload s={16}/>} onClick={()=>fileRef.current?.click()} disabled={uploading}>{uploading?'Caricamento…':'Carica documento'}</Btn>:null}>
      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg" onChange={handleUpload} className="hidden"/>
      {docList}
    </Page>
  );
}

function DepliantPage(){
  const{documenti,activeArticles,loadDocumenti,showToast}=useContext(DataCtx);
  const{isAdmin}=useContext(AuthCtx);
  const{isPhone}=useDevice();
  const[uploading,setUploading]=useState(false);
  const[search,setSearch]=useState('');
  const fileRef=useRef();

  const archivioDepliant=useMemo(()=>documenti.filter(d=>String(d.tipo||'').toLowerCase()==='depliant'),[documenti]);
  const catalogoDepliant=useMemo(()=>activeArticles
    .filter(a=>a.depliant_file)
    .map(a=>({
      id:`art-${a.id}`,
      nome:a.nome||'Articolo senza nome',
      filename:a.depliant_file,
      original_name:a.depliant_file,
      created_at:a.updated_at||a.created_at||'',
      source:'catalogo',
      sku:a.sku||'',
      categoria:a.categoria||'',
    })),[activeArticles]);

  const normalizedSearch=search.trim().toLowerCase();
  const matchItem=item=>!normalizedSearch||[
    item.nome,
    item.original_name,
    item.sku,
    item.categoria,
    item.filename,
  ].filter(Boolean).some(v=>String(v).toLowerCase().includes(normalizedSearch));

  const visibleArchivio=archivioDepliant.filter(matchItem);
  const visibleCatalogo=catalogoDepliant.filter(matchItem);

  const handleUpload=async(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    const nome=prompt('Nome del depliant:',file.name.replace(/\.[^.]+$/,''))||'';
    if(!nome.trim()){
      if(fileRef.current)fileRef.current.value='';
      return;
    }
    setUploading(true);
    try{
      const reader=new FileReader();
      const b64=await new Promise((res,rej)=>{reader.onload=()=>res(reader.result.split(',')[1]);reader.onerror=rej;reader.readAsDataURL(file)});
      await api('documento_create','POST',{file_data:b64,file_name:file.name,nome:nome.trim(),tipo:'depliant'});
      showToast('Depliant caricato');
      await loadDocumenti();
    }catch(err){showToast(err.message,'error')}
    finally{
      setUploading(false);
      if(fileRef.current)fileRef.current.value='';
    }
  };

  const deleteDepliant=async(id)=>{
    if(!confirm('Eliminare questo depliant?'))return;
    try{
      await api('documento_delete','DELETE',null,{id});
      showToast('Depliant eliminato');
      await loadDocumenti();
    }catch(err){showToast(err.message,'error')}
  };

  const renderItem=(item,isArchivio)=>(
    <div key={item.id} className="flex items-center gap-4 py-3 px-2 hover:bg-gray-50 rounded-xl">
      <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center shrink-0"><IcFile s={20} c="text-sky-500"/></div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-navy-600 break-words">{S(item.nome)}</div>
        <div className="text-xs text-gray-400 break-words">
          {S(item.original_name||item.filename)}
          {item.sku?` · SKU ${S(item.sku)}`:''}
          {item.categoria?` · ${S(item.categoria)}`:''}
          {item.created_at?` · ${fmtDate(String(item.created_at).substring(0,10))}`:''}
        </div>
      </div>
      <span className={`text-xs px-2 py-1 rounded-full ${isArchivio?'bg-sky-100 text-sky-700':'bg-indigo-100 text-indigo-700'}`}>{isArchivio?'Archivio':'Catalogo'}</span>
      <Btn s="sm" v="secondary" icon={<IcDl s={14}/>} onClick={()=>downloadStoredFile(item.filename,item.original_name||item.nome||'depliant')}>Scarica</Btn>
      {isArchivio&&isAdmin?<button onClick={()=>deleteDepliant(item.id)} className="p-1.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-600"><IcTrash s={16}/></button>:null}
    </div>
  );

  return(<Page title="Depliant" subtitle="Materiali commerciali scaricabili" actions={isAdmin?<Btn s={isPhone?'sm':'md'} icon={<IcUpload s={16}/>} onClick={()=>fileRef.current?.click()} disabled={uploading}>{uploading?'Caricamento...':'Carica'}</Btn>:null}>
    <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={handleUpload} className="hidden"/>
    <PageToolbar>
      <div className="relative flex-1 w-full"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca depliant..." className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400 min-h-[44px]"/>
      </div>
    </PageToolbar>

    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <Card title="Archivio Depliant">
        {visibleArchivio.length===0?<Empty icon={<IcFile s={32} c="text-sky-400"/>} title="Nessun depliant in archivio" sub="Carica PDF o documenti commerciali generici da mettere a disposizione del team."/>:
          <div className="divide-y">{visibleArchivio.map(item=>renderItem(item,true))}</div>}
      </Card>
      <Card title="Depliant Collegati agli Articoli">
        {visibleCatalogo.length===0?<Empty icon={<IcPkg s={32} c="text-indigo-400"/>} title="Nessun depliant collegato al catalogo" sub="I depliant caricati negli articoli compariranno qui, pronti da scaricare."/>:
          <div className="divide-y">{visibleCatalogo.map(item=>renderItem(item,false))}</div>}
      </Card>
    </div>
  </Page>);
}

// ========== STAMPA (PDF Generation) ==========
function StampaPage({initialPrevId}){
  const{preventivi,getCliente,activeArticles,documenti,settings,loadPreventivi,loadClienti,loadArticoli,loadDocumenti}=useContext(DataCtx);
  const{isAdmin}=useContext(AuthCtx);
  const{isPhone}=useDevice();
  const AZ=getAzienda(settings);
  const[selPrevId,setSelPrevId]=useState('');
  const[sections,setSections]=useState({offerta:true,benefici:false,depliant:false,documenti:[]});
  const[generating,setGenerating]=useState(false);
  const printRef=useRef();
  const benefRef=useRef();
  useEffect(()=>{
    loadPreventivi({includeRighe:false}).catch(()=>{});
    loadClienti({enrich:'0',light:'1'}).catch(()=>{});
    loadArticoli().catch(()=>{});
    loadDocumenti().catch(()=>{});
  },[loadPreventivi,loadClienti,loadArticoli,loadDocumenti]);
  const prev=preventivi.find(p=>p.id===parseInt(selPrevId));
  const showNoleggio=prev?(prev.mostra_noleggio!==0&&prev.mostra_noleggio!=='0'):false;

  useEffect(()=>{
    if(initialPrevId)setSelPrevId(String(initialPrevId));
  },[initialPrevId]);

  const toggleDoc=(id)=>setSections(s=>{const docs=s.documenti.includes(id)?s.documenti.filter(x=>x!==id):[...s.documenti,id];return{...s,documenti:docs}});
  const toggleSec=(k)=>setSections(s=>({...s,[k]:!s[k]}));

  // Calculate for selected
  const r=useMemo(()=>{
    if(!prev)return null;
    const righeCalc=(prev.righe||[]).map(riga=>{
      const art=findArticleByReference(activeArticles,riga.articolo_nome);
      if(!art||!riga.qta)return{...riga,calc:{prezzoBase:0,prezzoNetto:0,totale:0}};
      const prezzoBase=art.prezzo_listino||calcPrezzi(art).prezzoConsigliato;
      const prezzoNetto=prezzoBase*(1-(riga.sconto||0));
      const totale=Math.round(riga.qta*prezzoNetto*100)/100;
      return{...riga,calc:{prezzoBase,prezzoNetto,totale}};
    });
    const totaleRighe=righeCalc.reduce((s,r)=>s+r.calc.totale,0);
    const imponibile=totaleRighe+(Number(prev.trasporto)||0)-(Number(prev.trade_in)||0);
    const aliqIva=parseFloat(settings.aliquota_iva)||0.22;
    const iva=imponibile*aliqIva;const totaleFattura=imponibile+iva;
    let rt=[];try{rt=JSON.parse(settings.rate_table||'[]')}catch(e){}
    const dk=prev.noleggio_durata||'60';let coeff=0;
    for(let i=rt.length-1;i>=0;i--){if(imponibile>=rt[i].importo){coeff=rt[i].mesi[dk]||0;break}}
    const impNol=imponibile*(1-(prev.noleggio_anticipo||0));
    const rataMensile=coeff>0?Math.round(impNol*coeff/100*100)/100:0;
    const fiscalBenefits=calcFiscalBenefits(settings,imponibile);
    const bestFiscalBenefit=fiscalBenefits.length?[...fiscalBenefits].sort((a,b)=>b.saving-a.saving)[0]:null;
    return{righeCalc,totaleRighe,imponibile,iva,totaleFattura,rataMensile,coeff,fiscalBenefits,bestFiscalBenefit};
  },[prev,activeArticles,settings]);

  const cl=prev?getCliente(prev.cliente_id):null;
  const clienteNome=useMemo(()=>{
    if(cl?.azienda)return S(cl.azienda);
    const full=[cl?.nome,cl?.cognome].filter(Boolean).join(' ').trim();
    if(full)return S(full);
    if(prev?.cliente_azienda)return S(prev.cliente_azienda);
    if(prev?.cliente_nome)return S(prev.cliente_nome);
    if(prev?.cliente)return S(prev.cliente);
    return 'Cliente non assegnato';
  },[cl,prev]);
  const clienteContatto=useMemo(()=>{
    const full=[cl?.nome,cl?.cognome].filter(Boolean).join(' ').trim();
    if(cl?.azienda&&full)return full;
    if(!cl?.azienda&&full&&full!==clienteNome)return full;
    if(prev?.cliente_nome&&prev.cliente_nome!==clienteNome)return S(prev.cliente_nome);
    return'';
  },[cl,prev,clienteNome]);

  // Depliant available from articles in the preventivo
  const depliants=useMemo(()=>{
    if(!prev)return[];
    return(prev.righe||[]).map(riga=>{
      const art=findArticleByReference(activeArticles,riga.articolo_nome);
      return art&&art.depliant_file?{nome:art.nome,file:art.depliant_file}:null;
    }).filter(Boolean);
  },[prev,activeArticles]);

  const generatePDF=async()=>{
    if(!prev||!r)return;
    setGenerating(true);
    try{
      const PDFLib=await ensurePdfLib();
      const{PDFDocument}=PDFLib;
      const finalPdf=await PDFDocument.create();
      const paginationOptions={pageWidth:595.28,pageHeight:841.89,margin:20,scale:2,topInset:64,bottomInset:26};

      // 1) Offerta page
      if(sections.offerta&&printRef.current){
        await appendElementAsPaginatedPdf(finalPdf,printRef.current,paginationOptions);
      }

      // 2) Benefici page
      if(sections.benefici&&benefRef.current){
        await appendElementAsPaginatedPdf(finalPdf,benefRef.current,paginationOptions);
      }

      // 3) Depliant PDFs
      if(sections.depliant){
        for(const dep of depliants){
          try{
            const fd=await api('file_download','GET',null,{filename:dep.file});
            const bytes=Uint8Array.from(atob(fd.data),c=>c.charCodeAt(0));
            const depPdf=await PDFDocument.load(bytes);
            const pages=await finalPdf.copyPages(depPdf,depPdf.getPageIndices());
            pages.forEach(p=>finalPdf.addPage(p));
          }catch(e){console.error('Depliant error:',dep.file,e)}
        }
      }

      // 4) Documents
      for(const docId of sections.documenti){
        const doc=documenti.find(d=>d.id===docId);
        if(!doc)continue;
        try{
          const fd=await api('file_download','GET',null,{filename:doc.filename});
          if(fd.mime==='application/pdf'){
            const bytes=Uint8Array.from(atob(fd.data),c=>c.charCodeAt(0));
            const docPdf=await PDFDocument.load(bytes);
            const pages=await finalPdf.copyPages(docPdf,docPdf.getPageIndices());
            pages.forEach(p=>finalPdf.addPage(p));
          }else{
            // Image document
            const imgBytes=Uint8Array.from(atob(fd.data),c=>c.charCodeAt(0));
            const img=fd.mime.includes('png')?await finalPdf.embedPng(imgBytes):await finalPdf.embedJpg(imgBytes);
            const page=finalPdf.addPage([595.28,841.89]);
            const{width,height}=page.getSize();
            const scale=Math.min(width/img.width,height/img.height)*0.9;
            page.drawImage(img,{x:(width-img.width*scale)/2,y:(height-img.height*scale)/2,width:img.width*scale,height:img.height*scale});
          }
        }catch(e){console.error('Document error:',doc.filename,e)}
      }

      await decoratePdfWithPageChrome(finalPdf,{logoSrc:settings?.az_logo||APP_LOGO_URL,headerMode:'after-first'});

      // Download
      const pdfBytes=await finalPdf.save();
      const blob=new Blob([pdfBytes],{type:'application/pdf'});
      const url=URL.createObjectURL(blob);
      const a=document.createElement('a');
      a.href=url;a.download=`Offerta_${prev.codice}_${today()}.pdf`;a.click();
      URL.revokeObjectURL(url);
    }catch(e){console.error('PDF Error:',e);alert('Errore generazione PDF: '+e.message)}
    finally{setGenerating(false)}
  };

  if(!prev)return(
    <Page title="Stampa documenti" subtitle="PDF offerta, benefici e allegati">
      <Sel label="Seleziona preventivo" value={selPrevId} onChange={setSelPrevId}
        options={preventivi.map(p=>({value:String(p.id),label:S(p.codice)+' - '+S(getCliente(p.cliente_id)?.azienda||'N/D')}))} placeholder="-- Seleziona --" className="max-w-md"/>
      <Empty icon={<IcPrint s={36} c="text-accent-400"/>} title="Seleziona un preventivo" sub="Scegli un preventivo per generare i documenti di stampa"/>
    </Page>);

  const bestFiscalBenefit=r?.bestFiscalBenefit||null;
  const fiscalBenefits=r?.fiscalBenefits||[];

  return(<Page title="Stampa documenti" subtitle={S(prev.codice)} actions={
    <div className="flex gap-2 flex-wrap">
      <Btn v="secondary" s={isPhone?'sm':'lg'} icon={<IcPrint s={18}/>} onClick={()=>window.print()}>Stampa</Btn>
      <Btn s={isPhone?'sm':'lg'} icon={<IcPrint s={18}/>} onClick={generatePDF} disabled={generating}>{generating?'PDF...':'Genera PDF'}</Btn>
    </div>
  }>
    <PrintPageChrome settings={settings}/>
    <PageToolbar className="no-print">
      <Sel value={selPrevId} onChange={setSelPrevId}
        options={preventivi.map(p=>({value:String(p.id),label:S(p.codice)+' - '+S(getCliente(p.cliente_id)?.azienda||'N/D')}))} placeholder="-- Seleziona --" className="flex-1 min-w-[200px]"/>
    </PageToolbar>

    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
      <div>
        {/* Offerta preview */}
        <div ref={printRef} className="bg-white rounded-2xl border border-slate-200 p-6 md:p-10 shadow-sm max-w-[980px] mx-auto overflow-hidden print:shadow-none print:border-0 print:p-0 print-break-avoid print-with-page-chrome" style={{fontFamily:'Inter,Arial,sans-serif'}}>
          <div className="space-y-5 pb-6 border-b-2 border-navy-600 print-break-avoid">
            <div className="grid grid-cols-1 md:grid-cols-[4.8cm_minmax(0,1fr)] gap-6 items-start">
              <div className="border border-slate-200 rounded-xl bg-white min-h-[4.8cm] flex items-center justify-center p-3">
                <CompanyLogo settings={settings} maxH={116} maxW="100%" style={{maxWidth:'100%',width:'100%',margin:'0 auto'}}/>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 text-right md:max-w-[6.6cm] md:ml-auto min-w-0 wrap-safe">
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 mb-1">Azienda</div>
                <div className="font-semibold text-sm leading-tight text-navy-600 wrap-safe">{AZ.nome}</div>
                {AZ.sottotitolo?<div className="text-[11px] text-gray-500 mt-1 leading-tight">{AZ.sottotitolo}</div>:null}
                <div className="mt-2 space-y-1 text-[11px] leading-tight text-gray-500 wrap-safe">
                  {AZ.via?<div>{AZ.via}</div>:null}
                  {(AZ.cap||AZ.citta||AZ.provincia||AZ.paese)?<div>{[AZ.cap,AZ.citta,AZ.provincia,AZ.paese].filter(Boolean).join(' ')}</div>:null}
                  {AZ.tel?<div>{'Tel. '+AZ.tel}</div>:null}
                  {AZ.email?<div className="break-all">{AZ.email}</div>:null}
                  {AZ.web?<div className="break-all">{AZ.web}</div>:null}
                  {AZ.piva?<div>{'P.IVA '+AZ.piva}</div>:null}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-5 items-start">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 min-w-0 wrap-safe">
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 mb-2">Cliente</div>
                <div className="font-bold text-navy-600 text-lg leading-tight wrap-safe">{clienteNome}</div>
                {clienteContatto?<div className="text-sm text-gray-600 mt-1">{'Alla c.a. '+clienteContatto}</div>:null}
                {cl?.via?<div className="text-sm text-gray-600 mt-3">{S(cl.via)}</div>:null}
                {(cl?.cap||cl?.citta||cl?.provincia||cl?.regione)?<div className="text-sm text-gray-600">{[cl.cap,cl.citta,cl.provincia,cl.regione].filter(Boolean).join(' ')}</div>:null}
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                  {cl?.piva?<div>{'P.IVA: '+S(cl.piva)}</div>:null}
                  {cl?.email?<div>{'Email: '+S(cl.email)}</div>:null}
                  {cl?.telefono?<div>{'Tel: '+S(cl.telefono)}</div>:null}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-right min-w-[180px] max-w-[220px] wrap-safe">
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 mb-1">Offerta</div>
                <div className="text-sm font-semibold text-navy-600">{'N. '+S(prev.codice)}</div>
                <div className="text-xs text-gray-500 mt-1">{'Data '+fmtDate(prev.data)}</div>
                {prev.agente?<div className="text-xs text-gray-500 mt-2">{'Commerciale: '+S(prev.agente)}</div>:null}
              </div>
            </div>
          </div>

          <div className="my-7"></div>

          <div className="rounded-2xl border border-slate-200 overflow-hidden print-break-avoid">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" style={{borderCollapse:'collapse'}}>
              <thead>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.15em]" style={{width:'40px'}}>#</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.15em]">Descrizione</th>
                  <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.15em]" style={{width:'70px'}}>Qta</th>
                  <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.15em]" style={{width:'120px'}}>Prezzo</th>
                  <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.15em]" style={{width:'80px'}}>Sc.</th>
                  <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.15em]" style={{width:'130px'}}>Totale</th>
                </tr>
              </thead>
              <tbody>{(r?r.righeCalc:[]).filter(rr=>rr.articolo_nome&&rr.qta>0).map((riga,i)=>{
                const art=findArticleByReference(activeArticles,riga.articolo_nome);
                const hasFoto=art&&art.foto;
                const hasDesc=art&&art.descrizione_tecnica;
                return(<React.Fragment key={i}>
                  <tr className={`border-t border-slate-200 ${i%2?'bg-slate-50/70':'bg-white'}`}>
                    <td className="px-4 py-4 text-gray-400 align-top">{i+1}</td>
                    <td className="px-4 py-4 align-top">
                      <div className="font-semibold text-navy-600">{S(riga.articolo_nome)}</div>
                      {hasDesc?<div className="text-xs text-gray-500 leading-relaxed mt-1 whitespace-pre-line">{S(art.descrizione_tecnica)}</div>:null}
                    </td>
                    <td className="px-4 py-4 text-center align-top font-medium">{riga.qta}</td>
                    <td className="px-4 py-4 text-right align-top">{fmt(riga.calc.prezzoBase)}</td>
                    <td className="px-4 py-4 text-center align-top">{riga.sconto?(riga.sconto*100).toFixed(0)+'%':'-'}</td>
                    <td className="px-4 py-4 text-right align-top font-bold text-navy-600">{fmt(riga.calc.totale)}</td>
                  </tr>
                  {hasFoto?<tr className={i%2?'bg-slate-50/70':'bg-white'}>
                    <td></td>
                    <td colSpan="5" className="px-4 pb-4">
                      <img src={art.foto} alt={S(art.nome)} style={{width:'84px',height:'84px',objectFit:'contain',borderRadius:'8px',border:'1px solid #e5e7eb'}}/>
                    </td>
                  </tr>:null}
                </React.Fragment>);
              })}</tbody>
            </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-6 mt-7 items-start print-break-avoid">
            <div className="space-y-4">
              {(prev.note1||prev.note2||prev.note3||prev.note4)?<div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-gray-400 mb-3">Note e condizioni</div>
                <div className="space-y-2">{[prev.note1,prev.note2,prev.note3,prev.note4].filter(Boolean).map((n,i)=><div key={i} className="flex gap-2 text-sm text-gray-600">
                  <span className="text-accent-400 font-bold">{i+1}.</span><span>{S(n)}</span>
                </div>)}</div>
              </div>:null}
              {showNoleggio&&r&&r.rataMensile>0?<div className="rounded-xl border border-accent-200 bg-accent-50 px-5 py-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent-400 mb-2">Noleggio strumentale</div>
                <div className="text-sm text-gray-600">{'Proposta su '+S(prev.noleggio_durata)+' mesi'}</div>
                <div className="text-3xl font-bold text-accent-400 mt-1">{fmt(r.rataMensile)+' + IVA/mese'}</div>
              </div>:null}
              {sections.benefici&&bestFiscalBenefit?<div className="rounded-xl border border-green-200 bg-green-50 px-5 py-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-green-600 mb-2">Agevolazione fiscale evidenziata</div>
                <div className="font-semibold text-green-700">{S(bestFiscalBenefit.name)}</div>
                <div className="text-sm text-green-700 mt-1">{'Costo reale finale stimato: '+fmt(bestFiscalBenefit.realCost)}</div>
                <div className="text-xs text-green-600 mt-1">{'Risparmio complessivo '+fmt(bestFiscalBenefit.saving)+' · '+fmt(bestFiscalBenefit.discountPct,'pct')}</div>
              </div>:null}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="bg-navy-600 text-white px-5 py-3 text-sm font-semibold">Riepilogo economico</div>
              <div className="px-5 py-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Subtotale articoli</span><span className="font-medium">{fmt(r?r.totaleRighe:0)}</span></div>
                {Number(prev.trasporto)>0?<div className="flex justify-between"><span className="text-gray-600">Trasporto e installazione</span><span className="font-medium">{fmt(prev.trasporto)}</span></div>:null}
                {Number(prev.trade_in)>0?<div className="flex justify-between"><span className="text-gray-600">Permuta</span><span className="font-medium text-green-600">{'-'+fmt(prev.trade_in)}</span></div>:null}
                <div className="border-t pt-2 flex justify-between font-semibold text-navy-600"><span>Imponibile</span><span>{fmt(r?r.imponibile:0)}</span></div>
                <div className="flex justify-between text-gray-500"><span>IVA 22%</span><span>{fmt(r?r.iva:0)}</span></div>
                <div className="border-t pt-3 flex justify-between text-lg font-bold text-accent-400"><span>Totale offerta</span><span>{fmt(r?r.totaleFattura:0)}</span></div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 mt-10 pt-6 border-t border-slate-200">
            <div className="rounded-xl border border-slate-200 px-5 py-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-gray-400 mb-2">Il fornitore</div>
              <div className="text-sm text-gray-600 mb-10">{AZ.nome}</div>
              <div className="border-b border-slate-300 mb-2"></div>
              <div className="text-xs text-gray-400">Firma e timbro</div>
            </div>
            <div className="rounded-xl border border-slate-200 px-5 py-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-gray-400 mb-2">Per accettazione</div>
              <div className="text-sm text-gray-600 mb-10">{S(cl?cl.azienda||cl.nome:'Il cliente')}</div>
              <div className="border-b border-slate-300 mb-2"></div>
              <div className="text-xs text-gray-400">Firma cliente</div>
            </div>
          </div>
        </div>

        {/* Benefici (hidden for capture) */}
        {sections.benefici&&r?<div ref={benefRef} className="bg-white rounded-xl border border-slate-200 p-6 md:p-8 mt-4 max-w-[980px] mx-auto print:border-0 print:p-0 print-with-page-chrome">
          <div className="flex items-start justify-between gap-6 pb-5 border-b border-slate-200">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Agevolazioni fiscali</div>
              <h2 className="text-2xl font-bold text-navy-600">Scenari fiscali configurabili</h2>
              <div className="text-sm text-slate-500 mt-1">{'Offerta '+S(prev.codice)+' · '+S(cl?.azienda||cl?.nome||'Cliente')}</div>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 px-5 py-4 text-right min-w-[220px]">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Investimento</div>
              <div className="text-2xl font-bold text-navy-600">{fmt(r.imponibile)}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 mb-6">
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Migliore scenario</div>
              <div className="font-semibold text-navy-600">{bestFiscalBenefit?S(bestFiscalBenefit.name):'Nessuna agevolazione attiva'}</div>
              <div className="text-sm text-slate-500 mt-1">{bestFiscalBenefit?'Calcolo basato sui parametri attivi':'Configura le agevolazioni nelle impostazioni'}</div>
            </div>
            <div className="rounded-2xl bg-green-50 border border-green-100 p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-green-600 mb-2">Risparmio complessivo</div>
              <div className="text-2xl font-bold text-green-700">{bestFiscalBenefit?fmt(bestFiscalBenefit.saving):fmt(0)}</div>
              <div className="text-sm text-green-600 mt-1">{bestFiscalBenefit?fmt(bestFiscalBenefit.discountPct,'pct')+' dell\'investimento':'Nessun valore disponibile'}</div>
            </div>
            <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-blue-600 mb-2">Costo reale finale</div>
              <div className="text-2xl font-bold text-blue-700">{bestFiscalBenefit?fmt(bestFiscalBenefit.realCost):fmt(r.imponibile)}</div>
              <div className="text-sm text-blue-600 mt-1">{bestFiscalBenefit?'Valore stimato dopo incentivi e impatto fiscale':'Nessuna simulazione attiva'}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm rounded-2xl overflow-hidden">
              <thead>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="px-3 py-3 text-left text-[11px] uppercase tracking-[0.15em]">Scenario</th>
                  <th className="px-3 py-3 text-right text-[11px] uppercase tracking-[0.15em]">Incentivo</th>
                  <th className="px-3 py-3 text-right text-[11px] uppercase tracking-[0.15em]">Sabatini</th>
                  <th className="px-3 py-3 text-right text-[11px] uppercase tracking-[0.15em]">Risparmio fiscale</th>
                  <th className="px-3 py-3 text-right text-[11px] uppercase tracking-[0.15em]">Tasse</th>
                  <th className="px-3 py-3 text-right text-[11px] uppercase tracking-[0.15em]">Costo reale</th>
                  <th className="px-3 py-3 text-right text-[11px] uppercase tracking-[0.15em]">Riduzione</th>
                </tr>
              </thead>
              <tbody>
                {fiscalBenefits.length===0?<tr><td colSpan="7" className="px-3 py-6 text-center text-gray-400">Nessuna agevolazione fiscale attiva.</td></tr>:fiscalBenefits.map((benefit,idx)=>{
                  const isBest=bestFiscalBenefit&&bestFiscalBenefit.id===benefit.id;
                  return(<tr key={benefit.id||idx} className={`border-t border-slate-200 ${isBest?'bg-green-50/70':(idx%2?'bg-slate-50/50':'bg-white')}`}>
                    <td className="px-3 py-3">
                      <div className="font-semibold text-navy-600">{S(benefit.name)}</div>
                      {isBest?<div className="text-[11px] text-green-600 mt-1">Scenario consigliato</div>:null}
                    </td>
                    <td className="px-3 py-3 text-right">{fmt(benefit.incentive)}</td>
                    <td className="px-3 py-3 text-right">{fmt(benefit.sabatini)}</td>
                    <td className="px-3 py-3 text-right">{fmt(benefit.fiscalSaving)}</td>
                    <td className="px-3 py-3 text-right">{fmt(benefit.taxes)}</td>
                    <td className="px-3 py-3 text-right font-semibold text-navy-600">{fmt(benefit.realCost)}</td>
                    <td className="px-3 py-3 text-right font-medium">{fmt(benefit.discountPct,'pct')}</td>
                  </tr>);
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-5 text-xs text-slate-500 leading-relaxed">
            Le agevolazioni riportate sono configurabili nelle impostazioni e possono essere modificate, aggiunte o rimosse in base agli aggiornamenti normativi.
          </div>
        </div>:null}
      </div>

      {/* Sidebar: section selector */}
      <div className="no-print space-y-4">
        <Card title="Contenuto PDF">
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={sections.offerta} onChange={()=>toggleSec('offerta')} className="w-4 h-4 text-accent-400 rounded"/>
              <span className="text-sm font-medium">Offerta / Preventivo</span></label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={sections.benefici} onChange={()=>toggleSec('benefici')} className="w-4 h-4 text-accent-400 rounded"/>
              <span className="text-sm font-medium">Agevolazioni fiscali</span></label>
            {depliants.length>0?<React.Fragment>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={sections.depliant} onChange={()=>toggleSec('depliant')} className="w-4 h-4 text-accent-400 rounded"/>
                <span className="text-sm font-medium">Depliant Articoli</span></label>
              {sections.depliant?<div className="ml-7 space-y-1">
                {depliants.map((d,i)=><div key={i} className="text-xs text-gray-500 flex items-center gap-1"><IcClip s={12}/>{S(d.nome)}</div>)}
              </div>:null}
            </React.Fragment>:null}
          </div>
        </Card>
        {documenti.length>0?<Card title="Documenti Allegati">
          <div className="space-y-3">
            {documenti.map(d=>(
              <label key={d.id} className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={sections.documenti.includes(d.id)} onChange={()=>toggleDoc(d.id)} className="w-4 h-4 text-accent-400 rounded"/>
                <div><div className="text-sm font-medium">{S(d.nome)}</div>
                  <div className="text-xs text-gray-400">{S(d.tipo)}</div></div>
              </label>))}
          </div>
        </Card>:null}
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="text-xs text-gray-500 mb-1">Pagine stimate</div>
          <div className="text-2xl font-bold text-navy-600">{(sections.offerta?1:0)+(sections.benefici?1:0)+(sections.depliant?depliants.length:0)+sections.documenti.length}</div>
        </div>
      </div>
    </div>
  </Page>);
}

// ========== IMPOSTAZIONI (Admin) ==========
function SettingsGroup({id,title,children,description='',action=null,isOpen,onToggle}){
  return(<Card className="settings-group">
    <div className="settings-group__head flex items-start justify-between gap-3">
      <button type="button" onClick={()=>onToggle(id)} className="flex-1 min-w-0 text-left">
        <div className="font-semibold text-navy-600">{title}</div>
        {description?<div className="text-xs text-gray-500 mt-1">{description}</div>:null}
      </button>
      <div className="flex items-center gap-2 shrink-0">
        {action}
        <button type="button" onClick={()=>onToggle(id)} aria-expanded={isOpen} className={`settings-group__chevron ${isOpen?'is-open':''}`}>›</button>
      </div>
    </div>
    {isOpen?<div className="settings-group__body mt-4 pt-4 border-t border-slate-100">{children}</div>:null}
  </Card>);
}

function ImpostazioniPage(){
  const{isAdmin,isManager}=useContext(AuthCtx);
  const{settings,loadSettings,showToast,loadArticoli,loadClienti,loadPreventivi,loadOpportunita,loadOpportunityStats,loadDocumenti,invalidateArticoliCache}=useContext(DataCtx);
  const{isPhone}=useDevice();
  const[s,setS]=useState({});
  const[rateTable,setRateTable]=useState([]);
  const[fiscalBenefits,setFiscalBenefits]=useState([]);
  const[section,setSection]=useState(isAdmin?'generali':'documenti');
  const[openGroups,setOpenGroups]=useState({});
  const defaultOpenGroups=isPhone?{moduli:true}:{moduli:true,generali:true,performance:true};
  const isGroupOpen=id=>openGroups[id]!==undefined?!!openGroups[id]:!!defaultOpenGroups[id];
  const[backupArchives,setBackupArchives]=useState([]);
  const[backupBusy,setBackupBusy]=useState('');
  const[debugData,setDebugData]=useState({entries:[],summary:{total:0,by_level:{},by_source:{}},meta:{}});
  const[debugBusy,setDebugBusy]=useState('');
  const backupFileRef=useRef();
  useEffect(()=>{
    setS({...settings});
    try{setRateTable(JSON.parse(settings.rate_table||'[]'))}catch(e){setRateTable([])}
    setFiscalBenefits(parseFiscalBenefits(settings));
  },[settings]);
  useEffect(()=>{setSection(isAdmin?'generali':'documenti')},[isAdmin]);
  const u=(k,v)=>setS(p=>({...p,[k]:v}));
  const toggleGroup=id=>setOpenGroups(prev=>({...prev,[id]:!(prev[id]!==undefined?prev[id]:defaultOpenGroups[id])}));

  const DURATE=['18','24','30','36','48','60'];
  const updateRate=(idx,field,value)=>{
    setRateTable(prev=>{const next=[...prev];next[idx]={...next[idx]};
      if(field==='importo'){next[idx].importo=parseFloat(value)||0}
      else{next[idx].mesi={...next[idx].mesi,[field]:parseFloat(value)||0};}
      return next;});
  };
  const addRateRow=()=>setRateTable(prev=>[...prev,{importo:0,mesi:{'18':0,'24':0,'30':0,'36':0,'48':0,'60':0}}]);
  const removeRateRow=(idx)=>setRateTable(prev=>prev.filter((_,i)=>i!==idx));
  const backupAutoEnabled=settingEnabled(s.backup_auto_enabled??'0');
  const updateFiscalBenefit=(idx,field,value)=>setFiscalBenefits(prev=>prev.map((item,i)=>i!==idx?item:{
    ...item,
    [field]:field==='name'||field==='tax_base_mode'||field==='id'?value:field==='enabled'?!!value:toNum(value)
  }));
  const addFiscalBenefit=()=>setFiscalBenefits(prev=>[...prev,normalizeFiscalBenefit({id:`benefit_${Date.now()}`,name:`Nuova agevolazione ${prev.length+1}`,enabled:true,cash_rate:0,cash_cap:0,cash_multiplier:1,sabatini_rate:0,sabatini_min_amount:0,ammortamento_rate:0,tax_rate:0.24,tax_base_mode:'none'},prev.length)]);
  const removeFiscalBenefit=idx=>setFiscalBenefits(prev=>prev.filter((_,i)=>i!==idx));

  const refreshAllData=useCallback(async()=>{
    await Promise.all([
      loadSettings(),
      loadArticoli(),
      loadClienti(),
      loadPreventivi(),
      loadOpportunita(),
      loadOpportunityStats(),
      loadDocumenti(),
    ]);
  },[loadSettings,loadArticoli,loadClienti,loadPreventivi,loadOpportunita,loadOpportunityStats,loadDocumenti]);

  const loadBackupArchives=useCallback(async()=>{
    if(!isAdmin)return;
    try{
      const d=await api('backup_archives');
      setBackupArchives(d.data||[]);
    }catch(e){showToast(e.message,'error')}
  },[isAdmin,showToast]);

  const loadDebugLogs=useCallback(async()=>{
    if(!isAdmin)return;
    setDebugBusy('load');
    try{
      const d=await api('debug_logs','GET',null,{limit:200});
      setDebugData(d.data||{entries:[],summary:{total:0,by_level:{},by_source:{}},meta:{}});
    }catch(e){showToast(e.message,'error')}
    finally{setDebugBusy('')}
  },[isAdmin,showToast]);

  useEffect(()=>{
    if(isAdmin&&section==='generali'){
      loadBackupArchives();
      loadDebugLogs();
    }
  },[isAdmin,section,loadBackupArchives,loadDebugLogs]);

  const save=async()=>{
    try{
      const data={...s,rate_table:JSON.stringify(rateTable),fiscal_benefits:JSON.stringify(fiscalBenefits)};
      await api('settings_update','PUT',data);
      try{localStorage.setItem('cplvcrm_perf_light',settingEnabled(s.performance_light_mode??'0')?'1':'0');}catch(e){}
      invalidateArticoliCache();
      showToast('Impostazioni salvate');loadSettings();
    }catch(e){showToast(e.message,'error')}
  };

  const downloadFullBackup=async()=>{
    try{
      triggerUrlDownload(apiUrl('backup_export_file'));
      showToast('Backup completo scaricato');
    }catch(e){showToast(e.message,'error')}
  };

  const createSavedBackup=async()=>{
    setBackupBusy('create');
    try{
      await api('backup_create','POST',{keep_count:Math.max(1,Math.min(100,parseInt(s.backup_keep_count)||10)),source:'manuale'});
      showToast('Backup salvato nel server');
      loadBackupArchives();
    }catch(e){showToast(e.message,'error')}
    finally{setBackupBusy('')}
  };

  const downloadSavedBackup=async(filename)=>{
    try{
      triggerUrlDownload(apiUrl('backup_download_file',{filename}));
    }catch(e){showToast(e.message,'error')}
  };

  const deleteSavedBackup=async(filename)=>{
    if(!confirm('Eliminare questo backup salvato?'))return;
    setBackupBusy(`delete:${filename}`);
    try{
      await api('backup_delete','DELETE',null,{filename});
      showToast('Backup eliminato');
      loadBackupArchives();
    }catch(e){showToast(e.message,'error')}
    finally{setBackupBusy('')}
  };

  const restoreSavedBackup=async(filename)=>{
    if(!confirm('Ripristinare questo backup? Il database attuale e i file caricati verranno sostituiti completamente.'))return;
    setBackupBusy(`restore:${filename}`);
    try{
      await api('backup_restore_saved','POST',{filename});
      await refreshAllData();
      await loadBackupArchives();
      showToast('Backup ripristinato con successo');
    }catch(e){showToast(e.message,'error')}
    finally{setBackupBusy('')}
  };

  const importAndRestoreBackup=async(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    if(!confirm('Caricare questo backup e ripristinare tutto? Il database attuale e i file caricati verranno sostituiti completamente.')){
      e.target.value='';
      return;
    }
    setBackupBusy('import');
    try{
      let payload=null;
      let completed=false;
      for(const base of orderedApiBases()){
        const formData=new FormData();
        formData.append('backup_file',file,file.name);
        const res=await fetch(apiUrl('backup_import_restore',{},base),{
          method:'POST',
          credentials:'same-origin',
          headers:csrfToken?{'X-CSRF-Token':csrfToken}:{},
          body:formData,
        }).catch(err=>({__fetchError:err}));
        if(res?.__fetchError){
          await sendClientDebugLog('backup-import-network','Errore rete durante import backup',{error:res.__fetchError?.message||String(res.__fetchError),base},'error');
          continue;
        }
        const nextCsrf=res.headers.get('x-csrf-token');
        if(nextCsrf)csrfToken=nextCsrf;
        const rawText=await res.text();
        try{payload=rawText?JSON.parse(rawText):{}}catch(err){
          await sendClientDebugLog('backup-import-non-json','Risposta non JSON durante import backup',{status:res.status,response:rawText.substring(0,1000),base},'error');
          continue;
        }
        if(!res.ok||payload.success===false){
          if((res.status===404||res.status===405||res.status>=500)&&base!==orderedApiBases()[orderedApiBases().length-1]){
            continue;
          }
          throw new Error(payload.error||'Errore durante l\'import del backup');
        }
        rememberApiBase(base);
        completed=true;
        break;
      }
      if(!completed||!payload){
        throw new Error('Il server non ha restituito una risposta valida. Verifica il collegamento API o i limiti del server.');
      }
      await refreshAllData();
      await loadBackupArchives();
      showToast('Backup importato e ripristinato');
    }catch(err){showToast(err.message,'error')}
    finally{setBackupBusy('');e.target.value=''}
  };

  const clearDebugLogs=async()=>{
    if(!confirm('Svuotare tutti i log di debug?'))return;
    setDebugBusy('clear');
    try{
      await api('debug_logs_clear','DELETE');
      setDebugData({entries:[],summary:{total:0,by_level:{},by_source:{}},meta:{...debugData.meta,file_exists:false,file_size:0}});
      showToast('Log di debug svuotati');
    }catch(e){showToast(e.message,'error')}
    finally{setDebugBusy('')}
  };

  const exportDebugLogs=()=>{
    try{
      triggerUrlDownload(apiUrl('debug_logs_export'));
      showToast('Export log avviato');
    }catch(e){showToast(e.message,'error')}
  };

  return(<Page title="Impostazioni" subtitle="Moduli, parametri commerciali, backup e documenti">
    <TabBarScroll tabs={[{k:'generali',l:'Generali'},{k:'documenti',l:'Documenti'}].filter(t=>isAdmin||t.k==='documenti')} active={section} onChange={setSection}/>
    {section==='documenti'?<DocumentiPage embedded/>:<React.Fragment>
    <SettingsGroup id="moduli" title="Moduli Abilitati" description="Attiva o disattiva le aree principali dell'applicazione." isOpen={isGroupOpen('moduli')} onToggle={toggleGroup}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          {key:MODULE_SETTING_KEYS.crm,title:'CRM',desc:'Dashboard, pipeline, clienti, catalogo, analisi e utenti.'},
          {key:MODULE_SETTING_KEYS.preventivi,title:'Preventivi',desc:'Preventivi, offerte e stampa documenti.'},
          {key:MODULE_SETTING_KEYS.hive,title:'Mansioni',desc:'Area mansioni, struttura gerarchica, riunioni e operativita interna.'},
          {key:MODULE_SETTING_KEYS.assistenza,title:'Assistenza',desc:'Cartelle cliente, diario interventi tecnici, allegati e stampa schede.'},
          {key:MODULE_SETTING_KEYS.stimaConsumi,title:'Stima consumi',desc:'Stima annuale inchiostro per cliente con prezzi da catalogo e report.'},
        ].map(mod=>{
          const enabled=settingEnabled(s[mod.key]);
          return <button key={mod.key} onClick={()=>u(mod.key,enabled?'0':'1')}
            className={`text-left border rounded-xl p-4 transition ${enabled?'border-green-300 bg-green-50':'border-gray-200 bg-white hover:bg-gray-50'}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold text-navy-600">{mod.title}</div>
              <span className={`text-xs px-2 py-1 rounded-full ${enabled?'bg-green-100 text-green-700':'bg-gray-100 text-gray-600'}`}>{enabled?'Attivo':'Disattivo'}</span>
            </div>
            <div className="text-xs text-gray-500 mt-2">{mod.desc}</div>
          </button>;
        })}
      </div>
    </SettingsGroup>
    <SettingsGroup id="generali" title="Generali" description="Parametri generali del gestionale." isOpen={isGroupOpen('generali')} onToggle={toggleGroup}><div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Inp label="IVA %" type="number" value={(parseFloat(s.aliquota_iva)||0.22)*100} onChange={v=>u('aliquota_iva',v/100)}/>
      <Inp label="Soglia margine %" type="number" value={(parseFloat(s.soglia_margine_pct)||0.35)*100} onChange={v=>u('soglia_margine_pct',v/100)}/>
      <Sel label="Valuta" value={s.valuta||'EUR'} onChange={v=>u('valuta',v)} options={['EUR','USD','GBP']}/>
    </div></SettingsGroup>
    <SettingsGroup id="assistenza-listino" title="Assistenza da Listino" description="Articoli del listino usati automaticamente nei preventivi di intervento per KM e ore viaggio." isOpen={isGroupOpen('assistenza-listino')} onToggle={toggleGroup}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Articolo KM" value={s.assistenza_km_articolo||'KM'} onChange={v=>u('assistenza_km_articolo',v)} placeholder="KM"/>
        <Inp label="Articolo Ore Viaggio" value={s.assistenza_ore_viaggio_articolo||'ORE VIAGGIO'} onChange={v=>u('assistenza_ore_viaggio_articolo',v)} placeholder="ORE VIAGGIO"/>
      </div>
    </SettingsGroup>
    <SettingsGroup id="fatturato-online" title="Ricerca fatturato online" description="Recupera fatturato da siti pubblici gratuiti (CompanyReports, RegistroAziende). OpenAPI resta opzionale." isOpen={isGroupOpen('fatturato-online')} onToggle={toggleGroup}>
      <p className="text-sm text-slate-600 mb-4">La modalità predefinita interroga fonti pubbliche senza costi API. Per OpenAPI (a pagamento) inserisci il Bearer token e seleziona il provider dedicato. La ricerca usa ragione sociale e, se presente, Partita IVA del cliente.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Sel label="Provider" value={s.fatturato_api_provider||'web'} onChange={v=>u('fatturato_api_provider',v)} options={[{value:'web',label:'Siti pubblici gratuiti (consigliato)'},{value:'auto',label:'Automatico: siti gratuiti, poi OpenAPI se token presente'},{value:'openapi',label:'Solo OpenAPI (Company + Imprese)'},{value:'imprese',label:'Solo OpenAPI Imprese Italia'},{value:'company',label:'Solo OpenAPI Company API'}]}/>
        <Inp label="Bearer token OpenAPI (opzionale)" type="password" value={s.fatturato_api_token||''} onChange={v=>u('fatturato_api_token',v)} placeholder="Solo se usi OpenAPI a pagamento"/>
        <label className="flex items-center gap-2 text-sm md:col-span-2"><input type="checkbox" checked={settingEnabled(s.fatturato_api_sandbox??'0')} onChange={e=>u('fatturato_api_sandbox',e.target.checked?'1':'0')}/> Usa ambiente sandbox OpenAPI (test.company.openapi.com / test.imprese.openapi.it)</label>
      </div>
    </SettingsGroup>
    <SettingsGroup id="performance" title="Prestazioni e connessione lenta" description="Riduce i dati scaricati all'avvio: utile con poca banda o database clienti molto grandi." isOpen={isGroupOpen('performance')} onToggle={toggleGroup}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={settingEnabled(s.performance_light_mode??'0')} onChange={e=>u('performance_light_mode',e.target.checked?'1':'0')}/> Modalità connessione lenta (listini leggeri, meno dati iniziali)</label>
        <Inp label="Clienti per pagina" type="number" value={s.performance_client_page_size||'50'} onChange={v=>u('performance_client_page_size',String(Math.max(20,Math.min(200,parseInt(v)||50))))}/>
      </div>
      <p className="text-xs text-slate-500 mt-2">Suggerimento: l'elenco clienti carica 50 record per volta. Il catalogo viene messo in cache nel browser per le visite successive.</p>
    </SettingsGroup>
    <SettingsGroup id="provvigioni" title="Provvigioni" description="Parametri di calcolo delle provvigioni." isOpen={isGroupOpen('provvigioni')} onToggle={toggleGroup}><div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <Sel label="Base Calcolo" value={s.base_calcolo_provv||'Fatturato'} onChange={v=>u('base_calcolo_provv',v)} options={['Fatturato','Margine']}/>
      <Inp label="Margine Min %" type="number" value={(parseFloat(s.margine_min_provv)||0.25)*100} onChange={v=>u('margine_min_provv',v/100)}/>
      <Inp label="Margine Max %" type="number" value={(parseFloat(s.margine_max_provv)||0.5)*100} onChange={v=>u('margine_max_provv',v/100)}/>
      <Inp label="Provv Min %" type="number" value={(parseFloat(s.provv_min)||0)*100} onChange={v=>u('provv_min',v/100)}/>
      <Inp label="Provv Max %" type="number" value={(parseFloat(s.provv_max)||0.1)*100} onChange={v=>u('provv_max',v/100)}/>
    </div></SettingsGroup>
    <SettingsGroup id="sconti-rivenditore" title="Regole Sconto Rivenditore" description="Sconti fissi per categoria articolo quando il cliente è un rivenditore." isOpen={isGroupOpen('sconti-rivenditore')} onToggle={toggleGroup}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['A','B','C','D'].map(cat=>(
          <Inp key={cat} label={`Sconto Categoria ${cat} %`} type="number" value={toNum(s[`reseller_discount_${cat}`]??0)*100} onChange={v=>u(`reseller_discount_${cat}`,v/100)}/>
        ))}
      </div>
    </SettingsGroup>
    <SettingsGroup id="sconti-cliente" title="Regole Sconto Cliente" description="Per clienti continuativi: A fisso, B progressivo per quantità, C e D trattativa specifica." isOpen={isGroupOpen('sconti-cliente')} onToggle={toggleGroup}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Inp label="Categoria A %" type="number" value={toNum(s.client_discount_A??0.10)*100} onChange={v=>u('client_discount_A',v/100)}/>
        <Inp label="Categoria B da Kg" type="number" value={toNum(s.client_discount_B_start_qty??10)} onChange={v=>u('client_discount_B_start_qty',v)}/>
        <Inp label="Categoria B sconto iniziale %" type="number" value={toNum(s.client_discount_B_start??0.10)*100} onChange={v=>u('client_discount_B_start',v/100)}/>
        <Inp label="Categoria B step Kg" type="number" value={toNum(s.client_discount_B_step_qty??10)} onChange={v=>u('client_discount_B_step_qty',v)}/>
        <Inp label="Categoria B step sconto %" type="number" value={toNum(s.client_discount_B_step??0.10)*100} onChange={v=>u('client_discount_B_step',v/100)}/>
        <Inp label="Categoria B massimo %" type="number" value={toNum(s.client_discount_B_max??0.40)*100} onChange={v=>u('client_discount_B_max',v/100)}/>
        <Inp label="Categoria C %" type="number" value={toNum(s.client_discount_C??0)*100} onChange={v=>u('client_discount_C',v/100)}/>
        <Inp label="Categoria D %" type="number" value={toNum(s.client_discount_D??0)*100} onChange={v=>u('client_discount_D',v/100)}/>
      </div>
    </SettingsGroup>

    {/* Rate Table for Noleggio Strumentale */}
    <SettingsGroup id="noleggio" title="Aliquote Noleggio Strumentale" description="Coefficienti mensili (%) per il calcolo della rata di noleggio strumentale." isOpen={isGroupOpen('noleggio')} onToggle={toggleGroup} action={<Btn s="sm" icon={<IcPlus s={14}/>} onClick={e=>{e.stopPropagation();addRateRow();if(!isGroupOpen('noleggio'))toggleGroup('noleggio')}}>Aggiungi fascia</Btn>}>
      <p className="text-xs text-gray-500 mb-4">Per ogni fascia di importo, indica il coefficiente per ciascuna durata.</p>
      {rateTable.length===0?<div className="text-sm text-gray-400 text-center py-4">Nessuna aliquota configurata</div>:
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 border-b">
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Da importo</th>
            {DURATE.map(d=><th key={d} className="px-2 py-2 text-center text-xs font-semibold text-gray-600">{d+' mesi'}</th>)}
            <th className="px-2 py-2" style={{width:'40px'}}></th>
          </tr></thead>
          <tbody>{rateTable.map((row,i)=>(
            <tr key={i} className="border-b hover:bg-gray-50">
              <td className="px-2 py-1.5">
                <input type="number" value={row.importo||0} onChange={e=>updateRate(i,'importo',e.target.value)}
                  className="w-24 px-2 py-1 border rounded text-sm text-right focus:ring-2 focus:ring-accent-400 outline-none" step="1000"/></td>
              {DURATE.map(d=><td key={d} className="px-1 py-1.5">
                <input type="number" value={row.mesi?.[d]||0} onChange={e=>updateRate(i,d,e.target.value)}
                  className="w-16 px-2 py-1 border rounded text-sm text-center focus:ring-2 focus:ring-accent-400 outline-none" step="0.001"/></td>)}
              <td className="px-1 py-1.5">
                <button onClick={()=>removeRateRow(i)} className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-600"><IcTrash s={14}/></button></td>
            </tr>))}</tbody>
        </table>
      </div>}
      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
        <div className="text-xs text-blue-700 font-medium mb-1">Come funziona il calcolo</div>
        <div className="text-xs text-blue-600">{'Rata mensile = Importo netto (dopo anticipo) x Coefficiente / 100. Il sistema seleziona automaticamente la fascia corretta in base all\'importo del preventivo.'}</div>
      </div>
    </SettingsGroup>

    <SettingsGroup id="agevolazioni" title="Agevolazioni Fiscali" description="Configura scenari fiscali e contributivi. Puoi aggiungere, modificare o rimuovere agevolazioni in base alle normative vigenti." isOpen={isGroupOpen('agevolazioni')} onToggle={toggleGroup} action={<Btn s="sm" icon={<IcPlus s={14}/>} onClick={e=>{e.stopPropagation();addFiscalBenefit();if(!isGroupOpen('agevolazioni'))toggleGroup('agevolazioni')}}>Nuova agevolazione</Btn>}>
      <div className="space-y-4">
        {fiscalBenefits.length===0?<div className="text-sm text-gray-400 text-center py-4">Nessuna agevolazione configurata</div>:fiscalBenefits.map((benefit,idx)=>(
          <div key={benefit.id||idx} className="border border-slate-200 rounded-[24px] p-4 md:p-5 bg-white/70">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={!!benefit.enabled} onChange={e=>updateFiscalBenefit(idx,'enabled',e.target.checked)} className="w-4 h-4 accent-blue-600"/>
                <div>
                  <div className="font-semibold text-navy-600">{benefit.name||`Agevolazione ${idx+1}`}</div>
                  <div className="text-xs text-gray-500">Scenario fiscale e contributivo stampabile nell'offerta</div>
                </div>
              </div>
              <Btn s="sm" v="danger" onClick={()=>removeFiscalBenefit(idx)}>Elimina</Btn>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Inp label="Nome agevolazione" value={benefit.name||''} onChange={v=>updateFiscalBenefit(idx,'name',v)} className="md:col-span-2 xl:col-span-4"/>
              <Inp label="Incentivo principale %" type="number" value={toNum(benefit.cash_rate)*100} onChange={v=>updateFiscalBenefit(idx,'cash_rate',v/100)}/>
              <Inp label="Moltiplicatore incentivo" type="number" value={toNum(benefit.cash_multiplier)||1} onChange={v=>updateFiscalBenefit(idx,'cash_multiplier',v)} />
              <Inp label="Tetto incentivo €" type="number" value={toNum(benefit.cash_cap)} onChange={v=>updateFiscalBenefit(idx,'cash_cap',v)}/>
              <Inp label="Sabatini %" type="number" value={toNum(benefit.sabatini_rate)*100} onChange={v=>updateFiscalBenefit(idx,'sabatini_rate',v/100)}/>
              <Inp label="Minimo importo Sabatini €" type="number" value={toNum(benefit.sabatini_min_amount)} onChange={v=>updateFiscalBenefit(idx,'sabatini_min_amount',v)}/>
              <Inp label="Risparmio fiscale %" type="number" value={toNum(benefit.ammortamento_rate)*100} onChange={v=>updateFiscalBenefit(idx,'ammortamento_rate',v/100)}/>
              <Inp label="Aliquota tasse %" type="number" value={toNum(benefit.tax_rate)*100} onChange={v=>updateFiscalBenefit(idx,'tax_rate',v/100)}/>
              <Sel label="Tasse da calcolare su" value={benefit.tax_base_mode||'none'} onChange={v=>updateFiscalBenefit(idx,'tax_base_mode',v)} options={FISCAL_TAX_BASE_OPTIONS}/>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
        Formula usata: costo reale = imponibile - incentivo principale - sabatini - risparmio fiscale - tasse sui contributi. Le tasse sono negative e vengono calcolate in base alla base selezionata.
      </div>
    </SettingsGroup>

    <SettingsGroup id="logo" title="Logo Aziendale" description="Logo usato nell'intestazione delle offerte." isOpen={isGroupOpen('logo')} onToggle={toggleGroup}>
      <p className="text-xs text-gray-500 mb-3">Carica il logo aziendale che apparirà nell'intestazione delle offerte. Dimensione consigliata: 400×150 px, formato PNG o JPG.</p>
      <div className="flex items-center gap-6">
        <div className="border rounded-lg p-4 bg-gray-50 flex items-center justify-center" style={{minWidth:'200px',minHeight:'80px'}}>
          {s.az_logo?<img src={s.az_logo} alt="Logo" style={{maxHeight:'80px',maxWidth:'200px',objectFit:'contain'}}/>:
          <span className="text-sm text-gray-400">Nessun logo caricato</span>}
        </div>
        <div className="space-y-2">
          <input type="file" accept="image/png,image/jpeg,image/webp" id="logoUpload" style={{display:'none'}} onChange={e=>{
            const file=e.target.files[0];if(!file)return;
            const reader=new FileReader();
            reader.onload=ev=>{
              const img=new Image();img.onload=()=>{
                const MAX=400;let w=img.width,h=img.height;
                if(w>MAX){h=h*(MAX/w);w=MAX;}
                if(h>MAX){w=w*(MAX/h);h=MAX;}
                const c=document.createElement('canvas');c.width=w;c.height=h;
                c.getContext('2d').drawImage(img,0,0,w,h);
                u('az_logo',c.toDataURL('image/png'));
              };img.src=ev.target.result;
            };reader.readAsDataURL(file);
          }}/>
          <Btn s="sm" onClick={()=>document.getElementById('logoUpload').click()}>Carica Logo</Btn>
          {s.az_logo?<Btn s="sm" v="danger" onClick={()=>u('az_logo','')}>Rimuovi</Btn>:null}
        </div>
      </div>
    </SettingsGroup>

    <SettingsGroup id="azienda" title="Dati Aziendali" description="Dati mostrati nell'intestazione e nel piè di pagina delle offerte." isOpen={isGroupOpen('azienda')} onToggle={toggleGroup}>
      <p className="text-xs text-gray-500 mb-3">Questi dati appariranno nell'intestazione e nel piè di pagina delle offerte.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Inp label="Ragione Sociale" value={s.az_nome||''} onChange={v=>u('az_nome',v)} placeholder="Complevia"/>
        <Inp label="Sottotitolo" value={s.az_sottotitolo||''} onChange={v=>u('az_sottotitolo',v)} placeholder="DIGITAL SOLUTIONS"/>
        <Inp label="Telefono" value={s.az_tel||''} onChange={v=>u('az_tel',v)} placeholder="+39 055 6272593"/>
        <Inp label="Email" value={s.az_email||''} onChange={v=>u('az_email',v)} placeholder="info@complevia.it"/>
        <Inp label="Sito Web" value={s.az_web||''} onChange={v=>u('az_web',v)} placeholder="www.complevia.it"/>
        <Inp label="Indirizzo" value={s.az_via||''} onChange={v=>u('az_via',v)} placeholder="Via del Lavoro 1/A"/>
        <Inp label="CAP" value={s.az_cap||''} onChange={v=>u('az_cap',v)} placeholder="50041"/>
        <Inp label="Città" value={s.az_citta||''} onChange={v=>u('az_citta',v)} placeholder="Calenzano"/>
        <Inp label="Provincia" value={s.az_provincia||''} onChange={v=>u('az_provincia',v)} placeholder="FI"/>
        <Inp label="Paese" value={s.az_paese||''} onChange={v=>u('az_paese',v)} placeholder="ITALY"/>
        <Inp label="P.IVA" value={s.az_piva||''} onChange={v=>u('az_piva',v)} placeholder="06594350487"/>
      </div>
    </SettingsGroup>

    <SettingsGroup id="accessi-utenti" title="Accesso Utenti" description="Link usati nelle email automatiche inviate quando crei nuovi utenti." isOpen={isGroupOpen('accessi-utenti')} onToggle={toggleGroup}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Link accesso app" value={s.app_access_url||''} onChange={v=>u('app_access_url',v)} placeholder="https://tuodominio.it/complevia"/>
        <Inp label="Link istruzioni" value={s.user_instruction_url||''} onChange={v=>u('user_instruction_url',v)} placeholder="https://tuodominio.it/istruzioni-complevia"/>
      </div>
      <div className="mt-3 text-xs text-gray-500">Se lasci vuoti questi campi, Complevia proverà a usare il dominio del sito e, per le istruzioni, il link di accesso come fallback.</div>
    </SettingsGroup>

    {isAdmin?<SettingsGroup id="debug" title="Debug e Diagnostica" description="Registra automaticamente errori PHP, API e browser, con elenco centralizzato per rendere il debug immediato." isOpen={isGroupOpen('debug')} onToggle={toggleGroup} action={<div className="flex gap-2"><Btn s="sm" v="ghost" onClick={loadDebugLogs} disabled={!!debugBusy}>Aggiorna</Btn><Btn s="sm" v="secondary" onClick={exportDebugLogs}>Esporta</Btn><Btn s="sm" v="danger" onClick={clearDebugLogs} disabled={!!debugBusy||!debugData.entries.length}>Svuota</Btn></div>}>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className={`border rounded-xl p-4 cursor-pointer transition ${settingEnabled(s.debug_enabled??'1')?'border-green-300 bg-green-50':'border-gray-200 bg-white'}`} onClick={()=>u('debug_enabled',settingEnabled(s.debug_enabled??'1')?'0':'1')}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Debug globale</div>
          <div className="mt-2 font-semibold text-navy-600">{settingEnabled(s.debug_enabled??'1')?'Attivo':'Disattivo'}</div>
        </div>
        <div className={`border rounded-xl p-4 cursor-pointer transition ${settingEnabled(s.debug_php_enabled??'1')?'border-green-300 bg-green-50':'border-gray-200 bg-white'}`} onClick={()=>u('debug_php_enabled',settingEnabled(s.debug_php_enabled??'1')?'0':'1')}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Errori PHP</div>
          <div className="mt-2 font-semibold text-navy-600">{settingEnabled(s.debug_php_enabled??'1')?'Registrati':'Disattivati'}</div>
        </div>
        <div className={`border rounded-xl p-4 cursor-pointer transition ${settingEnabled(s.debug_api_enabled??'1')?'border-green-300 bg-green-50':'border-gray-200 bg-white'}`} onClick={()=>u('debug_api_enabled',settingEnabled(s.debug_api_enabled??'1')?'0':'1')}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Errori API</div>
          <div className="mt-2 font-semibold text-navy-600">{settingEnabled(s.debug_api_enabled??'1')?'Registrati':'Disattivati'}</div>
        </div>
        <div className={`border rounded-xl p-4 cursor-pointer transition ${settingEnabled(s.debug_client_enabled??'1')?'border-green-300 bg-green-50':'border-gray-200 bg-white'}`} onClick={()=>u('debug_client_enabled',settingEnabled(s.debug_client_enabled??'1')?'0':'1')}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Errori browser</div>
          <div className="mt-2 font-semibold text-navy-600">{settingEnabled(s.debug_client_enabled??'1')?'Registrati':'Disattivati'}</div>
        </div>
        <Inp label="Log da conservare" type="number" value={Math.max(100,Math.min(5000,parseInt(s.debug_keep_entries)||500))} onChange={v=>u('debug_keep_entries',Math.max(100,Math.min(5000,parseInt(v)||100)))} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200"><div className="text-xs text-slate-500">Eventi letti</div><div className="text-2xl font-bold text-navy-600 mt-1">{debugData.summary?.total||0}</div></div>
        <div className="bg-red-50 rounded-xl p-4 border border-red-100"><div className="text-xs text-red-500">Critici / errori</div><div className="text-2xl font-bold text-red-700 mt-1">{(debugData.summary?.by_level?.critical||0)+(debugData.summary?.by_level?.error||0)}</div></div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100"><div className="text-xs text-amber-600">Warning</div><div className="text-2xl font-bold text-amber-700 mt-1">{debugData.summary?.by_level?.warning||0}</div></div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100"><div className="text-xs text-blue-500">Dimensione log</div><div className="text-2xl font-bold text-blue-700 mt-1">{fmtFileSize(debugData.meta?.file_size||0)}</div></div>
      </div>
      <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
        I log raccolgono errori PHP fatali, eccezioni API, risposte anomale e problemi Javascript del browser. Se disattivi una categoria, la raccolta si ferma ma i log già presenti restano consultabili finché non li svuoti.
      </div>
      <div className="mt-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="font-semibold text-navy-600">Ultimi eventi di debug</div>
          <div className="text-xs text-gray-500">{`PHP ${debugData.meta?.php_version||'-'} · Complevia ${debugData.meta?.plugin_version||'-'}`}</div>
        </div>
        {debugData.entries?.length===0?<div className="text-sm text-gray-400 py-4">Nessun evento registrato.</div>:
        <div className="space-y-3 max-h-[580px] overflow-y-auto pr-1">{debugData.entries.map(entry=>{
          const level=String(entry.level||'info');
          const badgeClass=level==='critical'||level==='error'?'bg-red-100 text-red-700':level==='warning'?'bg-amber-100 text-amber-700':'bg-slate-100 text-slate-700';
          return <details key={entry.id} className="border rounded-xl px-4 py-3 bg-white/70">
            <summary className="list-none cursor-pointer">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${badgeClass}`}>{level}</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">{S(entry.source||'system')}</span>
                    <span className="text-xs text-gray-400">{fmtDateTime(entry.time)}</span>
                  </div>
                  <div className="font-medium text-navy-600 mt-2 break-words">{S(entry.message||'Evento senza messaggio')}</div>
                </div>
                <div className="text-xs text-gray-500 md:text-right break-all">{S(entry.request?.uri||entry.context?.url||'')}</div>
              </div>
            </summary>
            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Request</div>
                <pre className="text-xs text-slate-700 whitespace-pre-wrap break-all m-0">{JSON.stringify(entry.request||{},null,2)}</pre>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Context</div>
                <pre className="text-xs text-slate-700 whitespace-pre-wrap break-all m-0">{JSON.stringify(entry.context||{},null,2)}</pre>
              </div>
            </div>
          </details>;
        })}</div>}
      </div>
    </SettingsGroup>:null}

    {isAdmin?<SettingsGroup id="backup" title="Backup e Ripristino" description="Scarica tutto il gestionale, ripristinalo su un'altra installazione e gestisci i backup automatici salvati nel server." isOpen={isGroupOpen('backup')} onToggle={toggleGroup}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Sel label="Backup automatico" value={backupAutoEnabled?'1':'0'} onChange={v=>u('backup_auto_enabled',v)} options={[{value:'1',label:'Attivo'},{value:'0',label:'Disattivo'}]}/>
        <Sel label="Frequenza" value={s.backup_frequency||'daily'} onChange={v=>u('backup_frequency',v)} options={[{value:'daily',label:'Giornaliero'},{value:'weekly',label:'Settimanale'},{value:'monthly',label:'Ogni 30 giorni'}]} disabled={!backupAutoEnabled}/>
        <Inp label="Backup da conservare" type="number" value={Math.max(1,Math.min(100,parseInt(s.backup_keep_count)||10))} onChange={v=>u('backup_keep_count',Math.max(1,Math.min(100,parseInt(v)||1)))} />
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <Btn icon={<IcDl s={16}/>} onClick={downloadFullBackup} disabled={!!backupBusy}>{backupBusy==='download'?'Preparazione...':'Scarica backup completo'}</Btn>
        <Btn v="secondary" icon={<IcCheck s={16}/>} onClick={createSavedBackup} disabled={!!backupBusy}>{backupBusy==='create'?'Salvataggio...':'Crea backup salvato'}</Btn>
        <Btn v="secondary" icon={<IcUpload s={16}/>} onClick={()=>backupFileRef.current?.click()} disabled={!!backupBusy}>{backupBusy==='import'?'Ripristino...':'Carica backup e ripristina'}</Btn>
        <input ref={backupFileRef} type="file" accept=".json,application/json" className="hidden" onChange={importAndRestoreBackup}/>
      </div>
      <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
        Il backup completo include database operativo, utenti, mansioni, preventivi, impostazioni e tutti i file caricati. Per attivare il backup automatico salva prima le impostazioni. I backup automatici usano WP-Cron, quindi partono quando il sito riceve traffico.
      </div>
      <div className="mt-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="font-semibold text-navy-600">Backup salvati</div>
          <Btn s="sm" v="ghost" onClick={loadBackupArchives} disabled={!!backupBusy}>Aggiorna</Btn>
        </div>
        {backupArchives.length===0?<div className="text-sm text-gray-400 py-4">Nessun backup salvato nel server</div>:
        <div className="space-y-3">{backupArchives.map(item=>(
          <div key={item.filename} className="border rounded-xl px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium text-navy-600 break-all">{S(item.filename)}</div>
                <div className="text-xs text-gray-500 mt-1">{fmtDate((item.created_at||'').slice(0,10))}{item.created_at&&item.created_at.length>10?' - '+item.created_at.slice(11,16):''}{item.source?' - '+S(item.source):''}{item.plugin_version?' - v'+S(item.plugin_version):''}</div>
                <div className="text-xs text-gray-400 mt-1">{`${fmtFileSize(item.size)} • ${item.counts?.files||0} file, ${((item.counts?.clienti||0)+(item.counts?.preventivi||0)+(item.counts?.hive_items||0))||0} record chiave`}</div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Btn s="sm" v="secondary" onClick={()=>downloadSavedBackup(item.filename)} disabled={!!backupBusy}>{backupBusy===`download:${item.filename}`?'Download...':'Scarica'}</Btn>
                <Btn s="sm" onClick={()=>restoreSavedBackup(item.filename)} disabled={!!backupBusy}>{backupBusy===`restore:${item.filename}`?'Ripristino...':'Ripristina'}</Btn>
                <Btn s="sm" v="danger" onClick={()=>deleteSavedBackup(item.filename)} disabled={!!backupBusy}>{backupBusy===`delete:${item.filename}`?'Elimino...':'Elimina'}</Btn>
              </div>
            </div>
          </div>
        ))}</div>}
      </div>
    </SettingsGroup>:null}

    <div className="flex gap-3 sticky bottom-0 z-10 py-3 -mx-1 px-1 bg-gradient-to-t from-white/95 to-transparent">
      <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva impostazioni</Btn>
    </div>
    </React.Fragment>}
  </Page>);
}

// ========== ANALISI OFFERTA (Admin/Manager) ==========
function AnalisiPage(){
  const{preventivi,getCliente,activeArticles,calcolaRiepilogo,settings,loadPreventivi,loadClienti,loadArticoli}=useContext(DataCtx);
  const{isPhone}=useDevice();
  const[selPrevId,setSelPrevId]=useState('');
  const prev=preventivi.find(p=>p.id===parseInt(selPrevId));
  const r=prev?calcolaRiepilogo(prev):null;
  const cl=prev?getCliente(prev.cliente_id):null;
  const canvasRef=useRef();
  const pieRef=useRef();
  const printRef=useRef();
  useEffect(()=>{
    loadPreventivi({includeRighe:false}).catch(()=>{});
    loadClienti({enrich:'0',light:'1'}).catch(()=>{});
    loadArticoli().catch(()=>{});
  },[loadPreventivi,loadClienti,loadArticoli]);
  const fiscalBenefits=useMemo(()=>r?calcFiscalBenefits(settings,r.imponibile):[],[settings,r?.imponibile]);
  const bestFiscalBenefit=useMemo(()=>{
    if(!fiscalBenefits.length)return null;
    return [...fiscalBenefits].sort((a,b)=>b.saving-a.saving)[0]||null;
  },[fiscalBenefits]);

  // Draw bar chart
  useEffect(()=>{
    if(!r||!canvasRef.current)return;
    const ctx=canvasRef.current.getContext('2d');
    const W=canvasRef.current.width=canvasRef.current.offsetWidth*2;
    const H=canvasRef.current.height=320;
    ctx.clearRect(0,0,W,H);
    const data=[
      {label:'Ricavo',value:r.imponibile,color:'#4472C4'},
      {label:'Costo',value:r.totaleCosto,color:'#E31E24'},
      {label:'Margine',value:r.margineLordo,color:'#22c55e'},
      {label:'Provvig.',value:r.provvEur,color:'#f59e0b'},
      {label:'Utile',value:r.utileNetto,color:'#1B3A6B'},
    ];
    const max=Math.max(...data.map(d=>Math.abs(d.value)),1);
    const barW=(W-140)/(data.length*1.6);
    const baseY=H-54;
    data.forEach((d,i)=>{
      const x=70+i*(barW*1.6);
      const h=Math.abs(d.value)/max*(baseY-44);
      const y=d.value>=0?baseY-h:baseY;
      ctx.fillStyle=d.color;
      ctx.beginPath();ctx.roundRect(x,y,barW,h,4);ctx.fill();
      ctx.fillStyle='#334155';ctx.font='600 18px Inter';ctx.textAlign='center';
      ctx.fillText(d.label,x+barW/2,H-18);
      ctx.fillStyle=d.color;ctx.font='700 16px Inter';
      ctx.fillText(new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(d.value),x+barW/2,y-8);
    });
  },[r]);

  // Draw pie chart
  useEffect(()=>{
    if(!r||!pieRef.current||r.imponibile<=0)return;
    const ctx=pieRef.current.getContext('2d');
    const W=pieRef.current.width=pieRef.current.offsetWidth*2;
    const H=pieRef.current.height=320;
    ctx.clearRect(0,0,W,H);
    const cx=W*0.3,cy=H/2,radius=Math.min(cx,cy)-28;
    const slices=[
      {label:'Costo merce',value:r.totaleCosto,color:'#E31E24'},
      {label:'Provvigione',value:r.provvEur,color:'#f59e0b'},
      {label:'MBO',value:r.mbo,color:'#8b5cf6'},
      {label:'Utile netto',value:r.utileNetto,color:'#22c55e'},
    ].filter(s=>s.value>0);
    let startAngle=-Math.PI/2;
    slices.forEach((s,i)=>{
      const angle=(s.value/r.imponibile)*Math.PI*2;
      ctx.beginPath();ctx.moveTo(cx,cy);ctx.arc(cx,cy,radius,startAngle,startAngle+angle);ctx.closePath();
      ctx.fillStyle=s.color;ctx.fill();
      startAngle+=angle;
    });
    // Legend
    const lx=W*0.58;
    ctx.font='600 16px Inter';ctx.textAlign='left';
    slices.forEach((s,i)=>{
      const ly=56+i*52;
      ctx.fillStyle=s.color;ctx.fillRect(lx,ly-10,16,16);
      ctx.fillStyle='#374151';ctx.fillText(s.label,lx+24,ly+2);
      ctx.fillStyle='#6b7280';ctx.font='14px Inter';
      ctx.fillText(fmt(s.value)+' ('+((s.value/r.imponibile)*100).toFixed(1)+'%)',lx+24,ly+20);
      ctx.font='600 16px Inter';
    });
  },[r]);

  // Articles breakdown
  const articoliAnalisi=useMemo(()=>{
    if(!r)return[];
    return r.righeCalc.filter(rr=>rr.articolo_nome&&rr.qta>0).map(riga=>{
      const art=findArticleByReference(activeArticles,riga.articolo_nome);
      const c=riga.calc||{};
      return{nome:riga.articolo_nome,qta:riga.qta,ricavo:c.totale||0,costo:c.costoRiga||0,margine:c.margineEur||0,marginePct:c.marginePct||0};
    });
  },[r,activeArticles]);

  if(!prev)return(
    <Page title="Analisi offerta" subtitle="Marginalità, ricavi e costi">
      <Sel label="Seleziona preventivo" value={selPrevId} onChange={setSelPrevId}
        options={preventivi.map(p=>({value:String(p.id),label:S(p.codice)+' - '+S(getCliente(p.cliente_id)?.azienda||'N/D')}))} placeholder="-- Seleziona --" className="max-w-md"/>
      <Empty icon={<IcChart s={36} c="text-accent-400"/>} title="Seleziona un preventivo" sub="Scegli un preventivo per analizzare costi, ricavi e marginalita"/>
    </Page>);

  const sogliaMarginePct=parseFloat(settings.soglia_margine_pct)||0.35;
  const isHealthy=r.marginePct>=sogliaMarginePct;

  return(<Page title="Analisi offerta" subtitle={S(prev.codice)} actions={
    <div className="flex flex-wrap items-center gap-2">
      <StatoBadge stato={String(prev.stato||'bozza')}/>
      <span className="text-sm text-slate-500 hidden sm:inline">{cl?S(cl.azienda||cl.nome):'N/D'}</span>
      <Btn v="secondary" s={isPhone?'sm':'md'} icon={<IcPrint s={16}/>} onClick={()=>window.print()}>Stampa</Btn>
    </div>
  }>
    <PageToolbar className="no-print">
      <Sel value={selPrevId} onChange={setSelPrevId}
        options={preventivi.map(p=>({value:String(p.id),label:S(p.codice)+' - '+S(getCliente(p.cliente_id)?.azienda||'N/D')}))} placeholder="-- Seleziona --" className="flex-1 min-w-[200px]"/>
    </PageToolbar>

    <div ref={printRef} className="space-y-6 print:space-y-4">
      <div className="hidden print:block">
        <div className="flex justify-between items-start gap-6 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Report analisi offerta</div>
            <div className="text-2xl font-bold text-navy-600">{S(prev.codice)}</div>
            <div className="text-sm text-slate-500 mt-1">{cl?S(cl.azienda||cl.nome):'Cliente non selezionato'}</div>
          </div>
          <div className="text-right text-sm text-slate-500">
            <div>{fmtDate(prev.data)}</div>
            <div className="mt-1">{S(prev.agente||'')}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.9fr)] gap-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {label:'Ricavo',value:fmt(r.imponibile),sub:'Imponibile',bg:'bg-blue-50',color:'text-blue-700'},
            {label:'Costo',value:fmt(r.totaleCosto),sub:'Costo merce',bg:'bg-red-50',color:'text-red-700'},
            {label:'Margine',value:fmt(r.margineLordo),sub:fmt(r.marginePct,'pct'),bg:isHealthy?'bg-green-50':'bg-red-50',color:isHealthy?'text-green-700':'text-red-700'},
            {label:'Utile netto',value:fmt(r.utileNetto),sub:fmt(r.margineNetto,'pct'),bg:'bg-slate-100',color:'text-slate-700'},
          ].map((kpi,i)=><div key={i} className={`${kpi.bg} rounded-[24px] border border-white/70 p-4`}>
            <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mt-2">{kpi.label}</div>
            <div className={`text-xs font-medium mt-1 ${kpi.color}`}>{kpi.sub}</div>
          </div>)}
        </div>

        <Card title="Sintesi operativa" className="print:break-inside-avoid">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Cliente</span><strong className="text-navy-600">{S(cl?.azienda||cl?.nome||'N/D')}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Commerciale</span><strong className="text-navy-600">{S(prev.agente||'-')}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Soglia margine</span><strong className="text-navy-600">{fmt(sogliaMarginePct,'pct')}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Stato marginalità</span><strong className={isHealthy?'text-green-600':'text-red-600'}>{isHealthy?'Margine sopra soglia':'Margine sotto soglia'}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Provvigione</span><strong className="text-navy-600">{fmt(r.provvEur)}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Noleggio</span><strong className="text-navy-600">{r.rataMensile>0?`${fmt(r.rataMensile)}/mese`: 'Non attivo'}</strong></div>
            {bestFiscalBenefit?<div className="mt-4 rounded-[22px] bg-green-50 border border-green-100 p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-green-600 mb-2">Migliore scenario fiscale</div>
              <div className="font-semibold text-green-700">{S(bestFiscalBenefit.name)}</div>
              <div className="text-sm text-green-700 mt-1">{'Costo reale stimato '+fmt(bestFiscalBenefit.realCost)}</div>
              <div className="text-xs text-green-600 mt-1">{'Risparmio '+fmt(bestFiscalBenefit.saving)+' · '+fmt(bestFiscalBenefit.discountPct,'pct')}</div>
            </div>:null}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card title="Composizione ricavi vs costi" className="print:break-inside-avoid print-break-avoid">
          <canvas ref={canvasRef} style={{width:'100%',height:'260px'}}/>
        </Card>
        <Card title="Distribuzione del ricavo" className="print:break-inside-avoid print-break-avoid">
          {r.imponibile>0?<canvas ref={pieRef} style={{width:'100%',height:'260px'}}/>:
          <p className="text-gray-400 text-center py-8">Nessun dato</p>}
        </Card>
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1.15fr)_minmax(380px,0.85fr)] gap-6">
        <Card title="Dettaglio per articolo" className="print:break-inside-avoid">
          <div className="overflow-x-auto"><table className="w-full text-sm min-w-[760px]">
            <thead><tr className="bg-navy-600 text-white">
              <th className="px-3 py-2.5 text-left">Articolo</th>
              <th className="px-3 py-2.5 text-center">Qta</th>
              <th className="px-3 py-2.5 text-right">Ricavo</th>
              <th className="px-3 py-2.5 text-right">Costo</th>
              <th className="px-3 py-2.5 text-right">Margine</th>
              <th className="px-3 py-2.5 text-center">M.%</th>
              <th className="px-3 py-2.5 text-left">Incidenza</th>
            </tr></thead>
            <tbody>{articoliAnalisi.map((a,i)=>{
              const pctRicavo=r.imponibile>0?(a.ricavo/r.imponibile)*100:0;
              const low=a.marginePct>0&&a.marginePct<sogliaMarginePct;
              return(<tr key={i} className={`border-b hover:bg-gray-50 ${i%2?'bg-gray-50/50':''}`}>
                <td className="px-3 py-2 font-medium text-navy-600">{S(a.nome)}</td>
                <td className="px-3 py-2 text-center">{a.qta}</td>
                <td className="px-3 py-2 text-right">{fmt(a.ricavo)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(a.costo)}</td>
                <td className="px-3 py-2 text-right">{fmt(a.margine)}</td>
                <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${low?'bg-red-100 text-red-700':a.marginePct>0?'bg-green-100 text-green-700':'text-gray-400'}`}>{a.marginePct>0?fmt(a.marginePct,'pct'):'-'}</span></td>
                <td className="px-3 py-2"><div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[120px]"><div className="bg-accent-400 h-2 rounded-full" style={{width:Math.min(pctRicavo,100)+'%'}}/></div>
                  <span className="text-xs text-gray-500">{pctRicavo.toFixed(1)}%</span></div></td>
              </tr>)})}
            </tbody>
          </table></div>
        </Card>

        <Card title="Agevolazioni fiscali" className="print:break-inside-avoid">
          {fiscalBenefits.length===0?<div className="text-sm text-gray-400">Nessuna agevolazione fiscale attiva nelle impostazioni.</div>:<div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead><tr className="bg-slate-100 text-slate-700">
                  <th className="px-3 py-2 text-left">Scenario</th>
                  <th className="px-3 py-2 text-right">Incentivo</th>
                  <th className="px-3 py-2 text-right">Sabatini</th>
                  <th className="px-3 py-2 text-right">Risparmio fiscale</th>
                  <th className="px-3 py-2 text-right">Tasse</th>
                  <th className="px-3 py-2 text-right">Costo reale</th>
                  <th className="px-3 py-2 text-right">Sconto reale</th>
                </tr></thead>
                <tbody>{fiscalBenefits.map((benefit,i)=>{
                  const isBest=bestFiscalBenefit&&bestFiscalBenefit.id===benefit.id;
                  return<tr key={benefit.id||i} className={`border-b ${isBest?'bg-green-50/80':(i%2?'bg-gray-50/40':'bg-white')}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-navy-600">{S(benefit.name)}</div>
                      {isBest?<div className="text-[11px] text-green-600 mt-1">Scenario più conveniente</div>:null}
                    </td>
                    <td className="px-3 py-2 text-right">{fmt(benefit.incentive)}</td>
                    <td className="px-3 py-2 text-right">{fmt(benefit.sabatini)}</td>
                    <td className="px-3 py-2 text-right">{fmt(benefit.fiscalSaving)}</td>
                    <td className="px-3 py-2 text-right">{fmt(benefit.taxes)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-navy-600">{fmt(benefit.realCost)}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmt(benefit.discountPct,'pct')}</td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
            {bestFiscalBenefit?<div className="rounded-[22px] border border-green-100 bg-green-50 p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-green-600 mb-2">Sintesi</div>
              <div className="text-sm text-green-700">{'Con '+S(bestFiscalBenefit.name)+' il costo reale finale stimato è '+fmt(bestFiscalBenefit.realCost)+', con un risparmio complessivo di '+fmt(bestFiscalBenefit.saving)+'.'}</div>
            </div>:null}
          </div>}
        </Card>
      </div>
    </div>
  </Page>);
}

function HiveItemModal({item,people,hives,onClose,onSaved}){
  const{user}=useContext(AuthCtx);
  const{showToast}=useContext(DataCtx);
  const def={title:'',description:'',item_type:'standard',status:'inbox',priority:'media',hive_id:'',owner_user_id:'',approver_user_id:'',due_at:'',due_time:'',visibility_level:'team',tags:'',notes:'',sla_minutes:'',requires_approval:0};
  const[f,setF]=useState(item?{...def,...item,hive_id:item.hive_id?String(item.hive_id):'',owner_user_id:item.owner_user_id?String(item.owner_user_id):'',approver_user_id:item.approver_user_id?String(item.approver_user_id):'',tags:Array.isArray(item.tags)?item.tags.join(', '):(item.tags||'')}:{...def,owner_user_id:String(user?.id||'')});
  const[comments,setComments]=useState([]);
  const[newComment,setNewComment]=useState('');
  const assignablePeople=useMemo(()=>people.filter(p=>parseInt(p.can_assign_to||0)===1),[people]);
  const approverPeople=useMemo(()=>people.filter(p=>['admin','manager'].includes(p.effective_role||effectiveMansioniRole(p.ruolo))),[people]);
  const u=(k,v)=>setF(p=>({...p,[k]:v}));

  const loadComments=useCallback(async()=>{
    if(!item?.id)return;
    try{const d=await api('hive_comments','GET',null,{item_id:item.id});setComments(d.data||[])}catch(e){}
  },[item?.id]);
  useEffect(()=>{loadComments()},[loadComments]);

  const save=async()=>{
    if(!f.title.trim()){showToast('Titolo obbligatorio','error');return}
    const ownerId=parseInt(f.owner_user_id)||null;
    const normalizedStatus=(ownerId&&f.status==='inbox')?'da_fare':(f.status||'inbox');
    const payload={
      title:f.title.trim(),
      description:(f.description||'').trim(),
      item_type:f.item_type||'standard',
      status:normalizedStatus,
      priority:f.priority||'media',
      hive_id:parseInt(f.hive_id)||null,
      owner_user_id:ownerId,
      approver_user_id:parseInt(f.approver_user_id)||null,
      due_at:f.due_at||null,
      due_time:f.due_time||'',
      visibility_level:f.visibility_level||'team',
      tags:(f.tags||'').split(',').map(s=>s.trim()).filter(Boolean),
      notes:(f.notes||'').trim(),
      sla_minutes:parseInt(f.sla_minutes)||0,
      requires_approval:!!f.requires_approval,
    };
    try{
      if(item?.id)await api('hive_item_update','PUT',payload,{id:item.id});
      else await api('hive_item_create','POST',payload);
      showToast(item?.id?'Mansione aggiornata':'Mansione creata');
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  const addComment=async()=>{
    if(!item?.id||!newComment.trim())return;
    try{
      await api('hive_comments','POST',{item_id:item.id,comment_text:newComment.trim()});
      setNewComment('');
      loadComments();
      showToast('Commento aggiunto');
    }catch(e){showToast(e.message,'error')}
  };

  const markDone=async()=>{
    if(!item?.id)return;
    try{
      await api('hive_item_update','PUT',{status:'completato'},{id:item.id});
      showToast('Mansione archiviata');
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  const deleteItem=async()=>{
    if(!item?.id)return;
    if(!confirm('Eliminare questa mansione?'))return;
    try{
      await api('hive_item_delete','DELETE',null,{id:item.id});
      showToast('Mansione eliminata');
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  return(<Modal title={item?.id?'Mansione':'Nuova Mansione'} onClose={onClose} wide>
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Titolo" value={f.title} onChange={v=>u('title',v)} required/>
        <Sel label="Tipo" value={f.item_type} onChange={v=>u('item_type',v)} options={HIVE_ITEM_TYPES}/>
        <Sel label="Stato" value={f.status} onChange={v=>u('status',v)} options={HIVE_ITEM_STATUSES}/>
        <Sel label="Priorità" value={f.priority} onChange={v=>u('priority',v)} options={HIVE_ITEM_PRIORITIES}/>
        <Sel label="Reparto" value={f.hive_id} onChange={v=>u('hive_id',v)} options={hives.map(h=>({value:String(h.id),label:S(h.name)}))} placeholder="Seleziona reparto"/>
        <Sel label="Assegnata a" value={f.owner_user_id} onChange={v=>u('owner_user_id',v)} options={assignablePeople.map(p=>({value:String(p.id),label:S(p.nome||p.username)}))} placeholder="Seleziona persona"/>
        <Sel label="Approvatore" value={f.approver_user_id} onChange={v=>u('approver_user_id',v)} options={approverPeople.map(p=>({value:String(p.id),label:S(p.nome||p.username)}))} placeholder="Nessuno"/>
        <Inp label="Scadenza" type="date" value={f.due_at||''} onChange={v=>u('due_at',v)}/>
        <Inp label="Orario" type="time" value={f.due_time||''} onChange={v=>u('due_time',v)}/>
        <Sel label="Visibilità" value={f.visibility_level} onChange={v=>u('visibility_level',v)} options={HIVE_ITEM_VISIBILITY}/>
        <Inp label="SLA (minuti)" type="number" value={f.sla_minutes||''} onChange={v=>u('sla_minutes',v)}/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Descrizione</label>
        <textarea value={f.description||''} onChange={e=>u('description',e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Tag (separati da virgola)" value={f.tags||''} onChange={v=>u('tags',v)}/>
        <label className="flex items-center gap-3 px-3 py-2 border rounded-lg self-end">
          <input type="checkbox" checked={!!f.requires_approval} onChange={e=>u('requires_approval',e.target.checked?1:0)} className="accent-accent-400"/>
          <span className="text-sm text-gray-700">Richiede approvazione</span>
        </label>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Note operative</label>
        <textarea value={f.notes||''} onChange={e=>u('notes',e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/>
      </div>
      {item?.id?<div className="border-t pt-4 space-y-3">
        <div className="text-sm font-semibold text-navy-600">Timeline commenti</div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {comments.length===0?<div className="text-sm text-gray-400">Nessun commento</div>:comments.map(c=><div key={c.id} className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">{S(c.user_name||c.username||'Utente')} · {fmtDate(String(c.created_at||'').slice(0,10))}</div>
            <div className="text-sm text-gray-700">{S(c.comment_text)}</div>
          </div>)}
        </div>
        <div className="flex gap-2">
          <input value={newComment} onChange={e=>setNewComment(e.target.value)} placeholder="Aggiungi commento..."
            className="flex-1 px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/>
          <Btn s="sm" onClick={addComment}>Invia</Btn>
        </div>
      </div>:null}
      <div className="flex justify-between gap-3">
        <div>
          {item?.id?<div className="flex gap-2">
            {item?.due_at?<Btn v="secondary" onClick={()=>window.open(taskCalendarUrl({...item,...f,title:f.title||item.title,description:f.description||item.description,notes:f.notes||item.notes,due_time:f.due_time||item.due_time,hive_name:item.hive_name,created_by_name:item.created_by_name}),'_blank')}>Google Calendar</Btn>:null}
            {item.status!=='completato'?<Btn v="secondary" onClick={markDone}>Fatto</Btn>:null}
            <Btn v="danger" onClick={deleteItem}>Cancella</Btn>
          </div>:null}
        </div>
        <div className="flex gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva</Btn>
        </div>
      </div>
    </div>
  </Modal>);
}

function HiveMeetingModal({meeting,hives,people,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const def={title:'',description:'',hive_id:'',participants:[],started_at:today(),actions:[{title:'',owner_user_id:'',due_at:today(),due_time:'',priority:'media',status:'da_fare',notes:''}]};
  const[f,setF]=useState(meeting?{...def,...meeting,hive_id:meeting.hive_id?String(meeting.hive_id):'',participants:meeting.participants||[],actions:meeting.actions||def.actions}:{...def});
  const assignablePeople=useMemo(()=>people.filter(p=>parseInt(p.can_assign_to||0)===1),[people]);
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const updAction=(idx,key,val)=>setF(p=>({...p,actions:p.actions.map((a,i)=>i===idx?{...a,[key]:val}:a)}));
  const addAction=()=>setF(p=>({...p,actions:[...(p.actions||[]),{title:'',owner_user_id:'',due_at:today(),due_time:'',priority:'media',status:'da_fare',notes:''}]}));
  const removeAction=idx=>setF(p=>({...p,actions:(p.actions||[]).filter((_,i)=>i!==idx)}));
  const toggleParticipant=id=>setF(p=>({...p,participants:(p.participants||[]).includes(id)?p.participants.filter(x=>x!==id):[...(p.participants||[]),id]}));

  const save=async(closeMeeting=false)=>{
    if(!f.title.trim()){showToast('Titolo riunione obbligatorio','error');return}
    const payload={
      title:f.title.trim(),
      description:(f.description||'').trim(),
      hive_id:parseInt(f.hive_id)||null,
      participants:(f.participants||[]).map(v=>parseInt(v)).filter(Boolean),
      started_at:f.started_at||today(),
      actions:(f.actions||[]).map(a=>({title:(a.title||'').trim(),owner_user_id:parseInt(a.owner_user_id)||null,due_at:a.due_at||null,due_time:a.due_time||'',priority:a.priority||'media',status:a.status||'da_fare',notes:(a.notes||'').trim()})).filter(a=>a.title),
      close_meeting:closeMeeting,
    };
    try{
      if(meeting?.id)await api('hive_meeting_update','PUT',payload,{id:meeting.id});
      else await api('hive_meeting_create','POST',payload);
      showToast(meeting?.id?'Riunione aggiornata':'Riunione creata');
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };

  return(<Modal title={meeting?.id?'Riunione Mansioni':'Nuova Riunione'} onClose={onClose} wide>
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Titolo riunione" value={f.title} onChange={v=>u('title',v)} required/>
        <Sel label="Reparto" value={f.hive_id} onChange={v=>u('hive_id',v)} options={hives.map(h=>({value:String(h.id),label:S(h.name)}))} placeholder="Seleziona reparto"/>
        <Inp label="Inizio" type="date" value={f.started_at||''} onChange={v=>u('started_at',v)}/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Note / Decisioni</label>
        <textarea value={f.description||''} onChange={e=>u('description',e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/>
      </div>
      <div>
        <div className="text-sm font-medium text-gray-600 mb-2">Partecipanti</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {people.map(p=><label key={p.id} className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm">
            <input type="checkbox" checked={(f.participants||[]).includes(p.id)|| (f.participants||[]).includes(String(p.id))} onChange={()=>toggleParticipant(p.id)} className="accent-accent-400"/>
            <span>{S(p.nome||p.username)}</span>
          </label>)}
        </div>
      </div>
      {!meeting?.id?<div className="border-t pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-navy-600">Azioni emerse</div>
          <Btn s="sm" icon={<IcPlus s={14}/>} onClick={addAction}>Aggiungi azione</Btn>
        </div>
        {(f.actions||[]).map((a,idx)=><div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-3 border rounded-xl p-3">
          <Inp label="Azione" value={a.title||''} onChange={v=>updAction(idx,'title',v)}/>
          <Sel label="Assegnata a" value={a.owner_user_id||''} onChange={v=>updAction(idx,'owner_user_id',v)} options={assignablePeople.map(p=>({value:String(p.id),label:S(p.nome||p.username)}))} placeholder="Persona"/>
          <Inp label="Scadenza" type="date" value={a.due_at||''} onChange={v=>updAction(idx,'due_at',v)}/>
          <Inp label="Orario" type="time" value={a.due_time||''} onChange={v=>updAction(idx,'due_time',v)}/>
          <Sel label="Priorità" value={a.priority||'media'} onChange={v=>updAction(idx,'priority',v)} options={HIVE_ITEM_PRIORITIES}/>
          <div className="flex items-end gap-2">
            <Sel value={a.status||'da_fare'} onChange={v=>updAction(idx,'status',v)} options={HIVE_ITEM_STATUSES.filter(s=>s.value!=='annullato')}/>
            <button onClick={()=>removeAction(idx)} className="p-2 text-gray-400 hover:text-red-600"><IcTrash s={16}/></button>
          </div>
        </div>)}
      </div>:<div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-700">Su una riunione già creata puoi aggiornare dati e chiuderla solo se le azioni esistenti hanno owner e data.</div>}
      <div className="flex justify-end gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        {meeting?.id?<Btn v="secondary" onClick={()=>save(true)}>Chiudi Riunione</Btn>:null}
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={()=>save(false)}>Salva</Btn>
      </div>
    </div>
  </Modal>);
}

function HiveHiveModal({hive,people,hives,onClose,onSaved}){
  const{showToast}=useContext(DataCtx);
  const def={name:'',description:'',parent_hive_id:'',manager_user_id:'',member_user_ids:[]};
  const[f,setF]=useState(hive?{...def,...hive,parent_hive_id:hive.parent_hive_id?String(hive.parent_hive_id):'',manager_user_id:hive.manager_user_id?String(hive.manager_user_id):'',member_user_ids:(hive.members||[]).map(m=>m.user_id)}:{...def});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));
  const toggleMember=id=>setF(p=>({...p,member_user_ids:(p.member_user_ids||[]).includes(id)?p.member_user_ids.filter(x=>x!==id):[...(p.member_user_ids||[]),id]}));
  const save=async()=>{
    if(!f.name.trim()){showToast('Nome reparto obbligatorio','error');return}
    try{
      await api('hive_hive_save',hive?.id?'PUT':'POST',{id:hive?.id,name:f.name.trim(),description:(f.description||'').trim(),parent_hive_id:parseInt(f.parent_hive_id)||null,manager_user_id:parseInt(f.manager_user_id)||null,member_user_ids:(f.member_user_ids||[]).map(v=>parseInt(v)).filter(Boolean)});
      showToast(hive?.id?'Reparto aggiornato':'Reparto creato');
      onSaved();
    }catch(e){showToast(e.message,'error')}
  };
  return(<Modal title={hive?.id?'Modifica Reparto':'Nuovo Reparto'} onClose={onClose} wide>
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Nome reparto" value={f.name} onChange={v=>u('name',v)} required/>
        <Sel label="Reparto superiore" value={f.parent_hive_id} onChange={v=>u('parent_hive_id',v)} options={hives.filter(h=>h.id!==hive?.id).map(h=>({value:String(h.id),label:S(h.name)}))} placeholder="Nessuno"/>
        <Sel label="Responsabile reparto" value={f.manager_user_id} onChange={v=>u('manager_user_id',v)} options={people.map(p=>({value:String(p.id),label:S(p.nome||p.username)}))} placeholder="Nessuno"/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Descrizione</label>
        <textarea value={f.description||''} onChange={e=>u('description',e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/>
      </div>
      <div>
        <div className="text-sm font-medium text-gray-600 mb-2">Membri reparto</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto border rounded-xl p-3">
          {people.map(p=><label key={p.id} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={(f.member_user_ids||[]).includes(p.id)|| (f.member_user_ids||[]).includes(String(p.id))} onChange={()=>toggleMember(p.id)} className="accent-accent-400"/>
            <span>{S(p.nome||p.username)}</span>
          </label>)}
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Btn v="secondary" onClick={onClose}>Annulla</Btn>
        <Btn v="success" icon={<IcCheck s={16}/>} onClick={save}>Salva</Btn>
      </div>
    </div>
  </Modal>);
}

function HiveFlowPage(){
  const{user}=useContext(AuthCtx);
  const{showToast}=useContext(DataCtx);
  const{isPhone}=useDevice();
  const role=effectiveMansioniRole(user?.ruolo);
  const canManage=['admin','manager'].includes(role);
  const canSettings=role==='admin';
  const[section,setSection]=useState('dashboard');
  const[loading,setLoading]=useState(true);
  const[dashboard,setDashboard]=useState(null);
  const[items,setItems]=useState([]);
  const[hives,setHives]=useState([]);
  const[meetings,setMeetings]=useState([]);
  const[approvals,setApprovals]=useState([]);
  const[activity,setActivity]=useState([]);
  const[people,setPeople]=useState([]);
  const[settings,setSettings]=useState({});
  const[quick,setQuick]=useState({text:'',owner_user_id:'',priority:'media',due_at:today(),due_time:'',hive_id:''});
  const[search,setSearch]=useState('');
  const[statusFilter,setStatusFilter]=useState('');
  const[hiveFilter,setHiveFilter]=useState('');
  const[ownerFilter,setOwnerFilter]=useState('');
  const[editItem,setEditItem]=useState(null);
  const[showItemModal,setShowItemModal]=useState(false);
  const[editHive,setEditHive]=useState(null);
  const[showHiveModal,setShowHiveModal]=useState(false);
  const[editMeeting,setEditMeeting]=useState(null);
  const[showMeetingModal,setShowMeetingModal]=useState(false);

  const refresh=useCallback(async()=>{
    setLoading(true);
    try{
      const requests=[api('hive_dashboard'),api('hive_items'),api('hive_hives'),api('hive_meetings'),api('hive_approvals'),api('hive_people'),canSettings?api('settings'):Promise.resolve({data:{}}),api('hive_activity')];
      const[dash,itemsRes,hivesRes,meetingsRes,approvalsRes,peopleRes,settingsRes,activityRes]=await Promise.all(requests);
      setDashboard(dash.data||null);
      setItems(itemsRes.data||[]);
      setHives(hivesRes.data||[]);
      setMeetings(meetingsRes.data||[]);
      setApprovals(approvalsRes.data||[]);
      setPeople(peopleRes.data||[]);
      setSettings(settingsRes.data||{});
      setActivity(activityRes.data||[]);
    }catch(e){showToast(e.message,'error')}
    finally{setLoading(false)}
  },[canSettings,showToast]);
  useEffect(()=>{refresh()},[refresh]);

  const saveHiveSettings=async()=>{
    try{
      await api('settings_update','PUT',{
        hive_reminder_days:settings.hive_reminder_days||'1',
        hive_stale_days:settings.hive_stale_days||'3',
        hive_digest_hour:settings.hive_digest_hour||'08:00'
      });
      showToast('Impostazioni Mansioni salvate');
    }catch(e){showToast(e.message,'error')}
  };

  const quickAdd=async()=>{
    if(!quick.text.trim()){showToast('Inserisci un testo rapido','error');return}
    try{
      const hasOwner=!!(parseInt(quick.owner_user_id)||null);
      await api('hive_item_create','POST',{
        title:quick.text.trim(),
        item_type:'micro',
        status:hasOwner?'da_fare':'inbox',
        owner_user_id:parseInt(quick.owner_user_id)||null,
        priority:quick.priority||'media',
        due_at:quick.due_at||null,
        due_time:quick.due_time||'',
        hive_id:parseInt(quick.hive_id)||null,
      });
      setQuick({text:'',owner_user_id:'',priority:'media',due_at:today(),due_time:'',hive_id:quick.hive_id});
      refresh();
      showToast(hasOwner?'Compito assegnato correttamente':'Mansione inserita in Inbox');
    }catch(e){showToast(e.message,'error')}
  };

  const filteredItems=useMemo(()=>items.filter(item=>{
    if(statusFilter&&item.status!==statusFilter)return false;
    if(hiveFilter&&String(item.hive_id||'')!==String(hiveFilter))return false;
    if(ownerFilter&&String(item.owner_user_id||'')!==String(ownerFilter))return false;
    if(search){
      const s=search.toLowerCase();
      return [item.title,item.description,item.owner_name,item.hive_name].filter(Boolean).some(v=>String(v).toLowerCase().includes(s));
    }
    return true;
  }),[items,search,statusFilter,hiveFilter,ownerFilter]);
  const activeItems=useMemo(()=>filteredItems.filter(item=>item.status!=='completato'),[filteredItems]);
  const archivedItems=useMemo(()=>filteredItems.filter(item=>item.status==='completato'),[filteredItems]);
  const myTodoItems=useMemo(()=>activeItems.filter(item=>String(item.owner_user_id||'')===String(user?.id||'')),[activeItems,user?.id]);
  const myInboxItems=useMemo(()=>filteredItems.filter(item=>item.status==='inbox'),[filteredItems]);
  const myAssignedOpen=useMemo(()=>activeItems.filter(item=>String(item.created_by||'')===String(user?.id||'')&&String(item.owner_user_id||'')!==String(user?.id||'')),[activeItems,user?.id]);
  const myAssignedDone=useMemo(()=>archivedItems.filter(item=>String(item.created_by||'')===String(user?.id||'')&&String(item.owner_user_id||'')!==String(user?.id||'')),[archivedItems,user?.id]);
  const myCompletedArchive=useMemo(()=>archivedItems.filter(item=>String(item.owner_user_id||'')===String(user?.id||'')),[archivedItems,user?.id]);
  const activeCreatedByMe=useMemo(()=>activeItems.filter(item=>String(item.created_by||'')===String(user?.id||'')),[activeItems,user?.id]);

  const myToday=(dashboard?.personal?.mine_today)||[];
  const reminders=(dashboard?.personal?.promemoria)||[];
  const todayOpen=activeItems.filter(i=>String(i.due_at||'').slice(0,10)===today()&&!['completato','annullato'].includes(i.status));
  const overdue=activeItems.filter(i=>i.is_overdue);
  const waiting=activeItems.filter(i=>i.status==='in_attesa');
  const approvalsPending=approvals.filter(a=>a.status==='da_approvare');
  const completedToday=archivedItems.filter(i=>String(i.completed_at||'').slice(0,10)===today());
  const teamLoad=useMemo(()=>{
    const map={};
    activeItems.forEach(item=>{
      const key=item.owner_name||'Non assegnata';
      if(!map[key])map[key]={open:0,blocked:0,overdue:0};
      if(!['completato','annullato'].includes(item.status))map[key].open++;
      if(item.status==='bloccato')map[key].blocked++;
      if(item.is_overdue)map[key].overdue++;
    });
    return Object.entries(map).sort((a,b)=>b[1].open-a[1].open);
  },[activeItems]);
  const assignablePeople=useMemo(()=>people.filter(p=>parseInt(p.can_assign_to||0)===1),[people]);
  const peopleMap=useMemo(()=>Object.fromEntries(people.map(p=>[String(p.id),p])),[people]);
  const hivesMap=useMemo(()=>Object.fromEntries(hives.map(h=>[String(h.id),h])),[hives]);
  const areaCounts=useMemo(()=>people.reduce((acc,person)=>{
    parseMansioniAssignments(person.mansioni_json).forEach(mansione=>{
      acc[mansione.area]=(acc[mansione.area]||0)+1;
    });
    return acc;
  },{amministrazione:0,tecnico:0,commerciale:0}),[people]);
  const peopleByManager=useMemo(()=>{
    const map={};
    people.forEach(person=>{
      const managerIds=(person.manager_user_ids||[]).length?(person.manager_user_ids||[]):[person.manager_user_id||'root'];
      if(managerIds.length===0){
        if(!map.root)map.root=[];
        map.root.push(person);
      }else{
        managerIds.forEach(managerId=>{
          const key=String(managerId||'root');
          if(!map[key])map[key]=[];
          map[key].push(person);
        });
      }
    });
    Object.values(map).forEach(list=>list.sort((a,b)=>(a.nome||a.username||'').localeCompare(b.nome||b.username||'','it',{sensitivity:'base'})));
    return map;
  },[people]);
  const departmentsByUser=useMemo(()=>{
    const map={};
    const addDepartment=(userId,name)=>{
      const key=String(userId||'');
      if(!key||key==='0'||!name)return;
      if(!map[key])map[key]=new Set();
      map[key].add(name);
    };
    hives.forEach(hive=>{
      addDepartment(hive.manager_user_id,hive.name);
      (hive.members||[]).forEach(member=>addDepartment(member.user_id||member.id,hive.name));
    });
    return Object.fromEntries(Object.entries(map).map(([key,set])=>[key,[...set].sort((a,b)=>a.localeCompare(b,'it',{sensitivity:'base'}))]));
  },[hives]);
  const roleCounts=useMemo(()=>people.reduce((acc,person)=>{
    const key=effectiveMansioniRole(person.ruolo);
    acc[key]=(acc[key]||0)+1;
    return acc;
  },{admin:0,manager:0,commerciale:0,operativo:0}),[people]);
  const sortedPeople=useMemo(()=>[...people].sort((a,b)=>(a.nome||a.username||'').localeCompare(b.nome||b.username||'','it',{sensitivity:'base'})),[people]);
  const currentPerson=useMemo(()=>people.find(p=>parseInt(p.is_current_user||0)===1)||peopleMap[String(user?.id||'')]||null,[people,peopleMap,user?.id]);
  const myManagers=useMemo(()=>{
    const directManagers=(currentPerson?.manager_people||[]).map(person=>peopleMap[String(person.id)]||person).filter(Boolean);
    const chainManagers=(currentPerson?.manager_chain_ids||[]).map(id=>peopleMap[String(id)]).filter(Boolean);
    const merged=[...directManagers,...chainManagers];
    return merged.filter((person,index,self)=>self.findIndex(entry=>String(entry.id)===String(person.id))===index);
  },[currentPerson,peopleMap]);
  const myDirectReports=useMemo(()=>peopleByManager[String(user?.id||'')]||[],[peopleByManager,user?.id]);
  const myAllReports=useMemo(()=>((currentPerson?.subordinate_ids||[]).map(id=>peopleMap[String(id)]).filter(Boolean)),[currentPerson,peopleMap]);
  const peerPeople=useMemo(()=>{
    const managerIds=currentPerson?.manager_user_ids||[];
    if(managerIds.length===0)return[];
    const pool=managerIds.flatMap(managerId=>peopleByManager[String(managerId)]||[]);
    return pool.filter((person,index,self)=>String(person.id)!==String(currentPerson.id)&&self.findIndex(entry=>String(entry.id)===String(person.id))===index);
  },[peopleByManager,currentPerson]);
  const orgRoots=useMemo(()=>people.filter(p=>!(p.manager_user_ids||[]).length||!(p.manager_user_ids||[]).some(id=>peopleMap[String(id)])),[people,peopleMap]);
  const renderHierarchyNode=(person,depth=0)=>{
    const children=peopleByManager[String(person.id)]||[];
    const departments=departmentsByUser[String(person.id)]||[];
    return <div key={person.id} className="space-y-2">
      <div className="border rounded-xl bg-white p-3" style={{marginLeft:Math.min(depth,4)*20}}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-navy-600 truncate">{S(person.nome||person.username)}</div>
            <div className="text-xs text-gray-500 truncate">{S(person.role_label||HIVE_ROLE_LABELS[person.ruolo]||person.ruolo)}{person.job_title?` · ${S(person.job_title)}`:''}</div>
            <div className="text-xs text-gray-400 truncate">{(person.manager_people||[]).length?`Riporta a ${(person.manager_people||[]).map(manager=>manager.nome||manager.username).filter(Boolean).join(', ')}`:'Vertice della struttura'}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs text-gray-400">Sotto</div>
            <div className="text-sm font-semibold text-navy-600">{person.direct_reports_count||0}</div>
          </div>
        </div>
        {departments.length?<div className="mt-2 flex flex-wrap gap-1">{departments.slice(0,3).map(name=><span key={name} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{S(name)}</span>)}</div>:null}
        {parseMansioniAssignments(person.mansioni_json).length?<div className="mt-2 flex flex-wrap gap-1">{parseMansioniAssignments(person.mansioni_json).slice(0,3).map((mansione,idx)=><span key={idx} className="text-[11px] bg-blue-50 text-blue-700 px-2 py-1 rounded-full">{S((mansione.title||mansioneAreaLabel(mansione.area))+' · '+mansioneLevelLabel(mansione.level))}</span>)}</div>:null}
        {person.mansioni?<div className="text-xs text-gray-600 mt-2 line-clamp-3">{S(person.mansioni)}</div>:null}
      </div>
      {children.length?<div className="space-y-2">{children.map(child=>renderHierarchyNode(child,depth+1))}</div>:null}
    </div>;
  };

  const deleteItem=async(itemId)=>{
    if(!itemId)return;
    if(!confirm('Eliminare questa mansione?'))return;
    try{
      await api('hive_item_delete','DELETE',null,{id:itemId});
      showToast('Mansione eliminata');
      refresh();
    }catch(e){showToast(e.message,'error')}
  };

  const markItemDone=async(itemId)=>{
    if(!itemId)return;
    try{
      await api('hive_item_update','PUT',{status:'completato'},{id:itemId});
      showToast('Mansione spostata in archivio');
      refresh();
    }catch(e){showToast(e.message,'error')}
  };

  if(loading)return<Loading/>;

  const sections=[
    {id:'dashboard',label:'Dashboard'},
    {id:'dafare',label:'Cose da fare'},
    {id:'assegnati',label:'Compiti assegnati'},
    {id:'archiviate',label:'Archivio'},
    {id:'struttura',label:'Struttura'},
    {id:'reparti',label:'Reparti'},
    {id:'riunioni',label:'Riunioni'},
    {id:'approvazioni',label:'Approvazioni'},
    {id:'report',label:'Report'},
    {id:'settings',label:'Impostazioni'},
  ];

  return(<Page title="Mansioni" subtitle="Task, reparti, riunioni e approvazioni">
    <Card className="no-print">
      <div className="hive-quick-grid">
        <input value={quick.text} onChange={e=>setQuick(p=>({...p,text:e.target.value}))} placeholder="Quick add: es. Richiamare cliente Rossi"
          className="md:col-span-2 px-3 py-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400 min-h-[44px]"/>
        <Sel value={quick.owner_user_id} onChange={v=>setQuick(p=>({...p,owner_user_id:v}))} options={assignablePeople.map(p=>({value:String(p.id),label:S(p.nome||p.username)}))} placeholder="Assegna a"/>
        <Sel value={quick.hive_id} onChange={v=>setQuick(p=>({...p,hive_id:v}))} options={hives.map(h=>({value:String(h.id),label:S(h.name)}))} placeholder="Reparto"/>
        <Sel value={quick.priority} onChange={v=>setQuick(p=>({...p,priority:v}))} options={HIVE_ITEM_PRIORITIES}/>
        <input type="time" value={quick.due_time||''} onChange={e=>setQuick(p=>({...p,due_time:e.target.value}))} className="px-3 py-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400 min-h-[44px]"/>
        <div className="flex gap-2 hive-quick-span-2">
          <input type="date" value={quick.due_at} onChange={e=>setQuick(p=>({...p,due_at:e.target.value}))} className="flex-1 px-3 py-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400 min-h-[44px]"/>
          <Btn onClick={quickAdd}>Aggiungi</Btn>
        </div>
      </div>
    </Card>

    <TabBarScroll tabs={sections.filter(s=>canSettings||s.id!=='settings').map(s=>({k:s.id,l:s.label}))} active={section} onChange={setSection}/>

    {(section==='dashboard'||section==='dafare'||section==='assegnati'||section==='archiviate'||section==='report')?<div className="flex flex-col lg:flex-row gap-3">
      <div className="relative flex-1"><IcSearch s={16} c="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Ricerca globale Mansioni..."
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-accent-400"/></div>
      <Sel value={statusFilter} onChange={setStatusFilter} options={HIVE_ITEM_STATUSES} placeholder="Tutti gli stati" className="min-w-[170px]"/>
      <Sel value={hiveFilter} onChange={setHiveFilter} options={hives.map(h=>({value:String(h.id),label:S(h.name)}))} placeholder="Tutti i reparti" className="min-w-[170px]"/>
      <Sel value={ownerFilter} onChange={setOwnerFilter} options={people.map(p=>({value:String(p.id),label:S(p.nome||p.username)}))} placeholder="Tutti i responsabili" className="min-w-[170px]"/>
      <Btn v="secondary" onClick={()=>{setSearch('');setStatusFilter('');setHiveFilter('');setOwnerFilter('')}}>Reset</Btn>
      <Btn icon={<IcPlus s={16}/>} onClick={()=>{setEditItem(null);setShowItemModal(true)}}>Nuova Mansione</Btn>
    </div>:null}

    {section==='dashboard'?<React.Fragment>
      <div className="stat-tile-grid">
        {[
          {label:'Cose da fare',value:dashboard?.personal?.mie_da_fare||myTodoItems.length,bg:'bg-blue-50'},
          {label:'Assegnati aperti',value:dashboard?.personal?.assegnati_aperti||myAssignedOpen.length,bg:'bg-slate-50'},
          {label:'Assegnati svolti',value:dashboard?.personal?.assegnati_svolti||myAssignedDone.length,bg:'bg-green-50'},
          {label:'Miei archiviati',value:dashboard?.personal?.mie_archiviate||myCompletedArchive.length,bg:'bg-emerald-50'},
          {label:'In ritardo',value:dashboard?.personal?.in_ritardo||0,bg:'bg-red-50'},
          {label:'In attesa',value:dashboard?.personal?.in_attesa||0,bg:'bg-amber-50'},
          {label:'Da approvare',value:dashboard?.personal?.da_approvare||0,bg:'bg-violet-50'},
          {label:'Inbox',value:myInboxItems.length,bg:'bg-slate-100'},
        ].map((kpi,i)=><div key={i} className={`stat-tile ${kpi.bg}`}><div className="text-xs text-gray-500">{kpi.label}</div><div className="text-2xl font-bold text-navy-600">{kpi.value}</div></div>)}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card title="Cose da fare">
          <div className="space-y-2">{myTodoItems.length===0?<div className="text-sm text-gray-400">Nessun compito in lavorazione.</div>:myTodoItems.slice(0,8).map(item=><div key={item.id} className="border rounded-xl p-3 bg-white">
            <button onClick={()=>{setEditItem(item);setShowItemModal(true)}} className="w-full text-left">
              <div className="font-medium text-navy-600">{S(item.title)}</div>
              <div className="text-xs text-gray-500 mt-1">{S(item.hive_name||'Senza reparto')} · assegnato da {S(item.created_by_name||'-')}</div>
            </button>
            <div className="mt-3 flex justify-end gap-2">
              <Btn s="sm" v="secondary" onClick={()=>markItemDone(item.id)}>Fatto</Btn>
            </div>
          </div>)}</div>
        </Card>
        <Card title="Compiti assegnati">
          <div className="space-y-2">{myAssignedOpen.length===0?<div className="text-sm text-gray-400">Nessun compito assegnato aperto.</div>:myAssignedOpen.slice(0,8).map(item=><button key={item.id} onClick={()=>{setEditItem(item);setShowItemModal(true)}} className="w-full text-left border rounded-xl p-3 hover:border-accent-300">
            <div className="font-medium text-navy-600">{S(item.title)}</div>
            <div className="text-xs text-gray-500 mt-1">A {S(item.owner_name||'Non assegnata')} · {hiveStatusLabel(item.status)}{item.due_at?` · ${fmtTaskDue(item.due_at,item.due_time)}`:''}</div>
          </button>)}</div>
        </Card>
        <Card title="Compiti assegnati svolti">
          <div className="space-y-2">{myAssignedDone.length===0?<div className="text-sm text-gray-400">Nessun compito assegnato già chiuso.</div>:myAssignedDone.slice(0,8).map(item=><button key={item.id} onClick={()=>{setEditItem(item);setShowItemModal(true)}} className="w-full text-left border rounded-xl p-3 hover:border-green-200 bg-green-50/50">
            <div className="font-medium text-navy-600">{S(item.title)}</div>
            <div className="text-xs text-gray-500 mt-1">Chiusa da {S(item.owner_name||'-')} · {item.completed_at?fmtDate(String(item.completed_at).slice(0,10)):'Completata'}</div>
          </button>)}</div>
        </Card>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card title="Priorità di oggi">
          <div className="space-y-2">{myToday.length===0?<div className="text-sm text-gray-400">Nessuna mansione con scadenza oggi.</div>:myToday.map(item=><button key={item.id} onClick={()=>{setEditItem(item);setShowItemModal(true)}} className="w-full text-left border rounded-lg px-3 py-2 hover:border-accent-300">
            <div className="font-medium text-navy-600">{S(item.title)}</div>
            <div className="text-xs text-gray-500">{S(item.hive_name||'Senza reparto')} · {hivePriorityLabel(item.priority)}</div>
          </button>)}</div>
        </Card>
        <Card title="Promemoria">
          <div className="space-y-2">{reminders.length===0?<div className="text-sm text-gray-400">Nessun promemoria.</div>:reminders.map(item=><button key={item.id} onClick={()=>{setEditItem(item);setShowItemModal(true)}} className="w-full text-left border rounded-lg px-3 py-2 hover:border-accent-300">
            <div className="font-medium text-navy-600">{S(item.title)}</div>
            <div className="text-xs text-gray-500">{hiveStatusLabel(item.status)} · {item.due_at?fmtTaskDue(item.due_at,item.due_time):'Nessuna data'}</div>
          </button>)}</div>
        </Card>
      </div>
    </React.Fragment>:null}

    {section==='dafare'?<div className="space-y-6">
      <Card title="Le tue cose da fare">
        <div className="space-y-3">{myTodoItems.length===0?<div className="text-sm text-gray-400">Nessun compito assegnato a te.</div>:myTodoItems.map(item=><div key={item.id} className="border rounded-2xl p-4 bg-white">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <button onClick={()=>{setEditItem(item);setShowItemModal(true)}} className="text-left flex-1">
              <div className="font-semibold text-navy-600">{S(item.title)}</div>
              <div className="text-sm text-gray-500 mt-1">{S(item.hive_name||'Senza reparto')} · assegnato da {S(item.created_by_name||'-')}</div>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className={`text-xs px-2 py-1 rounded-full ${hiveStatusClass(item.status)}`}>{hiveStatusLabel(item.status)}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${hivePriorityClass(item.priority)}`}>{hivePriorityLabel(item.priority)}</span>
                {item.due_at?<span className={`text-xs px-2 py-1 rounded-full ${item.is_overdue?'bg-red-100 text-red-700':'bg-slate-100 text-slate-700'}`}>{item.is_overdue?'Scaduto':'Scadenza'} · {fmtTaskDue(item.due_at,item.due_time)}</span>:null}
              </div>
            </button>
            <div className="flex gap-2 shrink-0">
              <Btn s="sm" v="secondary" onClick={()=>{setEditItem(item);setShowItemModal(true)}}>Apri</Btn>
              <Btn s="sm" onClick={()=>markItemDone(item.id)}>Fatto</Btn>
            </div>
          </div>
        </div>)}</div>
      </Card>
    </div>:null}

    {section==='assegnati'?<div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card title="Compiti assegnati aperti">
          <div className="space-y-3">{myAssignedOpen.length===0?<div className="text-sm text-gray-400">Nessun compito aperto assegnato da te.</div>:myAssignedOpen.map(item=><div key={item.id} className="border rounded-2xl p-4 bg-white">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <button onClick={()=>{setEditItem(item);setShowItemModal(true)}} className="text-left flex-1">
                <div className="font-semibold text-navy-600">{S(item.title)}</div>
                <div className="text-sm text-gray-500 mt-1">A {S(item.owner_name||'Non assegnata')} · {S(item.hive_name||'Senza reparto')}</div>
                <div className="text-xs text-gray-400 mt-1">{item.due_at?`Scadenza ${fmtTaskDue(item.due_at,item.due_time)}`:'Senza scadenza'} · {hiveStatusLabel(item.status)}</div>
              </button>
              <div className="flex gap-2 shrink-0">
                <Btn s="sm" v="secondary" onClick={()=>{setEditItem(item);setShowItemModal(true)}}>Apri</Btn>
                <Btn s="sm" v="danger" onClick={()=>deleteItem(item.id)}>Cancella</Btn>
              </div>
            </div>
          </div>)}</div>
        </Card>
        <Card title="Compiti assegnati svolti">
          <div className="space-y-3">{myAssignedDone.length===0?<div className="text-sm text-gray-400">Nessun compito svolto da chi hai incaricato.</div>:myAssignedDone.map(item=><button key={item.id} onClick={()=>{setEditItem(item);setShowItemModal(true)}} className="w-full text-left border rounded-2xl p-4 bg-green-50/50 hover:border-green-200">
            <div className="font-semibold text-navy-600">{S(item.title)}</div>
            <div className="text-sm text-gray-500 mt-1">Svolto da {S(item.owner_name||'-')}</div>
            <div className="text-xs text-gray-400 mt-1">{item.completed_at?`Completato il ${fmtDate(String(item.completed_at).slice(0,10))}`:'Completato'}</div>
          </button>)}</div>
        </Card>
      </div>
      <Card title="Tutti i compiti creati da te">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="bg-navy-600 text-white">
            <th className="px-3 py-2.5 text-left">Compito</th><th className="px-3 py-2.5 text-left">Assegnato a</th><th className="px-3 py-2.5 text-left">Stato</th><th className="px-3 py-2.5 text-left">Scadenza</th><th className="px-3 py-2.5 text-left"></th>
          </tr></thead>
          <tbody>{activeCreatedByMe.concat(myAssignedDone).map((item,i)=><tr key={item.id} className={`border-b ${i%2?'bg-gray-50/50':''}`}>
            <td className="px-3 py-2"><div className="font-medium text-navy-600">{S(item.title)}</div><div className="text-xs text-gray-400">{S(item.hive_name||'Senza reparto')}</div></td>
            <td className="px-3 py-2">{S(item.owner_name||'-')}</td>
            <td className="px-3 py-2"><span className={`text-xs px-2 py-1 rounded-full ${hiveStatusClass(item.status)}`}>{hiveStatusLabel(item.status)}</span></td>
            <td className="px-3 py-2">{item.due_at?fmtTaskDue(item.due_at,item.due_time):'-'}</td>
            <td className="px-3 py-2"><div className="flex gap-2 justify-end"><Btn s="sm" v="secondary" onClick={()=>{setEditItem(item);setShowItemModal(true)}}>Apri</Btn>{item.status!=='completato'?<Btn s="sm" v="danger" onClick={()=>deleteItem(item.id)}>Cancella</Btn>:null}</div></td>
          </tr>)}</tbody>
        </table></div>
      </Card>
    </div>:null}

    {section==='struttura'?<div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {[
          {label:'Persone visibili',value:people.length,color:'text-navy-600'},
          {label:'Manager',value:roleCounts.manager||0,color:'text-blue-700'},
          {label:'Operativi',value:roleCounts.operativo||0,color:'text-slate-700'},
          {label:'Amministrazione',value:areaCounts.amministrazione||0,color:'text-violet-700'},
          {label:'Tecnico',value:areaCounts.tecnico||0,color:'text-cyan-700'},
          {label:'Commerciale',value:areaCounts.commerciale||0,color:'text-amber-700'},
          {label:'Compiti assegnabili',value:assignablePeople.length,color:'text-emerald-700'},
        ].map((item,idx)=><Card key={idx}>
          <div className="text-xs text-gray-500">{item.label}</div>
          <div className={`text-2xl font-bold ${item.color}`}>{item.value}</div>
        </Card>)}
      </div>

      <Card title="Crescita della struttura">
        <div className="text-sm text-gray-600">
          L'organigramma cresce collegando ogni nuovo dipendente a un responsabile diretto e compilando il suo mansionario.
          {role==='admin'?' Puoi gestire persone, ruoli e linea gerarchica da Utenti e Gerarchie, mentre qui controlli il quadro operativo completo.':' Qui vedi in tempo reale sopra, sotto e pari livello della struttura che ti riguarda.'}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card title="La tua posizione">
          {currentPerson?<div className="space-y-2">
            <div className="font-semibold text-navy-600">{S(currentPerson.nome||currentPerson.username)}</div>
            <div className="text-sm text-gray-500">{S(currentPerson.role_label||HIVE_ROLE_LABELS[currentPerson.ruolo]||currentPerson.ruolo)}{currentPerson.job_title?` · ${S(currentPerson.job_title)}`:''}</div>
            <div className="text-xs text-gray-400">{myManagers.length?`${myManagers.length} responsabili configurati`:'Sei al vertice della gerarchia'}</div>
            <div className="text-xs text-gray-400">{`${myDirectReports.length} sotto diretti`}</div>
            {(departmentsByUser[String(currentPerson.id)]||[]).length?<div className="flex flex-wrap gap-1 pt-1">{(departmentsByUser[String(currentPerson.id)]||[]).map(name=><span key={name} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{S(name)}</span>)}</div>:null}
            {parseMansioniAssignments(currentPerson.mansioni_json).length?<div className="flex flex-wrap gap-1 pt-2">{parseMansioniAssignments(currentPerson.mansioni_json).map((mansione,idx)=><span key={idx} className="text-[11px] bg-navy-50 text-navy-600 px-2 py-1 rounded-full">{S((mansione.title||mansioneAreaLabel(mansione.area))+' · '+mansioneLevelLabel(mansione.level))}</span>)}</div>:null}
            <div className="text-sm text-gray-600">{S(currentPerson.mansioni||'Nessun mansionario compilato.')}</div>
          </div>:<div className="text-sm text-gray-400">Nessun profilo gerarchico disponibile.</div>}
        </Card>
        <Card title="Persone sopra di te">
          <div className="space-y-2">
            {myManagers.length===0?<div className="text-sm text-gray-400">Nessuna persona sopra di te configurata.</div>:myManagers.map(person=><div key={person.id} className="border rounded-lg px-3 py-2">
              <div className="font-medium text-navy-600">{S(person.nome||person.username)}</div>
              <div className="text-xs text-gray-500">{S(person.role_label||HIVE_ROLE_LABELS[person.ruolo]||person.ruolo)}{person.job_title?` · ${S(person.job_title)}`:''}</div>
            </div>)}
          </div>
        </Card>
        <Card title="Persone sotto di te">
          <div className="space-y-2">
            {myAllReports.length===0?<div className="text-sm text-gray-400">Nessuna persona sotto di te configurata.</div>:myAllReports.map(person=><div key={person.id} className="border rounded-lg px-3 py-2">
              <div className="font-medium text-navy-600">{S(person.nome||person.username)}</div>
              <div className="text-xs text-gray-500">{S(person.role_label||HIVE_ROLE_LABELS[person.ruolo]||person.ruolo)}{person.job_title?` · ${S(person.job_title)}`:''}</div>
              <div className="text-xs text-gray-400">{person.manager_name||person.manager_username?`Riporta a ${S(person.manager_name||person.manager_username)}`:'Senza responsabile diretto'}</div>
            </div>)}
          </div>
        </Card>
        <Card title="Pari livello">
          <div className="space-y-2">
            {peerPeople.length===0?<div className="text-sm text-gray-400">Nessun pari livello visibile.</div>:peerPeople.map(person=><div key={person.id} className="border rounded-lg px-3 py-2">
              <div className="font-medium text-navy-600">{S(person.nome||person.username)}</div>
              <div className="text-xs text-gray-500">{S(person.role_label||HIVE_ROLE_LABELS[person.ruolo]||person.ruolo)}{person.job_title?` · ${S(person.job_title)}`:''}</div>
              <div className="text-xs text-gray-400">{person.manager_name||person.manager_username?`Riporta a ${S(person.manager_name||person.manager_username)}`:'Stesso livello senza responsabile configurato'}</div>
            </div>)}
          </div>
        </Card>
      </div>

      <Card title="Organigramma Operativo">
        <div className="space-y-3">
          {orgRoots.length===0?<div className="text-sm text-gray-400">Nessuna gerarchia configurata.</div>:orgRoots.map(root=>renderHierarchyNode(root))}
        </div>
      </Card>

      <Card title="Riporti diretti">
        <div className="space-y-2">
          {myDirectReports.length===0?<div className="text-sm text-gray-400">Nessun riporto diretto configurato.</div>:myDirectReports.map(person=><div key={person.id} className="border rounded-lg px-3 py-2">
            <div className="font-medium text-navy-600">{S(person.nome||person.username)}</div>
            <div className="text-xs text-gray-500">{S(person.role_label||HIVE_ROLE_LABELS[person.ruolo]||person.ruolo)}{person.job_title?` · ${S(person.job_title)}`:''}</div>
            <div className="text-xs text-gray-400">{person.direct_reports_count?`${person.direct_reports_count} riporti diretti`: 'Nessun riporto diretto'}</div>
          </div>)}
        </div>
      </Card>

      <Card title="Mansionario Operativo">
        <div className="space-y-3">
          {sortedPeople.length===0?<div className="text-sm text-gray-400">Nessuna persona disponibile.</div>:sortedPeople.map(person=><div key={person.id} className="border rounded-xl p-4 bg-white">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-navy-600">{S(person.nome||person.username)}</div>
                <div className="text-sm text-gray-500">{S(person.role_label||HIVE_ROLE_LABELS[person.ruolo]||person.ruolo)}{person.job_title?` · ${S(person.job_title)}`:''}</div>
                <div className="text-xs text-gray-400 mt-1">{(person.manager_people||[]).length?`${(person.manager_people||[]).length} responsabili configurati`:'Senza responsabile diretto'}</div>
                {(departmentsByUser[String(person.id)]||[]).length?<div className="flex flex-wrap gap-1 mt-2">{(departmentsByUser[String(person.id)]||[]).map(name=><span key={name} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{S(name)}</span>)}</div>:null}
                {parseMansioniAssignments(person.mansioni_json).length?<div className="flex flex-wrap gap-1 mt-2">{parseMansioniAssignments(person.mansioni_json).map((mansione,idx)=><span key={idx} className="text-[11px] bg-blue-50 text-blue-700 px-2 py-1 rounded-full">{S((mansione.title||mansioneAreaLabel(mansione.area))+' · '+mansioneLevelLabel(mansione.level))}</span>)}</div>:null}
              </div>
              <div className="text-xs text-gray-400 shrink-0">{person.direct_reports_count?`${person.direct_reports_count} riporti diretti`:'Nessun riporto diretto'}</div>
            </div>
            <div className="text-sm text-gray-600 mt-3">{S(person.mansioni||'Mansionario non ancora compilato.')}</div>
          </div>)}
        </div>
      </Card>
    </div>:null}

    {section==='archiviate'?<div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card title="Archivio compiti svolti da te">
          <div className="space-y-3">
            {myCompletedArchive.length===0?<div className="text-sm text-gray-400">Nessun compito completato da te.</div>:myCompletedArchive.map(item=><div key={item.id} className="border rounded-xl p-4 bg-white">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="font-semibold text-navy-600">{S(item.title)}</div>
                  <div className="text-xs text-gray-500 mt-1">Assegnato da {S(item.created_by_name||'-')} · {S(item.hive_name||'Senza reparto')}</div>
                  <div className="text-xs text-gray-400 mt-1">{item.completed_at?`Archiviato il ${fmtDate(String(item.completed_at).slice(0,10))}`:'Archiviato'}</div>
                </div>
                <div className="flex gap-2">
                  <Btn s="sm" v="secondary" onClick={()=>{setEditItem(item);setShowItemModal(true)}}>Apri</Btn>
                </div>
              </div>
            </div>)}
          </div>
        </Card>
        <Card title="Compiti assegnati svolti">
          <div className="space-y-3">
            {myAssignedDone.length===0?<div className="text-sm text-gray-400">Nessun compito completato dalle persone a cui l'hai assegnato.</div>:myAssignedDone.map(item=><div key={item.id} className="border rounded-xl p-4 bg-white">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="font-semibold text-navy-600">{S(item.title)}</div>
                  <div className="text-xs text-gray-500 mt-1">Svolto da {S(item.owner_name||'-')} · {S(item.hive_name||'Senza reparto')}</div>
                  <div className="text-xs text-gray-400 mt-1">{item.completed_at?`Chiuso il ${fmtDate(String(item.completed_at).slice(0,10))}`:'Completato'}</div>
                </div>
                <div className="flex gap-2">
                  <Btn s="sm" v="secondary" onClick={()=>{setEditItem(item);setShowItemModal(true)}}>Apri</Btn>
                </div>
              </div>
            </div>)}
          </div>
        </Card>
      </div>
      <Card title="Archivio completo">
        <div className="space-y-3">
          {archivedItems.length===0?<div className="text-sm text-gray-400">Nessuna mansione archiviata.</div>:archivedItems.map(item=><div key={item.id} className="border rounded-xl p-4 bg-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="font-semibold text-navy-600">{S(item.title)}</div>
                <div className="text-xs text-gray-500 mt-1">{S(item.owner_name||'Non assegnata')} · assegnato da {S(item.created_by_name||'-')} · {S(item.hive_name||'Senza reparto')}</div>
                <div className="text-xs text-gray-400 mt-1">{item.completed_at?`Archiviata il ${fmtDate(String(item.completed_at).slice(0,10))}`:'Archiviata'}</div>
              </div>
              <div className="flex gap-2">
                <Btn s="sm" v="secondary" onClick={()=>{setEditItem(item);setShowItemModal(true)}}>Apri</Btn>
                <Btn s="sm" v="danger" onClick={()=>deleteItem(item.id)}>Cancella</Btn>
              </div>
            </div>
          </div>)}
        </div>
      </Card>
    </div>:null}

    {section==='reparti'?<div className="space-y-6">
      <div className="flex justify-end">{canManage?<Btn icon={<IcPlus s={16}/>} onClick={()=>{setEditHive(null);setShowHiveModal(true)}}>Nuovo Reparto</Btn>:null}</div>
      <Card title="Reparti impostati">
        <div className="text-sm text-gray-600">
          I reparti sono la base organizzativa. Puoi usarli per distribuire persone, capire il carico e costruire una struttura che cresce senza perdere chiarezza.
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {hives.map(h=><Card key={h.id} title={h.name} action={canManage?<Btn s="sm" v="secondary" onClick={()=>{setEditHive(h);setShowHiveModal(true)}}>Modifica</Btn>:null}>
          <div className="text-sm text-gray-500">{S(h.description||'Nessuna descrizione')}</div>
          <div className="mt-3 text-xs text-gray-500">{'Responsabile: '+S(h.manager_name||h.manager_username||'-')}</div>
          {h.parent_hive_id?<div className="mt-1 text-xs text-gray-500">{'Reparto superiore: '+S(hivesMap[String(h.parent_hive_id)]?.name||'-')}</div>:null}
          <div className="mt-2 text-xs text-gray-400">{(h.member_count||0)+' membri assegnati'}</div>
          <div className="mt-3 space-y-2">
            {(h.members||[]).length===0?<div className="text-xs text-gray-400">Nessuna persona assegnata a questo reparto.</div>:(h.members||[]).map(m=><div key={m.id} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2">
              <div>
                <div className="text-sm font-medium text-navy-600">{S(m.nome||m.username)}</div>
                <div className="text-xs text-gray-500">{S(HIVE_ROLE_LABELS[m.ruolo]||m.ruolo)}</div>
              </div>
              <span className="text-xs text-gray-400">{S(m.role_in_hive||'Membro')}</span>
            </div>)}
          </div>
        </Card>)}
      </div>
    </div>:null}

    {section==='riunioni'?<div className="space-y-4">
      <div className="flex justify-end"><Btn icon={<IcPlus s={16}/>} onClick={()=>{setEditMeeting(null);setShowMeetingModal(true)}}>Nuova Riunione</Btn></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {meetings.map(m=><Card key={m.id} title={m.title} action={<Btn s="sm" v="secondary" onClick={()=>{setEditMeeting(m);setShowMeetingModal(true)}}>Apri</Btn>}>
          <div className="text-sm text-gray-500">{S(m.description||'Nessuna nota')}</div>
          <div className="mt-3 text-xs text-gray-500">{'Partecipanti: '+((m.participants||[]).length||0)}</div>
          <div className="text-xs text-gray-500">{'Azioni generate: '+((m.action_item_ids||[]).length||0)}</div>
          <div className="text-xs text-gray-400 mt-2">{m.started_at?fmtDate(String(m.started_at).slice(0,10)):'-'}</div>
        </Card>)}
      </div>
    </div>:null}

    {section==='approvazioni'?<Card title="Approvazioni Mansioni">
      <div className="space-y-3">{approvals.length===0?<div className="text-sm text-gray-400">Nessuna approvazione.</div>:approvals.map(a=><div key={a.id} className="border rounded-xl p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="font-medium text-navy-600">{S(a.item_title||'Mansione')}</div>
          <div className="text-xs text-gray-500">{S(a.hive_name||'Senza reparto')} · {a.item_due_at?fmtTaskDue(a.item_due_at,a.item_due_time):'Senza data'} · {S(a.status)}</div>
        </div>
        <div className="flex gap-2">
          {a.status==='da_approvare'?<React.Fragment>
            <Btn s="sm" onClick={async()=>{try{await api('hive_approval_decide','PUT',{status:'approvato',decision_note:''},{id:a.id});refresh();showToast('Approvato')}catch(e){showToast(e.message,'error')}}}>Approva</Btn>
            <Btn s="sm" v="secondary" onClick={async()=>{try{await api('hive_approval_decide','PUT',{status:'in_attesa_integrazione',decision_note:''},{id:a.id});refresh();showToast('Rimandato')}catch(e){showToast(e.message,'error')}}}>Integrazione</Btn>
            <Btn s="sm" v="danger" onClick={async()=>{try{await api('hive_approval_decide','PUT',{status:'rifiutato',decision_note:''},{id:a.id});refresh();showToast('Rifiutato')}catch(e){showToast(e.message,'error')}}}>Rifiuta</Btn>
          </React.Fragment>:<span className={`text-xs px-2 py-1 rounded-full ${a.status==='approvato'?'bg-green-100 text-green-700':a.status==='rifiutato'?'bg-red-100 text-red-700':'bg-gray-100 text-gray-700'}`}>{S(a.status)}</span>}
        </div>
      </div>)}</div>
    </Card>:null}

    {section==='report'?<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Carico per persona">
        <div className="space-y-2">{teamLoad.length===0?<div className="text-sm text-gray-400">Nessun dato.</div>:teamLoad.map(([name,stats],idx)=><div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
          <span className="text-gray-600">{S(name)}</span>
          <span className="font-medium text-navy-600">{stats.open} aperti · {stats.overdue} scaduti · {stats.blocked} bloccati</span>
        </div>)}</div>
      </Card>
      <Card title="Colli di bottiglia">
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2 text-sm"><span className="text-red-700">Mansioni bloccate</span><strong className="text-red-700">{filteredItems.filter(i=>i.status==='bloccato').length}</strong></div>
          <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2 text-sm"><span className="text-amber-700">Mansioni senza update recenti</span><strong className="text-amber-700">{filteredItems.filter(i=>i.is_stale).length}</strong></div>
          <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2 text-sm"><span className="text-blue-700">Tempo medio presa in carico</span><strong className="text-blue-700">{filteredItems.length?Math.round(filteredItems.filter(i=>i.owner_user_id).length/filteredItems.length*100):0}% assegnati</strong></div>
        </div>
      </Card>
      <Card title="Audit Log Recente">
        <div className="space-y-2">{activity.length===0?<div className="text-sm text-gray-400">Nessuna attività tracciata.</div>:activity.slice(0,12).map(log=><div key={log.id} className="border rounded-lg px-3 py-2">
          <div className="text-sm font-medium text-navy-600">{S(log.action_label||log.action_type)}</div>
          <div className="text-xs text-gray-500">{S(log.user_name||log.username||'Sistema')} · {log.item_title?S(log.item_title):'Evento generale'} · {fmtDate(String(log.created_at||'').slice(0,10))}</div>
        </div>)}</div>
      </Card>
    </div>:null}

    {section==='settings'&&canSettings?<Card title="Impostazioni Mansioni">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Inp label="Reminder prima scadenza (giorni)" type="number" value={settings.hive_reminder_days||'1'} onChange={v=>setSettings(p=>({...p,hive_reminder_days:v}))}/>
        <Inp label="Mansioni ferme da (giorni)" type="number" value={settings.hive_stale_days||'3'} onChange={v=>setSettings(p=>({...p,hive_stale_days:v}))}/>
        <Inp label="Ora digest giornaliero" value={settings.hive_digest_hour||'08:00'} onChange={v=>setSettings(p=>({...p,hive_digest_hour:v}))}/>
      </div>
      <div className="mt-4 flex justify-end"><Btn v="success" onClick={saveHiveSettings}>Salva impostazioni</Btn></div>
    </Card>:null}

    {showItemModal?<HiveItemModal item={editItem} people={people} hives={hives} onClose={()=>setShowItemModal(false)} onSaved={()=>{setShowItemModal(false);refresh()}}/>:null}
    {showHiveModal?<HiveHiveModal hive={editHive} people={people} hives={hives} onClose={()=>setShowHiveModal(false)} onSaved={()=>{setShowHiveModal(false);refresh()}}/>:null}
    {showMeetingModal?<HiveMeetingModal meeting={editMeeting} people={people} hives={hives} onClose={()=>setShowMeetingModal(false)} onSaved={()=>{setShowMeetingModal(false);refresh()}}/>:null}
  </Page>);
}

// ========== MAIN APP ==========
function App(){
  const{user,isAdmin,isManager,isCommerciale,isOperativo,isHiveRole,ruolo,effectiveRole,realEffectiveRole,logout,canImpersonate,isImpersonating,impersonation,impersonationUsers,startImpersonation,stopImpersonation,permissionsEpoch}=useContext(AuthCtx);
  void permissionsEpoch;
  const{toast,setToast,settings}=useContext(DataCtx);
  const brandSettings={...INITIAL_BRAND_SETTINGS,...(settings||{})};
  const azienda=getAzienda(brandSettings);
  const isModulePage=APP_MODE==='module'&&PAGE_MODULE!=='hub';
  const moduleScope=MODULE_SCOPE;
  const pageAllowsTab=useCallback(sectionId=>{
    if(!moduleScope.length)return true;
    return moduleScope.includes(sectionId)||['profilo','manuale','utenti','impostazioni'].includes(sectionId);
  },[moduleScope]);
  const[printPrevId,setPrintPrevId]=useState(null);
  const[apiNotice,setApiNotice]=useState(null);
  const moduleCRMEnabled=settingEnabled(settings[MODULE_SETTING_KEYS.crm]);
  const modulePreventiviEnabled=settingEnabled(settings[MODULE_SETTING_KEYS.preventivi]);
  const moduleHiveEnabled=settingEnabled(settings[MODULE_SETTING_KEYS.hive]);
  const moduleAssistenzaEnabled=settingEnabled(settings[MODULE_SETTING_KEYS.assistenza]??'0');
  const moduleStimaConsumiEnabled=settingEnabled(settings[MODULE_SETTING_KEYS.stimaConsumi]??'0');
  const canAccessAssistenza=moduleAssistenzaEnabled&&String(ruolo||'').trim().toLowerCase()!=='rivenditore';
  const visibleSections=getEffectiveVisibleSectionsForUser(user);
  const isSectionVisible=useCallback(sectionId=>!visibleSections.length||visibleSections.includes(sectionId),[visibleSections]);
  const isSectionAllowed=useCallback(sectionId=>isSectionVisible(sectionId)&&pageAllowsTab(sectionId),[isSectionVisible,pageAllowsTab]);

  const RUOLO_LABELS=HIVE_ROLE_LABELS;
  const RUOLO_COLORS={
    admin:'bg-green-500/20 text-green-200',
    manager:'bg-blue-500/20 text-blue-200',
    commerciale:'bg-white/10 text-blue-200',
    rivenditore:'bg-white/10 text-cyan-200',
    operativo:'bg-slate-200/20 text-slate-100',
  };
  const openPrintForPreventivo=id=>{
    setPrintPrevId(id||null);
    setTab('stampa');
  };
  const isOperationalOnly=isOperativo||isHiveRole;
  const tabDefs={
    dashboard:{id:'dashboard',label:'Dashboard',icon:<IcHome s={18}/>},
    pipeline:{id:'pipeline',label:'Pipeline',icon:<IcTrend s={18}/>},
    preventivi:{id:'preventivi',label:'Preventivi',icon:<IcClipboard s={18}/>},
    stampa:{id:'stampa',label:'Stampa',icon:<IcPrint s={18}/>},
    stima_consumi:{id:'stima_consumi',label:'Stima consumi',icon:<IcChart s={18}/>},
    assistenza:{id:'assistenza',label:'Assistenza',icon:<IcWrench s={18}/>},
    clienti:{id:'clienti',label:'Clienti',icon:<IcUsers s={18}/>},
    spese:{id:'spese',label:'Nota spese',icon:<IcCard s={18}/>},
    listino:{id:'listino',label:'Listino',icon:<IcClipboard s={18}/>},
    catalogo:{id:'catalogo',label:'Catalogo',icon:<IcPkg s={18}/>},
    depliant:{id:'depliant',label:'Depliant',icon:<IcFile s={18}/>},
    analisi:{id:'analisi',label:'Analisi',icon:<IcChart s={18}/>},
    manuale:{id:'manuale',label:'Manuale',icon:<IcFile s={18}/>},
    profilo:{id:'profilo',label:'Profilo',icon:<IcUser s={18}/>},
    utenti:{id:'utenti',label:'Utenti',icon:<IcUser s={18}/>},
    hiveflow:{id:'hiveflow',label:'Mansioni',icon:<IcLayers s={18}/>},
    impostazioni:{id:'impostazioni',label:'Impostazioni',icon:<IcCogStack s={18}/>},
  };
  const moduleDefs=[
    moduleCRMEnabled?{id:'crm',label:'CRM',icon:<IcGrid s={18}/>,tabs:[
      (isAdmin||isManager)?tabDefs.dashboard:null,
      tabDefs.pipeline,
      (isAdmin||isManager)?tabDefs.analisi:null,
    ].filter(Boolean).filter(item=>isSectionAllowed(item.id))}:null,
    modulePreventiviEnabled?{id:'preventivi',label:'Preventivi',icon:<IcClipboard s={18}/>,tabs:[
      tabDefs.preventivi,
      tabDefs.stampa,
      moduleStimaConsumiEnabled?tabDefs.stima_consumi:null,
    ].filter(Boolean).filter(item=>isSectionAllowed(item.id))}:null,
    canAccessAssistenza?{id:'assistenza',label:'Assistenza',icon:<IcWrench s={18}/>,tabs:[
      tabDefs.assistenza,
    ].filter(item=>isSectionAllowed(item.id))}:null,
    moduleHiveEnabled?{id:'mansioni',label:'Mansioni',icon:<IcLayers s={18}/>,tabs:[
      tabDefs.hiveflow,
    ].filter(item=>isSectionAllowed(item.id))}:null,
  ].filter(Boolean).filter(item=>(item.tabs||[]).length>0);
  const utilityTabs=[
    isSectionAllowed('manuale')?tabDefs.manuale:null,
    tabDefs.profilo,
    isAdmin&&isSectionAllowed('utenti')?tabDefs.utenti:null,
    (isAdmin||isManager)&&isSectionAllowed('impostazioni')?tabDefs.impostazioni:null,
  ].filter(Boolean);
  const mainTabs=[
    isSectionAllowed('clienti')?tabDefs.clienti:null,
    isSectionAllowed('spese')?tabDefs.spese:null,
    isSectionAllowed('listino')?tabDefs.listino:null,
    isSectionAllowed('catalogo')?tabDefs.catalogo:null,
    isSectionAllowed('depliant')?tabDefs.depliant:null,
    ...moduleDefs,
  ].filter(Boolean);
  const utilityTabIds=utilityTabs.map(item=>item.id);
  const availableStandaloneTabIds=mainTabs.filter(item=>!('tabs' in item)).map(item=>item.id);
  const availableModuleTabIds=moduleDefs.flatMap(item=>(item.tabs||[]).map(tab=>tab.id));
  const availableTabIds=[...availableStandaloneTabIds,...availableModuleTabIds,...utilityTabIds];
  const hasBusinessTabs=availableStandaloneTabIds.length>0||availableModuleTabIds.length>0;
  const preferredStartTabs=[...(DEFAULT_TAB_OVERRIDE?[DEFAULT_TAB_OVERRIDE]:[]),'dashboard','preventivi','pipeline','spese','hiveflow','profilo','listino','assistenza','clienti','catalogo','depliant','manuale','utenti','impostazioni'];
  const defaultTab=preferredStartTabs.find(id=>availableTabIds.includes(id))||availableTabIds[0]||'profilo';
  const defaultModule=moduleDefs.find(item=>(item.tabs||[]).some(tab=>tab.id===defaultTab))?.id||moduleDefs[0]?.id||'';
  const[tab,setTab]=useState(defaultTab);
  const[visitedTabs,setVisitedTabs]=useState(()=>new Set([defaultTab]));
  const[activeModule,setActiveModule]=useState(defaultModule);
  useEffect(()=>{
    setVisitedTabs(prev=>{
      if(prev.has(tab))return prev;
      const next=new Set(prev);
      next.add(tab);
      return next;
    });
  },[tab]);
  useEffect(()=>{
    const handler=e=>{
      const detail=e?.detail||{};
      if(detail.state==='warning'){
        setApiNotice({type:'warning',message:detail.message||'Connessione instabile con il server.'});
        return;
      }
      if(detail.state==='connected'){
        setApiNotice(null);
      }
    };
    window.addEventListener('complevia-api-status',handler);
    return()=>window.removeEventListener('complevia-api-status',handler);
  },[]);
  const goToDefaultNavigation=useCallback((nextUser)=>{
    const nextVisible=getEffectiveVisibleSectionsForUser(nextUser);
    const nextHas=sectionId=>nextVisible.includes(sectionId);
    const nextModuleDefs=[
      moduleCRMEnabled?{id:'crm',tabs:[
        (['admin','manager'].includes(effectiveMansioniRole(nextUser?.ruolo)))&&nextHas('dashboard')?tabDefs.dashboard:null,
        nextHas('pipeline')?tabDefs.pipeline:null,
        (['admin','manager'].includes(effectiveMansioniRole(nextUser?.ruolo)))&&nextHas('analisi')?tabDefs.analisi:null,
      ].filter(Boolean)}:null,
      modulePreventiviEnabled?{id:'preventivi',tabs:[
        nextHas('preventivi')?tabDefs.preventivi:null,
        nextHas('stampa')?tabDefs.stampa:null,
        moduleStimaConsumiEnabled&&nextHas('stima_consumi')?tabDefs.stima_consumi:null,
      ].filter(Boolean)}:null,
      (moduleAssistenzaEnabled&&String(nextUser?.ruolo||'').trim().toLowerCase()!=='rivenditore')?{id:'assistenza',tabs:[nextHas('assistenza')?tabDefs.assistenza:null].filter(Boolean)}:null,
      moduleHiveEnabled?{id:'mansioni',tabs:[nextHas('hiveflow')?tabDefs.hiveflow:null].filter(Boolean)}:null,
    ].filter(Boolean).filter(item=>(item.tabs||[]).length>0);
    const nextStandalone=[
      nextHas('spese')?'spese':null,
      nextHas('listino')?'listino':null,
      nextHas('clienti')?'clienti':null,
      (moduleCRMEnabled&&['admin','manager'].includes(effectiveMansioniRole(nextUser?.ruolo))&&nextHas('catalogo'))?'catalogo':null,
      nextHas('depliant')?'depliant':null,
    ].filter(Boolean);
    const nextUtility=[
      'profilo',
      nextHas('manuale')?'manuale':null,
      (effectiveMansioniRole(nextUser?.ruolo)==='admin'&&nextHas('utenti'))?'utenti':null,
      (['admin','manager'].includes(effectiveMansioniRole(nextUser?.ruolo))&&nextHas('impostazioni'))?'impostazioni':null,
    ].filter(Boolean);
    const preferredNextTabs=[
      nextModuleDefs.find(mod=>(mod.tabs||[]).some(item=>item.id==='dashboard'))?.tabs?.find(item=>item.id==='dashboard')?.id||null,
      nextModuleDefs.find(mod=>(mod.tabs||[]).some(item=>item.id==='preventivi'))?.tabs?.find(item=>item.id==='preventivi')?.id||null,
      nextModuleDefs.find(mod=>(mod.tabs||[]).some(item=>item.id==='pipeline'))?.tabs?.find(item=>item.id==='pipeline')?.id||null,
      nextStandalone[0]||null,
      nextModuleDefs[0]?.tabs?.[0]?.id||null,
      nextUtility[0]||null,
      'profilo',
    ].filter(Boolean);
    const nextTab=preferredNextTabs[0]||'profilo';
    const owningModule=nextModuleDefs.find(mod=>(mod.tabs||[]).some(item=>item.id===nextTab))||null;
    setActiveModule(owningModule?.id||'');
    setTab(nextTab);
  },[moduleCRMEnabled,modulePreventiviEnabled,moduleAssistenzaEnabled,moduleHiveEnabled,moduleStimaConsumiEnabled]);
  useEffect(()=>{
    setActiveModule('');
    setTab(defaultTab);
  },[user?.id,defaultModule,defaultTab]);
  const standaloneTabIds=['clienti','spese','listino','catalogo','depliant'].filter(isSectionAllowed);
  useEffect(()=>{
    if(standaloneTabIds.includes(tab)){
      if(activeModule)setActiveModule('');
      return;
    }
    if(utilityTabIds.includes(tab)){
      if(activeModule)setActiveModule('');
      return;
    }
    const currentModule=moduleDefs.find(mod=>mod.id===activeModule)||null;
    const currentModuleHasTab=!!currentModule?.tabs?.some(item=>item.id===tab);
    if(currentModuleHasTab)return;
    const owningModule=moduleDefs.find(mod=>(mod.tabs||[]).some(item=>item.id===tab))||null;
    if(owningModule&&owningModule.id!==activeModule){
      setActiveModule(owningModule.id);
    }
  },[tab,moduleDefs,activeModule,utilityTabIds]);
  useEffect(()=>{
    if(moduleDefs.length===0||!activeModule)return;
    const currentModule=moduleDefs.find(mod=>mod.id===activeModule)||moduleDefs[0];
    const subTabs=currentModule?.tabs||[];
    if(subTabs.length>0&&!subTabs.some(t=>t.id===tab)){
      setTab(subTabs[0].id);
    }
  },[tab,moduleDefs,activeModule]);
  const isUtilityTab=utilityTabIds.includes(tab);
  const activeModuleDef=activeModule?moduleDefs.find(mod=>mod.id===activeModule)||null:null;
  const subTabs=!isUtilityTab&&!standaloneTabIds.includes(tab)?(activeModuleDef?.tabs||[]):[];
  const impersonationValue=isImpersonating?String(user?.id||''):'';
  const realRoleLabel=RUOLO_LABELS[realEffectiveRole]||RUOLO_LABELS[impersonation?.real_role]||realEffectiveRole;
  const{device,isPhone,isTablet}=useDevice();
  const[mobileMenuOpen,setMobileMenuOpen]=useState(false);
  useEffect(()=>{setMobileMenuOpen(false)},[tab,activeModule]);
  const navigateTab=(nextTab,nextModule='')=>{
    setActiveModule(nextModule||'');
    setTab(nextTab);
  };
  const isModuleTabActive=modId=>{
    const mod=moduleDefs.find(item=>item.id===modId);
    return !!mod&&(mod.tabs||[]).some(item=>item.id===tab);
  };
  const moduleSwitcherLinks=useMemo(()=>{
    const permissionMap={clienti:'clienti',preventivi:'preventivi',assistenza:'assistenza',spese:'spese',hub:''};
    return MODULE_LINKS.filter(link=>{
      const id=String(link?.id||'');
      if(id==='hub')return true;
      const section=permissionMap[id];
      if(!section)return false;
      if(section==='assistenza')return canAccessAssistenza&&isSectionVisible('assistenza');
      return isSectionVisible(section);
    });
  },[canAccessAssistenza,isSectionVisible]);
  const bottomNavItems=useMemo(()=>{
    const items=[];
    const push=(id,label,icon,onClick,isActive)=>{
      if(!availableTabIds.includes(id))return;
      items.push({id,label,icon,onClick,isActive});
    };
    if(isSectionAllowed('clienti'))push('clienti','Clienti',<IcUsers s={18}/>,()=>navigateTab('clienti',''),tab==='clienti');
    if(modulePreventiviEnabled&&isSectionAllowed('preventivi'))push('preventivi','Offerte',<IcClipboard s={18}/>,()=>{
      const mod=moduleDefs.find(m=>m.id==='preventivi');
      navigateTab(mod?.tabs?.[0]?.id||'preventivi','preventivi');
    },tab==='preventivi'||tab==='stampa'||tab==='stima_consumi'||activeModule==='preventivi');
    const crmMod=moduleDefs.find(m=>m.id==='crm');
    if(crmMod?.tabs?.length)push(crmMod.tabs[0].id,'CRM',crmMod.icon,()=>navigateTab(crmMod.tabs[0].id,'crm'),activeModule==='crm');
    const assistMod=moduleDefs.find(m=>m.id==='assistenza');
    if(assistMod?.tabs?.length)push('assistenza','Assist.',<IcWrench s={18}/>,()=>navigateTab('assistenza','assistenza'),tab==='assistenza');
    items.push({id:'__menu',label:'Altro',icon:<IcGrid s={18}/>,onClick:()=>setMobileMenuOpen(true),isActive:mobileMenuOpen||isUtilityTab});
    return items.slice(0,5);
  },[tab,activeModule,mobileMenuOpen,isUtilityTab,moduleDefs,availableTabIds,modulePreventiviEnabled,isSectionAllowed]);
  const renderMainNav=compact=>mainTabs.map(item=>('tabs' in item)?(
    <SegmentedNavButton key={item.id} compact={compact}
      active={!isUtilityTab&&activeModule===item.id&&!['clienti','listino','catalogo','spese','depliant'].includes(tab)}
      onClick={()=>{setActiveModule(item.id);if(!(item.tabs||[]).some(tabItem=>tabItem.id===tab))setTab(item.tabs?.[0]?.id||tab);}}
      icon={item.icon} label={item.label}/>
  ):(
    <SegmentedNavButton key={item.id} compact={compact} active={tab===item.id}
      onClick={()=>{setActiveModule('');setTab(item.id);}}
      icon={item.icon} label={item.label}/>
  ));
  const currentPageLabel=isModulePage?PAGE_TITLE:(tabDefs[tab]?.label||subTabs.find(item=>item.id===tab)?.label||'Complevia');

  if(!user)return<LoginPage/>;

  return(<div className={`app-shell cplv-ui-mac mobile-safe device-${device}`}>
    <div className="hero-orb w-72 h-72 bg-sky-100 top-[-80px] left-[-60px]"></div>
    <div className="hero-orb w-80 h-80 bg-blue-100 top-[120px] right-[-80px]"></div>
    <header className={`no-print relative z-30 px-3 pt-3 md:px-5 md:pt-5 ${isPhone?'app-header-compact':''}`}>
      <div className="glass-bar max-w-[1400px] mx-auto rounded-[24px] md:rounded-[30px] px-3 py-2.5 md:px-6 md:py-4 text-slate-900">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {isPhone?<button type="button" className="app-header-menu-btn shrink-0" aria-label="Menu" onClick={()=>setMobileMenuOpen(true)}><IcMenu s={18}/></button>:null}
          <div className="toolbar-chip !px-2.5 !py-1.5 md:!px-3 md:!py-2 shrink-0">
            <CompanyLogo settings={brandSettings} maxH={isPhone?28:36} maxW={isPhone?'120px':'180px'} className="shrink-0"/>
          </div>
          <div className="min-w-0 flex-1">
            {!isPhone?<p className="section-hint">Complevia</p>:null}
            <h1 className="font-bold text-sm md:text-[1.15rem] tracking-[-0.04em] text-slate-900 truncate">{S(azienda.nome)}</h1>
            {isPhone?<p className="text-xs text-slate-500 truncate">{S(currentPageLabel)}</p>:null}
          </div>
          <div className="app-header-actions--desktop flex flex-col gap-2 md:items-end shrink-0">
            <span className="text-xs text-slate-500 hidden lg:block">{new Date().toLocaleDateString('it-IT',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</span>
            <div className="flex items-center gap-2 flex-wrap md:justify-end">
              {canImpersonate?<div className="toolbar-chip hidden xl:flex">
                <IcEye s={14} c="text-slate-400"/>
                <select value={impersonationValue} onChange={async e=>{
                  const value=e.target.value;
                  try{
                    if(!value){
                      const nextUser=await stopImpersonation();
                      showToast('Rientrato in modalità amministratore');
                      goToDefaultNavigation(nextUser);
                    }else{
                      const target=impersonationUsers.find(row=>String(row.id)===String(value));
                      const nextUser=await startImpersonation(Number(value));
                      showToast(`Stai simulando ${S(target?.nome||target?.username||'utente')}`);
                      goToDefaultNavigation(nextUser);
                    }
                  }catch(err){showToast(err.message,'error')}
                }} className="bg-transparent text-xs font-semibold text-slate-700 outline-none max-w-[180px]">
                  <option value="">Simula utente</option>
                  {impersonationUsers.map(item=><option key={item.id} value={String(item.id)}>{S(item.nome||item.username)} · {S(item.role_label||item.ruolo||'Utente')}</option>)}
                </select>
              </div>:null}
              {!isPhone?utilityTabs.map(item=><Btn key={item.id} s="sm" v={tab===item.id?'primary':'secondary'} onClick={()=>navigateTab(item.id,'')} className="!px-3.5 !py-2">{item.icon}{item.label}</Btn>):null}
              <span className="toolbar-chip text-xs font-medium hidden sm:inline-flex max-w-[220px] truncate">
                <IcUser s={12}/><span className="truncate">{(user.nome||user.username)}</span></span>
              <Btn s="sm" v="ghost" onClick={logout} className="!px-3 !py-2"><IcLogout s={14}/>{!isPhone?' Esci':''}</Btn>
            </div>
          </div>
        </div>
      </div>
    </header>

    <nav className="no-print relative z-20 px-3 pt-2 md:px-5 md:pt-4 space-y-2 md:space-y-3">
      {apiNotice?<div className="max-w-[1400px] mx-auto">
        <div className="rounded-[22px] border border-amber-200 bg-amber-50/95 px-4 py-3 text-sm text-amber-800 shadow-[0_10px_24px_rgba(15,23,42,.05)] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0"><IcAlert s={16}/><span className="truncate">{apiNotice.message}</span></div>
          <button type="button" onClick={()=>setApiNotice(null)} className="text-amber-700 hover:text-amber-900 text-xs font-semibold shrink-0">Chiudi</button>
        </div>
      </div>:null}
      {isModulePage?<div className="max-w-[1400px] mx-auto">
        <div className="glass-bar rounded-[24px] p-2">
          <div className="segmented-wrap no-scrollbar">
            {moduleSwitcherLinks.map(link=><a key={link.id} href={link.url} className={`toolbar-chip !no-underline ${String(link.id)===String(PAGE_MODULE)?'bg-blue-600 text-white border-blue-500':'text-slate-700'}`}>{link.title}</a>)}
          </div>
        </div>
      </div>:null}
      {!isModulePage?<div className="app-nav-desktop max-w-[1400px] mx-auto">
        <div className="glass-bar rounded-[28px] p-2 md:p-2.5">
          <div className="segmented-wrap">{renderMainNav(false)}</div>
        </div>
      </div>:null}
      {!isModulePage?<div className="app-nav-tablet max-w-[1400px] mx-auto">
        <div className="glass-bar rounded-[24px] p-2 app-nav-tablet">
          <div className="segmented-wrap no-scrollbar">{renderMainNav(true)}</div>
        </div>
      </div>:null}
      {subTabs.length>0&&activeModule?<div className={`max-w-[1400px] mx-auto ${isPhone?'px-0':'px-0'}`}>
        <div className={`glass-bar rounded-[20px] md:rounded-[24px] p-2 ${isPhone?'mx-0':''}`}>
          <div className={`${isPhone||isTablet?'app-subnav-chips no-scrollbar':'segmented-wrap'}`}>
            {subTabs.map(t=><SegmentedNavButton key={t.id} compact={isPhone||isTablet} active={tab===t.id} onClick={()=>setTab(t.id)} icon={t.icon} label={t.label}/>)}
          </div>
        </div>
      </div>:null}
    </nav>

    <main className="app-main">
      {!hasBusinessTabs?<Empty icon={<IcSettings s={36} c="text-accent-400"/>} title="Nessun modulo attivo" sub="L'amministratore deve abilitare almeno un modulo nelle impostazioni." action={(isAdmin||isManager)?<Btn onClick={()=>{setActiveModule('');setTab('impostazioni')}}>Apri impostazioni</Btn>:null}/>:null}
      {visitedTabs.has('dashboard')&&tab==='dashboard'&&moduleCRMEnabled&&(isAdmin||isManager)&&<DashboardPage/>}
      {visitedTabs.has('hiveflow')&&tab==='hiveflow'&&moduleHiveEnabled&&<HiveFlowPage/>}
      {visitedTabs.has('pipeline')&&tab==='pipeline'&&moduleCRMEnabled&&<PipelinePage/>}
      {visitedTabs.has('preventivi')&&tab==='preventivi'&&modulePreventiviEnabled&&<PreventiviPage onOpenPrint={openPrintForPreventivo}/>}
      {visitedTabs.has('assistenza')&&tab==='assistenza'&&canAccessAssistenza&&<AssistenzaPage/>}
      {visitedTabs.has('clienti')&&tab==='clienti'&&<ClientiPage/>}
      {visitedTabs.has('spese')&&tab==='spese'&&<SpesePage/>}
      {visitedTabs.has('listino')&&tab==='listino'&&<ListinoPage/>}
      {visitedTabs.has('catalogo')&&tab==='catalogo'&&moduleCRMEnabled&&(isAdmin||isManager)&&<CatalogoPage/>}
      {visitedTabs.has('depliant')&&tab==='depliant'&&<DepliantPage/>}
      {visitedTabs.has('analisi')&&tab==='analisi'&&moduleCRMEnabled&&(isAdmin||isManager)&&<AnalisiPage/>}
      {visitedTabs.has('stampa')&&tab==='stampa'&&modulePreventiviEnabled&&<StampaPage initialPrevId={printPrevId}/>}
      {visitedTabs.has('stima_consumi')&&tab==='stima_consumi'&&moduleStimaConsumiEnabled&&<StimaConsumiPage/>}
      {visitedTabs.has('manuale')&&tab==='manuale'&&<ManualePage/>}
      {visitedTabs.has('profilo')&&tab==='profilo'&&<ProfiloPage/>}
      {visitedTabs.has('utenti')&&tab==='utenti'&&isAdmin&&<UtentiPage/>}
      {visitedTabs.has('impostazioni')&&tab==='impostazioni'&&(isAdmin||isManager)&&<ImpostazioniPage/>}
    </main>

    {isPhone&&!isModulePage?<nav className="app-bottom-nav no-print" aria-label="Navigazione principale">
      {bottomNavItems.map(item=><button key={item.id} type="button" className={`app-bottom-nav__item ${item.isActive?'is-active':''}`} onClick={item.onClick}>
        <span className="app-bottom-nav__icon">{item.icon}</span>
        <span className="truncate w-full text-center">{item.label}</span>
      </button>)}
    </nav>:null}

    {isPhone&&mobileMenuOpen?<div className="app-menu-sheet no-print" role="dialog" aria-modal="true" onClick={()=>setMobileMenuOpen(false)}>
      <div className="app-menu-sheet__panel" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div><p className="section-hint">Navigazione</p><h3 className="text-lg font-bold text-slate-900">{S(azienda.nome)}</h3></div>
          <button type="button" aria-label="Chiudi menu" onClick={()=>setMobileMenuOpen(false)} className="w-11 h-11 rounded-full border border-white/90 bg-white/90 inline-flex items-center justify-center"><IcX s={20}/></button>
        </div>
        <div className="app-menu-sheet__group">
          <div className="app-menu-sheet__label">Sezioni</div>
          {mainTabs.filter(item=>!('tabs' in item)).map(item=><button key={item.id} type="button" className={`app-menu-sheet__item ${tab===item.id?'is-active':''}`} onClick={()=>navigateTab(item.id,'')}>
            <span className="w-9 h-9 rounded-full bg-slate-100 inline-flex items-center justify-center">{item.icon}</span><span>{item.label}</span>
          </button>)}
        </div>
        {moduleDefs.map(mod=><div key={mod.id} className="app-menu-sheet__group">
          <div className="app-menu-sheet__label">{mod.label}</div>
          {(mod.tabs||[]).map(item=><button key={item.id} type="button" className={`app-menu-sheet__item ${tab===item.id?'is-active':''}`} onClick={()=>navigateTab(item.id,mod.id)}>
            <span className="w-9 h-9 rounded-full bg-slate-100 inline-flex items-center justify-center">{item.icon}</span><span>{item.label}</span>
          </button>)}
        </div>)}
        <div className="app-menu-sheet__group">
          <div className="app-menu-sheet__label">Account</div>
          {utilityTabs.map(item=><button key={item.id} type="button" className={`app-menu-sheet__item ${tab===item.id?'is-active':''}`} onClick={()=>navigateTab(item.id,'')}>
            <span className="w-9 h-9 rounded-full bg-slate-100 inline-flex items-center justify-center">{item.icon}</span><span>{item.label}</span>
          </button>)}
          {canImpersonate&&impersonationUsers.length>0?<div className="px-2 py-2">
            <label className="text-xs text-slate-500 block mb-1">Simula utente</label>
            <select value={impersonationValue} onChange={async e=>{
              const value=e.target.value;
              try{
                if(!value){const nextUser=await stopImpersonation();showToast('Rientrato in modalità amministratore');goToDefaultNavigation(nextUser);setMobileMenuOpen(false)}
                else{const nextUser=await startImpersonation(Number(value));showToast('Simulazione attiva');goToDefaultNavigation(nextUser);setMobileMenuOpen(false)}
              }catch(err){showToast(err.message,'error')}
            }} className="field-shell w-full px-3 py-2.5 rounded-[18px] text-sm">
              <option value="">Nessuna simulazione</option>
              {impersonationUsers.map(item=><option key={item.id} value={String(item.id)}>{S(item.nome||item.username)}</option>)}
            </select>
          </div>:null}
          <button type="button" className="app-menu-sheet__item" onClick={()=>{setMobileMenuOpen(false);logout();}}>
            <span className="w-9 h-9 rounded-full bg-rose-50 text-rose-600 inline-flex items-center justify-center"><IcLogout s={18}/></span><span>Esci</span>
          </button>
        </div>
      </div>
    </div>:null}

    {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
  </div>);
}

function Root(){
  return<ErrorBoundary><DebugClientBridge/><AuthProvider><DataProvider><UiProvider><App/></UiProvider></DataProvider></AuthProvider></ErrorBoundary>;
}
const rootEl=document.getElementById('root');
if(rootEl){
  try{
    const bootFallback=document.getElementById('cplvcrm-boot-fallback');
    if(bootFallback)bootFallback.remove();
    ReactDOM.createRoot(rootEl).render(<Root/>);
  }catch(err){
    console.error('Complevia boot error',err);
    rootEl.innerHTML='<div style="padding:24px;font-family:system-ui,sans-serif;max-width:640px;margin:40px auto"><h2 style="color:#dc2626">Errore avvio applicazione</h2><p style="color:#475569">Ricarica la pagina. Se il problema resta, controlla la console del browser o reinstalla il plugin.</p><pre style="background:#f8fafc;padding:12px;border-radius:8px;font-size:12px;overflow:auto">'+String(err&&err.message||err)+'</pre></div>';
  }
}
